/**
 * PAGE: Ball By Ball
 *
 * RESPONSIBILITY:
 * - Layout container for scoring UI
 *
 * MUST NEVER:
 * - Contain cricket logic
 * - Mutate backend state directly
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useScoringStore } from "../../store/scoring.store";
import { emptyBallDraft, useBallInput } from "../../context/BallInputContext";
import { loadBallSession } from "../../hooks/useBallSession";
import { useMatchRecorder } from "../../hooks/useMatchRecorder";
import { useFileVideoUpload } from "../../hooks/useFileVideoUpload";
import { saveLocalMatchFile, clearLocalMatchFile } from "../../hooks/useLocalMatchFile";
import SpatialPicker from "../../components/ballbyball/SpatialPicker";
import ReleaseTrajectoryPicker from "../../components/ballbyball/ReleaseTrajectoryPicker";
import VideoPlayer from "../../components/ballbyball/VideoPlayer";
import MatchRecorderControls from "../../components/ballbyball/MatchRecorderControls";
import ImpactOnBatsmanPicker from "../../components/ballbyball/ImpactOnBatsmanPicker";
import ScoreRibbon from "../../components/ballbyball/ScoreRibbon";
import RecorderStatusPill from "../../components/ballbyball/RecorderStatusPill";
import CodingHealthBar from "../../components/ballbyball/CodingHealthBar";
import FinalisingOverlay from "../../components/ballbyball/FinalisingOverlay";
import CodingWalkthrough from "../../components/ballbyball/CodingWalkthrough";
import VoiceScoringPanel from "../../components/ballbyball/VoiceScoringPanel";
import VoiceTutorialModal, { shouldShowVoiceTour, clearVoiceTourSeen } from "../../components/ballbyball/VoiceTutorialModal";
import VoiceCalibrationGauntlet, { shouldShowCalibration, clearCalibrationFlag } from "../../components/ballbyball/VoiceCalibrationGauntlet";
import VoiceIntentPrompt, { getVoiceIntent, setVoiceIntent } from "../../components/ballbyball/VoiceIntentPrompt";
import { useVoiceScoring, parseVoicePhrase } from "../../hooks/useVoiceScoring";
import { useBallReadback, ballOutcomeSummary } from "../../hooks/useBallReadback";
import { useScoringGuardRails, dismissGuardRail } from "../../hooks/useScoringGuardRails";
import { useScoringHistoryLog } from "../../hooks/useScoringHistoryLog";
import { useUndoBookmarks } from "../../hooks/useUndoBookmarks";
import { trackEvent } from "../../lib/trackEvent";
import api from "../../api/axios";
import OfflineIndicator from "../../components/ballbyball/OfflineIndicator";
import { useBallSyncQueue } from "../../hooks/useBallSyncQueue";
import ZoneMatrixPicker from "../../components/ballbyball/ZoneMatrixPicker";
import EditBallDialog from "../../components/ballbyball/EditBallDialog";
import SubstitutionModal from "../../components/ballbyball/SubstitutionModal";
import EditPlayingXIModal from "../../components/ballbyball/EditPlayingXIModal";
import LiveScorecardModal from "../../components/ballbyball/LiveScorecardModal";
import ScorerChat from "../../components/ballbyball/ScorerChat";
import { SyncPill, IntegrityBadge, CopyLiveLinkButton, SaveCounterPill } from "../../components/ballbyball/HeaderBadges";
import { useTranslation } from "../../i18n/useTranslation";
import { useScorerLock } from "../../hooks/useScorerLock";
import ActivityWizard from "../../components/ballbyball/activity/ActivityWizard";
import ActivityTracker from "../../components/ballbyball/activity/ActivityTracker";
import ActivityAccordion from "../../components/ballbyball/activity/ActivityAccordion";
import VoiceCommandsHelp from "../../components/ballbyball/VoiceCommandsHelp";
import ProgressDots from "../../components/ballbyball/activity/ProgressDots";
import MissingFieldsChips from "../../components/ballbyball/activity/MissingFieldsChips";
import CricketFieldEngine from "../../field-engine/CricketFieldEngine";
import { resolvePosition as resolveFieldingPosition } from "../../field-engine/positionResolver";
import {
  saveBall,
  newIdempotencyKey,
  undoBall,
  undoLastNBalls,
  listBallTrash,
  restoreBallFromTrash,
  endActiveInnings,
  startNextInnings,
  applyDls,
  applyPenalty,
  endMatch,
  declareInnings,
  listInningsBalls,
  getBallForEdit,
  updateBall,
  setFirstOverBowlingEnd,
  startPowerSurge,
  takeFloatingPowerplay,
  takeStrategicTimeout,
} from "../../api/ballbyball.api";
import { ClipboardList, RefreshCcw, RotateCcw, RotateCw, Sparkles, MessageSquare, Zap, History, Mic, Gauge, MoreVertical } from "lucide-react";
import { aiTagMatch, aiSuggestBall, aiScoreBall, getMatchPartnerships } from "../../api/matches.api";
import useAuthStore from "../../store/auth.store";
import useWalletStore from "../../store/wallet.store";
import { useToast } from "../../components/common/ToastHost";
import QcLink from "../../components/common/QcLink";
import { matchPlayer } from "../../utils/playerMatch";
import { formatBallReference, formatInningsProgress, runRates } from "../../utils/hundredDisplay";
import {
  setPending as setPendingAiObservation,
  commitForBall as commitAiObservationForBall,
  fireObserveOnSave as fireAiObserveOnSave,
} from "../../utils/aiObservationStash";

// Pricing v2: AI Tag + AI Suggest are gated to TRIAL + B2B contracts only
// (paid B2C tiers don't include them). Source of truth is the user's
// `features` array from /api/accounts/me/ — populated server-side from
// PlanFeature. SPA gating is UX-only; backend enforces.

// AI Score visibility kill-switch. 2026-06-10 — founder hid the action
// pending an accuracy re-validation pass; backend endpoint, handler, and
// the rest of the wiring are intact, so flipping this back to `true`
// re-exposes the button without any other edits.
const SHOW_AI_SCORE = false;

// Tooltip glossary for the wicket-modal buttons. New B2C scorers (freelance,
// not lifelong umpires) won't recognise jargon like MANKAD or HIT_BALL_TWICE
// — these one-line explainers surface on hover.
const WICKET_TYPE_HINTS = {
  BOWLED: "Ball hits stumps after passing the bat",
  CAUGHT: "Fielder catches the ball off the bat (or glove)",
  LBW: "Leg-before-wicket — pad blocks ball that would've hit stumps",
  RUN_OUT: "Batter dismissed running between wickets",
  STUMPED: "Keeper takes off bails while batter outside crease",
  HIT_WICKET: "Batter dislodges own stumps during shot",
  RETIRED_HURT: "Batter retires due to injury (can return)",
  RETIRED_OUT: "Batter retires without injury (cannot return)",
  TIMED_OUT: "New batter takes too long to take guard (Law 40)",
  HIT_BALL_TWICE: "Batter wilfully hits the ball twice (Law 34)",
  OBSTRUCTING_FIELD: "Batter blocks/distracts a fielder (Law 37)",
  HANDLED_BALL: "Legacy term — now part of Obstructing the Field",
  MANKAD: "Run-out at non-striker's end during delivery (Law 38)",
  ABSENT_HURT: "Batter unable to come out — recorded as not-out absent",
  RETIRED: "Generic retirement (umpire-recorded)",
};

import {
  BREAK_TYPES,
  ACTIVITIES_CONFIG,
  bowlerTypeFromDelivery,
  ACTIVITY_COLUMNS,
  ACTIVITY_FLOW,
  STROKE_AUTO_FILL,
} from "./bbb-constants";

// Live scoring writes over_number starting at 1, so the innings' first
// over is 1 and "0.1" is its display form. Ingested matches store the
// same thing starting at 0, which is why this cannot be a constant
// inside formatBallReference — every screen that reads INGESTED balls
// derives the base from the innings instead (see deriveOverBase). This
// page only ever drives a live, in-progress match, so the base is fixed.
const LIVE_OVER_BASE = 1;

// A format's scheduled overs, for the rain / DLS / overs-remaining
// modals. The MatchType row is the source of truth (T10 = 10); the
// table only covers a payload that arrived without it. This used to be
// six copies of `T20 ? 20 : ODI ? 50 : TEST ? 90 : 20`, which handed
// every T10 (and T15 / T30) a 20-over default.
const FORMAT_DEFAULT_OVERS = {
  T10: 10, T15: 15, T20: 20, T20I: 20, T20_BBL: 20, T20_IPL: 20,
  T30: 30, HUNDRED: 20, ODI: 50, ODI_I: 50, LISTA: 50,
};
function formatDefaultOvers(matchType) {
  const code = (matchType?.code || "").toUpperCase();
  if (code === "TEST" || code === "FIRST_CLASS") return 90; // per day
  return Number(matchType?.max_overs) || FORMAT_DEFAULT_OVERS[code] || 20;
}

export default function BallByBallPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const toast = useToast();
  const scorerLock = useScorerLock(id);
  const {
    aggregate,
    players,
    battingStats,
    bowlingStats,
    actions,
    events,
    matchEnded,
    battingTeamName,
    bowlingTeamName,
    battingTeamId,
    bowlingTeamId,
    inningsId,
    currentInningsNumber,
    umpires,
    thirdUmpire,
    fourthUmpire,
    isSuperOver,
    superOverIndex,
    // 2026-06-10 — bowling-end context for the pre-1st-ball picker
    // modal + the current-end chip.
    firstOverBowlingEnd,
    stadiumEndAName,
    stadiumEndBName,
  } = useScoringStore();
  // Off-field officials are one-each, so build a single-row list per
  // role for the DRS / wicket dropdowns. Distinct from `umpires` (which
  // is the on-field roster — those are NOT eligible to be 3rd / 4th
  // umpires per ICC playing conditions).
  const thirdUmpireOptions = thirdUmpire ? [thirdUmpire] : [];
  const fourthUmpireOptions = fourthUmpire ? [fourthUmpire] : [];
  const { ball, setBall } = useBallInput();
  const { user: authUser } = useAuthStore();
  // Plan-driven AI gates — server populates user.features from PlanFeature.
  // Staff bypasses for our own ops testing.
  const userFeatures = authUser?.features || [];
  // 2026-06-02 — Test matches have ~5400 balls vs T20's 120. Running per-
  // ball AI on every Test delivery would be cost-prohibitive (Sonnet ~₹3+
  // per delivery × 5400 = ₹16K/match) and B2C plans already disallow it
  // server-side. Hide every AI surface in the SPA when the match is Test
  // format so operators don't see disabled / gated buttons.
  const isTestMatch = (
    (aggregate?.match_type?.code || "").toUpperCase() === "TEST"
    || aggregate?.match_type?.is_test === true
  );
  const canUseAiTag      = !isTestMatch && (authUser?.is_staff || userFeatures.includes("ai_tagging"));
  const canUseAiSuggest  = !isTestMatch && (authUser?.is_staff || userFeatures.includes("ai_suggest_ball"));
  const canUseAiBeta     = canUseAiSuggest;  // legacy name still used downstream
  const isOrg =
    authUser?.account_type === "organization" || authUser?.account_type_v2 === "B2B";
  const [videoStream, setVideoStream] = useState(null);
  const videoPlayerRef = useRef(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiToast, setAiToast] = useState(null);

  // Promo banner — surfaces AI Tag, but only to users who can actually use
  // it. Pricing v2 restricts ai_tagging to TRIAL + B2B; paid B2C users would
  // hit a 403 from the gated endpoint and the banner would look misleading.
  // Persists dismissal in localStorage so it never shows twice.
  const PROMO_KEY = "zcd_b2c_ai_tag_promo_dismissed";
  const [showAiPromo, setShowAiPromo] = useState(false);
  useEffect(() => {
    if (isOrg) return;
    if (!canUseAiTag) return;     // not granted by their plan → no promo
    try {
      if (localStorage.getItem(PROMO_KEY) !== "1") setShowAiPromo(true);
    } catch { /* localStorage unavailable, no banner */ }
  }, [isOrg, canUseAiTag]);
  const dismissAiPromo = () => {
    try { localStorage.setItem(PROMO_KEY, "1"); } catch { /* ignore */ }
    setShowAiPromo(false);
  };

  // Low-balance toast — once-per-session nudge when running below 20 ZC.
  const walletBalance = useWalletStore((s) => s.balance);
  const [lowBalanceWarned, setLowBalanceWarned] = useState(false);
  const checkLowBalance = () => {
    if (lowBalanceWarned) return;
    const c = walletBalance?.balance_credits ?? null;
    if (c == null) return;
    if (c < 20 && c >= 0) {
      flashAiToast(false, `Only ${c} Zencredits left — top up to keep AI features working.`);
      setLowBalanceWarned(true);
    }
  };
  // B2B users (organisation accounts) use the AWS IVS live-stream pipeline;
  // B2C users stay on the chunked MediaRecorder→S3 path. Detected once at
  // mount from the auth store.
  const recorderTier = authUser?.account_type_v2 === "B2B" ? "B2B" : "B2C";
  const recorder = useMatchRecorder({ matchId: id, stream: videoStream, tier: recorderTier });

  // Auto-resume an in-flight recording after a tab refresh.
  //
  // The browser API surface forces a hard reality: a page refresh kills
  // the MediaRecorder + the camera MediaStream, so we can't physically
  // record-through-refresh. What we CAN do is: detect the operator had
  // a recording in flight (backend has an UPLOADING MatchRecording
  // row), wait for the camera to come back up post-refresh, and
  // re-attach a new MediaRecorder pointed at the SAME multipart upload.
  // The operator skips the Start click; the master MP4 gets a brief
  // ~1-3s gap during refresh (camera re-acquisition latency) but the
  // recording continues from the right next-part-number with
  // video_start_ms cumulative on the master timeline.
  //
  // Gates:
  //   * recorder.state must be "idle" — don't fire twice or interrupt
  //     a manual Start the operator just clicked.
  //   * videoStream must be live — VideoPlayer.jsx acquires getUserMedia
  //     on mount + populates this; without a stream, MediaRecorder can't
  //     attach.
  //   * Only fires for B2C (chunked) — B2B/IVS resume is a separate
  //     workstream because IVS sessions can't be picked up from a new
  //     tab mid-broadcast.
  useEffect(() => {
    if (!videoStream || !id) return;
    if (recorderTier !== "B2C") return;
    if (recorder.state !== "idle") return;
    // Fire-and-forget. resume() exits silently if the backend says
    // there is no active recording (404). On success it flips state
    // to "recording" and the rest of the page picks up via the
    // existing liveActive check.
    recorder.resume();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoStream, id, recorderTier]);
  // File-mode upload: handles the "Upload from computer" workflow, where the
  // scorer plays a pre-recorded match file locally, marks clip in/out off the
  // file's playhead, and uploads the whole file to S3 at the end so the same
  // MediaConvert slicer that runs after live recording can cut per-ball clips.
  const fileUpload = useFileVideoUpload({ matchId: id });
  // Effective video source — live recording always wins if it's running;
  // otherwise fall back to the file-upload hook (which is "live" as soon as
  // a file is loaded, so clip in/out can save offsets before the upload
  // even starts). Both surfaces expose the same { isLive, videoTimestampMs,
  // recordingId } so the rest of the BBB UI doesn't care which one is in
  // play.
  const liveActive = recorder.state === "recording" || recorder.state === "starting" || recorder.state === "stopping";
  const videoSource = liveActive
    ? {
        isLive: recorder.isLive,
        videoTimestampMs: recorder.videoTimestampMs,
        recordingId: recorder.recordingId,
      }
    : {
        isLive: fileUpload.isLive,
        videoTimestampMs: fileUpload.fileTimeMs,
        recordingId: fileUpload.recordingId,
      };
  const [selectionModal, setSelectionModal] = useState({
    open: false,
    type: null,
    forced: false,
  });
  const [scorecardOpen, setScorecardOpen] = useState(false);
  const [wicketModalOpen, setWicketModalOpen] = useState(false);
  const [moreOptionsOpen, setMoreOptionsOpen] = useState(false);
  // 2026-05-29 — Overs Remaining adjustment modal (founder ask). Lets
  // the operator edit max_overs on the active innings with a reason
  // (rain, bad light, etc.) audit-logged. Default max_overs comes from
  // match_type (T20=20 / ODI=50 / Test=90).
  //
  // 2026-06-03 — split into two independent sections. Each has its own
  // input + reason + Save:
  //   - "New max overs"   → edits per-innings cap (apply-dls)
  //   - "Remaining overs" → edits remaining: per-innings for limited
  //                         overs, per-DAY for Test (setOversLeftManual,
  //                         so the chip resets to 90 on day change).
  const [oversRemainingOpen, setOversRemainingOpen] = useState(false);
  const [oversRemainingInput, setOversRemainingInput] = useState("");
  const [oversAdjustReason, setOversAdjustReason] = useState("");
  const [oversAdjustSaving, setOversAdjustSaving] = useState(false);
  const [newMaxOversInput, setNewMaxOversInput] = useState("");
  const [newMaxReason, setNewMaxReason] = useState("");
  const [newMaxSaving, setNewMaxSaving] = useState(false);
  // Optional powerplay override entered alongside an overs reduction.
  // Operators set this when a rain-cut innings needs the PP boundary
  // to scale down (e.g. 20→10 overs → PP 4→2). Blank = don't touch the
  // existing override. Persisted via PATCH /api/matches/matches/{id}/
  // immediately after the apply-dls call succeeds.
  const [reducedPPOversInput, setReducedPPOversInput] = useState("");
  // Optional per-bowler over cap, set alongside an overs reduction.
  // T20 20→10 over rain rule typically drops the bowler quota from
  // 4 to 2 (max_overs / 5, ICC). Blank = inherit the format default.
  // Persisted on Match.powerplay_override.max_overs_per_bowler — the
  // backend's law engine (apps/scoring/engine/laws.py) reads this and
  // blocks the over-start if the bowler is already at quota.
  const [reducedMaxOversPerBowlerInput, setReducedMaxOversPerBowlerInput] = useState("");
  // 2026-06-04 — Manual-runs popover (founder ask). Opens off the small
  // "..." chip next to the boundary toggle in the run-chip row. Lets the
  // operator enter any value 0..10 — needed for the 3-ran + 4-overthrow-
  // boundary case (total 7) where the chip row only goes to 6.
  const [manualRunsOpen, setManualRunsOpen] = useState(false);
  const [manualRunsInput, setManualRunsInput] = useState("");
  // 2026-05-30 — Fielding Penalty modal (founder ask). Operator awards
  // 5 runs to the batting team from either the current ball (no
  // from_ball_id) or a specific past ball (picked from recentBalls).
  // Backend persists with is_fielding_penalty=True + from_ball FK.
  const [fieldingPenaltyOpen, setFieldingPenaltyOpen] = useState(false);
  const [fieldingPenaltyMode, setFieldingPenaltyMode] = useState("current"); // "current" | "past"
  const [fieldingPenaltyBallId, setFieldingPenaltyBallId] = useState("");
  const [fieldingPenaltySaving, setFieldingPenaltySaving] = useState(false);
  const OVERS_ADJUST_REASONS = [
    "End current innings",
    "Rain",
    // 2026-06-10 — two pre-fill reasons used by the entry-point
    // buttons we ship. Listed here so the <select> can show the
    // pre-filled option visually (otherwise React renders the
    // <select> blank when the controlled value doesn't match any
    // <option>, which made the "Save new max" button appear
    // broken — operator's pre-filled reason was in state, but the
    // user couldn't see it / wasn't sure they could click Save).
    "Match overs reduction (rain)",
    "Rain affected at start",
    "Bad light",
    "Wet outfield",
    "Dangerous pitch conditions",
    "Extreme weather",
    "Lightning",
    "Fog or smoke",
    "Player injuries",
    "Slow over rates",
    "Technical interruptions",
    "Crowd disturbances",
    "Security issues",
    "Medical emergencies",
    "Early end of innings",
    "Others",
  ];
  const [dlsRuns, setDlsRuns] = useState("");
  const [dlsOvers, setDlsOvers] = useState("");
  const [penaltyRuns, setPenaltyRuns] = useState("");
  const [penaltyAwardedTo, setPenaltyAwardedTo] = useState("BATTING");
  const [penaltyReason, setPenaltyReason] = useState("");
  const [penaltyReasonOther, setPenaltyReasonOther] = useState("");
  const [savePending, setSavePending] = useState(false);
  const [saveInFlight, setSaveInFlight] = useState(false);
  // saveProgress state removed — progress bar now animates via CSS keyframes,
  // no setInterval, no re-renders. See zcd-save-progress in the Save Ball JSX.
  const saveTimerRef = useRef(null);
  const [swapAnimKey, setSwapAnimKey] = useState(0);
  const [handOverrides, setHandOverrides] = useState({});
  // 2026-06-05 — per-bowler bowling-style override map. Same shape as
  // handOverrides — keyed by player_id, value is a BOWLING_STYLE_CHOICES
  // code (RAF / LAMF / RAO / …) the coder picked for the next delivery.
  // Reset alongside handOverrides on innings change.
  const [styleOverrides, setStyleOverrides] = useState({});
  const [wicketKeeperId, setWicketKeeperId] = useState(null);
  // Track which innings the operator has manually picked a keeper for.
  // Without this, the auto-detect effect below blew away the manual
  // pick on every state refresh (players.bowlers identity changes on
  // every save → effect re-ran → setWicketKeeperId(XI's is_wicket_keeper)
  // → keeper reverted to the original XI choice even after the operator
  // explicitly swapped to a substitute mid-innings).
  const keeperManualForInningsRef = useRef(null);
  const setWicketKeeperManually = (id) => {
    keeperManualForInningsRef.current = { innings: currentInningsNumber, id };
    setWicketKeeperId(id || null);
  };
  const [wicketKeeperModalOpen, setWicketKeeperModalOpen] = useState(false);
  const [umpireBowlerEndId, setUmpireBowlerEndId] = useState(null);
  const [umpireSquareLegId, setUmpireSquareLegId] = useState(null);
  const [umpireModalOpen, setUmpireModalOpen] = useState(false);
  const [umpirePromptDismissed, setUmpirePromptDismissed] = useState(false);
  // Hydration flag — the modal-open effect MUST NOT fire until we've
  // consulted localStorage for this innings. Without this gate there
  // was a render-order race: server response set umpires +
  // currentInningsNumber in the same render, both effects queued, but
  // the modal-open effect ran first and read the still-pre-hydration
  // umpirePromptDismissed=false → it opened the modal before the
  // hydration effect had a chance to set dismissed=true. Operator
  // saw the umpire prompt every time they came back from QC / refreshed
  // even though their pick was sitting in localStorage. Now: modal-open
  // effect bails until umpiresHydrated is true.
  const [umpiresHydrated, setUmpiresHydrated] = useState(false);

  // Per-innings umpire-prompt persistence. The modal should fire exactly
  // once per innings — at start of innings 1 and again at start of
  // innings 2. localStorage stamp tied to match + innings number;
  // hydrate on mount, set on save / skip, clear automatically when
  // innings changes.
  const umpiresStorageKey = (inningsNum) => `zcd_umpires_${id}_${inningsNum || "0"}`;
  // Restore previously-picked umpires + dismissed flag whenever the
  // active innings number changes (initial mount, after server fetch,
  // after innings transition). Falls through silently when storage is
  // empty / unavailable.
  useEffect(() => {
    if (!id || !currentInningsNumber) {
      // Reset hydration on every transition so a remount with a fresh
      // server fetch can re-hydrate cleanly.
      setUmpiresHydrated(false);
      return;
    }
    try {
      const raw = localStorage.getItem(umpiresStorageKey(currentInningsNumber));
      if (!raw) {
        // Fresh innings, no resolution yet — reset state so the modal
        // will open via the existing gate effect.
        setUmpireBowlerEndId(null);
        setUmpireSquareLegId(null);
        setUmpirePromptDismissed(false);
      } else {
        const saved = JSON.parse(raw);
        if (saved.bowlerEnd) setUmpireBowlerEndId(saved.bowlerEnd);
        if (saved.squareLeg) setUmpireSquareLegId(saved.squareLeg);
        // Mark dismissed so the auto-open effect doesn't pop the modal
        // even if both IDs happened to be null (user picked Skip).
        setUmpirePromptDismissed(true);
      }
    } catch { /* localStorage disabled / private mode */ }
    setUmpiresHydrated(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, currentInningsNumber]);
  // Persist whenever the operator picks or skips.
  const persistUmpiresForInnings = (bowlerEnd, squareLeg, dismissed) => {
    if (!id || !currentInningsNumber) return;
    try {
      localStorage.setItem(
        umpiresStorageKey(currentInningsNumber),
        JSON.stringify({ bowlerEnd: bowlerEnd || null, squareLeg: squareLeg || null, dismissed: !!dismissed }),
      );
    } catch { /* ignore */ }
  };
  const [structuredRegion, setStructuredRegion] = useState(null);
  const [followOnModalOpen, setFollowOnModalOpen] = useState(false);
  // 2026-06-03 — guard against double-click on Yes/No follow-on buttons.
  // Without this, two clicks fire two POSTs: the first creates inn3
  // (follow-on, WI batting), the second closes inn3 and creates inn4
  // with a 4th-innings chase target → corrupted scorecard. Seen in
  // Keerthi's IND vs WI Test 2026-06-03 — data manually repaired and
  // backend now also rejects the second POST, but keep this so the user
  // gets immediate visual feedback instead of a 409 toast.
  const [followOnSubmitting, setFollowOnSubmitting] = useState(false);
  const [superOverModalOpen, setSuperOverModalOpen] = useState(false);
  const [activeZoneMatrix, setActiveZoneMatrix] = useState("pitch");
  const [spatialTab, setSpatialTab] = useState("pitch");        // pitch | impact | wagon
  // 2026-05-27 — T220.1 mobile BBP tab strip. Below xl the operator
  // sees ONE column at a time instead of scrolling past all three.
  //   "spatial"  → middle column (pitch/impact/wagon picker)
  //   "activity" → right column (voice help + activity accordion)
  //   "score"    → left column (score input, runs, extras, save)
  // Default "spatial" because the first action per ball is the cell
  // pick. Auto-advances on wagon set → activity → stroke set → score.
  // Tap the tab strip to manually override at any time.
  // 2026-05-28 — tab strip order is Score | Spatial | Performance per
  // founder ask, but the DEFAULT tab stays "spatial" because that's
  // where the first coding action lives (cell pick / voice landing
  // surface). If the default were "score", voice utterances like
  // "yorker outside off" would set spatial fields the operator can't
  // see until they manually tap the Spatial tab. Auto-advance
  // (spatial-done → Performance, analytics-done → Score) still moves
  // them to the right tab when their work shifts.
  const [mobileBbpTab, setMobileBbpTab] = useState("spatial");
  // T220.3 — mobile sticky header popover. The compact header only
  // shows score / overs / batting team / target / inn on phones.
  // SyncPill / SaveCounter / IntegrityBadge / CopyLiveLink / Health /
  // Watchers / Shortcuts toggle / ProgressDots / MissingFields chips
  // all collapse behind this dropdown — operator taps ⓘ to expand.
  const [mobileHeaderInfoOpen, setMobileHeaderInfoOpen] = useState(false);
  // T220.5 — iPad landscape mode (1024–1279px landscape). Sits between
  // phone (single-tab strip) and desktop (3-col). In this mode we hide
  // the tab strip, show spatial + activity side-by-side, and collapse
  // the score column into a slim bottom bar above the T220.2 action
  // bar — operator sees both spatial and activity at once without
  // tabbing, but still hits PTT / Save from the same place.
  const [isIpadLandscape, setIsIpadLandscape] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia(
      "(min-width: 1024px) and (max-width: 1279px) and (orientation: landscape)"
    );
    const apply = () => setIsIpadLandscape(mq.matches);
    apply();
    mq.addEventListener?.("change", apply);
    return () => mq.removeEventListener?.("change", apply);
  }, []);
  // Track per-ball whether the operator has already advanced from a stage,
  // so re-edits don't keep yanking them back.
  const [tabAutoAdvanced, setTabAutoAdvanced] = useState({ pitch: false, impact: false, wagon: false, activity: false });
  // Voice AI fallback toggle. Persisted per device in localStorage so
  // Krishna's preference survives reloads. Default OFF (opt-in per
  // founder ask) — the regex parser handles ~95% of phrases for free;
  // AI fallback fires only when regex returns unrecognized, charging
  // 1 ZC per fallback call. Plans with skip_credit_gate (team codes,
  // B2B) get it free via the wallet mixin.
  // Pending voice-utterance IDs for the current in-progress ball.
  // Each Whisper transcribe pushes its server-side utterance_id here;
  // when the ball saves, the IDs are POSTed to /api/scoring/voice/link/
  // so each VoiceUtterance row is stamped with the saved ball_id.
  // After link the ref clears, ready for the next ball.
  const pendingVoiceUtterancesRef = useRef([]);
  const pushVoiceUtterance = useCallback((utteranceId) => {
    if (utteranceId) pendingVoiceUtterancesRef.current.push(utteranceId);
  }, []);

  // CF-P0-05 — AI suggestion stash for observe-correction.
  // Persistence + cross-page access moved into utils/aiObservationStash.js
  // (localStorage-backed). Survives refresh AND lets EditBalls.jsx fire
  // observe-correction too. setPending → commitForBall → fireObserveOnSave.

  const [voiceAiFallback, setVoiceAiFallback] = useState(() => {
    // Default ON now that confidence gates (>=0.9 auto-apply, <0.6
    // suppress) keep noise out. Operator can still flip it off via
    // the ✨ AI button. Unset key → default true.
    try { return localStorage.getItem("voice_ai_fallback") !== "0"; } catch { return true; }
  });
  const handleVoiceAiToggle = (next) => {
    setVoiceAiFallback(next);
    try { localStorage.setItem("voice_ai_fallback", next ? "1" : "0"); } catch {}
  };
  // First-time voice tutorial. Opens once per browser (localStorage flag)
  // the first time the operator lands on BBP with a speech-supported
  // browser. "Replay tour" link on VoiceScoringPanel re-opens it on
  // demand (clears the flag then sets `voiceTourOpen` true).
  const [voiceTourOpen, setVoiceTourOpen] = useState(false);
  useEffect(() => {
    // 2026-05-27: gate auto-launch to desktop (lg+ = 1024px). On
    // phones and tablets the modal pops over the small viewport and
    // its audio samples are intrusive; operators replay manually via
    // VoiceScoringPanel's tour link if they need it. shouldShowVoiceTour
    // still gates first-visit-per-browser; the lg check just keeps it
    // off mobile / tablet screens entirely.
    const isDesktop = typeof window !== "undefined"
      && window.matchMedia
      && window.matchMedia("(min-width: 1024px)").matches;
    if (!isDesktop || !shouldShowVoiceTour()) return;
    const t = setTimeout(() => {
      if (shouldShowVoiceTour() && voice?.supported) setVoiceTourOpen(true);
    }, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const replayVoiceTour = () => { clearVoiceTourSeen(); setVoiceTourOpen(true); };

  // Voice intent prompt — 2026-06-11 founder ask. On the FIRST visit
  // to a given match's coding screen, ask the operator whether they
  // intend to use voice coding. Yes → run the 60s calibration. No →
  // skip calibration and go straight to coding. Per-match decision
  // persisted in localStorage so re-entering the same match never
  // re-prompts. Replaces the previous "once per browser, auto-show
  // calibration" flow; the gauntlet itself is unchanged.
  const [calibrationOpen, setCalibrationOpen] = useState(false);
  const [voiceIntentOpen, setVoiceIntentOpen] = useState(false);
  useEffect(() => {
    if (!id) return;
    // If voice isn't supported by this browser at all, don't ask — the
    // question would be moot and the prompt would block coding on a
    // device that physically cannot do voice.
    if (!voice?.supported) return;
    // Already answered for this match — never re-ask.
    if (getVoiceIntent(id) !== null) return;
    // Hold the prompt until any tutorial finishes, so it doesn't stack
    // on top of another modal.
    const t = setTimeout(() => {
      if (getVoiceIntent(id) === null && !voiceTourOpen) {
        setVoiceIntentOpen(true);
      }
    }, 600);
    return () => clearTimeout(t);
    // 2026-06-11 — voice.supported intentionally NOT in the deps array
    // even though the callback reads it. `const voice = useVoiceScoring(...)`
    // is declared ~1900 lines below this useEffect (line ~2530), so
    // putting voice?.supported into the deps array hit the TDZ during
    // render — Krishna reported "Cannot access 'cs' before initialization"
    // entering the coding page. Reading voice.supported INSIDE the
    // callback is safe (effect runs post-commit, by which time the
    // hook has executed); reading it in the dep array runs synchronously
    // during render and fails. voice.supported is a one-shot browser
    // feature check that doesn't change after mount, so we don't need
    // re-fires when it flips — id alone is the right dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleVoiceIntentYes = () => {
    setVoiceIntent(id, "yes");
    setVoiceIntentOpen(false);
    // Run the 60s calibration gauntlet. Mandatory variant so the
    // operator can't skip — the founder ask is explicit that Yes →
    // calibration. After completion the gauntlet closes itself + sets
    // the global "calibration done" flag.
    setCalibrationOpen(true);
  };
  const handleVoiceIntentNo = () => {
    setVoiceIntent(id, "no");
    setVoiceIntentOpen(false);
    // No further action — operator proceeds straight to coding. The
    // voice panel still exists if they later change their mind; the
    // Calibrate link there re-runs the gauntlet on demand.
  };

  // Replay button on the voice panel — clears both the global
  // calibration flag and the per-match intent so the gauntlet opens
  // again. Useful when an operator wants to re-calibrate mid-match
  // (e.g. switched headset).
  const replayCalibration = () => {
    clearCalibrationFlag();
    setVoiceIntent(id, "yes");
    setCalibrationOpen(true);
  };

  // Optional TTS read-back of every saved ball. Off by default — the
  // operator toggles via the help modal or a Settings setting.
  const ballReadback = useBallReadback();
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [editingBallId, setEditingBallId] = useState(null);
  const [editBallDialogOpen, setEditBallDialogOpen] = useState(false);
  const [closureModal, setClosureModal] = useState(false);
  const [closureSaving, setClosureSaving] = useState(false);
  const closureInFlightRef = useRef(false);
  // Match-settings modal — rain-affected + custom powerplay overs. Loaded from
  // /api/matches/<id>/ when opened, PATCHed back on save.
  const [matchSettingsOpen, setMatchSettingsOpen] = useState(false);
  const [isRainAffected, setIsRainAffected] = useState(false);
  const [customPPOvers, setCustomPPOvers] = useState("");
  const [customMaxOversPerBowler, setCustomMaxOversPerBowler] = useState("");
  // 2026-06-09 — Floodlights tri-state. NULL = not tracked. Hydrates
  // from /api/matches/matches/<id>/ when More Options opens.
  const [floodlightsOn, setFloodlightsOn] = useState(null);
  const [floodlightsBusy, setFloodlightsBusy] = useState(false);
  // Impact-player / Concussion-sub modal — opened from More Options.
  const [substitutionModal, setSubstitutionModal] = useState(null); // null | "IMPACT" | "CONCUSSION"

  // 2026-07-08 — mid-match Playing XI editor. Opened from the kebab in
  // BallLayout (via window event) or from a tap on a player name in the
  // Scorecard XIList. initialPlayerId preselects the player when the
  // tap-to-edit path fires.
  const [editXIOpen, setEditXIOpen] = useState(false);
  const [editXIInitialPlayerId, setEditXIInitialPlayerId] = useState(null);
  useEffect(() => {
    const onOpen = (e) => {
      setEditXIInitialPlayerId(e?.detail?.playerId || null);
      setEditXIOpen(true);
    };
    window.addEventListener("edit-playing-xi:open", onOpen);
    return () => window.removeEventListener("edit-playing-xi:open", onOpen);
  }, []);

  // 2026-06-10 — one-time recording-warning popup. Founder ask: every
  // scorer needs to be reminded ONCE per match that they must press
  // "Start Recording" before scoring, otherwise clip-in/clip-out won't
  // save the video. Gated by per-match localStorage so it doesn't
  // re-prompt on every tab refresh.
  const recordingWarningKey = id ? `zcd_rec_warning_seen_${id}` : null;
  const [showRecordingWarning, setShowRecordingWarning] = useState(false);
  useEffect(() => {
    if (!recordingWarningKey) return;
    try {
      const seen = localStorage.getItem(recordingWarningKey);
      if (!seen) setShowRecordingWarning(true);
    } catch { /* localStorage unavailable — surface the warning anyway */
      setShowRecordingWarning(true);
    }
  }, [recordingWarningKey]);
  const dismissRecordingWarning = () => {
    try { if (recordingWarningKey) localStorage.setItem(recordingWarningKey, "1"); }
    catch { /* ignore — still close the modal */ }
    setShowRecordingWarning(false);
  };

  // 2026-06-10 — pre-1st-ball stadium-end picker. Founder ask: capture
  // which end of the ground Over 1 is bowled from. Backend then auto-
  // derives every subsequent ball's bowling_end by over alternation
  // (odd over → same as Over 1; even → opposite).
  // Show conditions:
  //   - the operator hasn't already picked (firstOverBowlingEnd is "")
  //   - we're at the very start (legalBalls === 0)
  // 2026-06-10 v2 — dropped the requirement that the stadium has end
  // names set. The modal now fires for ANY stadium; if end names
  // aren't configured, the buttons show "End A" / "End B" so the
  // operator can still record the bowling end. Stadium names take
  // precedence when present. Operator can also re-open the picker
  // any time via the manual "Pick" button on the on-field strip.
  const [endPickerBusy, setEndPickerBusy] = useState(false);
  const [endPickerError, setEndPickerError] = useState("");
  const [endPickerForceOpen, setEndPickerForceOpen] = useState(false);
  const _aggregateLegalBalls = aggregate?.legal_balls ?? 0;
  const showEndPicker = Boolean(
    inningsId &&
    (
      endPickerForceOpen ||
      (!firstOverBowlingEnd && _aggregateLegalBalls === 0)
    )
  );
  const handlePickEnd = async (which /* "A" | "B" */) => {
    if (!inningsId) return;
    setEndPickerBusy(true);
    setEndPickerError("");
    try {
      await setFirstOverBowlingEnd(inningsId, which);
      const fresh = await loadBallSession(id);
      if (fresh) applyServerState(fresh);
      setEndPickerForceOpen(false);
    } catch (e) {
      setEndPickerError(
        e?.response?.data?.detail ||
        e?.message ||
        "Failed to save bowling end. Please try again."
      );
    } finally {
      setEndPickerBusy(false);
    }
  };

  // Bowling-team substitute fielders — squad reserves (MatchSquad minus
  // PlayingXI). Fetched once when the bowling team is known and refreshed
  // whenever an impact/concussion swap changes the XI. Used to populate
  // the "Sub" optgroup in the wagon-wheel fielder picker so the operator
  // can record fielding by a substitute on the field.
  const [subFielders, setSubFielders] = useState([]);
  useEffect(() => {
    if (!id || !bowlingTeamId) return;
    let alive = true;
    (async () => {
      try {
        const api = (await import("../../api/axios")).default;
        const res = await api.get(`/api/matches/${id}/squad/`);
        if (!alive) return;
        const t1 = res.data?.team1;
        const t2 = res.data?.team2;
        const team = t1?.team_id === bowlingTeamId ? t1 : (t2?.team_id === bowlingTeamId ? t2 : null);
        setSubFielders(team?.reserves || []);
      } catch { /* no-op — picker just shows XI */ }
    })();
    return () => { alive = false; };
  }, [id, bowlingTeamId, substitutionModal]);

  // Refresh the live rain-affected flag whenever the More-options modal
  // opens. The toggle below "End Current Innings" is the source of truth
  // here — without this fetch the toggle would stay stuck at the initial
  // false from useState across page reloads.
  useEffect(() => {
    if (!moreOptionsOpen || !id) return;
    let alive = true;
    (async () => {
      try {
        const r = await (await import("../../api/axios")).default.get(`/api/matches/matches/${id}/`);
        if (!alive) return;
        setIsRainAffected(!!r.data?.is_rain_affected);
        // 2026-06-09 — floodlights_on is the source of truth for the
        // toggle below. Null is a valid value (= not tracked).
        const fl = r.data?.floodlights_on;
        setFloodlightsOn(fl === true || fl === false ? fl : null);
      } catch { /* keep current optimistic value */ }
    })();
    return () => { alive = false; };
  }, [moreOptionsOpen, id]);

  // 2026-06-09 — Floodlights toggle handler. PATCHes /api/matches/<id>/
  // with floodlights_on flipped; updates local state optimistically and
  // reverts on failure.
  const handleToggleFloodlights = async () => {
    if (!id || floodlightsBusy) return;
    const next = floodlightsOn === true ? false : true;
    setFloodlightsBusy(true);
    setFloodlightsOn(next);
    try {
      const apiAxios = (await import("../../api/axios")).default;
      await apiAxios.patch(`/api/matches/matches/${id}/`, {
        floodlights_on: next,
      });
    } catch (e) {
      setFloodlightsOn(floodlightsOn);
      alert(
        "Couldn't update floodlights: "
        + (e?.response?.data?.detail || e?.message || "unknown error")
      );
    } finally {
      setFloodlightsBusy(false);
    }
  };
  // UX: per-ball ephemeral state
  const [lastBallFlash, setLastBallFlash] = useState(null);   // {text, kind} after save
  const [strikeToast, setStrikeToast]   = useState(null);     // "Striker now: <name>"
  const [undoSecondsLeft, setUndoSecondsLeft] = useState(0);  // countdown on UNDO button (legacy)
  // Stack of payloads for balls the operator UNDOed, in case they want to REDO.
  // Cleared on the next manual save (so old undones don't leak through after
  // the operator has moved on). Each entry is the buildBallPayload() output
  // captured at save time.
  //
  // T137: redoStack persisted to localStorage per-match. Without this an
  // accidental F5 between undo and redo loses the redo history forever.
  // Capped at 20 entries to match RECENT_SAVES_CAP — beyond that the
  // operator probably can't usefully chain that many redoes anyway.
  const REDO_STORAGE_KEY = `zcd_redo_stack_${id}`;
  const [redoStack, setRedoStack] = useState(() => {
    try {
      const raw = localStorage.getItem(REDO_STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.slice(-20) : [];
    } catch { return []; }
  });
  // Mirror to localStorage on every change. useEffect rather than wrapping
  // each setter so we have one source of truth and don't miss writes
  // from functional updaters elsewhere.
  useEffect(() => {
    try {
      if (redoStack.length === 0) {
        localStorage.removeItem(REDO_STORAGE_KEY);
      } else {
        localStorage.setItem(REDO_STORAGE_KEY, JSON.stringify(redoStack));
      }
    } catch { /* quota / private mode — non-fatal */ }
  }, [redoStack, REDO_STORAGE_KEY]);
  // Recent successful-save payloads, newest at the end (capped at 20
  // entries). handleUndoBall pops from here and pushes onto redoStack
  // — so back-to-back undos each have a real payload to preserve,
  // instead of the prior single-slot ref that only remembered the
  // VERY LAST save (which made the second undo lose its history and
  // disabled Redo prematurely). Kept as a ref because we never want
  // a render on push/pop and the array is opaque to React anyway.
  const recentSavedPayloadsRef = useRef([]);
  const RECENT_SAVES_CAP = 20;
  // Convenience: latest saved payload, derived from the array. Used
  // by code paths that previously read lastSavedPayloadRef directly.
  const lastSavedPayloadRef = useRef(null);
  // Save confirmation + error visibility — was silent on failure before; now
  // every save outcome flips one of these so the operator sees what happened.
  const [savedCount, setSavedCount] = useState(0);            // session-local count
  const [lastSavedAt, setLastSavedAt] = useState(null);       // Date of last successful save
  const [saveError, setSaveError] = useState("");             // visible banner
  // Keyboard shortcuts off by default to prevent accidental scoring (e.g. an
  // operator typing in a different tab or a pressed-and-stuck key). Toggled
  // via the header button. Persisted across reloads via localStorage so each
  // operator can keep their preference without re-toggling every session.
  const [shortcutsEnabled, setShortcutsEnabled] = useState(() => {
    try { return localStorage.getItem("zcd_kbd_shortcuts") === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("zcd_kbd_shortcuts", shortcutsEnabled ? "1" : "0"); } catch { /* localStorage unavailable */ }
  }, [shortcutsEnabled]);

  // Draft-of-current-ball persistence. The `ball` state in
  // BallInputContext is in-memory only; a tab crash, accidental
  // refresh, or navigating away would wipe a half-typed wicket panel.
  // Mirror to localStorage on every change and rehydrate on mount —
  // keyed by matchId so two matches in different tabs don't collide.
  // Cleared on successful save via the handleSaveBall path.
  useEffect(() => {
    if (!id) return;
    try {
      const raw = localStorage.getItem(`zcd_ball_draft_${id}`);
      if (raw) {
        const draft = JSON.parse(raw);
        // Only restore if the draft has at least one operator-set
        // field — empty defaults aren't worth restoring (they'd just
        // overwrite the fresh-mount state with identical zeros).
        // T214 / P0-2: include spatial + analytics in the "is this
        // worth restoring?" check. A ball with only pitch/stump/wagon
        // entered (mid-coding workflow) used to be dropped on refresh
        // because the predicate ignored spatial. Same for a stroke
        // chip without runs typed. Now any operator-set field qualifies.
        const sp = draft?.spatial || {};
        const an = draft?.analytics || {};
        const spatialTouched = !!(
          sp.pitch_zone || sp.stump_zone || sp.height_zone ||
          sp.batter_stump_zone || sp.structured_position ||
          sp.structured_region_id != null ||
          sp.wagon_wheel_x != null || sp.wagon_wheel_y != null
        );
        const analyticsTouched = !!(
          an.stroke || an.shot_connection || an.foot_movement ||
          an.air_movement || an.delivery_type
        );
        const meaningful = draft && (
          draft.completed_runs > 0 ||
          draft.has_wicket || draft.is_wide || draft.is_no_ball ||
          draft.is_bye || draft.is_leg_bye || draft.dismissed_player_id ||
          spatialTouched || analyticsTouched
        );
        if (meaningful) {
          // Merge nested objects rather than overwriting them. Without
          // this the prev.spatial defaults are replaced wholesale and
          // any field not in the draft becomes undefined.
          setBall((prev) => ({
            ...prev,
            ...draft,
            spatial:   { ...(prev.spatial   || {}), ...(draft.spatial   || {}) },
            analytics: { ...(prev.analytics || {}), ...(draft.analytics || {}) },
          }));
        }
      }
    } catch { /* JSON parse / quota — ignore */ }
    // Only on mount; saves happen via the separate setter-side effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  // Persist on every change (debounced via the natural batching of
  // React renders — no need for a setTimeout dance).
  useEffect(() => {
    if (!id || !ball) return;
    try {
      localStorage.setItem(`zcd_ball_draft_${id}`, JSON.stringify({
        completed_runs: ball.completed_runs,
        is_wide: ball.is_wide, is_no_ball: ball.is_no_ball,
        is_bye: ball.is_bye, is_leg_bye: ball.is_leg_bye,
        is_boundary: ball.is_boundary, is_short_run: ball.is_short_run,
        is_quick_running: ball.is_quick_running,
        has_wicket: ball.has_wicket, wicket_type: ball.wicket_type,
        dismissed_player_id: ball.dismissed_player_id,
        dismissed_by_id:     ball.dismissed_by_id,
        caught_by_id:        ball.caught_by_id,
        stumped_by_id:       ball.stumped_by_id,
        run_out_fielder_1_id: ball.run_out_fielder_1_id,
        run_out_fielder_2_id: ball.run_out_fielder_2_id,
        run_out_subtype:      ball.run_out_subtype,
        appealed_for:         ball.appealed_for,
        // T214 / P0-2: spatial + analytics now persisted too. A ball
        // with only pitch/wagon/stroke entered used to be dropped on
        // refresh; now it survives until the operator either commits
        // it or explicitly clears.
        spatial:   ball.spatial,
        analytics: ball.analytics,
      }));
    } catch { /* quota / private mode */ }
  }, [id, ball]);

  // Live-feed subscriber count — polled from
  // /api/matches/matches/<id>/live-subscribers/ every ~15 s. Soft
  // signal, shown as a small "N watching" badge so the operator knows
  // their work is being consumed in real time.
  //
  // The doubled `matches/` is not a typo. backend/urls.py includes
  // matches.urls under `api/matches/`, and this route is registered
  // inside it as `matches/<uuid>/live-subscribers/`. Sibling calls on
  // this page (squad/, warnings/, commentary/) are registered one level
  // up, which is why they read differently.
  //
  // This used to call the single-level path, which matches no route at
  // all, so it 404'd for every match. The catch swallowed it and the
  // interval kept firing, so a few abandoned scoring tabs produced
  // 1,031 dead requests in a day (2026-09-01 access log) — every one
  // taking a gunicorn worker slot away from the analyzer. Hence the two
  // guards below: a permanent failure stops the poll for good, and a
  // backgrounded tab does not poll at all.
  const [liveSubscribers, setLiveSubscribers] = useState(0);
  useEffect(() => {
    if (!id) return undefined;
    let alive = true;
    let interval = null;
    const stop = () => {
      if (interval) { clearInterval(interval); interval = null; }
    };
    const poll = async () => {
      // Nobody is reading the badge on a hidden tab.
      if (typeof document !== "undefined" && document.hidden) return;
      try {
        const api = (await import("../../api/axios")).default;
        const res = await api.get(`/api/matches/matches/${id}/live-subscribers/`);
        if (alive) setLiveSubscribers(res?.data?.count || 0);
      } catch (e) {
        // 404 = no such match, 403 = not ours. Neither becomes true by
        // waiting, so give up instead of retrying every 15 s forever.
        // Any other failure (5xx, network blip) keeps the prior count
        // and retries on the next tick.
        const status = e?.response?.status;
        if (status === 404 || status === 403) stop();
      }
    };
    // Coming back to the tab should refresh the badge immediately
    // rather than showing a value up to 15 s stale.
    const onVisible = () => { if (!document.hidden) poll(); };
    document.addEventListener("visibilitychange", onVisible);
    poll();
    interval = setInterval(poll, 15000);
    return () => {
      alive = false;
      stop();
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [id]);
  const [closureType, setClosureType] = useState("ALL_OUT");
  const [resultModal, setResultModal] = useState(false);
  const [resultType, setResultType] = useState("DECISIVE");
  const [recentBalls, setRecentBalls] = useState([]);
  // Ball IDs that were saved while voice scoring was active — sourced
  // from localStorage (set by the save handler when pendingVoiceUtterancesRef
  // is non-empty at submit). Used to render a 🎤 badge in the recent-
  // balls strip. Frontend-only MVP — backend serializer-level voice
  // metadata can replace this later (T124 follow-up).
  const voiceCodedBallIds = useMemo(() => {
    try {
      const raw = localStorage.getItem(`zcd_voice_balls_${id}`);
      return new Set(raw ? JSON.parse(raw) : []);
    } catch { return new Set(); }
  }, [id, recentBalls]);

  // Persona-1 / Feature-1: smart guard rails. Watches recent balls
  // for patterns that likely mean the operator slipped (4 sixes in
  // a row, no strike rotation for 12 balls, etc) and surfaces a
  // non-blocking chip above the save area. One-tap dismiss stops
  // re-prompts for the same situation.
  const [dismissTick, setDismissTick] = useState(0);   // re-render on dismiss
  const guardRails = useScoringGuardRails({ matchId: id, recentBalls, dismissTick });

  // T143: per-match action history log. Records every save / undo /
  // redo / batch-undo / edit. Rendered as the collapsible scrubber
  // panel at the bottom of the page. Persisted to localStorage so
  // a refresh doesn't lose the timeline.
  const historyLog = useScoringHistoryLog(id);
  const [historyOpen, setHistoryOpen] = useState(false);

  // T144: undo bookmarks. Operator drops a marker capturing the
  // current balls_recorded; later restore = batch-undo of the
  // delta back to that marker. Local-only, per-match.
  const bookmarks = useUndoBookmarks(id);

  // T147: ghost-ball trash. Server-side 24h restore window. Loaded
  // when the operator opens the history panel so we don't poll
  // wastefully. Refreshed after every undo too, so the panel
  // shows fresh entries even if it was already open.
  const [trash, setTrash] = useState([]);
  const refreshTrash = useCallback(async () => {
    if (!id) return;
    try {
      const r = await listBallTrash(id);
      setTrash(r.data?.results || []);
    } catch { /* silent — trash is optional */ }
  }, [id]);
  useEffect(() => {
    if (historyOpen) refreshTrash();
  }, [historyOpen, refreshTrash]);

  const handleRestoreFromTrash = async (trashId) => {
    try {
      await restoreBallFromTrash(trashId);
      // Re-load both: trash (the row is now restored) and the live
      // recent-balls strip (which got a new ball).
      refreshTrash();
      if (inningsId) {
        listInningsBalls(inningsId)
          .then((r) => setRecentBalls((r?.data?.results || []).slice(-12)))
          .catch(() => {});
      }
      setAiToast({ ok: true, msg: "Restored from trash." });
      setTimeout(() => setAiToast(null), 3000);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Restore failed";
      setSaveError(msg);
      setTimeout(() => setSaveError(""), 5000);
    }
  };

  // Helper: drop a bookmark right now, optionally with a label.
  const dropBookmark = (label) => {
    const last = recentBalls[recentBalls.length - 1];
    const summary = last
      ? `${formatBallReference(aggregate?.match_type, { overNumber: last.over_number, ballInOver: last.ball_in_over, overBase: LIVE_OVER_BASE })} · ${last.striker || ""}`
      : "start of innings";
    bookmarks.drop({
      label: label || `Bookmark @ ${summary}`,
      balls_recorded: aggregate?.balls_recorded ?? 0,
      last_ball_summary: summary,
    });
    historyLog.push("bookmark_drop", { label, balls_recorded: aggregate?.balls_recorded ?? 0 });
  };

  // Helper: restore to a bookmark = batch-undo (current_balls - bookmark_balls).
  // Routed through the existing preview pill (T140) so the operator
  // gets a 6s confirm window before the destructive action.
  const restoreToBookmark = (bookmark) => {
    const current = aggregate?.balls_recorded ?? 0;
    const n = current - (bookmark.balls_recorded || 0);
    if (n <= 0) {
      setSaveError("Already at or before this bookmark — nothing to restore.");
      setTimeout(() => setSaveError(""), 4000);
      return;
    }
    if (n > 25) {
      setSaveError(`Restore would undo ${n} balls — exceeds the 25-ball live-undo cap. Use the QC rebuild tool.`);
      setTimeout(() => setSaveError(""), 6000);
      return;
    }
    const diff = {
      over: `to "${bookmark.label}"`,
      summary: `Remove ${n} ball${n === 1 ? "" : "s"}`,
      striker: "(restore)",
    };
    setPendingUndo({ ...diff, _batchN: n });
    if (undoPreviewTimerRef.current) clearTimeout(undoPreviewTimerRef.current);
    undoPreviewTimerRef.current = setTimeout(() => setPendingUndo(null), 8000);
  };
  // M3: current/unbroken partnership for the ScoreRibbon chip. Refetched
  // after every ball save so the chip stays in sync. Backend endpoint
  // computes from the ball stream, so it correctly resets on wickets
  // (unlike the legacy striker.runs + non_striker.runs sum which leaks
  // across retired-hurt / return scenarios).
  const [currentPartnership, setCurrentPartnership] = useState(null);
  const [milestone, setMilestone] = useState(null);  // {text, kind} | null
  // Dedup set of milestones already announced this innings — string
  // keys like "fifty:<player_id>", "hundred:<player_id>",
  // "fifer:<bowler_id>". Without this the toast re-fired on every
  // subsequent ball as long as the player's running total was *still*
  // at the milestone value (e.g. batter sits on 50 for 6 dot balls →
  // 6 toasts; or a fifer bowler bowls another over → another toast).
  const announcedMilestonesRef = useRef(new Set());
  const [pastBallsOpen, setPastBallsOpen] = useState(false);
  const [pastBalls, setPastBalls] = useState([]);
  const [pastBallsLoading, setPastBallsLoading] = useState(false);
  const bowlerSwapRef = useRef(false);
  const bowlerReleaseSides = ["Around", "Diagonal", "Over"];
  // 2026-05-29 — "Old" added between "New" and "Repl" per founder ask.
  // Auto-switch logic: the FIRST ball of the match defaults to "New";
  // every subsequent ball defaults to "Old" (operator can manually pick
  // any other). Replacement balls (ball gets damaged → umpire calls
  // for a new one) are always operator-picked via the Repl button.
  const ballTypes = ["New", "Old", "Repl"]

  // BBP init failures used to silently leave `aggregate=null` →
  // the page rendered "Loading…" forever with no visible error.
  // The QA agent caught this 2026-05-30 because the screenshot was
  // indistinguishable from a slow load. Surface the error so future
  // hangs are diagnosable from the DOM (and the agent can capture
  // them as test failures).
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const data = await loadBallSession(id); // ✅ CORRECT
        const agg = data?.aggregate;
        const nextState = data?.next_state ?? {};
        const nextStriker = nextState?.striker ?? agg?.current_striker ?? null;
        const nextNonStriker =
          nextState?.non_striker ?? agg?.current_non_striker ?? null;
        const nextBowler = nextState?.bowler ?? agg?.current_bowler ?? null;

        if (nextStriker || nextNonStriker || nextBowler) {
          setBall((prev) => ({
            ...prev,
            striker: nextStriker ?? prev.striker ?? null,
            non_striker: nextNonStriker ?? prev.non_striker ?? null,
            bowler: nextBowler ?? prev.bowler ?? null,
          }));
        }
      } catch (err) {
        // Capture enough that the operator (or QA agent) can identify
        // which call failed. Common cases: 401 (auth-store hydration
        // race), 500 (server error in initialise_ball_by_ball_session),
        // network error.
        const status = err?.response?.status;
        const detail =
          err?.response?.data?.detail ||
          err?.response?.data?.message ||
          err?.message ||
          String(err);
        console.error("loadBallSession failed:", err);
        setLoadError({ status, detail });
      }
    })();
  }, [id, setBall]);

  const applyServerState = (data) => {
    useScoringStore.setState((prev) => {
      // Bug fix (innings change): if the server sends a new
      // batting_team_id but no batting_team_name, the previous
      // `|| prev.battingTeamName` fallback preserved the OLD innings's
      // team-name string while the IDs swapped. Result: bowler-pick
      // modal labelled the wrong team while the player list was right.
      //
      // Now: only fall back to prev.X when the team_id ALSO hasn't
      // changed. Once team_id moves, discard the stale name. As a
      // secondary fallback derive from the new players list (which
      // carries team_name on each row), if available.
      const battingIdChanged = data.batting_team_id != null && data.batting_team_id !== prev.battingTeamId;
      const bowlingIdChanged = data.bowling_team_id != null && data.bowling_team_id !== prev.bowlingTeamId;
      const battersList = data.players?.batters || [];
      const bowlersList = data.players?.bowlers || [];
      const inferBattingName = battersList.find((p) => p?.team_name)?.team_name || "";
      const inferBowlingName = bowlersList.find((p) => p?.team_name)?.team_name || "";
      const battingTeamName =
        data.batting_team_name
        || (battingIdChanged ? inferBattingName : (prev.battingTeamName || inferBattingName || ""));
      const bowlingTeamName =
        data.bowling_team_name
        || (bowlingIdChanged ? inferBowlingName : (prev.bowlingTeamName || inferBowlingName || ""));
      return ({
      aggregate: data.aggregate,
      battingStats: data.batting_stats,
      bowlingStats: data.bowling_stats,
      players: data.players,
      actions: data.actions,
      events: data.events,
      matchEnded: Boolean(data.match_ended),
      matchTitle: data.match_title || prev.matchTitle || "",
      battingTeamName,
      bowlingTeamName,
      battingTeamId: data.batting_team_id ?? prev.battingTeamId ?? null,
      bowlingTeamId: data.bowling_team_id ?? prev.bowlingTeamId ?? null,
      inningsId: data.innings_id ?? prev.inningsId ?? null,
      currentInningsNumber:
        data.current_innings_number ?? prev.currentInningsNumber ?? null,
      umpires: data.umpires || prev.umpires || [],
      thirdUmpire: data.third_umpire ?? prev.thirdUmpire ?? null,
      fourthUmpire: data.fourth_umpire ?? prev.fourthUmpire ?? null,
      matchReferee: data.match_referee ?? prev.matchReferee ?? null,
      // Server-derived Test day/session/overs-today — overrides any
      // stale localStorage on the SPA side. null on limited overs;
      // the topbar chips hide themselves in that case.
      testState: data.test_state ?? prev.testState ?? null,
      // 2026-06-06 (v2) — Super Over context. Always overwrite (not
      // nullish-fallback) so that transitioning OUT of an SO innings
      // drops the flag instead of leaving it stuck from the prior SO.
      isSuperOver: Boolean(data.is_super_over),
      superOverIndex: data.super_over_index ?? null,
      // 2026-06-10 — bowling-end context (refreshed on every server tick).
      firstOverBowlingEnd: data.first_over_bowling_end ?? prev.firstOverBowlingEnd ?? "",
      stadiumEndAName: data.stadium_end_a_name ?? prev.stadiumEndAName ?? "",
      stadiumEndBName: data.stadium_end_b_name ?? prev.stadiumEndBName ?? "",
      });
    });
    // Auto-detect the wicket-keeper from the bowling-team XI so the Quick set
    // "Keeper took it" button isn't locked at session start. The PlayingXI
    // is_wicket_keeper flag is set when the operator confirms the XI screen;
    // the backend now surfaces it on the read-model.
    const keeperFromXI = (data?.players?.bowlers || []).find((p) => p.is_wicket_keeper);
    if (keeperFromXI?.id) {
      setWicketKeeperId((curr) => curr || keeperFromXI.id);
    }
    setStructuredRegion(null);
    setActiveZoneMatrix("pitch");

    const nextState = data?.next_state ?? {};
    const nextStriker =
      nextState?.striker ?? data.aggregate?.current_striker ?? null;
    const nextNonStriker =
      nextState?.non_striker ?? data.aggregate?.current_non_striker ?? null;
    const nextBowler = nextState?.bowler ?? data.aggregate?.current_bowler ?? null;

    setBall((prev) => ({
      striker: nextStriker,
      non_striker: nextNonStriker,
      bowler: nextBowler,

      completed_runs: 0,
      is_wide: false,
      is_no_ball: false,
      is_bye: false,
      is_leg_bye: false,
      is_boundary: false,
      is_short_run: false,
      is_quick_running: false,
      is_free_hit: false,
      no_ball_type: "",

      has_wicket: false,
      wicket_type: null,
      dismissed_player_id: null,
      dismissed_by_id: null,
      caught_by_id: null,
      stumped_by_id: null,
      run_out_fielder_1_id: null,
      run_out_fielder_2_id: null,
      decision_given_by_umpire: null,

      batters_crossed: null,
      run_out_subtype: "",
      is_dead_ball: false,
      dead_ball_reason: "",
      appealed_for: "",
      // 2026-07-07 — Opta/vendor gap-review batch. Live scorer usually
      // leaves both blank; can be filled in the EditBall dialog on
      // any ball post-hoc. bowler_spell is server-derived.
      batter_guard: "",
      fielding_position_landed: "",

      analytics_enabled: true,
      analytics: {
        delivery_type: "",
        foot_movement: "",
        air_movement: "",
        control: "",
        shot_connection: "",
        stroke: "",
        bat_subject: "",
        shot_intent: "",
        outcome_quality: "",
        batsman_activity: "",
        keeper_activity: "",
        bowler_plan: "",
        spin_direction: "",
        drift_direction: "",
        turn_after_pitch: "",
        bounce_category: "",
      },

      spatial: {
        shot_zone_x: null,
        shot_zone_y: null,
        wagon_wheel_x: null,
        wagon_wheel_y: null,
        pitch_zone: null,
        stump_zone: null,
        height_zone: null,
        batter_stump_zone: null,
        batter_impact_height: "",
        batter_impact_line: "",
        batter_impact_x: null,
        batter_impact_y: null,
        structured_region_id: null,
        structured_slice_index: null,
        structured_band_index: null,
        structured_position: null,
      },

      fielding: {
        fielders: [
          {
            player: null,
            action: "",
            fielding_position: "",
            runs_saved: null,
            runs_misfielded: null,
            overthrow_runs: null,
            difficulty: null,
          },
        ],
      },

      drs: {
        is_appealed: false,
        review_team_side: "",
        review_team_id: null,
        on_field_decision: "",
        overruled: false,
        final_decision: "",
        decision_given_by_umpire: null,
        third_umpire: null,
        impact_zone: "",
        wicket_impact: "",
        soft_signal: "",
        spike: false,
      },

      release: {
        bowler_release_x: null,
        bowler_release_y: null,
        bowler_release_position:
          prev?.release?.bowler_release_position || "Over",

        // 2026-08-03 — bowler release point (founder ask). Dropdown below
        // the wagon wheel; resets to NORMAL on every new ball (it codes a
        // per-delivery anomaly, so carrying the previous pick would
        // mis-stamp ordinary deliveries).
        bowler_release_point: "NORMAL",

        // wicket_keeper_position deliberately dropped from the live-ball
        // default. The "Keeper" chip group under Performance writes to
        // analytics.keeper_activity which is the canonical place for
        // the keeper's stance. Defaulting this here too stamped every
        // ball with "Diagonal" and made the Edit dialog's Release tab
        // look permanently set to "Diagonal"/"WK Up".
        wicket_keeper_position: prev?.release?.wicket_keeper_position || "",

        ball_type: prev?.release?.ball_type || "",
        is_break: false,
        break_type: "",
        speed_kph: null,
      },

      trajectory: {
        x: null,
        y: null,
      },

      video: {
        video_source_id: videoSource.recordingId || "",
        video_start_ms: null,
        video_end_ms: null,
        camera_angle: "",
      },
    }));
  };

  const buildBallPayload = () => {
    const payload = { ...ball };
    delete payload.has_wicket;
    delete payload.analytics_enabled;
    // CF-P1-05 — free_hit carry-over inference. If the immediately
    // previous ball was a no-ball and this ball isn't already marked as
    // a free-hit, infer it. The post-save reset at line ~1597 sets
    // is_free_hit=true on the next-ball draft, but the operator can
    // accidentally clear that toggle (manual click) or the draft can
    // race a refresh that loses the flag. Belt-and-braces inference at
    // payload-build time guarantees the free_hit flag chains correctly
    // when the prior ball was a no-ball.
    // Only for a NEW ball: during an edit "the previous ball" here is the
    // latest one, not the ball before the one being edited.
    if (!editingBallId && !payload.is_free_hit && !payload.is_no_ball) {
      const prev = recentBalls?.[recentBalls.length - 1];
      const prevWasNoBall = !!(prev && (prev.is_no_ball || prev.runs?.no_balls));
      if (prevWasNoBall) {
        payload.is_free_hit = true;
      }
    }
    // Video clip offsets — operator's explicit Clip in/out (file mode or
    // live) take precedence. Promote them to TOP LEVEL of the payload so
    // backend sets Ball.video_start_ms/end_ms (which is what the slicer
    // queries) and creates the BallVideo placeholder with the correct
    // deterministic ball_clip_key S3 path. Drop the nested video dict —
    // its video_source_id was the recording UUID, not the eventual clip
    // key, and would have made the slicer write to the wrong location.
    const explicitStart = payload.video?.video_start_ms;
    const explicitEnd = payload.video?.video_end_ms;
    // Round at the boundary — backend serializer is IntegerField and
    // rejects floats with a 400 ("A valid integer is required."). The
    // upstream sources (recorder.elapsedMs, fileUpload.fileTimeMs) are
    // already rounded, but defense-in-depth here so any future
    // refactor that introduces a float clip timestamp doesn't reintroduce
    // the "save fails after starting recording" regression.
    if (editingBallId) {
      // Editing a past ball keeps its clip. The rolling 10-second window
      // below is "now" in the live recording, which re-pointed the past
      // ball's clip at whatever was being filmed at edit time (audit C
      // #139). The edit dialog's Clip tools remain the way to re-time it.
      delete payload.video_start_ms;
      delete payload.video_end_ms;
    } else if (explicitStart != null && explicitEnd != null) {
      payload.video_start_ms = Math.round(explicitStart);
      payload.video_end_ms = Math.round(explicitEnd);
    } else {
      // Fallback for live-recording legacy: 10-sec rolling window for
      // operators who don't manually click Clip in/out while live.
      const elapsed = recorder?.elapsedMs ?? null;
      const recActive = recorder?.state === "recording" && elapsed != null;
      if (recActive) {
        payload.video_start_ms = Math.max(0, Math.round(elapsed - 10000));
        payload.video_end_ms = Math.round(elapsed);
      } else {
        delete payload.video_start_ms;
        delete payload.video_end_ms;
      }
    }
    delete payload.video;
    if (
      payload.spatial?.shot_zone_x === null &&
      payload.spatial?.shot_zone_y === null &&
      payload.spatial?.wagon_wheel_x === null &&
      payload.spatial?.wagon_wheel_y === null &&
      !payload.spatial?.pitch_zone &&
      !payload.spatial?.stump_zone &&
      !payload.spatial?.height_zone &&
      !payload.spatial?.batter_stump_zone &&
      !payload.spatial?.batter_impact_height &&
      !payload.spatial?.batter_impact_line &&
      payload.spatial?.batter_impact_x == null &&
      payload.spatial?.batter_impact_y == null &&
      !payload.spatial?.structured_region_id &&
      payload.spatial?.structured_slice_index == null &&
      payload.spatial?.structured_band_index == null &&
      !payload.spatial?.structured_position
    ) {
      delete payload.spatial;
    } else if (payload.spatial) {
      const s = payload.spatial;
      // Auto-derive batter_impact_* from existing height_zone / batter_stump_zone selection
      // so the modern schema columns get populated from the existing Height/Batter-Stump UI.
      // 2026-06-10 — was 'Shoulder: "SHLDR"' but backend's Ball.
      // batter_impact_height enum expects 'SHOULDER' (BATTER_IMPACT_HEIGHTS
      // in EditBallDialog). The mismatch silently dropped shoulder picks
      // — voice-coded "impact shoulder" set height_zone="Shoulder" but
      // never propagated to batter_impact_height because "SHLDR" failed
      // the enum check on save.
      const HEIGHT_MAP = {
        Head: "HEAD", Shoulder: "SHOULDER", Chest: "CHEST", Waist: "WAIST",
        Thigh: "THIGH", Knee: "KNEE", Shin: "SHIN", Yorker: "YORK",
      };
      const LINE_MAP = {
        WOO: "WOFF", OO: "OFF", OS: "MOFF", MS: "MID",
        LS: "MLEG", DL: "LEG", WDL: "WLEG",
      };
      if (!s.batter_impact_height && HEIGHT_MAP[s.height_zone]) {
        s.batter_impact_height = HEIGHT_MAP[s.height_zone];
      }
      if (!s.batter_impact_line && LINE_MAP[s.batter_stump_zone]) {
        s.batter_impact_line = LINE_MAP[s.batter_stump_zone];
      }
      if (!s.batter_impact_height) delete s.batter_impact_height;
      if (!s.batter_impact_line)   delete s.batter_impact_line;
      if (s.batter_impact_x == null) delete s.batter_impact_x;
      if (s.batter_impact_y == null) delete s.batter_impact_y;
    }
    if (payload.analytics) {
      // Drop empty analytics keys so backend doesn't store ""
      Object.keys(payload.analytics).forEach((k) => {
        if (payload.analytics[k] === "" || payload.analytics[k] == null) {
          delete payload.analytics[k];
        }
      });
      if (Object.keys(payload.analytics).length === 0) delete payload.analytics;
    }
    if (payload.batters_crossed == null) delete payload.batters_crossed;
    // Strip empty appealed_for / no_ball_type / dead_ball_reason — sending
    // "" persists the empty string server-side instead of treating it as
    // "no value", which then re-loads as "" on the next session and
    // confuses the UI's "is anything set?" checks.
    if (!payload.appealed_for) delete payload.appealed_for;
    if (!payload.no_ball_type) delete payload.no_ball_type;
    if (!payload.dead_ball_reason) delete payload.dead_ball_reason;
    if (!payload.run_out_subtype) delete payload.run_out_subtype;
    // 2026-07-07 — Opta/vendor gap-review batch. Both blank when
    // scorer skipped the (optional) picker.
    if (!payload.batter_guard) delete payload.batter_guard;
    if (!payload.fielding_position_landed) delete payload.fielding_position_landed;
    // ✅ NEW FIELDING LOGIC (multi-fielders)
    if (payload.fielding?.fielders?.length) {
      // CF-P0-11 — auto-fill fielding_position from CricketFieldEngine
      // resolver when the wagon-wheel position is set but the operator
      // hasn't picked a position chip. The same slice/band the wagon
      // wheel uses already maps cleanly to one of the 39 fielding
      // positions; leaving it blank when we can derive it loses the
      // signal for downstream analytics + the press report.
      const sliceIdx = payload.spatial?.structured_slice_index ?? null;
      const bandIdx = payload.spatial?.structured_band_index ?? null;
      const batterHand = (() => {
        const sid = ball?.striker ?? null;
        if (!sid) return "RIGHT";
        const p = (players?.batters || []).find((b) => b?.id === sid);
        return p?.batting_hand === "LEFT" ? "LEFT" : "RIGHT";
      })();
      const autoPos = (sliceIdx != null && bandIdx != null)
        ? resolveFieldingPosition(sliceIdx, bandIdx, batterHand)
        : null;
      const validFielders = payload.fielding.fielders
        .filter((f) => f.player) // remove empty rows
        .map((f) => ({
          player: f.player,
          fielding_position:
            f.fielding_position
            || (autoPos && autoPos !== "Unknown" ? autoPos : "")
            || "",
          action: f.action || "",
          runs_saved: f.runs_saved ?? null,
          runs_misfielded: f.runs_misfielded ?? null,
          overthrow_runs: f.overthrow_runs ?? null,
          is_substitute: !!f.is_substitute,
          difficulty:
            f.action === "Caught" || f.action === "Dropped"
              ? (f.difficulty ?? null)
              : null,
        }));

      if (validFielders.length > 0) {
        payload.fielding = {
          fielders: validFielders,
        };
      } else {
        delete payload.fielding;
      }
    } else {
      delete payload.fielding;
    }
    // Substitute-fielder wiring for catches — Law 24. SpatialPicker stores
    // is_substitute + replacing_player on fielders[0]; persist_ball expects
    // top-level caught_by_is_substitute + caught_by_replacing_player_id.
    const firstFielder = ball.fielding?.fielders?.[0];
    if (firstFielder?.is_substitute) {
      payload.caught_by_is_substitute = true;
      if (firstFielder.replacing_player) {
        payload.caught_by_replacing_player_id = firstFielder.replacing_player;
      }
    }
    if (!payload.drs?.is_appealed) {
      delete payload.drs;
    } else {
      // Strip empty modern-DRS fields
      const d = payload.drs;
      if (!d.impact_zone)   delete d.impact_zone;
      if (!d.wicket_impact) delete d.wicket_impact;
      if (!d.soft_signal)   delete d.soft_signal;
    }
    if (
      payload.release?.bowler_release_x === null &&
      payload.release?.bowler_release_y === null &&
      !payload.release?.bowler_release_position &&
      !payload.release?.bowler_release_point &&
      !payload.release?.wicket_keeper_position &&
      !payload.release?.ball_type &&
      !payload.release?.is_break &&
      !payload.release?.break_type &&
      payload.release?.speed_kph == null
    ) {
      delete payload.release;
    } else if (payload.release) {
      if (!payload.release.is_break) payload.release.break_type = "";
      if (payload.release.speed_kph == null) delete payload.release.speed_kph;
    }

    // 2026-06-10 — mirror the bowler-release-position pick (BBP main
    // flow uses short labels "Over" / "Around" / "Diagonal") to the
    // new Ball.bowling_angle column on the backend. Founder ask:
    // over-the-wicket / round-the-wicket needs to be queryable per
    // ball without joining BallReleaseData.
    const _angleSrc = ball.release?.bowler_release_position || "";
    const _angleMap = {
      "Over":       "OVER_THE_WICKET",
      "Around":     "ROUND_THE_WICKET",
      "Diagonal":   "DIAGONAL",
      // Backend canonical values pass through unchanged (used by the
      // EditBallDialog dropdown which uses the long enum).
      "OVER_THE_WICKET":  "OVER_THE_WICKET",
      "ROUND_THE_WICKET": "ROUND_THE_WICKET",
      "DIAGONAL":         "DIAGONAL",
      "WIDE_OVER":        "WIDE_OVER",
      "WIDE_ROUND":       "WIDE_ROUND",
    };
    payload.bowling_angle = _angleMap[_angleSrc] || "";
    if (payload.trajectory?.x === null && payload.trajectory?.y === null) {
      delete payload.trajectory;
    } else {
      payload.trajectory = [
        {
          sequence_index: 1,
          x: payload.trajectory.x,
          y: payload.trajectory.y,
          time_ms: 0,
        },
      ];
    }

    if (!ball.has_wicket) {
      delete payload.wicket_type;
      delete payload.dismissed_player_id;
      delete payload.dismissed_by_id;
      delete payload.caught_by_id;
      delete payload.wicket_third_umpire;
      delete payload.wicket_fourth_umpire;
      delete payload.caught_by_is_substitute;
      delete payload.caught_by_replacing_player_id;
      delete payload.stumped_by_id;
      delete payload.run_out_fielder_1_id;
      delete payload.run_out_fielder_2_id;
      delete payload.decision_given_by_umpire;
    }

    if (displayStrikerId) {
      payload.striker_hand = getEffectiveHand(
        displayStrikerId,
        getBatterHand(displayStrikerId),
      );
    }
    if (displayBowlerId) {
      payload.bowler_hand = getEffectiveHand(
        displayBowlerId,
        getBowlerHand(displayBowlerId),
      );
      // 2026-06-05 — per-ball bowling style override. Empty string sends
      // "use player default" to the backend; explicit code overrides.
      payload.bowler_style = getEffectiveBowlerStyle(
        displayBowlerId,
        getBowlerStyle(displayBowlerId),
      );
    }
    payload.umpire_bowler_end = umpireBowlerEndId || null;
    payload.umpire_square_leg = umpireSquareLegId || null;

    return payload;
  };;

  const ballQueue = useBallSyncQueue({
    matchId: id,
    onSync: async () => {
      // After each successful sync, refresh session state from server
      const fresh = await loadBallSession(id);
      if (fresh) applyServerState(fresh);
      // A queue-flushed ball is just as "saved" as a direct POST —
      // bump the session counter so SaveCounterPill reflects total
      // throughput, not just the synchronous-save path. (Without this,
      // a brief network blip would silently undercount + the QA agent's
      // counter-delta check fails on any ball that took the queue
      // path, even though server-side state is correct.)
      setSavedCount((c) => c + 1);
      setLastSavedAt(new Date());
    },
  });

  // Voice-charge bookkeeping: lastChargedVoiceTs is the most recent
  // voice.lastApplied.ts we've already debited for. On each saveBall, if
  // voice has a fresher applied event, debit 2 ZC and bump the marker.
  // Non-blocking — a 402 here surfaces via the global wallet modal but
  // doesn't undo the saved ball (the ball is the operator's authoritative
  // record; we'd rather over-collect on the next top-up than lose data).
  const lastChargedVoiceTsRef = useRef(0);
  const maybeChargeVoiceBall = () => {
    const ts = voice?.lastApplied?.ts;
    if (!ts || ts <= lastChargedVoiceTsRef.current) return;
    lastChargedVoiceTsRef.current = ts;
    // Idempotency-keyed on ts so accidental retries within the same
    // request lifecycle (network blip, double-click) collapse to one debit.
    api
      .post(
        "/api/scoring/ai/voice-ball-charge/",
        {},
        { headers: { "X-Idempotency-Key": `voice-${ts}` } },
      )
      .catch((e) => {
        // 402 → global modal will open via interceptor; nothing more to do.
        // Other errors: log + carry on; the saved ball isn't reversed.
        if (e?.response?.status !== 402) {
          console.warn("voice-ball-charge failed:", e?.response?.data || e.message);
        }
      });
  };

  // The draft after a ball is saved -- or queued offline -- so nothing
  // from it leaks into the next delivery. `rotateStrike` is for the
  // offline path only: online, applyServerState sets the batters from
  // the server; offline there is no server answer, so an odd number of
  // completed runs swaps them here (audit C #144). The end of an over is
  // still the operator's to call offline, as is the bowler.
  const clearDraftForNextBall = (payload, { rotateStrike = false } = {}) => {
    const rotated = (prev) => ((payload.completed_runs || 0) % 2 === 1
      ? { striker: prev.non_striker, non_striker: prev.striker }
      : {});
    // Auto-clear single-ball toggles so they don't leak to next ball.
    //    Free-hit is the exception: a no-ball must trigger the NEXT ball
    //    as a free hit, so keep is_free_hit set when last ball was a NB.
    //    Spatial fields are reset to explicit nulls (not empty {}) so
    //    SpatialPicker / ZoneMatrixPicker / wagon-wheel SVG read defined
    //    values rather than `undefined`, preventing stale-UI flicker.
    setBall((prev) => ({
      ...prev,
      ...(rotateStrike ? rotated(prev) : {}),
      is_wide: false, is_no_ball: false, is_bye: false, is_leg_bye: false,
      is_short_run: false, is_quick_running: false, is_dead_ball: false,
      // Chain: a no-ball makes the NEXT ball a free hit, and a free hit
      // that is itself a wide or no-ball carries over (Law 21.19).
      // Decided from the ball just SAVED: applyServerState above has
      // already reset the draft, so prev.is_no_ball was always false
      // here and the free hit never came up (audit C #142).
      is_free_hit: !!payload.is_no_ball || (!!payload.is_free_hit && !!payload.is_wide),
      is_boundary: false,
      completed_runs: 0,
      no_ball_type: "",
      // 2026-07-07 — fielding_position_landed is a per-ball outcome
      // (where the ball ended up on THIS delivery) → reset every
      // ball. batter_guard persists until the striker changes it
      // manually (batters rarely change guard mid-over).
      fielding_position_landed: "",
      wicket_type: null,
      has_wicket: false,
      dismissed_player_id: null, dismissed_by_id: null,
      caught_by_id: null, stumped_by_id: null,
      run_out_fielder_1_id: null, run_out_fielder_2_id: null,
      spatial: {
        shot_zone_x: null, shot_zone_y: null,
        wagon_wheel_x: null, wagon_wheel_y: null,
        pitch_zone: null, stump_zone: null,
        height_zone: null, batter_stump_zone: null,
        batter_impact_height: "", batter_impact_line: "",
        batter_impact_x: null, batter_impact_y: null,
        structured_region_id: null,
        structured_slice_index: null,
        structured_band_index: null,
        structured_position: null,
      },
      analytics: {},
    }));
    // Defensive: also clear the local picker state so the wagon-wheel pin
    // and pitch-matrix highlights immediately disappear for the next ball.
    setStructuredRegion(null);
    setActiveZoneMatrix("pitch");
    setSpatialTab("pitch");  };

  const handleSaveBall = async (idemKey) => {
    const payload = buildBallPayload();
    if (editingBallId) {
      // Editing always requires server (PATCH) — block if offline
      if (!ballQueue.online) {
        alert("You're offline. Past-ball edits need a connection. Reconnect and try again.");
        return;
      }
      await updateBall(editingBallId, payload);
      setEditingBallId(null);
      const fresh = await loadBallSession(id);
      if (fresh) applyServerState(fresh);
      return;
    }

    if (!ballQueue.online) {
      // Queue locally; the server is the only source of truth for the
      // score -- but the DRAFT must move on, or the next ball is entered
      // over this one's runs, extras and wicket (audit C #144).
      await ballQueue.enqueue(payload, idemKey);
      clearDraftForNextBall(payload, { rotateStrike: true });
      flashAiToast(true, "Saved offline — it will sync when you're back online. Check the striker and bowler at the end of an over.");
      return;
    }

    try {
      const res = await saveBall(id, payload, idemKey);
      applyServerState(res.data);
      // A retirement with no runs on that ball is between deliveries: it
      // doesn't use up a ball, so the over isn't finished yet. Say so, or
      // the coder thinks the over is stuck (WCPL 2026 final, 2026-10-14).
      // With runs taken (e.g. "1 leg bye, then retired out") it IS the
      // delivery and the over moves on as normal.
      if (
        ["RETIRED_OUT", "RETIRED_HURT", "RETIRED_NOT_OUT"].includes(payload?.wicket_type)
        && !(Number(payload?.completed_runs) > 0)
        && !payload?.is_wide && !payload?.is_no_ball
      ) {
        flashAiToast(true, "Retirement saved between balls, so no ball was used. Code the next delivery to continue the over. If runs were taken on that ball, edit it and add the runs.");
      }
      // Link any pending voice utterances to the just-saved ball so
      // post-match QC can play back what was said for each ball.
      // Fire-and-forget — link failure must not block scoring flow.
      const savedBallId = res?.data?.ball_id;
      // CF-P0-05 — move the pending AI suggestion into the per-ball
      // stash so a later EditBallDialog edit can fire observe-
      // correction. No-op when no AI suggestion was applied for this
      // ball. Persisted to localStorage so it survives refresh.
      commitAiObservationForBall(savedBallId);
      const pendingIds = pendingVoiceUtterancesRef.current;
      if (savedBallId && pendingIds.length > 0) {
        const ids = [...pendingIds];
        pendingVoiceUtterancesRef.current = [];
        // Mark this ball as voice-coded in localStorage so the recent-
        // balls strip can render a 🎤 badge. Per-match key so the
        // store doesn't accumulate across matches.
        try {
          const key = `zcd_voice_balls_${id}`;
          const raw = localStorage.getItem(key);
          const list = raw ? JSON.parse(raw) : [];
          if (!list.includes(savedBallId)) list.push(savedBallId);
          // Cap at 1000 entries — more than any single match could have.
          localStorage.setItem(key, JSON.stringify(list.slice(-1000)));
        } catch { /* ignore quota / parse errors */ }
        (async () => {
          try {
            await api.post("/api/scoring/voice/link/", {
              ball_id: savedBallId,
              utterance_ids: ids,
            });
          } catch (e) {
            // Restore so a future retry can link them. Capped at 50
            // to prevent unbounded growth on persistent failure.
            pendingVoiceUtterancesRef.current = [
              ...ids,
              ...pendingVoiceUtterancesRef.current,
            ].slice(-50);
            console.warn("voice utterance link failed:", e?.response?.data || e);
          }
        })();
      }
      // Bookkeeping for visible save confirmation
      setSavedCount((c) => c + 1);
      setLastSavedAt(new Date());
      setSaveError("");
      // Optional hands-free read-back of the ball outcome via TTS.
      // No-op if the operator hasn't enabled it (default off).
      ballReadback.speak(ballOutcomeSummary(payload));
      // Voice-mode per-ball charge (2 ZC). Fires only when voice contributed
      // to the saved state — detected by comparing voice.lastApplied.ts
      // against the last-charged timestamp. Idempotency-keyed on ts so a
      // retry never double-charges.
      maybeChargeVoiceBall();
      // Wallet hydrate + low-balance nudge — Zencredits change when AI
      // features run, and B2C scorers should see "running low" in time to
      // top up before they hit the 402 modal mid-innings.
      useWalletStore.getState().fetchBalance().then(() => checkLowBalance());
      // ── Post-save UX feedback ─────────────────────────────────────────
      // 1) Flash a 2s confirmation strip with the ball's outcome summary.
      const summaryParts = [];
      if (payload.is_wide)    summaryParts.push("wide");
      if (payload.is_no_ball) summaryParts.push("no-ball");
      if (payload.is_bye)     summaryParts.push("bye");
      if (payload.is_leg_bye) summaryParts.push("leg-bye");
      if (payload.is_free_hit)summaryParts.push("free hit");
      if (payload.wicket_type)summaryParts.push("WICKET (" + payload.wicket_type + ")");
      const runsText = `${payload.completed_runs ?? 0}r${payload.is_boundary ? " (boundary)" : ""}`;
      const flash = { text: `${runsText}${summaryParts.length ? " · " + summaryParts.join(" · ") : ""}`,
                      kind: payload.wicket_type ? "wicket"
                          : payload.is_boundary ? "boundary"
                          : "ok" };
      setLastBallFlash(flash);
      setTimeout(() => setLastBallFlash(null), 2200);

      // 2) Strike rotation toast, from the server's answer for the NEXT
      //    ball. Guessing "odd runs = the non-striker" was wrong on the
      //    last ball of an over, where the end-of-over change swaps them
      //    back, and missed runs taken on a wide (audit C #149).
      const nextStrikerId = res?.data?.next_state?.striker
        ?? res?.data?.aggregate?.current_striker ?? null;
      if (nextStrikerId && String(nextStrikerId) !== String(payload.striker)) {
        const newStrikerName =
          (players?.batters || []).find((p) => String(p.id) === String(nextStrikerId))?.name
          || "the other batter";
        setStrikeToast(newStrikerName);
        setTimeout(() => setStrikeToast(null), 1800);
      }

      // 3) Undo / redo bookkeeping. The 30s legacy timer stays for the
      //    in-bar visual hint, but UNDO is no longer gated by it — operator
      //    can undo any saved ball, then REDO if they undid by mistake.
      //    Push onto recentSavedPayloadsRef (capped) so multiple undos
      //    each preserve their own payload onto the redoStack. Clear
      //    the redo stack on each manual save so old undones don't
      //    leak through after the operator has moved on.
      lastSavedPayloadRef.current = payload;
      recentSavedPayloadsRef.current = [
        ...recentSavedPayloadsRef.current,
        payload,
      ].slice(-RECENT_SAVES_CAP);
      setRedoStack([]);
      setUndoSecondsLeft(30);
      // T143: log this save into the history scrubber. ball_id from
      // the response so the timeline row can open Edit Ball later.
      historyLog.push("save", {
        ball_id: res?.data?.ball_id || null,
        runs: payload.completed_runs ?? 0,
        wicket: !!payload.has_wicket,
        striker: payload.striker || null,
      });
      // T212: scorer activation events. Both client-side-deduped via
      // localStorage so the same user only fires "first_X" once per
      // device. Cross-device dedup happens server-side in the
      // ActivationEvent table (T204) via an EXISTS check.
      trackEvent("first_ball_saved", {
        match_id: id,
        ball_id: res?.data?.ball_id || null,
      }, { once: true, persona: "scorer" });
      // First voice-save lights up only when at least one voice
      // utterance contributed to this ball.
      if (pendingIds && pendingIds.length > 0) {
        trackEvent("first_voice_save", {
          match_id: id,
          ball_id: res?.data?.ball_id || null,
          utterance_count: pendingIds.length,
        }, { once: true, persona: "scorer" });
      }
      // Successful save — wipe the draft so a refresh doesn't replay
      // a payload the server already accepted.
      try { localStorage.removeItem(`zcd_ball_draft_${id}`); } catch { /* ignore */ }

      clearDraftForNextBall(payload);
    } catch (e) {
      // Network failure mid-flight → queue, under THIS attempt's key: the
      // request may have landed with only the reply lost, and the server
      // must see the queued copy as the same ball (audit C #140).
      if (!navigator.onLine || e?.code === "ERR_NETWORK") {
        await ballQueue.enqueue(payload, idemKey);
        clearDraftForNextBall(payload, { rotateStrike: true });
        flashAiToast(true, "Connection lost — this ball is queued and will sync automatically.");
        return;
      }
      throw e;
    }
  };

  const openPastBalls = async () => {
    if (!inningsId) return;
    setPastBallsOpen(true);
    setPastBallsLoading(true);
    try {
      const res = await listInningsBalls(inningsId);
      setPastBalls(res?.data?.results || []);
    } finally {
      setPastBallsLoading(false);
    }
  };

  const enterEditMode = async (ballId) => {
    try {
      const res = await getBallForEdit(ballId);
      const b = res?.data || {};
      // Hydrate BallInputContext from server-shaped payload, onto a BLANK
      // draft: spreading the next-ball draft carried its fielding, dead-ball,
      // appeal and clip fields into the edited ball (audit C #138).
      setBall((prev) => {
        const base = emptyBallDraft();
        return {
        ...base,
        analytics_enabled: prev.analytics_enabled,
        striker: b.striker || null,
        non_striker: b.non_striker || null,
        bowler: b.bowler || null,
        completed_runs: (b.runs?.runs_off_bat || 0) + (b.runs?.byes || 0) + (b.runs?.leg_byes || 0),
        is_wide: !!b.runs?.wides,
        is_no_ball: !!b.runs?.no_balls,
        is_bye: !!b.runs?.byes,
        is_leg_bye: !!b.runs?.leg_byes,
        is_boundary: !!b.runs?.is_boundary,
        is_short_run: !!b.runs?.is_short_run,
        is_quick_running: !!b.runs?.is_quick_running,
        is_free_hit: !!b.runs?.is_free_hit,
        no_ball_type: b.runs?.no_ball_type || "",
        has_wicket: !!b.wicket,
        wicket_type: b.wicket?.wicket_type || null,
        dismissed_player_id: b.wicket?.dismissed_player_id || null,
        dismissed_by_id: b.wicket?.dismissed_by_id || null,
        caught_by_id: b.wicket?.caught_by_id || null,
        stumped_by_id: b.wicket?.stumped_by_id || null,
        run_out_fielder_1_id: b.wicket?.run_out_fielder_1_id || null,
        run_out_fielder_2_id: b.wicket?.run_out_fielder_2_id || null,
        batters_crossed: b.batters_crossed ?? null,
        // 2026-07-07 — Opta/vendor gap-review batch. Hydrate the two new
        // per-ball fields so the edit dialog opens with saved values.
        batter_guard: b.batter_guard || "",
        fielding_position_landed: b.fielding_position_landed || "",
        is_dead_ball: !!b.is_dead_ball,
        dead_ball_reason: b.dead_ball_reason || "",
        appealed_for: b.appealed_for || "",
        run_out_subtype: b.run_out_subtype || "",
        analytics: { ...(base.analytics || {}), ...(b.analytics || {}) },
        spatial:   { ...(base.spatial   || {}), ...(b.spatial   || {}) },
        release:   { ...(base.release   || {}), ...(b.release   || {}) },
        drs:       { ...(base.drs       || {}), ...(b.drs       || {}), is_appealed: !!b.drs?.is_appealed },
        };
      });
      setEditingBallId(ballId);
      setPastBallsOpen(false);
    } catch (e) {
      alert("Failed to load ball: " + (e?.message || "unknown"));
    }
  };

  const cancelEdit = async () => {
    setEditingBallId(null);
    const fresh = await loadBallSession(id);
    if (fresh) applyServerState(fresh);
  };

  const clearSaveTimers = () => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
  };

  // Single-click default delay. Spinners bowl every ~15-20 sec so a
  // 3-sec confirmation window was too slow — operators couldn't keep
  // up. Dropped to 1 sec; the cancel window stays meaningful but
  // doesn't block the next delivery.
  // Operator can ALSO toggle "Quick mode" (see fastSaveMode below) to
  // skip the wait entirely, or double-click Save Ball to instant-save
  // a single ball without flipping the toggle.
  const SAVE_DELAY_MS = 1000;
  // Per-operator preference — persisted via localStorage so the toggle
  // sticks across reloads. (Could move to backend later; pure UX so
  // local is fine for now.)
  const [fastSaveMode, setFastSaveMode] = useState(() => {
    try { return localStorage.getItem("zcd_fast_save_mode") === "1"; }
    catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("zcd_fast_save_mode", fastSaveMode ? "1" : "0"); }
    catch { /* quota / private mode */ }
  }, [fastSaveMode]);

  // Wicket confirmation (2026-10-01). A ball with a wicket is never
  // saved straight from Save Ball: a pop-up shows the dismissed batter
  // (score + balls), bowler, dismissal type and fielders, and only
  // "Correct" saves it. The confirmed wicket's fields are remembered so
  // the save that follows — and a retry after a failed save — goes
  // through, while any edit to the wicket asks again.
  const [wicketConfirmOpen, setWicketConfirmOpen] = useState(false);
  const confirmedWicketSigRef = useRef(null);
  const wicketSig = ball?.has_wicket
    ? JSON.stringify([
        ball.wicket_type, ball.dismissed_player_id, ball.dismissed_by_id,
        ball.caught_by_id, ball.stumped_by_id,
        ball.run_out_fielder_1_id, ball.run_out_fielder_2_id,
      ])
    : null;
  const wicketNeedsConfirm = () =>
    !!wicketSig && !!ball?.wicket_type && confirmedWicketSigRef.current !== wicketSig;

  const performSaveWithErrorReporting = async () => {
    setSaveInFlight(true);
    // Auto-retry transient 5xx + network errors before surfacing the
    // visible banner. Validation errors (4xx) fail fast — retrying them
    // is pointless. Backoff: 500ms, 1500ms, 3500ms (total ~5.5s worst
    // case before we give up). The operator sees nothing during the
    // retries; if it eventually succeeds, the next ball isn't blocked.
    const isTransient = (err) => {
      if (!err) return false;
      if (!navigator.onLine || err.code === "ERR_NETWORK") return true;
      const status = err?.response?.status;
      // The first attempt is still being recorded server-side: wait for it.
      if (status === 409 && err?.response?.data?.code === "save_in_progress") return true;
      if (!status) return true; // network blip with no response
      return status >= 500 && status < 600;
    };
    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
    let lastErr = null;
    // One key for this ball across every attempt (audit C #140): a retry
    // after a lost reply is replayed by the server, never re-recorded.
    const idemKey = newIdempotencyKey();
    for (const delay of [0, 500, 1500, 3500]) {
      if (delay) await sleep(delay);
      try {
        await handleSaveBall(idemKey);
        confirmedWicketSigRef.current = null;
        setSaveInFlight(false);
        return;
      } catch (e) {
        lastErr = e;
        if (!isTransient(e)) break;
      }
    }
    // All retries exhausted (or non-transient error) — surface visibly.
    const msg =
      lastErr?.response?.data?.detail
      || (typeof lastErr?.response?.data === "string" ? lastErr.response.data : null)
      || (lastErr?.response?.data && JSON.stringify(lastErr.response.data).slice(0, 240))
      || lastErr?.message
      || "Save failed";
    setSaveError(msg);
    // Cancel the 30s "UNDO last ball" affordance — the last save
    // FAILED, so undo would actually undo the *previous* ball.
    setUndoSecondsLeft(0);
    setTimeout(() => setSaveError(""), 8000);
    setSaveInFlight(false);
  };

  // Re-pointed every render so the delayed save sends the draft as it is
  // when the timer fires.
  const latestSaveRef = useRef(null);
  latestSaveRef.current = performSaveWithErrorReporting;

  const startDelayedSave = () => {
    if (savePending || saveInFlight) return;
    if (wicketNeedsConfirm()) { setWicketConfirmOpen(true); return; }
    setSavePending(true);
    // No setInterval here — the progress bar's fill animates via a
    // CSS @keyframes (see zcd-save-progress in the JSX below).
    // Previously a 50ms interval fired setSaveProgress 20×/sec which
    // re-rendered the entire 4000-LOC BBB tree and made the
    // Performance metrics pane visibly flicker + drop click events.
    saveTimerRef.current = setTimeout(async () => {
      clearSaveTimers();
      setSavePending(false);
      // The LATEST save, not the one this click's render captured: a run,
      // extra or wicket tapped during the delay used to be dropped,
      // because that closure saw the draft as it was at the click
      // (audit C #141).
      await latestSaveRef.current();
    }, SAVE_DELAY_MS);
  };

  const cancelDelayedSave = () => {
    clearSaveTimers();
    setSavePending(false);
  };

  // Skip-the-wait path — double-click on Save Ball, or any caller that
  // wants instant persistence (e.g. shortcut key). Cancels any pending
  // delay timer first so we don't fire twice.
  const performImmediateSave = async () => {
    if (saveInFlight) return;
    if (wicketNeedsConfirm()) { cancelDelayedSave(); setWicketConfirmOpen(true); return; }
    cancelDelayedSave();
    setSaveError("");
    await performSaveWithErrorReporting();
  };

  // T140: visual diff preview before undo. Holds the pending-undo
  // payload + a computed diff string; rendering this surface a
  // confirm pill near the Undo button. Operator one-clicks to
  // confirm, dismiss, or just walks away (auto-times-out after 6s).
  // A localStorage "skip preview" flag lets power users opt out.
  const [pendingUndo, setPendingUndo] = useState(null);
  const undoPreviewTimerRef = useRef(null);
  const SKIP_UNDO_PREVIEW_KEY = "zcd_skip_undo_preview";

  // T145: reason annotation. After a successful undo, surface a
  // small popover asking why. The answer is logged via trackEvent
  // (event_key="undo_reason") so the per-persona analytics dash
  // can break operator-error patterns down. Self-dismisses after
  // 8 seconds if the operator ignores it.
  const [undoReasonPrompt, setUndoReasonPrompt] = useState(null);
  const undoReasonTimerRef = useRef(null);
  const SKIP_UNDO_REASON_KEY = "zcd_skip_undo_reason";

  const captureUndoReason = (reasonKey, meta = {}) => {
    if (undoReasonTimerRef.current) {
      clearTimeout(undoReasonTimerRef.current);
      undoReasonTimerRef.current = null;
    }
    // Fire-and-forget analytics. Wrapping in trackEvent so dedupe
    // semantics are caller-controlled (none here — every undo is
    // a fresh datapoint, not a "first X").
    trackEvent("undo_reason", {
      reason: reasonKey,
      match_id: id,
      ...meta,
    }, { persona: "scorer" });
    setUndoReasonPrompt(null);
  };

  const showUndoReasonPrompt = (meta) => {
    // Operator opt-out persists per-user — power scorers who do dozens
    // of undos per match don't want the popover every time.
    let skip = false;
    try { skip = localStorage.getItem(SKIP_UNDO_REASON_KEY) === "1"; } catch {}
    if (skip) return;
    setUndoReasonPrompt({ ts: Date.now(), ...meta });
    if (undoReasonTimerRef.current) clearTimeout(undoReasonTimerRef.current);
    undoReasonTimerRef.current = setTimeout(() => setUndoReasonPrompt(null), 8000);
  };

  // Compute a human-readable diff for the ball that's about to be
  // undone. Pure function over recentBalls — no server call, no API.
  // Returns null if there's no last ball to describe.
  const computeUndoDiff = () => {
    const last = recentBalls[recentBalls.length - 1];
    if (!last) return null;
    const over = formatBallReference(aggregate?.match_type, { overNumber: last.over_number, ballInOver: last.ball_in_over, overBase: LIVE_OVER_BASE });
    const bits = [];
    const runsTotal = (last.runs_off_bat ?? 0) + (last.extras ?? 0);
    if (runsTotal > 0) bits.push(`–${runsTotal} run${runsTotal === 1 ? "" : "s"}`);
    if (last.wicket) bits.push("–1 wicket");
    if (last.is_wide) bits.push("wide");
    if (last.is_no_ball) bits.push("no-ball");
    if (last.is_legal === false && !last.is_wide && !last.is_no_ball) bits.push("dead-ball");
    const summary = bits.length ? bits.join(" · ") : "no score impact";
    const striker = last.striker || "striker";
    return { over, summary, striker };
  };

  // Replaces the direct handleUndoBall on the click path. If the
  // operator has opted out, falls through to handleUndoBall directly.
  // Otherwise sets pendingUndo and starts the 6s auto-dismiss timer.
  const handleUndoBallRequest = () => {
    let skip = false;
    try { skip = localStorage.getItem(SKIP_UNDO_PREVIEW_KEY) === "1"; } catch {}
    if (skip) return handleUndoBall();
    const diff = computeUndoDiff();
    if (!diff) return handleUndoBall();   // nothing to preview; fall through
    setPendingUndo(diff);
    if (undoPreviewTimerRef.current) clearTimeout(undoPreviewTimerRef.current);
    undoPreviewTimerRef.current = setTimeout(() => setPendingUndo(null), 6000);
  };

  const confirmPendingUndo = () => {
    if (undoPreviewTimerRef.current) {
      clearTimeout(undoPreviewTimerRef.current);
      undoPreviewTimerRef.current = null;
    }
    // T141: if a batch undo was queued (picker selected N>0), call
    // the bulk endpoint; otherwise fall through to single-ball undo.
    const batchN = pendingUndo?._batchN;
    setPendingUndo(null);
    if (batchN && batchN > 1) {
      handleUndoLastN(batchN);
    } else {
      handleUndoBall();
    }
  };

  const cancelPendingUndo = () => {
    if (undoPreviewTimerRef.current) {
      clearTimeout(undoPreviewTimerRef.current);
      undoPreviewTimerRef.current = null;
    }
    setPendingUndo(null);
  };

  // T138: undoInFlight guard. Two fast clicks on the Undo button used
  // to fire two POSTs racing each other — the second one would delete
  // a different ball with no operator intent. Mirror the redoInFlight
  // pattern but as a ref (we don't need to re-render on the change).
  const undoInFlightRef = useRef(false);
  const handleUndoBall = async () => {
    if (undoInFlightRef.current) return;
    undoInFlightRef.current = true;
    // Capture the client-side fallback BEFORE the server call. Source
    // order on the push:
    //   1. Server response `last_undone_ball` — authoritative; survives
    //      page reloads + back-to-back undos because the deleted ball's
    //      payload is rebuilt from its DB rows on the server.
    //   2. Local recent-saves fallback (older backends / first ball
    //      after reload before we've seen a save).
    const localFallback =
      recentSavedPayloadsRef.current[recentSavedPayloadsRef.current.length - 1]
      ?? lastSavedPayloadRef.current
      ?? null;
    try {
      const res = await undoBall(id);
      applyServerState(res.data);
      const snapshot = res.data?.last_undone_ball || localFallback;
      // T139: reset is_free_hit on the in-progress next-ball form.
      // Free-hit only persists across saves when the JUST-PREVIOUS ball
      // was a no-ball (Law 21). After an undo, we don't know what the
      // new previous-ball is (could be a regular delivery, could be
      // another no-ball). Conservative call: clear the flag so the
      // operator doesn't accidentally score the next ball as a free-hit
      // when it shouldn't be. If it actually should be (undo was of a
      // post-NB ball), the operator can re-tick the chip — minor cost
      // vs. silently mis-scoring.
      setBall((prev) => ({ ...prev, is_free_hit: false }));
      // Explicit recent-balls refresh — applyServerState updates the
      // aggregate but the recent-balls strip is fetched separately via
      // listInningsBalls. The useEffect that watches aggregate.runs /
      // wickets / legal_balls misses the case where the undone ball was
      // a wide/no-ball/dead (none of those counters changed), so the
      // strip would still show the deleted ball until next event. Force
      // a refetch here so undo always visibly removes the ball.
      if (inningsId) {
        listInningsBalls(inningsId)
          .then((r) => setRecentBalls((r?.data?.results || []).slice(-12)))
          .catch(() => {});
      }
      if (snapshot) {
        setRedoStack((prev) => [...prev, snapshot]);
        // Pop the just-undone ball off the recent-saves history so
        // the NEXT undo gets the next-most-recent snapshot (not the
        // same one we just used). Also refresh the single-slot ref
        // for legacy readers.
        recentSavedPayloadsRef.current = recentSavedPayloadsRef.current.slice(0, -1);
        lastSavedPayloadRef.current =
          recentSavedPayloadsRef.current[recentSavedPayloadsRef.current.length - 1] ?? null;
      }
      // T145: prompt for why. Single ball — kind="single".
      showUndoReasonPrompt({ kind: "single" });
      // T143: timeline entry for the undo. Captures the snapshot's
      // ball reference so the scrubber row can show "Undid ball 14.3".
      historyLog.push("undo", {
        runs: snapshot?.completed_runs ?? 0,
        wicket: !!snapshot?.has_wicket,
      });
      // T212: scorer activation event — fires once per device on
      // the operator's first ever undo. Useful funnel metric: how
      // soon does a new scorer rely on the undo path?
      trackEvent("first_undo", { match_id: id }, { once: true, persona: "scorer" });
      // T147: refresh trash so the panel shows the just-undone entry.
      refreshTrash();
    } catch (e) {
      const msg =
        e?.response?.data?.detail
        || e?.message
        || "Undo failed";
      setSaveError(msg);
      setTimeout(() => setSaveError(""), 6000);
    } finally {
      undoInFlightRef.current = false;
    }
  };

  // T141: bulk undo. Single round-trip, single rebuild server-side
  // for the last N balls. Pushes all undone payloads onto redoStack
  // in newest-first order so the next Redo replays the most-recent
  // ball first (matching the user's mental model).
  const handleUndoLastN = async (n) => {
    if (undoInFlightRef.current) return;
    if (!n || n < 1) return;
    undoInFlightRef.current = true;
    try {
      const res = await undoLastNBalls(id, n);
      applyServerState(res.data);
      setBall((prev) => ({ ...prev, is_free_hit: false }));
      const undone = res.data?.last_undone_balls || [];
      if (undone.length > 0) {
        setRedoStack((prev) => [...prev, ...undone]);
      }
      // Trim the recent-saves local fallback to match the server.
      // slice(0, -0) is slice(0, 0) -- it emptied the whole list when the
      // server returned no undone balls (audit C #148).
      if (undone.length > 0) {
        recentSavedPayloadsRef.current =
          recentSavedPayloadsRef.current.slice(0, -undone.length);
      }
      lastSavedPayloadRef.current =
        recentSavedPayloadsRef.current[recentSavedPayloadsRef.current.length - 1] ?? null;
      if (inningsId) {
        listInningsBalls(inningsId)
          .then((r) => setRecentBalls((r?.data?.results || []).slice(-12)))
          .catch(() => {});
      }
      const summary = res.data?.undo_summary;
      if (summary) {
        setSaveError("");
        // Re-use the existing transient toast slot to confirm the
        // bulk action; aiToast has shorter dwell than saveError.
        setAiToast({
          ok: true,
          msg: `Undid ${summary.deleted_count} ball${summary.deleted_count === 1 ? "" : "s"} (${summary.first_undone_over} → ${summary.last_undone_over}). Redo available.`,
        });
        setTimeout(() => setAiToast(null), 4500);
      }
      // T145: prompt for the bulk-undo reason. kind="batch" + count
      // lets the analytics dash separate "1-ball typo fixes" from
      // "5-ball whole-over redos" — they hint at different root causes.
      showUndoReasonPrompt({ kind: "batch", count: summary?.deleted_count || n });
      // T143: timeline entry for the bulk undo. Span lets the row
      // render "Undid 5 balls (14.3 → 13.5)".
      historyLog.push("undo_batch", {
        count: summary?.deleted_count || n,
        first_over: summary?.first_undone_over,
        last_over:  summary?.last_undone_over,
      });
      refreshTrash();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Bulk undo failed";
      setSaveError(msg);
      setTimeout(() => setSaveError(""), 6000);
    } finally {
      undoInFlightRef.current = false;
    }
  };

  const [redoInFlight, setRedoInFlight] = useState(false);
  const handleRedoBall = async () => {
    // Read state synchronously, do side effects outside setState. The
    // prior implementation fired saveBall inside setRedoStack's updater
    // function — a React anti-pattern that runs twice in StrictMode and
    // doesn't gate against rapid double-clicks. Now: optimistic pop,
    // async save, restore on error. Inflight guard prevents two redoes
    // racing the same payload.
    if (redoInFlight) return;
    if (redoStack.length === 0) return;
    const payload = redoStack[redoStack.length - 1];
    // Defensive: if the undo response gave us a null/empty payload
    // (server-side serialisation failure on that ball), we can't redo
    // it — pop the dead entry and surface a clear message instead of
    // POSTing an empty body that 400s on required-field validation.
    if (!payload || typeof payload !== "object" || !payload.striker) {
      setRedoStack((prev) => prev.slice(0, -1));
      setSaveError("Can't redo this ball — its details weren't captured. Re-score it manually.");
      setTimeout(() => setSaveError(""), 6000);
      return;
    }
    setRedoInFlight(true);
    setRedoStack((prev) => prev.slice(0, -1));
    try {
      const res = await saveBall(id, payload);
      applyServerState(res.data);
      // Refresh the recent-balls strip — same reason as in undo:
      // wide / no-ball / dead-ball redoes don't bump the
      // legal_balls / wickets counters that the useEffect watches,
      // so without an explicit refresh the redone ball wouldn't
      // appear in the timeline strip.
      if (inningsId) {
        listInningsBalls(inningsId)
          .then((r) => setRecentBalls((r?.data?.results || []).slice(-12)))
          .catch(() => {});
      }
      lastSavedPayloadRef.current = payload;
      recentSavedPayloadsRef.current = [
        ...recentSavedPayloadsRef.current,
        payload,
      ].slice(-RECENT_SAVES_CAP);
      setSavedCount((c) => c + 1);
      setLastSavedAt(new Date());
      setSaveError("");
      setUndoSecondsLeft(30);
      useWalletStore.getState().fetchBalance().then(() => checkLowBalance());
      // T143: timeline entry for the redo.
      historyLog.push("redo", {
        runs: payload.completed_runs ?? 0,
        wicket: !!payload.has_wicket,
      });
    } catch (e) {
      // Restore the popped entry so the operator can retry — REDO
      // shouldn't silently lose data on transient failure.
      setRedoStack((r) => [...r, payload]);
      const msg = e?.response?.data?.detail || e?.message || "Redo failed";
      setSaveError(msg);
      setTimeout(() => setSaveError(""), 6000);
    } finally {
      setRedoInFlight(false);
    }
  };

  // ------ AI-suggest (closed beta) ----------------------------------------
  const flashAiToast = (ok, msg) => {
    setAiToast({ ok, msg });
    setTimeout(() => setAiToast(null), 4000);
  };

  // Reverse-lookup: backend returns suggestions keyed by analytics fieldName
  // (delivery_type, stroke, foot_movement, ...). Map back to the chip-group
  // label that handleActivitySelect expects.
  const FIELD_TO_GROUP = useMemo(() => {
    const m = {};
    Object.entries(ACTIVITIES_CONFIG).forEach(([group, cfg]) => {
      if (cfg?.fieldName) m[cfg.fieldName] = group;
    });
    return m;
  }, []);

  const handleAiSuggest = async () => {
    if (aiBusy) return;
    setAiBusy(true);
    try {
      const frames = await videoPlayerRef.current?.captureFrames(8, 350);
      if (!frames || frames.length === 0) {
        flashAiToast(false, "No video frames available — load a clip / start a stream first.");
        return;
      }
      const res = await aiSuggestBall({
        frames,
        context: {
          match_id: id,
          bowler_type: bowlerType || null,
          batter_hand: displayStrikerId
            ? getEffectiveHand(displayStrikerId, getBatterHand(displayStrikerId))
            : "RIGHT",
          runs_off_bat: ball?.runs_off_bat ?? null,
          is_wide: !!ball?.is_wide,
          is_no_ball: !!ball?.is_no_ball,
          has_wicket: !!ball?.has_wicket,
        },
      });
      const s = res.data?.suggestions || {};
      let applied = 0;
      Object.entries(s).forEach(([field, value]) => {
        if (!value || typeof value !== "string") return;
        const group = FIELD_TO_GROUP[field];
        if (!group) return;
        handleActivitySelect(group, value);
        applied += 1;
      });
      // Wagon position — special-cased; field is structured_position on spatial.
      const wagon = (s.structured_position || "").trim();
      if (wagon) {
        setBall((prev) => ({
          ...prev,
          spatial: {
            ...prev.spatial,
            structured_position: wagon,
          },
        }));
        applied += 1;
      }
      const conf = s.confidence || "?";
      const charged = res.data?.credits_charged ?? 0;
      // CF-P0-05 — stash the suggestion so we can fire observe-
      // correction if the operator later overrides via EditBallDialog.
      // feature_code must match ai_learning.models.FeatureCode (lowercase
      // string values); the backend ObserveView 400s on anything else.
      setPendingAiObservation({
        feature_code: "ai_tag",
        context: {
          match_id: id,
          bowler_type: bowlerType || null,
        },
        ai_output: { ...s },
        model_name: res.data?.model_name || "",
      });
      flashAiToast(
        true,
        `AI suggested ${applied} fields (confidence: ${conf}). ${charged > 0 ? `${charged} Zencredits charged. ` : ""}Tap chips to override.`,
      );
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "AI suggest failed";
      flashAiToast(false, msg);
    } finally {
      setAiBusy(false);
    }
  };

  // ------ AI Score — per-ball OUTCOME inference (Phase C rewire) ---------
  // Sibling of AI Suggest above (kept for the few callers still on the
  // legacy chip-fill flow). AI Score fills the OUTCOME fields the umpire
  // would call: runs_off_bat, extras_type + extras_runs, wicket_type.
  // Lower raw cost than Suggest (smaller prompt + tighter schema) so we
  // charge 2 ZC instead of 7.
  const handleAiScore = async () => {
    if (aiBusy) return;
    setAiBusy(true);
    try {
      const frames = await videoPlayerRef.current?.captureFrames(8, 350);
      if (!frames || frames.length === 0) {
        flashAiToast(false, "No video frames available — load a clip / start a stream first.");
        return;
      }
      const res = await aiScoreBall({
        frames,
        context: {
          match_id: id,
          bowler_type: bowlerType || null,
          batter_hand: displayStrikerId
            ? getEffectiveHand(displayStrikerId, getBatterHand(displayStrikerId))
            : "RIGHT",
        },
      });
      const out = res.data?.outcome || res.data || {};
      const charged = res.data?.credits_charged ?? 0;
      const conf = out.confidence || "?";
      // Apply the umpire-equivalent outcome to the working ball draft.
      // Only fill fields the model returned with non-null confidence —
      // never overwrite operator-set values with `null` / "".
      setBall((prev) => {
        const next = { ...prev };
        if (typeof out.runs_off_bat === "number") {
          next.runs_off_bat = out.runs_off_bat;
          next.completed_runs = out.runs_off_bat;
        }
        if (typeof out.extras_runs === "number" && out.extras_runs > 0 && out.extras_type) {
          // Toggle the matching extras flag.
          if (out.extras_type === "WIDE")    next.is_wide = true;
          if (out.extras_type === "NO_BALL") next.is_no_ball = true;
          if (out.extras_type === "BYES")    next.is_bye = true;
          if (out.extras_type === "LEG_BYES")next.is_leg_bye = true;
        }
        if (out.wicket_type && out.wicket_type.trim()) {
          next.has_wicket = true;
          next.wicket_type = out.wicket_type;
          // CF-P0-09 — try to resolve dismissed_player_hint to a real
          // player id so the wicket modal opens with the dismissal
          // pre-filled. The hint is free-text ("striker", "mid-off
          // fielder", or sometimes a name) — match against striker /
          // non_striker first (most common case), then fall back to
          // the batters roster by name token.
          const hint = (out.dismissed_player_hint || "").toLowerCase().trim();
          if (hint) {
            if (/^striker$|^batsman$|^batter$/.test(hint)) {
              next.dismissed_player_id = prev.striker || null;
            } else if (/non.?striker/.test(hint)) {
              next.dismissed_player_id = prev.non_striker || null;
            } else {
              const match = (players?.batters || []).find((b) =>
                (b?.full_name || "").toLowerCase().includes(hint)
                || hint.includes((b?.full_name || "").toLowerCase()),
              );
              if (match) next.dismissed_player_id = match.id;
            }
          }
        }
        return next;
      });
      // CF-P0-09 — when AI Score returns a wicket, auto-open the wicket
      // modal so the operator finishes the dismissed_by + caught_by
      // resolution without having to find the button. The dismissal
      // type is already on the draft so the modal opens with the
      // wicket_type pre-selected and the operator just confirms.
      if (out.wicket_type && out.wicket_type.trim()) {
        setWicketModalOpen(true);
      }
      // CF-P0-05 — stash this suggestion for observe-correction if the
      // operator later overrides via EditBallDialog. feature_code must
      // match ai_learning.models.FeatureCode (lowercase string values).
      setPendingAiObservation({
        feature_code: "ai_score",
        context: {
          match_id: id,
          bowler_type: bowlerType || null,
        },
        ai_output: {
          runs_off_bat: out.runs_off_bat ?? null,
          extras_type: out.extras_type ?? null,
          extras_runs: out.extras_runs ?? null,
          wicket_type: out.wicket_type ?? null,
        },
        model_name: res.data?.model_name || "",
      });
      const hintBits = [];
      if (typeof out.runs_off_bat === "number") hintBits.push(`${out.runs_off_bat} runs`);
      if (out.extras_type && out.extras_runs)   hintBits.push(`${out.extras_runs} ${out.extras_type.toLowerCase().replace("_"," ")}`);
      if (out.wicket_type)                      hintBits.push(`wicket: ${out.wicket_type}`);
      if (out.dismissed_player_hint)            hintBits.push(`(${out.dismissed_player_hint})`);
      const summary = hintBits.length ? hintBits.join(" · ") : "no clear outcome";
      flashAiToast(
        true,
        `AI Score (${conf}): ${summary}.${charged > 0 ? ` ${charged} ZC charged.` : ""}`,
      );
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "AI Score failed";
      flashAiToast(false, msg);
    } finally {
      setAiBusy(false);
    }
  };

  // ------ AI tag (whole-match commentary fill, existing endpoint) ---------
  const handleAiTagMatch = async () => {
    if (aiBusy) return;
    setAiBusy(true);
    try {
      const res = await aiTagMatch(id, { fill_missing_only: true, max: 200 });
      const { tagged = 0, skipped = 0, error } = res.data || {};
      if (error) flashAiToast(false, `AI tag error: ${error}`);
      else flashAiToast(true, `AI-tagged ${tagged} balls (${skipped} already complete).`);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "AI tag failed";
      flashAiToast(false, msg);
    } finally {
      setAiBusy(false);
    }
  };

  // Voice scoring — needs save/undo/cancel handlers above to be declared first
  const voice = useVoiceScoring({
    activeSpatialTab: spatialTab,
    currentSpatial: ball?.spatial,
    // 2026-05-28 — pass the picker modal context so the voice hook can
    // route bare-name utterances ("arun lion", "mohan tiger") straight
    // to handleSelectPlayer when the bowler / striker / non-striker
    // picker is open. candidates flips between batters and bowlers
    // depending on which picker is showing.
    selectionModalContext: {
      open: selectionModal.open,
      type: selectionModal.type,
      candidates:
        selectionModal.type === "bowler"
          ? (players?.bowlers || [])
          : (selectionModal.type === "striker" || selectionModal.type === "non_striker")
            ? (players?.batters || [])
            : [],
    },
    onModalNamePick: (playerId) => handleSelectPlayer(playerId),
    setBall,
    onSave:   () => { if (!savePending && !saveInFlight) startDelayedSave(); },
    // T140: voice undo also routes through the preview path, so an
    // accidentally-heard "undo" gives the operator a 6-second window
    // to cancel. T146 will add a voice-specific "say confirm" loop
    // on top of this. For now the visual preview is the gate.
    onUndo:   () => { handleUndoBallRequest(); },
    // T141: voice bulk undo — "undo last 5". Drives the same preview
    // pill the picker uses, so the operator always gets a 6s window
    // to cancel an accidentally-heard bulk command.
    onUndoLastN: (n) => {
      const diff = { over: `last ${n}`, summary: `Remove last ${n} balls`, striker: "(batch)" };
      setPendingUndo({ ...diff, _batchN: n });
      if (undoPreviewTimerRef.current) clearTimeout(undoPreviewTimerRef.current);
      undoPreviewTimerRef.current = setTimeout(() => setPendingUndo(null), 6000);
    },
    onCancel: () => { cancelDelayedSave(); },
    // AI fallback — when the toggle is on, unrecognized voice phrases
    // are sent to Claude Haiku via /api/scoring/ai/voice-parse/.
    // bowlerType (PACE/SPIN) is a hint Claude uses to pick from the
    // right delivery-type pool. Inline-computed to avoid a hoisting
    // issue (bowlerType const is declared further down for the right
    // pane).
    aiFallbackEnabled: voiceAiFallback,
    bowlerType: bowlerTypeFromDelivery(ball?.analytics?.delivery_type),
    // Striker handedness — drives cell-by-number column mirroring on
    // pitch/impact matrices when a LH batter is on strike. Inlined
    // here because the displayStrikerId / getBatterHand /
    // getEffectiveHand helpers are declared FURTHER DOWN in the
    // component body, so referencing them in this useVoiceScoring
    // props object would hit a temporal-dead-zone ReferenceError
    // at render time (caught by the team 2026-05-26: "app is not
    // working"). Lookup is identical — read striker id off
    // ball + scoringStore, then map to batting_hand off players.batters.
    batterHand: (() => {
      const sid = ball?.striker ?? null;
      if (!sid) return "RIGHT";
      const p = (players?.batters || []).find((b) => b?.id === sid);
      const h = p?.batting_hand;
      return h === "LEFT" ? "LEFT" : "RIGHT";
    })(),
    // Open the wicket dismissal picker on bare "wicket" / "out" /
    // "dismissal" / "how's that" (Krishna ask 2026-05-26).
    onOpenWicketModal: () => setWicketModalOpen(true),
    // Left-panel action buttons (founder report 2026-05-26).
    // Each handler mirrors what clicking the button does on screen.
    onRedo: () => handleRedoBall(),
    onClipIn: () => {
      const ts = videoSource.videoTimestampMs;
      // 2026-05-28 — surface the no-video case so operators don't see
      // a silent failure. Voice command was previously a no-op when no
      // recording / live stream was loaded, leading to "clip in not
      // working" reports. Now there's an explicit toast either way.
      if (ts == null) {
        flashAiToast(false, "Clip in: no video loaded — start a recording or live stream first.");
        return;
      }
      setBall((prev) => ({
        ...prev,
        video: { ...prev.video, video_start_ms: ts },
      }));
      flashAiToast(true, `▶ Clip in @ ${(ts / 1000).toFixed(1)}s`);
    },
    onClipOut: () => {
      const ts = videoSource.videoTimestampMs;
      if (ts == null) {
        flashAiToast(false, "Clip out: no video loaded — start a recording or live stream first.");
        return;
      }
      setBall((prev) => ({
        ...prev,
        video: { ...prev.video, video_end_ms: ts },
      }));
      flashAiToast(true, `■ Clip out @ ${(ts / 1000).toFixed(1)}s`);
    },
    onOpenDrs: () => {
      // 2026-05-28 — was opening the wicket modal because DRS used to
      // be rolled in there. The form now has a dedicated DRS panel
      // that surfaces when ball.drs.is_appealed is true (line ~4617).
      // Founder ask: "drs" voice should ONLY toggle the DRS appeal,
      // not navigate to the wicket modal. Set is_appealed=true and
      // the inline panel reveals itself for the operator to fill in
      // Review By / On-field Decision / Final Decision.
      setBall((prev) => ({
        ...prev,
        drs: { ...(prev.drs || {}), is_appealed: true },
      }));
      flashAiToast(true, "DRS appeal flagged — fill in Review By / decision in the panel below");
    },
    onOpenQc: () => {
      navigate(`/ball/qc/${id}`);
    },
    onOpenEditBall: () => {
      setEditingBallId(recentBalls[0]?.id || null);
      setEditBallDialogOpen(true);
    },
    // Cross-ball voice editing — "edit ball 2.4 to wide" (founder ask
    // 2026-05-26). Find the ball with the spoken over+ball-in-over
    // reference in recentBalls; open the edit dialog on it. The inner
    // phrase becomes an aiTagMatch-style intent the operator can
    // verify before saving. For now we just open the dialog with the
    // ball selected — the operator types or voice-codes the change
    // inside the modal (the dialog has its own field controls).
    // Voice "undo ball X.Y" — find the ball by reference, open the
    // edit dialog. Doesn't auto-delete (destructive); operator clicks
    // the Delete button inside the dialog if that's what they want.
    // Founder ask 2026-05-26.
    onUndoBallByRef: (over, ball) => {
      const target = (recentBalls || []).find((b) =>
        Number(b.over_number) === Number(over) && Number(b.ball_in_over) === Number(ball)
      );
      if (!target) {
        flashAiToast(false, `No ball ${over}.${ball} in recent — use Edit Ball list.`);
        return;
      }
      setEditingBallId(target.id);
      setEditBallDialogOpen(true);
      flashAiToast(true, `Opening ball ${over}.${ball} — click Delete inside to remove it.`);
    },
    onEditBallByRef: (over, ball, inner) => {
      const target = (recentBalls || []).find((b) =>
        Number(b.over_number) === Number(over) && Number(b.ball_in_over) === Number(ball)
      );
      if (!target) {
        flashAiToast(false, `No ball ${over}.${ball} in recent — try Edit Ball and pick from the list.`);
        return;
      }
      // Parse the inner phrase through the same parser the live-scoring
      // voice uses, then PATCH the ball directly with the result. No
      // dialog needed — the operator's edit completes in one breath.
      // Founder ask 2026-05-26: "edit ball 2.4 to wide" should finish
      // without opening a modal. Fall back to opening the modal if the
      // phrase is unrecognised.
      const innerParsed = (() => {
        try {
          // Reuse the module-scope `parse` exported from useVoiceScoring.
          // batterHand for that ball uses the same striker convention.
          return parseVoicePhrase(inner);
        } catch { return null; }
      })();
      if (innerParsed && innerParsed.kind === "ball" && innerParsed.updates) {
        const u = innerParsed.updates;
        const patch = { ...u };
        // The voice parser packs spatial/analytics under __ keys for
        // SPA-side merging; updateBall API expects nested objects.
        if (u.__spatial)   { patch.spatial   = u.__spatial;   delete patch.__spatial; }
        if (u.__analytics) { patch.analytics = u.__analytics; delete patch.__analytics; }
        (async () => {
          try {
            await updateBall(target.id, patch);
            // Refresh recent balls so the strip shows the new state.
            if (inningsId) {
              listInningsBalls(inningsId).then((r) => setRecentBalls((r?.data?.results || []).slice(-12)));
            }
            flashAiToast(true, `✓ Ball ${over}.${ball} updated: ${innerParsed.label || inner}`);
          } catch (e) {
            console.warn("cross-ball edit PATCH failed:", e?.response?.data || e);
            flashAiToast(false, `Couldn't patch ball ${over}.${ball} — opening editor instead.`);
            setEditingBallId(target.id);
            setEditBallDialogOpen(true);
          }
        })();
      } else {
        // Inner phrase didn't parse cleanly → fall back to the dialog
        // so the operator can finish manually.
        setEditingBallId(target.id);
        setEditBallDialogOpen(true);
        flashAiToast(true, `Opening ball ${over}.${ball} — couldn't auto-apply "${inner}".`);
      }
    },
    // Scope-only navigation: spatial tabs + activity accordion sections.
    onNavigate: (target) => {
      // Critical: also flip tabAutoAdvanced so the auto-advance effects
      // (line ~1953, ~1979, ~1995) don't immediately yank the operator
      // back off the tab they just navigated to. Without this, saying
      // "pitch map" on a ball that already has pitch_zone set briefly
      // shows the pitch matrix and then the effect kicks the spatialTab
      // to "impact" on the very next render — the founder's symptom
      // 2026-05-26 ("pitch map doesn't toggle"). Mark all three flags
      // as "already advanced" so user intent wins.
      setTabAutoAdvanced({ pitch: true, impact: true });
      if (target === "pitch") {
        setSpatialTab("pitch");
        setActiveZoneMatrix("pitch");
      } else if (target === "impact") {
        setSpatialTab("impact");
        setActiveZoneMatrix("height");
      } else if (target === "wagon") {
        setSpatialTab("wagon");
      }
      // Metric scopes — could expand the matching accordion section
      // later; for now scope keywords without value are mostly used for
      // the spatial trio. (Accordion section state lives inside
      // ActivityAccordion; would need a controlled prop to drive it.)
    },
    // ── Match-state voice handlers ─────────────────────────────────────
    // 2026-05-28 — canonical ball state uses `bowler`/`striker`/
    // `non_striker` (NO `_id` suffix). Earlier versions wrote
    // `bowler_id`/`striker_id`/`non_striker_id`, which displayBowlerId
    // (`ball?.bowler ?? currentBowlerId`) never read — so voice
    // setters appeared to "work" (toast fired) but the picker modal
    // stayed open because `displayBowlerId` stayed null.
    // Also: when the selectionModal is open and the voice command
    // matches its type, call handleSelectPlayer (closes modal +
    // enforces guards) instead of touching ball directly.
    onSetBowler: (name) => {
      const bowlersList = players?.bowlers || [];
      const p = matchPlayer(name, bowlersList);
      if (!p) {
        flashAiToast(false, `No bowler match for "${name}". Try the surname only.`);
        return;
      }
      if (selectionModal.open && selectionModal.type === "bowler") {
        handleSelectPlayer(p.id);
      } else {
        setBall((prev) => ({ ...prev, bowler: p.id }));
      }
      flashAiToast(true, `Bowler → ${p.name || p.short_name}`);
    },
    onSetStriker: (name) => {
      const battersList = players?.batters || [];
      const p = matchPlayer(name, battersList);
      if (!p) {
        flashAiToast(false, `No batter match for "${name}". Try the surname only.`);
        return;
      }
      // Critical guard: striker and non-striker MUST be different
      // players. STT homophones like "Rajat" ↔ "Virat" or "Iyer" ↔
      // "Aiyar" routinely collapse two players to one; without this
      // check the strike rotation perpetuates the collision and all
      // runs pile onto the wrong batter (the actual bug Krishna hit
      // 2026-05-25 in the RCB vs MI match).
      if (displayNonStrikerId && String(displayNonStrikerId) === String(p.id)) {
        flashAiToast(
          false,
          `Striker can't be the same as non-striker (${p.name || p.short_name}). ` +
          `If voice mis-heard the name, say the surname only or pick from the dropdown.`,
        );
        return;
      }
      if (selectionModal.open && selectionModal.type === "striker") {
        handleSelectPlayer(p.id);
      } else {
        setBall((prev) => ({ ...prev, striker: p.id }));
      }
      flashAiToast(true, `Striker → ${p.name || p.short_name}`);
    },
    onSetNonStriker: (name) => {
      const battersList = players?.batters || [];
      const p = matchPlayer(name, battersList);
      if (!p) {
        flashAiToast(false, `No batter match for "${name}".`);
        return;
      }
      if (displayStrikerId && String(displayStrikerId) === String(p.id)) {
        flashAiToast(
          false,
          `Non-striker can't be the same as striker (${p.name || p.short_name}). ` +
          `If voice mis-heard the name, say the surname only or pick from the dropdown.`,
        );
        return;
      }
      if (selectionModal.open && selectionModal.type === "non_striker") {
        handleSelectPlayer(p.id);
      } else {
        setBall((prev) => ({ ...prev, non_striker: p.id }));
      }
      flashAiToast(true, `Non-striker → ${p.name || p.short_name}`);
    },
    onSetKeeper: (name) => {
      // Keeper comes from the bowling XI OR squad reserves (subs).
      // Including subFielders so voice picks ("keeper is Urvil") work
      // when the listed XI keeper is off the field.
      const candidates = [...(players?.bowlers || []), ...(subFielders || [])];
      const p = matchPlayer(name, candidates);
      if (p) {
        setWicketKeeperManually(p.id);
        flashAiToast(true, `Keeper → ${p.name || p.short_name}`);
      } else {
        flashAiToast(false, `No fielder match for "${name}".`);
      }
    },
    // 2026-05-27 — voice wicket-fielder handlers. Each resolves the
    // spoken name to a player via matchPlayer + bowling-team XI +
    // squad reserves, then sets the appropriate ball.* field AND
    // wicket_type / has_wicket if not already set (so "caught by
    // virat" works without a prior "wicket caught" utterance).
    onSetCaughtBy: (name) => {
      const candidates = [...(players?.bowlers || []), ...(subFielders || [])];
      const p = matchPlayer(name, candidates);
      if (!p) { flashAiToast(false, `No fielder match for "${name}".`); return; }
      setBall((prev) => ({
        ...prev,
        has_wicket: true,
        wicket_type: prev.wicket_type || "CAUGHT",
        caught_by_id: p.id,
        // Default dismissed player to striker when unset (the
        // caught-out batter); operator can override in the modal.
        dismissed_player_id: prev.dismissed_player_id || prev.striker_id || displayStrikerId || null,
        // Bowler-credit for CAUGHT — set unless already set.
        dismissed_by_id: prev.dismissed_by_id || prev.bowler_id || displayBowlerId || null,
      }));
      // T220.4: voice-fielder commands auto-close the wicket modal so
      // the operator doesn't have to tap ✕ after speaking the fielder.
      setWicketModalOpen(false);
      flashAiToast(true, `Caught by → ${p.name || p.short_name}`);
    },
    onSetStumpedBy: (name) => {
      const candidates = [...(players?.bowlers || []), ...(subFielders || [])];
      const p = matchPlayer(name, candidates);
      if (!p) { flashAiToast(false, `No fielder match for "${name}".`); return; }
      setBall((prev) => ({
        ...prev,
        has_wicket: true,
        wicket_type: prev.wicket_type || "STUMPED",
        stumped_by_id: p.id,
        dismissed_player_id: prev.dismissed_player_id || prev.striker_id || displayStrikerId || null,
        dismissed_by_id: prev.dismissed_by_id || prev.bowler_id || displayBowlerId || null,
      }));
      setWicketModalOpen(false);
      flashAiToast(true, `Stumped by → ${p.name || p.short_name}`);
    },
    onSetRunOutFielders: (names) => {
      const candidates = [...(players?.bowlers || []), ...(subFielders || [])];
      const p1 = matchPlayer(names[0], candidates);
      const p2 = names[1] ? matchPlayer(names[1], candidates) : null;
      if (!p1) { flashAiToast(false, `No fielder match for "${names[0]}".`); return; }
      setBall((prev) => ({
        ...prev,
        has_wicket: true,
        wicket_type: prev.wicket_type || "RUN_OUT",
        run_out_fielder_1_id: p1.id,
        run_out_fielder_2_id: p2 ? p2.id : (prev.run_out_fielder_2_id || null),
        dismissed_player_id: prev.dismissed_player_id || prev.striker_id || displayStrikerId || null,
        // RUN_OUT doesn't credit the bowler — keep dismissed_by_id null
        // unless operator already set it via a separate utterance.
      }));
      setWicketModalOpen(false);
      const label = p2 ? `${p1.name || p1.short_name} → ${p2.name || p2.short_name}` : (p1.name || p1.short_name);
      flashAiToast(true, `Run out → ${label}`);
    },
    onSetMankadBy: (name) => {
      // Mankad: non-striker run out by the bowler before delivery.
      // dismissed_by_id is the bowler (the one who effected it), and
      // the dismissed player is the NON-striker, not the striker.
      const candidates = [...(players?.bowlers || []), ...(subFielders || [])];
      const p = matchPlayer(name, candidates);
      if (!p) { flashAiToast(false, `No fielder match for "${name}".`); return; }
      setBall((prev) => ({
        ...prev,
        has_wicket: true,
        wicket_type: "MANKAD",
        run_out_subtype: "MANKAD",
        dismissed_player_id: prev.non_striker_id || displayNonStrikerId || null,
        dismissed_by_id: p.id,
      }));
      setWicketModalOpen(false);
      flashAiToast(true, `Mankad by → ${p.name || p.short_name}`);
    },
    onInningsEnd: (closureType) => {
      if (closureType) setClosureType(closureType);
      setClosureModal(true);
      flashAiToast(true, `Innings end modal opened${closureType ? ` (${closureType})` : ""}.`);
    },
    onStartBreak: (breakType) => {
      setBall((prev) => ({
        ...prev,
        release: {
          ...prev.release,
          is_break: true,
          break_type: breakType,
        },
      }));
      flashAiToast(true, `Break: ${breakType} (will apply on save).`);
    },
    onDLS: (target, overs) => {
      if (target != null && overs != null) {
        setDlsRuns(String(target));
        setDlsOvers(String(overs));
      }
      setMoreOptionsOpen(true);
      if (target != null && overs != null) {
        flashAiToast(true, `DLS pre-filled ${target} in ${overs} overs — confirm in More.`);
      } else {
        flashAiToast(true, "DLS panel opened — enter target + overs.");
      }
    },
    onNewBall: () => {
      setBall((prev) => ({
        ...prev,
        release: { ...prev.release, new_ball: true },
      }));
      flashAiToast(true, "New ball flagged for this delivery.");
    },
    // ── More-menu voice handlers (founder ask 2026-06-11) ───────────
    onEndMatch: () => {
      handleEndCurrentMatch();
      flashAiToast(true, "End match triggered.");
    },
    onEndOverNow: async () => {
      if (!inningsId) return;
      try {
        const { endOverNow } = await import("../../api/ballbyball.api");
        const r = await endOverNow(inningsId);
        flashAiToast(true, `Over completed with ${r?.data?.phantoms_created ?? 0} phantom ball(s).`);
        const fresh = await loadBallSession(id);
        if (fresh) applyServerState(fresh);
      } catch (e) {
        flashAiToast(false, "Couldn't end over — " + (e?.response?.data?.detail || e?.message || "unknown error"));
      }
    },
    onAddPhantomBall: async () => {
      if (!inningsId) return;
      try {
        const { addPhantomBall } = await import("../../api/ballbyball.api");
        const r = await addPhantomBall(inningsId);
        flashAiToast(true, `Phantom ball added at ${r?.data?.over_number}.${r?.data?.ball_in_over}.`);
        const fresh = await loadBallSession(id);
        if (fresh) applyServerState(fresh);
      } catch (e) {
        flashAiToast(false, "Couldn't add phantom ball — " + (e?.response?.data?.detail || e?.message || "unknown error"));
      }
    },
    onFloodlights: async (state) => {
      // state = true | false | null (toggle). Null delegates to the
      // existing toggle handler; explicit true/false sets directly.
      if (state === null || state === undefined) {
        handleToggleFloodlights();
        return;
      }
      if (!id || floodlightsBusy) return;
      setFloodlightsBusy(true);
      const prev = floodlightsOn;
      setFloodlightsOn(state);
      try {
        const apiAxios = (await import("../../api/axios")).default;
        await apiAxios.patch(`/api/matches/matches/${id}/`, { floodlights_on: state });
        flashAiToast(true, `Floodlights ${state ? "ON" : "OFF"}.`);
      } catch (e) {
        setFloodlightsOn(prev);
        flashAiToast(false, "Couldn't update floodlights: " + (e?.response?.data?.detail || e?.message || "unknown error"));
      } finally {
        setFloodlightsBusy(false);
      }
    },
    onPauseMatch: () => {
      if (!matchPaused) toggleMatchPaused();
      flashAiToast(true, "Match paused.");
    },
    onResumeMatch: () => {
      if (matchPaused) toggleMatchPaused();
      flashAiToast(true, "Match resumed.");
    },
    onTogglePause: () => {
      toggleMatchPaused();
      flashAiToast(true, matchPaused ? "Match resumed." : "Match paused.");
    },
    onRainAffected: async (state) => {
      // Mirrors the More-menu inline toggle so DLS unlocks.
      const next = !!state;
      setIsRainAffected(next);
      try {
        await (await import("../../api/axios")).default.patch(
          `/api/matches/matches/${id}/`,
          { is_rain_affected: next },
        );
        flashAiToast(true, next ? "Rain affected ON — DLS unlocked." : "Rain affected OFF.");
      } catch (e) {
        setIsRainAffected(!next);
        flashAiToast(false, "Couldn't update rain-affected: " + (e?.response?.data?.detail || e?.message || "unknown error"));
      }
    },
    onRainAffectedAtStart: () => {
      const mt = aggregate?.match_type?.code;
      const formatDefault = formatDefaultOvers(aggregate?.match_type);
      const currentMax = aggregate?.max_overs || aggregate?.match_type?.max_overs || formatDefault;
      setNewMaxOversInput(String(currentMax));
      setNewMaxReason("Rain affected at start");
      const remainingSeed = mt === "TEST"
        ? oversLeftToday
        : Math.max(0, currentMax - (aggregate?.completed_overs ?? 0));
      setOversRemainingInput(String(remainingSeed));
      setOversAdjustReason("Rain affected at start");
      setOversRemainingOpen(true);
      flashAiToast(true, "Rain affected at start — overs modal opened.");
    },
    onEndInningsRainAffected: () => {
      handleEndInningsRainAffected();
    },
    onOpenOversRemaining: () => {
      const mt = aggregate?.match_type?.code;
      const formatDefault = formatDefaultOvers(aggregate?.match_type);
      const currentMax = aggregate?.max_overs || aggregate?.match_type?.max_overs || formatDefault;
      setNewMaxOversInput(String(currentMax));
      setNewMaxReason("");
      const remainingSeed = mt === "TEST"
        ? oversLeftToday
        : Math.max(0, currentMax - (aggregate?.completed_overs ?? 0));
      setOversRemainingInput(String(remainingSeed));
      setOversAdjustReason("");
      setOversRemainingOpen(true);
      flashAiToast(true, "Overs remaining modal opened.");
    },
    onOpenImpact: () => {
      setSubstitutionModal("IMPACT");
      flashAiToast(true, "Impact Player modal opened.");
    },
    onOpenConcussion: () => {
      setSubstitutionModal("CONCUSSION");
      flashAiToast(true, "Concussion Sub modal opened.");
    },
    onTestStumps: () => {
      if (!isTestMatch) {
        flashAiToast(false, "Stumps applies only to Test matches.");
        return;
      }
      markTestStumps();
      flashAiToast(true, "Stumps — Test day advanced.");
    },
    onTestLunch: () => {
      if (!isTestMatch) {
        flashAiToast(false, "Lunch applies only to Test matches.");
        return;
      }
      markTestLunch();
      flashAiToast(true, "Lunch — Test session advanced.");
    },
    onTestTea: () => {
      if (!isTestMatch) {
        flashAiToast(false, "Tea applies only to Test matches.");
        return;
      }
      markTestTea();
      flashAiToast(true, "Tea — Test session advanced.");
    },
    onOpenMatchSettings: async () => {
      try {
        const r = await (await import("../../api/axios")).default.get(`/api/matches/matches/${id}/`);
        setIsRainAffected(!!r.data?.is_rain_affected);
        setCustomPPOvers(r.data?.powerplay_override?.powerplay_overs ?? "");
        setCustomMaxOversPerBowler(r.data?.powerplay_override?.max_overs_per_bowler ?? "");
      } catch { /* fall back to fresh state */ }
      setMatchSettingsOpen(true);
      flashAiToast(true, "Match settings opened.");
    },
    onOpenFieldingPenalty: () => {
      setFieldingPenaltyMode("current");
      setFieldingPenaltyBallId("");
      setFieldingPenaltyOpen(true);
      flashAiToast(true, "Fielding penalty modal opened.");
    },
  });

  const handleStartNextInnings = async () => {
    try {
      const res = await startNextInnings(id);
      applyServerState(res.data);
    } catch (err) {
      const detail =
        err?.response?.data?.detail || "Unable to start next innings";
      alert(detail);
    }
  };

  const handleStartNextInningsWithFollowOn = async (enforce) => {
    const res = await startNextInnings(id, { enforce_follow_on: enforce });
    applyServerState(res.data);
  };

  const handleStartSuperOver = async () => {
    const res = await startNextInnings(id, { is_super_over: true });
    applyServerState(res.data);
  };

  const handleGoToMatches = () => {
    navigate("/cricket/matches");
  };

  const handleEndCurrentInnings = async () => {
    setClosureModal(true);
    setMoreOptionsOpen(false);
  };

  // One-click "rain affected" end-of-innings — used when play is stopped
  // for weather. End the current innings with closure_type=RAIN_AFFECTED,
  // flip match.is_rain_affected=true so the DLS section unlocks for the
  // chase, then start the next innings.
  const handleEndInningsRainAffected = async () => {
    if (!window.confirm("End the current innings due to rain?\n\nThe innings will be tagged RAIN_AFFECTED and the match will be marked rain-affected so DLS is available for the chase.")) {
      return;
    }
    setMoreOptionsOpen(false);
    try {
      // 1. End the active innings, tagged RAIN_AFFECTED in the same call.
      await endActiveInnings(id, "RAIN_AFFECTED");
      const apiAxios = (await import("../../api/axios")).default;
      // 3. Mark the match itself rain-affected so the DLS section unlocks
      //    on subsequent loads of the More-options modal.
      try {
        await apiAxios.patch(`/api/matches/matches/${id}/`, {
          is_rain_affected: true,
        });
        setIsRainAffected(true);
      } catch { /* non-blocking */ }
      // 4. Start the next innings + refresh state.
      const res = await startNextInnings(id);
      applyServerState(res.data);
    } catch (e) {
      alert("Couldn't end innings: " + (e?.response?.data?.detail || e.message));
    }
  };

  // 2026-10-23 — single-flight + innings-pinned. The button stayed live
  // while end + start ran (~3 s on a slow day) and errors were unhandled,
  // so a coder clicked "End innings" 4 times on WCPL M5: the last click
  // landed after innings 2 had started and ended it with 0 balls, which
  // completed the match. The ref blocks re-entry; passing the innings id
  // makes the backend 409 if a stray call targets a newer innings.
  const confirmEndInnings = async () => {
    if (closureInFlightRef.current) return;
    closureInFlightRef.current = true;
    setClosureSaving(true);
    try {
      // Test cricket — DECLARED routes through the dedicated endpoint that
      // atomically marks the innings COMPLETED + persists closure_type=DECLARED.
      // Bypasses the cosmetic-only PATCH path used for other closure types.
      if (closureType === "DECLARED") {
        await declareInnings(id);
      } else {
        // The operator's closure choice is saved by the same call (it used
        // to be PATCHed to a route that never existed, and was lost).
        await endActiveInnings(id, closureType, aggregate?.innings_id);
      }
      const res = await startNextInnings(id);
      applyServerState(res.data);
      setClosureModal(false);
    } catch (err) {
      alert(err?.response?.data?.detail || "Couldn't end the innings. Refresh the page and check the innings before trying again.");
    } finally {
      closureInFlightRef.current = false;
      setClosureSaving(false);
    }
  };

  const handleEndCurrentMatch = async () => {
    setResultModal(true);
    setMoreOptionsOpen(false);
  };

  const confirmEndMatch = async () => {
    await endMatch(id);
    // Save result_type on match (best-effort)
    try {
      await import("../../api/axios").then(({ default: api }) =>
        api.patch(`/api/matches/matches/${id}/`, { result_type: resultType })
      );
    } catch { /* non-blocking */ }
    const data = await loadBallSession(id);
    if (data) applyServerState(data);
    setResultModal(false);
  };

  const handleApplyDls = async () => {
    if (!inningsId) return;
    const runs = Number(dlsRuns);
    const overs = Number(dlsOvers);
    if (Number.isNaN(runs) || Number.isNaN(overs)) return;
    await applyDls(id, inningsId, runs, overs);
    const data = await loadBallSession(id);
    if (data) applyServerState(data);
    setMoreOptionsOpen(false);
  };

  const handleApplyPenalty = async () => {
    if (!inningsId) return;
    const runs = Number(penaltyRuns);
    if (Number.isNaN(runs) || runs <= 0) return;
    if (!penaltyReason) return;

    await applyPenalty(
      id,
      inningsId,
      runs,
      penaltyAwardedTo,
      penaltyReason,
      penaltyReason === "OTHER" ? penaltyReasonOther : ""
    );
    const data = await loadBallSession(id);
    if (data) applyServerState(data);
    setPenaltyRuns("");
    setPenaltyReason("");
    setPenaltyReasonOther("");
    setPenaltyAwardedTo("BATTING");
    setMoreOptionsOpen(false);
  };
  useEffect(() => {
    if (!currentInningsNumber) return;
    setHandOverrides({});
    setStyleOverrides({});
  }, [currentInningsNumber]);

  // Reload recent balls + detect milestones after aggregate changes
  useEffect(() => {
    if (!inningsId) return;
    let alive = true;
    listInningsBalls(inningsId).then((res) => {
      if (!alive) return;
      const balls = (res?.data?.results || []).slice(-12);
      setRecentBalls(balls);
    }).catch(() => {});
    return () => { alive = false; };
  }, [inningsId, aggregate?.runs, aggregate?.wickets, aggregate?.legal_balls]);

  // M3: keep currentPartnership fresh — fetch the unbroken partnership
  // from the server endpoint after every aggregate change. Cheap (one
  // SQL walk per innings) and gives the ScoreRibbon chip accurate
  // runs/balls counts even after retired-hurt return cases that
  // confused the legacy striker+non_striker sum.
  //
  // 2026-07-08 — legal_balls added to deps so dot legal deliveries also
  // refetch (previously the chip's balls counter froze through dot
  // streaks). Also: on a wicket / innings change we clear the state
  // synchronously BEFORE the fetch, so the chip flips to the fresh
  // pair's 0/0 immediately instead of showing the just-closed
  // partnership's numbers until the refetch lands (~200 ms window).
  const partnershipCtxRef = useRef({ wickets: 0, innings: null });
  useEffect(() => {
    if (!id || !aggregate) return;
    const wickets = aggregate.wickets ?? 0;
    const inn = aggregate.innings_number ?? null;
    const ctx = partnershipCtxRef.current;
    if (wickets > ctx.wickets || inn !== ctx.innings) {
      setCurrentPartnership(null);
    }
    partnershipCtxRef.current = { wickets, innings: inn };

    let alive = true;
    getMatchPartnerships(id).then((res) => {
      if (!alive) return;
      const innings = (res?.data?.innings || []);
      const innBlock = innings.find((i) => i.innings_number === aggregate?.innings_number);
      const partnerships = innBlock?.partnerships || [];
      const unbroken = partnerships.find((p) => p.current === true);
      setCurrentPartnership(unbroken || null);
    }).catch(() => { /* non-fatal — chip falls back to 0/0 while server is unreachable */ });
    return () => { alive = false; };
  }, [id, aggregate?.runs, aggregate?.wickets, aggregate?.legal_balls, aggregate?.innings_number]);

  // Reset the announced-milestones set when the innings changes — a
  // fresh innings deserves a fresh set of milestone toasts (innings 2's
  // 50 isn't suppressed because innings 1 had a 50 from the same
  // player).
  useEffect(() => {
    announcedMilestonesRef.current = new Set();
  }, [aggregate?.innings_number, inningsId]);

  // Milestone detection: batter 50/100, bowler fifer. Each (kind,
  // player) pair fires at most once per innings — see
  // announcedMilestonesRef. We also use `>=` instead of `===` so a
  // batter going 48 → 52 on a four doesn't skip the 50 milestone the
  // way the prior strict-equality check did. Once announced, the same
  // key is suppressed forever in this innings, so subsequent balls
  // don't re-fire the toast even if the running total still satisfies
  // the threshold.
  useEffect(() => {
    if (!battingStats) return undefined;
    const announced = announcedMilestonesRef.current;
    const candidates = [];
    battingStats.forEach((s) => {
      if ((s.runs ?? 0) >= 100) {
        const key = `hundred:${s.player_id}`;
        if (!announced.has(key)) candidates.push({ key, text: `💯 Century for ${getAnyPlayerName(s.player_id)}!`, kind: "hundred" });
      } else if ((s.runs ?? 0) >= 50) {
        const key = `fifty:${s.player_id}`;
        if (!announced.has(key)) candidates.push({ key, text: `🏏 50 for ${getAnyPlayerName(s.player_id)}!`, kind: "fifty" });
      }
    });
    (bowlingStats || []).forEach((s) => {
      if ((s.wickets ?? 0) >= 5) {
        const key = `fifer:${s.player_id}`;
        if (!announced.has(key)) candidates.push({ key, text: `🎯 Fifer for ${getAnyPlayerName(s.player_id)}!`, kind: "fifer" });
      }
    });
    if (candidates.length > 0) {
      candidates.forEach((m) => announced.add(m.key));
      setMilestone(candidates[0]);
      const t = setTimeout(() => setMilestone(null), 4000);
      return () => clearTimeout(t);
    }
    return undefined;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aggregate?.runs, aggregate?.wickets]);

  // Umpire-end swap driven by `aggregate.completed_overs` parity, NOT by
  // the `actions.new_bowler_select` flag. The flag-driven version
  // (pre-2026-06-04) swapped umpires once per over-end signal but never
  // UN-swapped on undo — so undoing the last ball of an over left the
  // umpires permanently swapped relative to the actual over state.
  //
  // Invariant: umpires should be swapped relative to their
  // start-of-innings positions exactly once per completed over. We
  // track the last-seen completed_overs and react to deltas:
  //   completed_overs increased → over just ended → swap once
  //   completed_overs decreased → over un-completed via undo → un-swap
  // This is naturally symmetric: any sequence of save+undo crossings
  // ends up with the umpire ends in the cricket-correct position.
  //
  // Parity check uses Math.abs because JS `%` preserves the dividend's
  // sign: `-1 % 2 === -1` (NOT 1). Without abs, the undo case (diff=-1)
  // silently no-op'd — that was the "umpires don't switch back on undo"
  // bug operators reported.
  //
  // The swap is also persisted to localStorage immediately so a page
  // refresh after the swap restores the correct post-over positions
  // (without persist, restore would replay the pre-swap state).
  const lastCompletedOversRef = useRef(null);
  useEffect(() => {
    const co = aggregate?.completed_overs ?? null;
    const prev = lastCompletedOversRef.current;
    if (prev != null && co != null && umpireBowlerEndId && umpireSquareLegId) {
      // 2026-06-22 — Hundred-aware swap cadence. Ends change every
      // `end_change_every_n_sets` completed sets (T20/ODI/Test/BBL=1,
      // Hundred=2 → swap every 10 balls, not every 5). Bucket the
      // completed_overs counter into blocks of n and swap when the
      // bucket index parity flips (odd delta of blocks).
      const nSets = Math.max(
        1,
        Number(aggregate?.match_type?.end_change_every_n_sets) || 1,
      );
      const currentBlock = Math.floor(co / nSets);
      const prevBlock = Math.floor(prev / nSets);
      const blockDelta = currentBlock - prevBlock;
      if (blockDelta !== 0 && Math.abs(blockDelta) % 2 === 1) {
        // Odd-block delta (typical: +1 block boundary crossed, or
        // -1 via undo). Swap the two ends; the operator-stored umpires
        // now reflect the post-block position. Even-block deltas (rare:
        // rebuild jump across multiple blocks) are intentionally a
        // no-op since each pair of swaps cancels.
        const newBowlerEnd = umpireSquareLegId;
        const newSquareLeg = umpireBowlerEndId;
        setUmpireBowlerEndId(newBowlerEnd);
        setUmpireSquareLegId(newSquareLeg);
        persistUmpiresForInnings(newBowlerEnd, newSquareLeg, true);
      }
    }
    lastCompletedOversRef.current = co;
    // Keep the legacy ref as a tombstone so anything still reading it
    // doesn't error — value is no longer meaningful.
    bowlerSwapRef.current = false;
  }, [aggregate?.completed_overs, umpireBowlerEndId, umpireSquareLegId]);

  const bowlerIdForFielding =
    ball?.bowler ?? aggregate?.current_bowler ?? null;

  // When the innings flips (1st → 2nd), the bowling team changes, so the
  // previous innings's keeper is no longer on the field. Re-derive from
  // the new bowling team's PlayingXI — the player marked
  // is_wicket_keeper on the XI screen.
  //
  // BUT only when the operator HASN'T explicitly picked a different
  // keeper for the current innings (tracked in keeperManualForInningsRef).
  // Without the gate the effect re-ran on every save (players.bowlers
  // is a fresh reference after each refresh) and silently reverted any
  // mid-innings substitute keeper back to the original XI choice — the
  // Sanju-injured-Urvil-keeping scenario operators reported. A manual
  // pick survives every save until the innings changes.
  useEffect(() => {
    if (!currentInningsNumber) return;
    const manual = keeperManualForInningsRef.current;
    if (manual && manual.innings === currentInningsNumber && manual.id) {
      // Operator already chose a keeper for this innings — leave it.
      return;
    }
    const keeperFromXI = (players?.bowlers || []).find((p) => p.is_wicket_keeper);
    setWicketKeeperId(keeperFromXI?.id || null);
  }, [currentInningsNumber, players?.bowlers]);

  // Clear the manual-pick tracker when the innings changes so the next
  // innings's auto-detect can run unobstructed.
  useEffect(() => {
    const manual = keeperManualForInningsRef.current;
    if (manual && manual.innings !== currentInningsNumber) {
      keeperManualForInningsRef.current = null;
    }
  }, [currentInningsNumber]);

  useEffect(() => {
    // CRITICAL: wait until the localStorage hydration above has run.
    // Otherwise this effect reads umpirePromptDismissed=false (the
    // initial value) and opens the modal before hydration can flip it
    // to true. Net result without this gate: operator gets the umpire
    // prompt on every QC-back-to-coding or page refresh, even when
    // their previous pick is sitting in localStorage.
    if (!umpiresHydrated) return;
    if (!umpires || umpires.length === 0) return;
    if (umpireBowlerEndId || umpireSquareLegId) return;
    if (umpireModalOpen) return;
    if (umpirePromptDismissed) return;
    setUmpireModalOpen(true);
  }, [
    umpiresHydrated,
    umpires,
    umpireBowlerEndId,
    umpireSquareLegId,
    umpireModalOpen,
    umpirePromptDismissed,
  ]);

  useEffect(() => {
    if (actions?.ask_follow_on && !followOnModalOpen) {
      setFollowOnModalOpen(true);
    }
  }, [actions?.ask_follow_on, followOnModalOpen]);

  // 2026-06-06 (v3) — Super Over prompt double-open guard. The
  // original useEffect reopened the modal between Yes click +
  // applyServerState landing because confirm_super_over stayed true
  // for that brief window. Track "user already responded to this
  // prompt" in a ref; reset only when confirm_super_over goes back
  // to false (i.e. the new innings has been hydrated).
  const superOverPromptRespondedRef = useRef(false);
  useEffect(() => {
    if (!actions?.confirm_super_over) {
      superOverPromptRespondedRef.current = false;
      return;
    }
    if (
      actions?.confirm_super_over
      && !superOverModalOpen
      && !superOverPromptRespondedRef.current
    ) {
      setSuperOverModalOpen(true);
    }
  }, [actions?.confirm_super_over, superOverModalOpen]);

  // ── Auto-advance spatial tabs ─────────────────────────────────────────
  // Once a pitch_zone is set, jump to "Height impact". Once height_zone is
  // set, jump to "Wagon wheel". Guarantees the operator always reaches the
  // wagon-wheel selection (which is mandatory per scoring rules).
  // Only fires once per ball — re-edits of an already-advanced tab don't
  // re-pull the user away.
  useEffect(() => {
    // 2026-05-25: dropped the spatialTab==='pitch' guard. Voice can set
    // pitch_zone from any tab (e.g. operator is on Wagon reviewing the
    // last ball, then voice-codes the new ball's "yorker outside off")
    // and they expect the SPA to advance to Impact for the next voice
    // phrase. The per-ball tabAutoAdvanced.pitch flag still prevents
    // re-firing when the operator manually flips back to pitch and tweaks.
    // Don't fight a user who's already on Impact (no-op if same).
    if (ball?.spatial?.pitch_zone && !tabAutoAdvanced.pitch && spatialTab !== "impact") {
      setSpatialTab("impact");
      setActiveZoneMatrix("height");
      setTabAutoAdvanced((s) => ({ ...s, pitch: true }));
    }
  }, [ball?.spatial?.pitch_zone, spatialTab, tabAutoAdvanced.pitch]);

  useEffect(() => {
    if (
      spatialTab === "impact" &&
      ball?.spatial?.height_zone &&
      !tabAutoAdvanced.impact
    ) {
      // Don't auto-advance to wagon when the wicket type doesn't have one
      // (bowled / LBW / stumped / etc.) — the operator is done with spatial.
      const noWagonWickets = new Set([
        "BOWLED", "LBW", "STUMPED", "MANKAD",
        "RETIRED", "RETIRED_HURT", "RETIRED_OUT",
        "TIMED_OUT", "HIT_WICKET",
      ]);
      const skipsWagon = ball?.has_wicket && noWagonWickets.has(ball?.wicket_type);
      if (!skipsWagon) {
        setSpatialTab("wagon");
      }
      setTabAutoAdvanced((s) => ({ ...s, impact: true }));
    }
  }, [ball?.spatial?.height_zone, ball?.has_wicket, ball?.wicket_type, spatialTab, tabAutoAdvanced.impact]);

  // Reset auto-advance flags after a ball is saved (so the next ball starts
  // fresh at "pitch" and re-advances naturally).
  useEffect(() => {
    if (!ball?.spatial?.pitch_zone && !ball?.spatial?.height_zone) {
      setTabAutoAdvanced({ pitch: false, impact: false, wagon: false, activity: false });
      // Mobile BBP tab resets to "spatial" on each new ball — that's
      // where the first coding action happens (cell pick / voice
      // landing). Order in the strip is still Score | Spatial |
      // Performance — only the default position differs.
      setMobileBbpTab("spatial");
    }
  }, [ball?.spatial?.pitch_zone, ball?.spatial?.height_zone]);

  // ── T220.1 mobile BBP tab auto-advance ───────────────────────────────
  // Once the wagon zone is set, jump from Spatial to Activity so the
  // operator picks stroke / foot / connection chips next. Once any
  // analytics field lands, jump to Score for runs + save. Each step
  // fires once per ball — re-edits don't yank the operator away.
  useEffect(() => {
    if (ball?.spatial?.structured_position
        && !tabAutoAdvanced.wagon
        && mobileBbpTab === "spatial") {
      setMobileBbpTab("activity");
      setTabAutoAdvanced((s) => ({ ...s, wagon: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ball?.spatial?.structured_position]);
  useEffect(() => {
    const hasAnalytics = ball?.analytics && (
      ball.analytics.stroke
      || ball.analytics.shot_connection
      || ball.analytics.foot_movement
    );
    if (hasAnalytics
        && !tabAutoAdvanced.activity
        && mobileBbpTab === "activity") {
      setMobileBbpTab("score");
      setTabAutoAdvanced((s) => ({ ...s, activity: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    ball?.analytics?.stroke,
    ball?.analytics?.shot_connection,
    ball?.analytics?.foot_movement,
  ]);

  // 2026-05-29 — Stroke auto-fill (founder ask). When the operator
  // picks a stroke and foot_movement / shot_connection are still empty,
  // apply the STROKE_AUTO_FILL defaults so they don't have to tap two
  // more chips for the standard mapping. Operator overrides still win
  // — the auto-fill only sets fields that are CURRENTLY blank.
  useEffect(() => {
    const stroke = ball?.analytics?.stroke;
    if (!stroke) return;
    const defaults = STROKE_AUTO_FILL[stroke];
    if (!defaults) return;
    const needFoot = !ball?.analytics?.foot_movement && defaults.foot;
    const needConn = !ball?.analytics?.shot_connection && defaults.conn;
    if (!needFoot && !needConn) return;
    setBall((prev) => ({
      ...prev,
      analytics: {
        ...(prev.analytics || {}),
        ...(needFoot ? { foot_movement: defaults.foot } : {}),
        ...(needConn ? { shot_connection: defaults.conn } : {}),
      },
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ball?.analytics?.stroke]);

  // 2026-05-29 — Ball type auto-default (founder ask). First ball of
  // each innings defaults to "New"; every subsequent ball defaults to
  // "Old". The aggregate's balls_recorded counter (0 at the start of
  // an innings, increments on every legal delivery) drives the
  // decision. Operator overrides still win — the auto-default only
  // sets ball_type when the field is currently empty.
  useEffect(() => {
    if (ball?.release?.ball_type) return;  // operator already picked
    if (!aggregate) return;
    const recorded = aggregate.balls_recorded ?? 0;
    const next = recorded === 0 ? "New" : "Old";
    setBall((prev) => ({
      ...prev,
      release: { ...(prev.release || {}), ball_type: next },
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aggregate?.balls_recorded, ball?.release?.ball_type]);

  // Voice may set height_zone OR a wagon position WITHOUT going through
  // the pitch-tab first ("impact chest middle", "wagon mid wicket").
  // Without this hint the operator stays on the pitch tab and doesn't
  // see the highlight that voice just placed (Krishna report 2026-05-25).
  // Switch the visible tab to match the spatial field that was filled.
  useEffect(() => {
    if (ball?.spatial?.height_zone && spatialTab !== "impact") {
      setSpatialTab("impact");
      setActiveZoneMatrix("height");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ball?.spatial?.height_zone]);
  useEffect(() => {
    if (ball?.spatial?.structured_position && spatialTab !== "wagon") {
      setSpatialTab("wagon");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ball?.spatial?.structured_position]);

  // Sync the local `structuredRegion` SVG-selection state from
  // ball.spatial whenever voice (or AI fallback) sets the region.
  // Without this, voice-coding "wagon deep cover" would update
  // ball.spatial.structured_region_id but the wagon-wheel SVG would
  // still look unselected — operator reports 2026-05-26 ("there's
  // no line drawn on voice coding"). The SVG reads selectedRegionId
  // from the structuredRegion prop, so we mirror the ball-level data
  // back into that state so the highlighted region + dashed
  // trajectory line both render.
  useEffect(() => {
    const sp = ball?.spatial;
    if (!sp?.structured_region_id) return;
    // Avoid setting if state already matches — prevents render loops
    // when the local click handler also wrote to structuredRegion.
    if (structuredRegion?.regionId === sp.structured_region_id) return;
    setStructuredRegion({
      regionId: sp.structured_region_id,
      sliceIndex: sp.structured_slice_index ?? null,
      bandIndex: sp.structured_band_index ?? null,
      position: sp.structured_position || "",
      // Voice doesn't supply pin coordinates — the SVG falls back to
      // the zone centroid + dashed line, which is the right visual
      // for "we know the position but not the exact pixel".
      x: sp.wagon_wheel_x ?? null,
      y: sp.wagon_wheel_y ?? null,
    });
  }, [
    ball?.spatial?.structured_region_id,
    ball?.spatial?.structured_slice_index,
    ball?.spatial?.structured_band_index,
    ball?.spatial?.structured_position,
    ball?.spatial?.wagon_wheel_x,
    ball?.spatial?.wagon_wheel_y,
    structuredRegion?.regionId,
  ]);

  // Undo-countdown ticker — runs 1Hz while > 0.
  useEffect(() => {
    if (undoSecondsLeft <= 0) return;
    const t = setTimeout(() => setUndoSecondsLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(t);
  }, [undoSecondsLeft]);

  useEffect(() => {
    if (!ball?.has_wicket) return;

    const wicketType = ball.wicket_type;
    const nextFielding = { ...(ball.fielding || {}) };

    if (wicketType === "CAUGHT") {
      nextFielding.action = "Catch";
      nextFielding.fielder1 = ball.caught_by_id || null;
    } else if (wicketType === "STUMPED") {
      nextFielding.action = "Stumping";
      nextFielding.fielder1 = ball.stumped_by_id || null;
    } else if (wicketType === "RUN_OUT") {
      nextFielding.action = "Run Out";
      nextFielding.fielder1 = ball.run_out_fielder_1_id || null;
      nextFielding.fielder2 = ball.run_out_fielder_2_id || null;
    } else if (wicketType === "HIT_WICKET") {
      nextFielding.action = "Hit Wicket";
    } else if (wicketType === "BOWLED") {
      nextFielding.action = "Bowled";
      nextFielding.fielder1 = bowlerIdForFielding || null;
    } else if (wicketType === "LBW") {
      nextFielding.action = "LBW";
    } else if (wicketType === "RETIRED_HURT") {
      nextFielding.action = "Retired Hurt";
    } else if (wicketType === "OBSTRUCTING_FIELD") {
      nextFielding.action = "Obstructing Field";
    } else if (wicketType === "HANDLED_BALL") {
      nextFielding.action = "Handled Ball";
    } else if (wicketType === "TIMED_OUT") {
      nextFielding.action = "Timed Out";
    }

    setBall((prev) => ({
      ...prev,
      fielding: {
        ...prev.fielding,
        ...nextFielding,
      },
    }));
  }, [
    ball?.has_wicket,
    ball?.wicket_type,
    ball?.caught_by_id,
    ball?.stumped_by_id,
    ball?.run_out_fielder_1_id,
    ball?.run_out_fielder_2_id,
    bowlerIdForFielding,
    setBall,
  ]);

  // Fielder picked beside the wagon wheel → wicket's Caught By. The
  // coder usually marks the catcher in the fielding picker first; when
  // the wicket is then set to CAUGHT, Caught By starts filled instead
  // of blank. Prefers a fielder whose action is "Caught", else the
  // first fielder. Keyed on the picked fielder (not caught_by_id), so
  // choosing someone else in the wicket panel afterwards sticks.
  const pickedCatcherId = (() => {
    const fs = (ball?.fielding?.fielders || []).filter((f) => f?.player);
    return (fs.find((f) => f.action === "Caught") || fs[0])?.player || null;
  })();
  useEffect(() => {
    if (!ball?.has_wicket || ball.wicket_type !== "CAUGHT" || !pickedCatcherId) return;
    setBall((prev) => (
      prev.caught_by_id === pickedCatcherId
        ? prev
        : { ...prev, caught_by_id: pickedCatcherId }
    ));
  }, [ball?.has_wicket, ball?.wicket_type, pickedCatcherId, setBall]);

  // Same for a stumping → Stumped By: the fielder whose action is
  // "Stumped" (the keeper), else the first fielder picked.
  const pickedStumperId = (() => {
    const fs = (ball?.fielding?.fielders || []).filter((f) => f?.player);
    return (fs.find((f) => f.action === "Stumped") || fs[0])?.player || null;
  })();
  useEffect(() => {
    if (!ball?.has_wicket || ball.wicket_type !== "STUMPED" || !pickedStumperId) return;
    setBall((prev) => (
      prev.stumped_by_id === pickedStumperId
        ? prev
        : { ...prev, stumped_by_id: pickedStumperId }
    ));
  }, [ball?.has_wicket, ball?.wicket_type, pickedStumperId, setBall]);

  // Same for a run out → the wicket's Run Out fielders. Fielder 1 is
  // whoever has a run-out "…, Out" action (the throw or direct hit that
  // dismissed), else any run-out action, else the first fielder.
  // Fielder 2 is the "Run Out- Assist" fielder, else the next fielder
  // listed. As with catches, only a change in the picker re-fills.
  const [pickedRunOut1, pickedRunOut2] = (() => {
    const fs = (ball?.fielding?.fielders || []).filter((f) => f?.player);
    const isRo = (f) => (f.action || "").startsWith("Run Out");
    const first =
      fs.find((f) => isRo(f) && f.action.endsWith(", Out"))
      || fs.find(isRo)
      || fs[0];
    const rest = fs.filter((f) => f !== first && f.player !== first?.player);
    const second = rest.find((f) => f.action === "Run Out- Assist") || rest[0];
    return [first?.player || null, second?.player || null];
  })();
  useEffect(() => {
    if (!ball?.has_wicket || ball.wicket_type !== "RUN_OUT" || !pickedRunOut1) return;
    setBall((prev) => {
      const f2 = pickedRunOut2 || prev.run_out_fielder_2_id || null;
      return prev.run_out_fielder_1_id === pickedRunOut1 && prev.run_out_fielder_2_id === f2
        ? prev
        : { ...prev, run_out_fielder_1_id: pickedRunOut1, run_out_fielder_2_id: f2 };
    });
  }, [ball?.has_wicket, ball?.wicket_type, pickedRunOut1, pickedRunOut2, setBall]);

  useEffect(() => {
    if (!ball?.has_wicket) return;
    if (ball.dismissed_player_id) return;
    const strikerId = ball?.striker ?? aggregate?.current_striker ?? null;
    if (!strikerId) return;
    setBall((prev) => ({
      ...prev,
      dismissed_player_id: strikerId,
    }));
  }, [ball?.has_wicket, ball.dismissed_player_id, ball?.striker, aggregate?.current_striker, setBall]);

  useEffect(() => {
    if (!wicketKeeperId) return;
    if (ball?.wicket_type !== "STUMPED") return;
    if (ball?.stumped_by_id) return;
    setBall((prev) => ({
      ...prev,
      stumped_by_id: wicketKeeperId,
    }));
  }, [wicketKeeperId, ball?.wicket_type, ball?.stumped_by_id, setBall]);

  const computeFinalDrsDecision = (onField, overruled) => {
    if (!onField) return "";
    if (onField === "WICKET") return overruled ? "NOT_OUT" : "WICKET";
    if (onField === "NOT_OUT") return overruled ? "WICKET" : "NOT_OUT";
    if (onField === "WIDE") return overruled ? "LEGAL" : "WIDE";
    if (onField === "NO_BALL") return overruled ? "LEGAL" : "NO_BALL";
    // On-field call was LEGAL; TV upheld a no-ball / wide on review.
    // overruled=true → the legal call is overturned, extras awarded.
    if (onField === "LEGAL_BALL_NO_BALL") return overruled ? "NO_BALL" : "LEGAL";
    if (onField === "LEGAL_BALL_WIDE")    return overruled ? "WIDE"    : "LEGAL";
    return "";
  };

  useEffect(() => {
    if (!ball?.drs?.is_appealed) return;
    const finalDecision = computeFinalDrsDecision(
      ball.drs?.on_field_decision,
      Boolean(ball.drs?.overruled)
    );

    setBall((prev) => ({
      ...prev,
      drs: {
        ...prev.drs,
        final_decision: finalDecision,
      },
    }));
  }, [ball?.drs?.is_appealed, ball?.drs?.on_field_decision, ball?.drs?.overruled, setBall]);

  useEffect(() => {
    if (!ball?.drs?.is_appealed) return;
    const finalDecision = ball.drs?.final_decision;
    if (!finalDecision) return;

    if (finalDecision !== "WICKET") {
      setBall((prev) => ({
        ...prev,
        has_wicket: false,
        wicket_type: null,
        dismissed_player_id: null,
        dismissed_by_id: null,
        caught_by_id: null,
        stumped_by_id: null,
        run_out_fielder_1_id: null,
        run_out_fielder_2_id: null,
      }));
    }

    if (finalDecision === "WIDE") {
      setBall((prev) => ({
        ...prev,
        is_wide: true,
        is_no_ball: false,
        is_bye: false,
        is_leg_bye: false,
        no_ball_type: "",
      }));
    } else if (finalDecision === "NO_BALL") {
      setBall((prev) => ({
        ...prev,
        is_no_ball: true,
        is_wide: false,
      }));
    } else if (finalDecision === "LEGAL" || finalDecision === "NOT_OUT") {
      setBall((prev) => ({
        ...prev,
        is_no_ball: false,
        is_wide: false,
        no_ball_type: "",
      }));
    }
  }, [ball?.drs?.is_appealed, ball?.drs?.final_decision, setBall]);



  const completedOvers = aggregate?.completed_overs ?? 0;
  const legalBalls = aggregate?.legal_balls ?? 0;
  // 2026-06-22 — Hundred awareness. Format reports balls_per_over=5
  // (a "set"); without honouring it, the X.Y display arithmetic uses
  // 6 and renders e.g. "4.5" for a Hundred innings at 23 balls when
  // the correct label is "4.3" (4 completed sets + 3 balls of the
  // next set). Mod by the actual balls_per_over.
  const formatBallsPerOver = Number(aggregate?.match_type?.balls_per_over) || 6;
  const ballsThisOver = legalBalls % formatBallsPerOver;
  const oversDisplay = `${completedOvers}.${ballsThisOver}`;
  // Current + required run rate shown beside every headline score.
  // rrr is null outside a limited-overs chase; both null for The Hundred.
  const { crr: currentRunRate, rrr: requiredRunRate } = runRates(aggregate, aggregate?.match_type);
  // The Hundred — operator-facing format is "Ball X of 100" + "Y balls
  // remaining" per the ECB spec. isHundredMatch toggles the score-ribbon
  // label between over.ball ("12.3 ov") and the ball-count notation.
  const isHundredMatch = (aggregate?.match_type?.code || "").toUpperCase() === "HUNDRED";
  const hundredMaxBalls = isHundredMatch
    ? (
        Number(aggregate?.max_overs)
        || Number(aggregate?.match_type?.max_overs)
        || 20
      ) * formatBallsPerOver
    : 0;
  const hundredBallsRemaining = isHundredMatch
    ? Math.max(0, hundredMaxBalls - legalBalls)
    : 0;
  // The Hundred phase strip — ECB analyst convention:
  //   Powerplay: balls 1-25   (first 5 sets)
  //   Middle:    balls 26-75  (next 10 sets)
  //   Death:     balls 76-100 (final 5 sets)
  // Chip reflects the phase currently in play. Ball 25 completes PP and
  // the chip flips to Middle; ball 75 completes Middle and flips to
  // Death. Boundaries are ball-aligned, not set-aligned — driven off
  // legalBalls, not completed_overs. progress = "N/25" style ratio the
  // chip renders alongside the phase name.
  //
  // 2026-07-10 — rain-affected overrides: when the operator has
  // customised the Hundred phases via PowerplayOverrideField's
  // Powerplay / Middle / Death rows, aggregate.match_type.powerplay_
  // windows carries the operator's ranges. The lookup below picks
  // the window whose [start_ball, end_ball] bracket contains
  // (legalBalls + 1) — i.e. the ball about to be bowled — so the chip
  // flips to Middle exactly when the operator's Powerplay end_ball is
  // completed.
  const HUNDRED_PHASE_STYLE = {
    POWERPLAY: { name: "Powerplay", classes: "bg-emerald-500/25 text-emerald-200 border-emerald-400/40" },
    MIDDLE:    { name: "Middle",    classes: "bg-amber-500/20 text-amber-200 border-amber-400/40" },
    DEATH:     { name: "Death",     classes: "bg-rose-500/25 text-rose-200 border-rose-400/40" },
  };
  const customHundredWindows = Array.isArray(aggregate?.match_type?.powerplay_windows)
    ? aggregate.match_type.powerplay_windows.filter(
        (w) => w && w.type && w.start_ball != null && w.end_ball != null,
      )
    : [];
  const hundredPhase = isHundredMatch ? (() => {
    // Custom operator windows take precedence when set.
    if (customHundredWindows.length > 0) {
      // Ball position = legalBalls + 1 (the delivery about to be
      // bowled). Falls back to 1 when the innings hasn't started.
      const pos = Math.max(1, legalBalls + 1);
      const match = customHundredWindows.find(
        (w) => pos >= w.start_ball && pos <= w.end_ball,
      );
      if (match) {
        const style = HUNDRED_PHASE_STYLE[match.type] || HUNDRED_PHASE_STYLE.MIDDLE;
        const span = match.end_ball - match.start_ball + 1;
        const count = Math.max(0, Math.min(span, legalBalls - match.start_ball + 1));
        return { name: style.name, count, total: span, classes: style.classes };
      }
      // legalBalls sits outside every configured window — fall through
      // to defaults so the operator still sees something meaningful.
    }
    if (legalBalls < 25) {
      return { name: "Powerplay", count: legalBalls, total: 25,
               classes: HUNDRED_PHASE_STYLE.POWERPLAY.classes };
    }
    if (legalBalls < 75) {
      return { name: "Middle",    count: legalBalls - 25, total: 50,
               classes: HUNDRED_PHASE_STYLE.MIDDLE.classes };
    }
    return { name: "Death",       count: Math.min(25, legalBalls - 75), total: 25,
             classes: HUNDRED_PHASE_STYLE.DEATH.classes };
  })() : null;
  // The Hundred strategic timeout marker. NULL until the batting captain
  // calls the ~2.5-min break. Once set, the More-menu button hides and
  // a chip on the header records the ball it was taken at.
  const hundredStrategicTimeoutBall = aggregate?.strategic_timeout_at_ball_number ?? null;
  const hundredStrategicTimeoutAvailable = isHundredMatch && hundredStrategicTimeoutBall == null;

  const handleTakeStrategicTimeout = async () => {
    if (!inningsId) return;
    const msg =
      "Take Strategic Timeout?\n\n" +
      "Records a ~2.5-minute break at the current ball. " +
      "One per innings — cannot be moved once recorded.";
    if (!window.confirm(msg)) return;
    try {
      await takeStrategicTimeout(inningsId, legalBalls);
      const fresh = await loadBallSession(id);
      if (fresh) applyServerState(fresh);
      setMoreOptionsOpen(false);
    } catch (e) {
      window.alert(
        `Could not take Strategic Timeout: ` +
        `${e?.response?.data?.detail || e?.message || "unknown error"}`,
      );
    }
  };

  // BBL phase + Power Surge state. Non-BBL matches get is_bbl = false
  // from the backend so all the BBL-only UI hides automatically.
  // current_over_phase ∈ {"powerplay","middle_overs","death_overs","normal"};
  // Surge overs are flagged separately via is_power_surge — during
  // Surge, phase == "powerplay" AND isPowerSurge == true.
  // power_surge_available is true when the operator is allowed to fire
  // off the 2-over Power Surge from the current over (BBL + over ≥ 11 +
  // not already activated + still fits inside the innings).
  const ppState = aggregate?.powerplay || {};
  const isBblMatch = !!ppState.is_bbl;
  const ppPhase = ppState.current_over_phase || "normal";
  const ppIsPowerSurge = !!ppState.is_power_surge;
  const ppCurrentOver = ppState.current_over || (completedOvers + 1);
  const ppSurgeAvailable = !!ppState.power_surge_available;
  const ppSurgeStart = ppState.power_surge_start_over;
  const ppInitialOvers = ppState.initial_powerplay_overs;
  // The valid activation window for the batting team to pick the Surge
  // START over. Opens at 11 (after the 10th over); ends at max_overs - 1
  // (so the 2-over window still fits). Used to render an "available
  // overs N–M" hint in the More menu + a header status chip while the
  // window is open.
  const ppWindowStart = ppState.power_surge_window_start;
  const ppWindowEnd = ppState.power_surge_window_end;
  // T10 floating powerplay: overs 1-2 mandatory, then ONE more
  // powerplay over the batting team picks from overs 3-9. The backend
  // only offers it at the start of an eligible over, before it's taken.
  const hasFloatingPp = !!ppState.has_floating_powerplay;
  const floatingPpOver = ppState.floating_powerplay_over;
  const floatingPpAvailable = !!ppState.floating_powerplay_available;
  const isFloatingPpOver = !!ppState.is_floating_powerplay;
  const floatingPpWindowEnd = ppState.floating_powerplay_window_end;

  // Active fielding-restriction marker (Law 41.7 over-rate). Latest
  // marker on the innings — penalties list is ordered DESC by
  // created_at, so the first `is_fielding_penalty=true` row wins.
  // null when none exist. The chip uses `fromBallLabel` ("12.4") when
  // the operator picked a past-ball anchor, "current" otherwise.
  const inningsPenalties = aggregate?.penalties || [];
  const fieldingRestriction = inningsPenalties.find(
    (p) => p.is_fielding_penalty
  ) || null;
  const fieldingRestrictionFromBallLabel = fieldingRestriction
    && fieldingRestriction.from_ball__over_number != null
    ? formatBallReference(aggregate?.match_type, {
        overNumber: fieldingRestriction.from_ball__over_number,
        ballInOver: fieldingRestriction.from_ball__ball_in_over,
        overBase: LIVE_OVER_BASE,
      })
    : "";

  const handleTakeFloatingPowerplay = async () => {
    if (!inningsId || !ppCurrentOver) return;
    const msg =
      `Take the floating powerplay in over ${ppCurrentOver}? ` +
      `This over will be a powerplay over (max 2 fielders outside the ` +
      `30-yard circle). The batting team gets one, and it cannot be ` +
      `moved once recorded.`;
    if (!window.confirm(msg)) return;
    try {
      await takeFloatingPowerplay(inningsId, ppCurrentOver);
      const fresh = await loadBallSession(id);
      if (fresh) applyServerState(fresh);
      setMoreOptionsOpen(false);
    } catch (e) {
      window.alert(
        `Could not take the floating powerplay: ` +
        `${e?.response?.data?.detail || e?.message || "unknown error"}`,
      );
    }
  };

  const handleStartPowerSurge = async () => {
    if (!inningsId) return;
    const overToStart = ppCurrentOver;
    if (!overToStart) return;
    // BBL rule: batting team picks the 2-over Surge window any time
    // after the 10th over (over 11+).
    const msg =
      `Activate Power Surge from over ${overToStart}? ` +
      `Overs ${overToStart} and ${overToStart + 1} will count as ` +
      `Power Surge (max 2 fielders outside the 30-yard circle — ` +
      `same as the initial powerplay). This is the batting team's ` +
      `choice and cannot be moved once recorded.`;
    if (!window.confirm(msg)) return;
    try {
      await startPowerSurge(inningsId, overToStart);
      const fresh = await loadBallSession(id);
      if (fresh) applyServerState(fresh);
      setMoreOptionsOpen(false);
    } catch (e) {
      window.alert(
        `Could not start Power Surge: ` +
        `${e?.response?.data?.detail || e?.message || "unknown error"}`,
      );
    }
  };
  const currentStrikerId = aggregate?.current_striker ?? null;
  const currentNonStrikerId = aggregate?.current_non_striker ?? null;
  const currentBowlerId = aggregate?.current_bowler ?? null;

  const displayStrikerId = ball?.striker ?? currentStrikerId;
  const displayNonStrikerId = ball?.non_striker ?? currentNonStrikerId;
  const displayBowlerId = ball?.bowler ?? currentBowlerId;

  const currentStrikerSet = Boolean(displayStrikerId);
  const currentNonStrikerSet = Boolean(displayNonStrikerId);
  const currentBowlerSet = Boolean(displayBowlerId);

  // 2026-06-01 — display name suffixes the jersey number in parens,
  // e.g. "Virat (7)". Founder ask 2026-06-01: name first, number in
  // brackets — easier to skim when several players share the same
  // first name. Applies to every surface that calls this helper:
  // bowler badge, batter rows, scorecard, fielder pickers, voice
  // read-back. Players without a jersey number fall through to the
  // plain name unchanged.
  const getDisplayName = (p) => {
    if (!p) return "Unknown";
    const baseName = p.nick_name
      || p.name
      || [p.first_name, p.last_name].filter(Boolean).join(" ")
      || "Unknown";
    const jersey = p.jersey_number != null && String(p.jersey_number).trim() !== ""
      ? String(p.jersey_number).trim()
      : null;
    return jersey ? `${baseName} (${jersey})` : baseName;
  };
  
  const getBatterName = (playerId) =>
    getDisplayName(players?.batters?.find((p) => p.id === playerId));

  const getBowlerName = (playerId) =>
    getDisplayName(players?.bowlers?.find((p) => p.id === playerId));

  const getAnyPlayerName = (playerId) =>
    getDisplayName(
      players?.batters?.find((p) => p.id === playerId) ||
        players?.bowlers?.find((p) => p.id === playerId),
    );

  const getBatterHand = (playerId) =>
    players?.batters?.find((p) => p.id === playerId)?.batting_hand ?? null;
  const getBowlerHand = (playerId) =>
    players?.bowlers?.find((p) => p.id === playerId)?.bowling_hand ?? null;
  const getBowlerHands = (playerId) =>
    players?.bowlers?.find((p) => p.id === playerId)?.bowling_hands ?? [];
  const isAmbidextrousBowler = (playerId) => {
    const hands = getBowlerHands(playerId);
    return Array.isArray(hands) && new Set(hands).size > 1;
  };
  const setBowlerHand = (playerId, hand) => {
    if (!playerId) return;
    setHandOverrides((prev) => ({ ...prev, [playerId]: hand }));
  };
  const getBowlerStyle = (playerId) =>
    players?.bowlers?.find((p) => p.id === playerId)?.bowling_style || "";
  const normalizeHand = (hand) => (hand === "LEFT" ? "LEFT" : "RIGHT");
  const getEffectiveHand = (playerId, defaultHand) =>
    normalizeHand(handOverrides[playerId] ?? defaultHand ?? "RIGHT");
  const toggleHand = (playerId, defaultHand) => {
    if (!playerId) return;
    setHandOverrides((prev) => {
      const current = getEffectiveHand(playerId, defaultHand);
      const next = current === "LEFT" ? "RIGHT" : "LEFT";
      return { ...prev, [playerId]: next };
    });
  };

  // 2026-06-05 — per-delivery bowler_style helpers. The default style
  // comes from Player.bowling_style on the bowlers list; the operator
  // can override per delivery from the inline picker (eg fast bowler
  // rolling a slower off-break, spinner cranking a quicker one). The
  // override gets sent in payload.bowler_style on save.
  const getEffectiveBowlerStyle = (playerId, defaultStyle) =>
    styleOverrides[playerId] ?? defaultStyle ?? "";
  const setBowlerStyleOverride = (playerId, style) => {
    if (!playerId) return;
    setStyleOverrides((prev) => ({ ...prev, [playerId]: style || "" }));
  };

  // BOWLING_STYLE_CHOICES — kept in sync with coredata.Player +
  // scoring.Ball.BOWLER_STYLE_CHOICES. Grouped for the picker.
  const BOWLING_STYLE_GROUPS = useMemo(() => ([
    { label: "Pace", codes: ["RAF", "LAF", "RAMF", "LAMF", "RAM", "LAM"] },
    { label: "Spin", codes: ["RAO", "LAO", "RAW", "LAW"] },
  ]), []);
  const BOWLING_STYLE_LABEL = {
    RAF: "RAF · Right arm fast",
    LAF: "LAF · Left arm fast",
    RAMF: "RAMF · Right arm medium fast",
    LAMF: "LAMF · Left arm medium fast",
    RAM: "RAM · Right arm medium",
    LAM: "LAM · Left arm medium",
    RAO: "RAO · Right arm off break",
    LAO: "LAO · Left arm orthodox",
    RAW: "RAW · Right arm wrist spin",
    LAW: "LAW · Left arm wrist spin",
  };

  const getBattingStat = (playerId) =>
    battingStats?.find((s) => s.player_id === playerId) ?? {
      runs: 0,
      balls: 0,
      fours: 0,
      sixes: 0,
    };

  // Batter strike rate for the live batters table: runs per 100 balls
  // faced, "—" until the batter has faced a ball.
  const batterStrikeRate = (stat) =>
    stat?.balls ? ((stat.runs * 100) / stat.balls).toFixed(2) : "—";

  const getBowlingStat = (playerId) =>
    bowlingStats?.find((s) => s.player_id === playerId) ?? {
      balls: 0,
      runs_conceded: 0,
      wickets: 0,
      wides: 0,
      no_balls: 0,
    };

  const strikerStat = displayStrikerId
    ? getBattingStat(displayStrikerId)
    : getBattingStat(null);
  const nonStrikerStat = displayNonStrikerId
    ? getBattingStat(displayNonStrikerId)
    : getBattingStat(null);
  const bowlerStat = displayBowlerId
    ? getBowlingStat(displayBowlerId)
    : getBowlingStat(null);

  const modalPlayers = useMemo(() => {
    // 2026-06-06 — Super Over cross-SO eligibility filtering removed
    // along with the rest of the SO flow.
    if (selectionModal.type === "bowler") {
      // Plain list of available bowlers — no "recommended" sort, no
      // emerald highlight. Per operator spec (2026-05-19) the picker
      // is a neutral roster; auto-ranking made operators second-guess
      // their own choice. We DO still filter out bowlers who've
      // finished their format quota (T20 = 4 overs, BBL = 4 overs,
      // ODI = 10 overs etc.) so they can't be picked by accident.
      const bowlersList = players?.bowlers ?? [];
      const ballsPerOver = aggregate?.match_type?.balls_per_over ?? 6;
      const maxOversPerBowler = aggregate?.match_type?.max_overs_per_bowler ?? null;
      const oversByBowler = {};
      (bowlingStats || []).forEach((s) => {
        oversByBowler[s.player_id] = Math.floor((s.balls || 0) / ballsPerOver);
      });
      // Previous-over bowler — flagged for "Just bowled" badge so the
      // operator doesn't accidentally pick them for back-to-back overs
      // (rule violation in every format).
      const lastLegal = [...recentBalls].reverse().find((b) => b.is_legal);
      const prevOverBowlerId = lastLegal?.bowler_id || null;
      // Consecutive-sets streak by the just-bowled bowler. For classical
      // formats this is 1 (Law 17.5: no back-to-back overs), so the
      // just-bowled bowler is always blocked. For The Hundred the cap is
      // 2 (captain may extend a spell to 10 balls) so we only block when
      // the streak has reached the cap — same bowler for the last 2
      // completed sets = the 3rd set must go to someone else.
      // recentBalls is capped at the last ~12 in the fetch above, which
      // covers the 2-set (10-ball) window we need for Hundred.
      const maxConsecutiveSets =
        aggregate?.match_type?.max_consecutive_sets_per_bowler ?? 1;
      let prevBowlerStreak = 0;
      if (prevOverBowlerId && lastLegal?.over_number) {
        let overCursor = lastLegal.over_number;
        while (overCursor >= 1) {
          const setBalls = recentBalls.filter(
            (b) => b.is_legal && b.over_number === overCursor,
          );
          if (setBalls.length === 0) break;
          const solo = setBalls.every((b) => b.bowler_id === prevOverBowlerId);
          if (!solo) break;
          prevBowlerStreak += 1;
          overCursor -= 1;
        }
      }
      return bowlersList
        .map((p) => ({
          ...p,
          _overs: oversByBowler[p.id] || 0,
          _atLimit: maxOversPerBowler != null && (oversByBowler[p.id] || 0) >= maxOversPerBowler,
          _justBowled: p.id === prevOverBowlerId,
          _prevBowlerStreak: p.id === prevOverBowlerId ? prevBowlerStreak : 0,
          _maxConsecutiveSets: maxConsecutiveSets,
        }))
        // Drop quota-finished bowlers entirely.
        .filter((p) => !p._atLimit)
        // Stable alphabetical sort — predictable for the operator.
        .sort((a, b) => {
          const an = (a.nick_name || `${a.first_name || ""} ${a.last_name || ""}`).trim();
          const bn = (b.nick_name || `${b.first_name || ""} ${b.last_name || ""}`).trim();
          return an.localeCompare(bn);
        });
    }
    // Striker / non-striker: filter dismissed players + the other-end batter
    const dismissedIds = new Set(
      (battingStats || [])
        .filter((s) => s.is_out)
        .map((s) => s.player_id),
    );
    const otherEnd =
      selectionModal.type === "striker" ? displayNonStrikerId :
      selectionModal.type === "non_striker" ? displayStrikerId : null;
    return (players?.batters ?? []).filter(
      (p) =>
        !dismissedIds.has(p.id) &&
        p.id !== otherEnd,
    );
  }, [selectionModal.type, players, battingStats, bowlingStats, recentBalls, aggregate, displayStrikerId, displayNonStrikerId]);

  // Aligned with backend Ball.wicket_type values (per persist_ball + Phase-1
  // tests). Single source of truth between SPA + API + scorecards.
  const wicketTypes = [
    "BOWLED",
    "LBW",
    "CAUGHT",
    "RUN_OUT",
    "STUMPED",
    "HIT_WICKET",
    "MANKAD",            // run-out at non-striker's end during delivery (Law 38)
    "HIT_BALL_TWICE",    // Law 34 — matches backend enum (was HIT_TWICE — mismatch fixed)
    "OBSTRUCTING_FIELD", // Law 37 — matches backend enum (was OBSTRUCTING — mismatch fixed)
    "HANDLED_BALL",      // Law 37 legacy — surfaced 2026-06-05 per founder ask
    "RETIRED_HURT",      // Law 25 — batter can return
    "RETIRED_OUT",       // Law 25 — batter cannot return without captain consent
    "TIMED_OUT",         // Law 40
  ];

  const bowlerWicketTypes = new Set([
    "BOWLED",
    "LBW",
    "CAUGHT",
    "STUMPED",
    "HIT_WICKET",
  ]);

  const isCaught = ball.wicket_type === "CAUGHT";
  const isStumped = ball.wicket_type === "STUMPED";
  const isRunOut = ball.wicket_type === "RUN_OUT";
  const mainGroups = useMemo(() => Object.entries(ACTIVITIES_CONFIG), []);
  // Bowler type derived from the selected delivery — drives chip-group visibility.
  const bowlerType = bowlerTypeFromDelivery(ball.analytics?.delivery_type);

  // Auto-defaults for Keeper + Bounce so the operator doesn't have to
  // tap them every ball. Each only fills in when the analytics field is
  // currently blank; any operator override persists across re-renders.
  //   Keeper  →  decided by the BOWLER'S Player profile. Prefers the
  //              precise bowling_style code (RAO / LAO / RAW / LAW = the
  //              4 spin styles where the keeper stands UP to the stumps).
  //              Falls back to bowling_type=SPIN for legacy players
  //              who never had bowling_style filled in. Anything else
  //              (pace / medium / unknown / no bowler picked) defaults
  //              to "Standard" — keeper stands back.
  //              The auto-default pre-fills the moment the bowler is
  //              selected, and is left blank until then. 2026-10-23 — it
  //              used to stamp "Standard" while the bowler was still
  //              unpicked (every new over: backend clears current_bowler),
  //              and the blank-only guard then kept that "Standard" once
  //              the spinner was picked → ball 1 of every spin over was
  //              wrong in QC. keeperAutoRef remembers the value WE filled,
  //              so a bowler change re-applies the default while a manual
  //              tap (any other value) is still preserved.
  //   Bounce  →  "STANDARD". The other values (EXTRA, KEPT_LOW,
  //              VARIABLE) are rare events the operator manually flags.
  const SPIN_BOWLING_STYLES = new Set(["RAO", "LAO", "RAW", "LAW"]);
  const bowlerRecord = useMemo(
    () => players?.bowlers?.find((p) => p.id === displayBowlerId) || null,
    [players?.bowlers, displayBowlerId],
  );
  const bowlerBowlingStyle = bowlerRecord?.bowling_style || "";
  const bowlerBowlingType  = bowlerRecord?.bowling_type  || "";
  const bowlerKnown = Boolean(bowlerRecord);
  const keeperAutoRef = useRef("");
  useEffect(() => {
    const a = ball?.analytics || {};
    let nextKeeper = a.keeper_activity;
    let nextBounce = a.bounce_category;
    const keeperIsAuto = !nextKeeper || nextKeeper === keeperAutoRef.current;
    if (bowlerKnown && keeperIsAuto) {
      const isSpinByStyle = SPIN_BOWLING_STYLES.has(bowlerBowlingStyle);
      // Fallback for players with no precise bowling_style code on
      // file: the legacy bowling_type categorical (PACE / MEDIUM / SPIN)
      // still tells us this is a spinner.
      const isSpinByType  = !bowlerBowlingStyle && bowlerBowlingType === "SPIN";
      nextKeeper = (isSpinByStyle || isSpinByType) ? "Up-to-stumps" : "Standard";
      keeperAutoRef.current = nextKeeper;
    }
    if (!nextBounce) {
      nextBounce = "STANDARD";
    }
    if (nextKeeper !== a.keeper_activity || nextBounce !== a.bounce_category) {
      setBall((prev) => ({
        ...prev,
        analytics: {
          ...prev.analytics,
          keeper_activity: nextKeeper,
          bounce_category: nextBounce,
        },
      }));
    }
  }, [
    bowlerKnown, bowlerBowlingStyle, bowlerBowlingType,
    ball?.analytics?.keeper_activity, ball?.analytics?.bounce_category,
    setBall,
  ]);
  // Beamer / Full Toss never bounce — so post-pitch attributes are
  // meaningless. Hide Bounce, Spin Direction, Drift and Turn for those
  // pitch zones. Mirrors how spin-only groups are hidden for pace bowlers.
  const NO_BOUNCE_PITCH_ZONES = new Set(["Beamer", "Full Toss"]);
  const POST_PITCH_GROUPS = new Set(["Bounce", "Spin Direction", "Drift", "Turn"]);
  const skipPostPitchGroups = NO_BOUNCE_PITCH_ZONES.has(ball?.spatial?.pitch_zone);

  // No-bat-contact shot connections: ball never met the bat, so Air
  // Movement (which describes the ball's flight at contact) is
  // meaningless. Same set used to skip wagon-wheel.
  const NO_AIR_MOVEMENT_CONNECTIONS = new Set([
    "Play and Miss", "Play and Miss (Legside)",
    "Shoulder Arms", "Left",
    "Padded", "Hit Pad", "Hit Body", "Hit Helmet",
  ]);
  const skipAirMovement = NO_AIR_MOVEMENT_CONNECTIONS.has(ball?.analytics?.shot_connection);

  // Control is only meaningful when the bowler imparted real flight on the
  // ball — Flat / Loopy / High. Ground (or empty) means no flight to
  // control. Match canonical option-list casing.
  const CONTROL_ELIGIBLE_AIR = new Set(["Flat", "Loopy", "High"]);
  const skipControl = !CONTROL_ELIGIBLE_AIR.has(ball?.analytics?.air_movement);

  const isGroupRelevant = (group) => {
    if (skipPostPitchGroups && POST_PITCH_GROUPS.has(group)) return false;
    if (skipAirMovement && group === "Air Movement") return false;
    if ((skipAirMovement || skipControl) && group === "Control") return false;
    const r = ACTIVITIES_CONFIG[group]?.relevant || "ALL";
    if (r === "ALL") return true;
    if (!bowlerType) return true;   // before delivery picked, show everything
    return r === bowlerType;
  };

  // Wipe stale analytics whenever a gating field changes so a hidden group
  // doesn't keep persisting an invisible value.
  useEffect(() => {
    if (!skipPostPitchGroups) return;
    setBall((prev) => {
      const a = prev.analytics || {};
      if (!a.bounce_category && !a.spin_direction && !a.drift_direction && !a.turn_after_pitch) {
        return prev;
      }
      return {
        ...prev,
        analytics: {
          ...a,
          bounce_category: "",
          spin_direction: "",
          drift_direction: "",
          turn_after_pitch: "",
        },
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skipPostPitchGroups]);

  // Law 21.18 auto-clear: a no-ball can ONLY carry a run-out style
  // dismissal (RUN_OUT / OBSTRUCTING_FIELD / HIT_BALL_TWICE / MANKAD).
  // If the operator has both the no-ball toggle and an
  // illegal-on-no-ball wicket type selected at save-time the dismissal
  // can't stand — the backend now silently strips it (Law 21.18), but
  // before save we proactively clear the wicket state in the UI so
  // the "pick the next batsman" modal never fires and the operator
  // sees explicit feedback explaining what happened.
  useEffect(() => {
    const NO_BALL_LEGAL = new Set([
      "RUN_OUT", "OBSTRUCTING_FIELD", "HIT_BALL_TWICE", "MANKAD",
    ]);
    if (!ball?.is_no_ball) return;
    const wt = ball?.wicket_type;
    if (!wt) return;
    if (NO_BALL_LEGAL.has(wt)) return;
    setBall((prev) => ({
      ...prev,
      has_wicket: false,
      wicket_type: null,
      dismissed_player_id: null,
      dismissed_by_id: null,
      caught_by_id: null,
      stumped_by_id: null,
      run_out_fielder_1_id: null,
      run_out_fielder_2_id: null,
    }));
    toast.info(
      `Law 21.18: ${wt} can't stand on a no-ball — wicket cleared. ` +
      `Only the no-ball extra (+1) will be recorded.`,
      { ttl: 5500 },
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ball?.is_no_ball, ball?.wicket_type]);

  // Shot-connection switching to a no-contact value clears Air Movement
  // (which then cascades — see the next effect).
  useEffect(() => {
    if (!skipAirMovement) return;
    setBall((prev) => {
      const a = prev.analytics || {};
      if (!a.air_movement && !a.control) return prev;
      return { ...prev, analytics: { ...a, air_movement: "", control: "" } };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skipAirMovement]);

  // Air movement changing away from Flat/Loopy/High clears Control.
  useEffect(() => {
    if (!skipControl) return;
    setBall((prev) => {
      const a = prev.analytics || {};
      if (!a.control) return prev;
      return { ...prev, analytics: { ...a, control: "" } };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skipControl]);
  const [selectedValues, setSelectedValues] = useState({});

  useEffect(() => {
    const nextSelected = {};
    mainGroups.forEach(([mainGroup, data]) => {
      const val = ball.analytics?.[data.fieldName];
      if (val) nextSelected[mainGroup] = val;
    });
    setSelectedValues(nextSelected);
  }, [ball.analytics, mainGroups]);

  const panelClass =
    "bg-bg-primary rounded-lg py-1 px-1.5 border border-bg-secondary shadow-sm";
  const btnBase =
    "px-2 py-0.5 rounded-md text-xs font-medium transition-all duration-150 ease-in-out touch-manipulation border";
  const btnPrimary = `${btnBase} bg-button-primary text-white border-button-primary hover:opacity-90`;
  const btnSecondary = `${btnBase} bg-bg-secondary text-text-primary border-border-default hover:bg-bg-main`;
  const btnGhost = `${btnBase} bg-transparent text-text-secondary border-transparent hover:text-text-primary`;
  const btnToggle = `${btnBase} bg-bg-secondary text-text-primary border-border-default hover:bg-bg-main`;
  const btnToggleActive = `${btnBase} bg-green-700 text-white border-green-700 shadow`;

  const strikerRequired =
    actions?.striker_select || actions?.new_striker_required;
  const nonStrikerRequired =
    actions?.non_striker_select || actions?.new_non_striker_required;
  const bowlerRequired =
    actions?.bowler_select || actions?.new_bowler_select;
  const wicketRequiredMissing =
    ball.has_wicket &&
    (!ball.wicket_type ||
      !ball.dismissed_player_id ||
      (bowlerWicketTypes.has(ball.wicket_type) && !ball.dismissed_by_id) ||
      (isCaught && !ball.caught_by_id) ||
      (isStumped && !ball.stumped_by_id) ||
      (isRunOut && !ball.run_out_fielder_1_id));
  // Wagon wheel = where the ball went off the bat. So it's only required when
  // the ball actually went off the bat. Skip enforcement when:
  //   - dead ball / wide (no bat contact)
  //   - bowled / LBW / stumped / mankad / retired / timed-out / hit-wicket
  //     (ball didn't come off the bat — it hit stumps, pad, was missed, or
  //      no delivery happened)
  // Wickets that DO need wagon wheel: caught, run-out (off-the-bat), hit-twice,
  //   obstructing — bat-met-ball is the rule.
  const NO_WAGON_WICKETS = new Set([
    "BOWLED", "LBW", "STUMPED", "MANKAD",
    "RETIRED", "RETIRED_HURT", "RETIRED_OUT",
    "TIMED_OUT", "HIT_WICKET",
  ]);
  // Shot-connection / stroke values that imply the ball never came off the
  // bat — wagon wheel is meaningless and should be skipped (Vikram, 2026-05-07).
  const NO_BAT_CONTACT_SHOT_CONNECTIONS = new Set([
    "Play and Miss", "Play and Miss (Legside)",
    "Shoulder Arms", "Left",
    "Padded", "Hit Pad", "Hit Body", "Hit Helmet",
  ]);
  const NO_BAT_CONTACT_STROKES = new Set(["No shot", "Leave"]);
  const noBatContact =
    NO_BAT_CONTACT_SHOT_CONNECTIONS.has(ball.analytics?.shot_connection) ||
    NO_BAT_CONTACT_STROKES.has(ball.analytics?.stroke);
  const wicketSkipsWagon = ball.has_wicket && NO_WAGON_WICKETS.has(ball.wicket_type);
  // Wagon wheel is RECOMMENDED — never blocks saving. The wagon-wheel
  // panel still highlights / mutes based on `wagonRequired` so the
  // operator gets a visual nudge to fill it in (4s, 6s, anything off
  // the bat), but they can save a dot ball / wide / pad-up etc. without
  // touching the wheel. wagonMissing is no longer included in the
  // missing-required gate below.
  const wagonRequired =
    !ball.is_dead_ball && !ball.is_wide && !wicketSkipsWagon && !noBatContact;
  // Critical data-integrity gate: striker and non-striker MUST be
  // different. STT homophones (Rajat ↔ Virat, Iyer ↔ Aiyar) and
  // mismatched cross-batter modal selections used to put the same
  // player at both ends; backend rejects the save, but blocking the
  // SAVE button up-front (with a clear inline warning) stops operators
  // from hitting the error repeatedly. See commit f34044d for the
  // root-cause backend fix; this is the operator-facing version.
  const strikerNonStrikerCollision =
    Boolean(displayStrikerId) &&
    Boolean(displayNonStrikerId) &&
    String(displayStrikerId) === String(displayNonStrikerId);

  // 2026-05-29 — proactive collision recovery (founder ask: "striker
  // and non-striker sometimes end up the same after a wicket + edit").
  // The save gate already blocks save, but operators were getting
  // stuck without noticing the inline warning. Now: as soon as we
  // detect the collision (and we're not already opening a picker for
  // something else), auto-open the non-striker picker forced=true so
  // the operator MUST pick a different player. handleSelectPlayer
  // refuses the matching id, so they can only land on a valid one.
  // Clears ball.non_striker first so the picker shows the full list.
  useEffect(() => {
    if (!strikerNonStrikerCollision) return;
    if (selectionModal.open) return;       // a picker is already up
    if (isMatchEnded || isInningsEnded) return;
    setBall((prev) => ({ ...prev, non_striker: null }));
    setSelectionModal({ open: true, type: "non_striker", forced: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strikerNonStrikerCollision]);

  const missingRequired =
    (strikerRequired && !displayStrikerId) ||
    (nonStrikerRequired && !displayNonStrikerId) ||
    (bowlerRequired && !displayBowlerId) ||
    !displayStrikerId ||
    !displayNonStrikerId ||
    !displayBowlerId ||
    wicketRequiredMissing ||
    strikerNonStrikerCollision;

  const handleActivitySelect = (mainGroup, item) => {
    const fieldName = ACTIVITIES_CONFIG[mainGroup].fieldName;
    setBall((prev) => ({
      ...prev,
      analytics: {
        ...prev.analytics,
        [fieldName]: item,
        ...(fieldName === "air_movement" &&
        !["Flat", "Loopy", "High"].includes(item)
          ? { control: "" }
          : {}),
      },
    }));

    const nextSelectedValues = {
      ...selectedValues,
      [mainGroup]: item,
    };

    if (fieldName === "air_movement" && !["Flat", "Loopy", "High"].includes(item)) {
      nextSelectedValues.Control = "";
    }

    setSelectedValues(nextSelectedValues);
  };
  const wicketSummary = ball.has_wicket
    ? (() => {
        const dismissedPlayer = getAnyPlayerName(ball.dismissed_player_id);
        const bowler = ball.dismissed_by_id
          ? ` b ${getAnyPlayerName(ball.dismissed_by_id)}`
          : "";

        // Handle the "c Fielder" or "st Fielder" part
        let action = "";
        if (isCaught && ball.caught_by_id) {
          action = ` c ${getAnyPlayerName(ball.caught_by_id)}`;
        } else if (isStumped && ball.stumped_by_id) {
          action = ` st ${getAnyPlayerName(ball.stumped_by_id)}`;
        } else if (isRunOut && ball.run_out_fielder_1_id) {
          const fielder1 = getAnyPlayerName(ball.run_out_fielder_1_id);
          // Scorecard style: "run out (Jadeja/Dhoni)".
          const fielder2 = ball.run_out_fielder_2_id
            ? `/${getAnyPlayerName(ball.run_out_fielder_2_id)}`
            : "";
          action = ` run out (${fielder1}${fielder2})`;
        }

        // If it's a direct bowled/LBW (no fielder involved), action will be empty
        // Example Output: "Virat Kohli c Dhoni b Ashwin" or "Virat Kohli lbw b Ashwin"
        if (ball.wicket_type === "BOWLED" || ball.wicket_type === "LBW") {
          return `${dismissedPlayer} ${ball.wicket_type.toLowerCase()}${bowler}`;
        }

        // Fallback for general wickets: "Dismissed Player c Fielder b Bowler"
        return `${dismissedPlayer}${action}${bowler}`;
      })()
    : "";

  const isMatchEnded =
    Boolean(actions?.end_match) ||
    Boolean(events?.match_end) ||
    Boolean(matchEnded);
  const isInningsEnded =
    Boolean(actions?.end_innings) || Boolean(events?.innings_end);

  // 2026-07-08 — auto-start-next-innings removed at founder ask.
  // Rationale: late corrections after the "last ball" (delayed wide /
  // no-ball / DRS review) routinely add a delivery to the just-ended
  // innings. Auto-starting the next innings closed the current one
  // server-side, blocking those corrections. Operator now confirms
  // explicitly via the innings-end modal below. Follow-on / super-over
  // choices were already gated on explicit operator input.
  //
  // Dismissal signatures — track WHICH aggregate snapshot the operator
  // already dismissed the prompt against. When they score a late
  // correction, the snapshot changes and the prompt reappears.
  const inningsEndSig = `${aggregate?.innings_number ?? 0}-${aggregate?.runs ?? 0}-${aggregate?.wickets ?? 0}-${aggregate?.legal_balls ?? 0}`;
  const [inningsEndDismissedSig, setInningsEndDismissedSig] = useState(null);
  const [matchEndDismissedSig, setMatchEndDismissedSig] = useState(null);
  const showInningsEndPrompt =
    isInningsEnded && !isMatchEnded && inningsEndDismissedSig !== inningsEndSig;
  const showMatchEndPrompt =
    isMatchEnded && matchEndDismissedSig !== inningsEndSig;
  // 2026-06-02 — In Play / Paused toggle. Operator can pause the
  // match (e.g., between balls during a rain delay or to step away)
  // without ending the innings. Persists per-match in localStorage so
  // a refresh doesn't accidentally resume.
  // NOTE: pure client-side gate. A determined operator can flip the
  // key in devtools; this is a UX safeguard, not a security control.
  // Server-side pause state needs a Match.state="PAUSED" enum + API.
  const PAUSE_KEY = `zcd_bbp_paused_${id}`;
  const [matchPaused, setMatchPaused] = useState(() => {
    try { return localStorage.getItem(PAUSE_KEY) === "1"; } catch { return false; }
  });
  const toggleMatchPaused = () => {
    setMatchPaused((prev) => {
      const next = !prev;
      try {
        if (next) localStorage.setItem(PAUSE_KEY, "1");
        else localStorage.removeItem(PAUSE_KEY);
      } catch { /* ignore */ }
      return next;
    });
  };

  // 2026-06-02 / refactored 2026-06-04 — Test day/session/overs-today.
  // The server now derives these from the actual ball history
  // (Stumps marks + distinct IST play dates + Lunch/Tea/Dinner). The
  // SPA reads `testState` from the scoring store and uses it as the
  // source of truth. localStorage stays as a fallback for offline /
  // pre-aggregate-load rendering and for operator manual overrides
  // (which persist locally until the next server response overwrites
  // them — that's intentional; an explicit save on the next ball
  // re-syncs everyone).
  const DAY_KEY = `zcd_bbp_test_day_${id}`;
  const SESS_KEY = `zcd_bbp_test_session_${id}`;
  const OVERS_START_KEY = `zcd_bbp_test_overs_at_day_start_${id}`;
  const serverTestState = useScoringStore((s) => s.testState);
  const [testDay, setTestDay] = useState(() => {
    try { return parseInt(localStorage.getItem(DAY_KEY), 10) || 1; } catch { return 1; }
  });
  const [testSession, setTestSession] = useState(() => {
    try { return parseInt(localStorage.getItem(SESS_KEY), 10) || 1; } catch { return 1; }
  });
  // completed_overs snapshot at the START of the current day. Used to
  // compute "overs bowled today" client-side. With server-derived
  // values landing on every applyServerState, this is now just a
  // visual heuristic — the server's overs_bowled_today wins below.
  const [testOversAtDayStart, setTestOversAtDayStart] = useState(() => {
    try { return parseInt(localStorage.getItem(OVERS_START_KEY), 10) || 0; } catch { return 0; }
  });
  const persistTest = (day, sess, start) => {
    try {
      localStorage.setItem(DAY_KEY, String(day));
      localStorage.setItem(SESS_KEY, String(sess));
      localStorage.setItem(OVERS_START_KEY, String(start));
    } catch { /* ignore */ }
  };

  // Whenever the server sends a fresh testState (init load, post-save,
  // post-undo, etc.) override the local copies so a refresh / device
  // swap can't strand the operator on stale "Day 1 Session 1" — the
  // exact bug Keerthi hit on the IND vs WI Test on 2026-06-04.
  useEffect(() => {
    if (!serverTestState) return;
    const sd = Number.isFinite(serverTestState.current_day) ? serverTestState.current_day : null;
    const ss = Number.isFinite(serverTestState.current_session) ? serverTestState.current_session : null;
    const ot = Number.isFinite(serverTestState.overs_bowled_today) ? serverTestState.overs_bowled_today : null;
    if (sd != null) setTestDay(sd);
    if (ss != null) setTestSession(ss);
    // overs_bowled_today is the count of overs bowled today; the
    // local state tracks "completed_overs snapshot at day start", so
    // we derive that: day_start_snapshot = current_completed_overs - overs_today.
    if (ot != null) {
      const currentCO = (aggregate?.completed_overs) ?? 0;
      setTestOversAtDayStart(Math.max(0, currentCO - ot));
    }
    if (sd != null && ss != null) {
      const startSnap = ot != null
        ? Math.max(0, ((aggregate?.completed_overs) ?? 0) - ot)
        : testOversAtDayStart;
      persistTest(sd, ss, startSnap);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverTestState?.current_day, serverTestState?.current_session, serverTestState?.overs_bowled_today, aggregate?.completed_overs]);
  const advanceTestSession = () => {
    setTestSession((prev) => {
      const next = Math.min(prev + 1, 4); // D/N Test has 4 sessions
      persistTest(testDay, next, testOversAtDayStart);
      return next;
    });
  };
  const advanceTestDay = () => {
    const newStart = (aggregate?.completed_overs ?? 0);
    setTestDay((prev) => {
      // Test caps at Day 5. Beyond that the match is over (or a draw),
      // so we hard-stop instead of rolling to Day 6.
      const next = Math.min(prev + 1, 5);
      persistTest(next, 1, newStart);
      return next;
    });
    setTestSession(1);
    setTestOversAtDayStart(newStart);
  };

  // 2026-06-02 — explicit Lunch / Tea / Stumps handlers driving the
  // day/session machine the way Test cricket actually works:
  //   Start  → Session 1 (Morning) → LUNCH
  //   Lunch  → Session 2 (Afternoon) → TEA
  //   Tea    → Session 3 (Evening) → STUMPS
  //   Stumps → next Day, Session 1 (cap at Day 5 Session 3)
  // Each handler stamps the break_type on the next-ball draft so the
  // recorded ball carries the break marker, then bumps state +
  // persists. Sessions are time-driven; no overs cap per session.
  const markBreakOnDraft = (label) => {
    setBall((prev) => ({
      ...prev,
      release: {
        ...(prev.release || {}),
        is_break: true,
        break_type: label,
      },
    }));
  };
  const markTestLunch = () => {
    markBreakOnDraft("Lunch (Test matches)");
    setTestSession((prev) => {
      const next = Math.max(prev, 2);
      persistTest(testDay, next, testOversAtDayStart);
      return next;
    });
  };
  const markTestTea = () => {
    markBreakOnDraft("Tea (Test matches)");
    setTestSession((prev) => {
      const next = Math.max(prev, 3);
      persistTest(testDay, next, testOversAtDayStart);
      return next;
    });
  };
  const markTestStumps = () => {
    markBreakOnDraft("Stumps (Test - end of day)");
    // If we're on Day 5 Session 3 already, the match should be ending —
    // don't roll forward into a phantom Day 6.
    if (testDay >= 5 && testSession >= 3) return;
    advanceTestDay();
  };
  const oversBowledToday = Math.max(
    0,
    (aggregate?.completed_overs ?? 0) - testOversAtDayStart,
  );
  const oversLeftToday = Math.max(0, 90 - oversBowledToday);

  // 2026-06-02 — day / session transitions are now driven by the
  // operator clicking Lunch / Tea / Stumps in the More menu. Sessions
  // are time-based in real Test cricket (no fixed overs-per-session),
  // and the day cap (90 overs) is just a quota for the day's play —
  // not a trigger. The previous auto-day-advance at 90 overs has been
  // removed so the cycle matches the founder spec exactly.

  // Manual overrides for the three counters — used when the auto-
  // derived numbers go wrong (operator started scoring mid-day, undo,
  // rebuilt, etc.). Each input is the SOURCE OF TRUTH once changed —
  // we just write to localStorage + state.
  const setTestDayManual = (n) => {
    const v = Math.max(1, parseInt(n, 10) || 1);
    setTestDay(v);
    persistTest(v, testSession, testOversAtDayStart);
  };
  const setTestSessionManual = (n) => {
    const v = Math.max(1, Math.min(4, parseInt(n, 10) || 1));
    setTestSession(v);
    persistTest(testDay, v, testOversAtDayStart);
  };
  const setOversLeftManual = (n) => {
    // overs-left → derive a new "overs at day start" so the chip math
    // works on the next render. left = 90 - (current - start), so
    // start = current - (90 - left).
    const left = Math.max(0, Math.min(90, parseInt(n, 10) || 0));
    const current = aggregate?.completed_overs ?? 0;
    const newStart = current - (90 - left);
    setTestOversAtDayStart(newStart);
    persistTest(testDay, testSession, newStart);
  };

  // 2026-07-08 — Gate on the *prompt* being visible, not on the raw
  // end flags. Once the operator taps "Continue scoring for late
  // corrections" on the innings/match-end modal, the prompt hides and
  // save re-enables so a delayed wide / no-ball / DRS review can land
  // as an extra ball on the current innings.
  const saveDisabled =
    missingRequired || showInningsEndPrompt || showMatchEndPrompt || saveInFlight || matchPaused;

  useEffect(() => {
    if (!aggregate || selectionModal.open) return;
    if (isMatchEnded || isInningsEnded) return;

    if (
      (actions?.new_bowler_select || actions?.bowler_select) &&
      !displayBowlerId
    ) {
      setSelectionModal({ open: true, type: "bowler", forced: true });
      return;
    }
    if (
      (actions?.new_striker_required || actions?.striker_select) &&
      !displayStrikerId
    ) {
      setSelectionModal({ open: true, type: "striker", forced: true });
      return;
    }
    if (
      (actions?.new_non_striker_required || actions?.non_striker_select) &&
      !displayNonStrikerId
    ) {
      setSelectionModal({ open: true, type: "non_striker", forced: true });
      return;
    }

    if (!currentStrikerSet) {
      setSelectionModal({ open: true, type: "striker", forced: true });
      return;
    }
    if (!currentNonStrikerSet) {
      setSelectionModal({ open: true, type: "non_striker", forced: true });
      return;
    }
    if (!currentBowlerSet) {
      setSelectionModal({ open: true, type: "bowler", forced: true });
    }
  }, [
    aggregate,
    selectionModal.open,
    currentStrikerSet,
    currentNonStrikerSet,
    currentBowlerSet,
    actions,
    displayStrikerId,
    displayNonStrikerId,
    displayBowlerId,
    isMatchEnded,
    isInningsEnded,
  ]);

  useEffect(() => {
    if (selectionModal.open && (isMatchEnded || isInningsEnded)) {
      setSelectionModal({ open: false, type: null, forced: false });
    }
  }, [selectionModal.open, isMatchEnded, isInningsEnded]);

  const openSelectionModal = (type) => {
    setSelectionModal({ open: true, type, forced: false });
  };

  const closeSelectionModal = () => {
    setSelectionModal({ open: false, type: null, forced: false });
  };

  const handleSelectPlayer = (playerId) => {
    if (!selectionModal.type) return;
    if (selectionModal.type === "striker") {
      if (displayNonStrikerId && playerId === displayNonStrikerId) {
        alert("Striker and non-striker can't be the same player.");
        return;
      }
      setBall((prev) => ({ ...prev, striker: playerId }));
    }
    if (selectionModal.type === "non_striker") {
      if (displayStrikerId && playerId === displayStrikerId) {
        alert("Striker and non-striker can't be the same player.");
        return;
      }
      setBall((prev) => ({ ...prev, non_striker: playerId }));
    }
    if (selectionModal.type === "bowler") {
      setBall((prev) => ({ ...prev, bowler: playerId }));
    }
    closeSelectionModal();
  };


  // ── Keyboard shortcuts ──────────────────────────────────────────────────
  // Gated by shortcutsEnabled toggle (header button). Off by default to
  // prevent accidental scoring when the operator is typing elsewhere or
  // bumps a key. The `?` shortcut to open the help modal is always allowed.
  useEffect(() => {
    const onKey = (e) => {
      // ignore when user is typing in an input/select/textarea/contenteditable
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select" || e.target?.isContentEditable) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      // `?` is always allowed so operators can discover/toggle shortcuts
      if (e.key === "?") { setShortcutsOpen((v) => !v); e.preventDefault(); return; }
      if (!shortcutsEnabled) return;  // master gate

      const k = e.key;
      // 2026-05-29 — Space is now the dot-ball shortcut (was "0").
      // Operators kept hitting "0" expecting a navigation behaviour
      // (page-down) since 1-6 are non-navigational. Space lines up
      // with the "tap to skip" muscle memory from other scorers.
      if (k === " " || e.code === "Space") {
        setBall((p) => ({ ...p, completed_runs: 0, is_boundary: false }));
        e.preventDefault(); return;
      }
      // digits 1-6 → runs (0 dropped per founder ask; use Space)
      if (k >= "1" && k <= "6") {
        const n = Number(k);
        setBall((p) => ({ ...p, completed_runs: n, is_boundary: n === 4 || n === 6 }));
        e.preventDefault(); return;
      }
      const lower = k.toLowerCase();
      if (lower === "w") {
        setBall((p) => {
          const turningOn = !p.is_wide;
          return {
            ...p,
            is_wide: turningOn,
            is_no_ball: turningOn ? false : p.is_no_ball,
            completed_runs: turningOn ? 0 : p.completed_runs,
            is_boundary: turningOn ? false : p.is_boundary,
          };
        });
        e.preventDefault(); return;
      }
      if (lower === "n") {
        setBall((p) => {
          const turningOn = !p.is_no_ball;
          return {
            ...p,
            is_no_ball: turningOn,
            is_wide: turningOn ? false : p.is_wide,
            completed_runs: turningOn ? 0 : p.completed_runs,
            is_boundary: turningOn ? false : p.is_boundary,
          };
        });
        e.preventDefault(); return;
      }
      if (lower === "b") { setBall((p) => ({ ...p, is_bye: !p.is_bye, is_leg_bye: false })); e.preventDefault(); return; }
      if (lower === "l") { setBall((p) => ({ ...p, is_leg_bye: !p.is_leg_bye, is_bye: false })); e.preventDefault(); return; }
      if (lower === "k") { setBall((p) => ({ ...p, has_wicket: !p.has_wicket })); e.preventDefault(); return; }
      if (lower === "u" || k === "Backspace") {
        // T140: if a preview is already showing, U confirms it (saves
        // an extra click for power users). Otherwise show the preview.
        if (pendingUndo) confirmPendingUndo();
        else handleUndoBallRequest();
        e.preventDefault(); return;
      }
      if (k === "Enter" || k === " ") {
        if (!saveDisabled && !savePending) startDelayedSave();
        e.preventDefault(); return;
      }
      if (k === "Escape") {
        if (savePending) { cancelDelayedSave(); e.preventDefault(); return; }
        if (shortcutsOpen) { setShortcutsOpen(false); e.preventDefault(); return; }
      }
      // (`?` is handled at the top so it works regardless of toggle state)
      // tab cycling within the spatial tabs (pitch -> impact -> wagon -> pitch)
      if (lower === "t") {
        setSpatialTab((c) => (c === "pitch" ? "impact" : c === "impact" ? "wagon" : "pitch"));
        e.preventDefault(); return;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [setBall, handleUndoBall, saveDisabled, savePending, startDelayedSave, cancelDelayedSave, shortcutsOpen, shortcutsEnabled]);

  if (!aggregate) {
    if (loadError) {
      return (
        <div
          data-testid="bbp-load-error"
          className="min-h-dvh bg-bg-main text-text-primary p-6 flex flex-col items-center justify-center"
        >
          <div className="max-w-xl w-full bg-rose-500/10 border border-rose-500/40 rounded-lg p-5 space-y-2">
            <h2 className="text-lg font-semibold text-rose-200">
              Couldn't load this match
            </h2>
            <p className="text-sm text-rose-100/90">
              {loadError.status
                ? `Server returned ${loadError.status}.`
                : "The scoring backend didn't respond."}{" "}
              {loadError.detail}
            </p>
            <p className="text-[11px] text-rose-200/70">
              Open browser DevTools → Network tab → reload to see the failing
              request. Or pass the screenshot + match id to the dev team.
            </p>
            <div className="flex gap-2 pt-2">
              <button
                onClick={() => window.location.reload()}
                className="px-3 py-1.5 rounded bg-rose-600 hover:bg-rose-500 text-white text-sm"
              >
                Retry
              </button>
              <button
                onClick={() => (window.location.href = "/cricket/matches")}
                className="px-3 py-1.5 rounded bg-bg-secondary text-text-primary text-sm"
              >
                Back to matches
              </button>
            </div>
          </div>
        </div>
      );
    }
    return <div data-testid="bbp-loading">Loading…</div>;
  }

  return (
    <div className={`min-h-dvh xl:h-dvh overflow-y-auto xl:overflow-hidden flex gap-1 p-1 pt-7 pb-28 xl:pt-1 xl:pb-1 bg-bg-main text-text-primary text-xs relative ${isIpadLandscape ? "flex-row pb-28" : "flex-col xl:flex-row"}`}>
      {/* Paused banner — sits above everything, visible regardless of which
          column the operator is looking at. Toggled from More → Match state. */}
      {matchPaused && (
        <div
          className="fixed top-7 xl:top-9 left-0 right-0 z-[55] bg-amber-500/95 text-amber-950 text-center py-1.5 px-4 text-xs font-semibold shadow-md cursor-pointer hover:bg-amber-500"
          onClick={toggleMatchPaused}
          role="button"
          title="Click to resume"
        >
          ⏸ MATCH PAUSED — tap to resume
        </div>
      )}
      {/* 2026-06-06 (v2) — Super Over banner. Same slot as the MATCH
          PAUSED amber strip but rose-toned so the operator can't miss
          the mode change. Shows the SO pair number, who's batting, and
          (for the chase half) the runs-to-win. 1 over · 2 wickets
          reminder spelled out so the rules are unambiguous. */}
      {isSuperOver && !matchPaused && (
        <div
          className="fixed top-7 xl:top-9 left-0 right-0 z-[55] bg-rose-500/95 text-white text-center py-1.5 px-4 text-xs font-semibold shadow-md tracking-wider"
          title="Super Over — 1 over · max 2 wickets · chase target if batting second"
        >
          ★ SUPER OVER {superOverIndex || ""}
          {battingTeamName ? ` · ${battingTeamName} batting` : ""}
          {aggregate?.is_chasing && aggregate?.target_runs != null && (
            <> · need {Math.max(0, (aggregate.target_runs + 1) - (aggregate.runs || 0))} to win</>
          )}
          {" "}· 1 over · 2 wickets
        </div>
      )}
      {/* 2026-05-27 layout cutoff bumped from lg (1024) to xl (1280).
          iPad landscape (1133–1180px) and most Android tablets fall
          into the lg range but their middle column wasn't wide enough
          for the pitch-map's `minmax(36px,1fr) × 7` grid (~332px min)
          inside `w-3/12` (~295px), so the pitch cells overflowed into
          the activity-accordion column. At xl+ (≥1280px) the middle
          column gets enough room (≥320px), so the 3-column layout
          activates cleanly. Below xl, columns stack vertically and the
          page scrolls naturally as one document — no overlap. */}
      {/* B2C AI-feature promo: rendered inline inside the left pane (see
          below) so it stays in document flow and doesn't overlap the
          middle / right panes' top headers. Kept as a JSX node here for
          legibility — actually mounted next to the action bar. */}
      {/* ── Sticky match-state header.
            Mobile (< xl, columns stacked): spans full width across the top.
            Desktop (xl, 3 columns): starts from the centre pane (xl:left-1/3)
            so the left pane reaches the very top of the viewport. */}
      <div className="absolute top-0 left-0 right-0 xl:left-1/3 h-7 xl:h-9 bg-bg-primary border-b border-border-default flex items-center px-3 pr-12 xl:pr-[340px] gap-2 xl:gap-3 z-40 text-xs overflow-x-auto">
        {/* T220.3: h-7 on phone / h-9 on desktop. pr-12 reserves 48px
            on mobile for the BallLayout top-right controls dropdown
            (fixed top-1 right-1 z-50) so its button doesn't sit on top
            of the score badge / wickets number. */}
        <span className="font-bold text-sm xl:text-base font-mono text-text-primary">
          {aggregate?.runs ?? 0}
          <span className="text-text-secondary">/</span>
          {aggregate?.wickets ?? 0}
        </span>
        <span className="text-text-secondary font-mono text-[11px] xl:text-xs">
          ({formatInningsProgress(aggregate?.match_type, { legalBalls: aggregate?.legal_balls ?? 0, completedOvers: aggregate?.completed_overs ?? 0 })})
        </span>
        {currentRunRate != null && (
          <span className="font-mono text-[11px] xl:text-xs text-text-secondary whitespace-nowrap" data-testid="bbb-crr">
            CRR <span className="text-text-primary">{currentRunRate.toFixed(2)}</span>
          </span>
        )}
        {requiredRunRate != null && (
          <span className="font-mono text-[11px] xl:text-xs text-amber-300 whitespace-nowrap" data-testid="bbb-rrr">
            RRR {requiredRunRate.toFixed(2)}
          </span>
        )}
        <span className="text-text-primary truncate max-w-[80px] sm:max-w-[140px] lg:max-w-[180px] text-[11px] xl:text-xs">{battingTeamName || ""}</span>
        {aggregate?.is_chasing && (aggregate?.revised_target_runs != null || aggregate?.target_runs != null) && (() => {
          // Cricket convention — "Target" universally means runs-to-win,
          // not opponent's first-innings total. The DB stores `target_runs`
          // as the raw opponent total (engine flips `target_achieved` when
          // runs > target_runs); the display must add +1 to get the
          // score-to-beat figure that operators expect.
          //
          // DLS targets are the exact "score to reach" per regulation, so
          // no +1 in that branch.
          const isDLS = aggregate.revised_target_runs != null;
          const targetToWin = isDLS
            ? aggregate.revised_target_runs
            : aggregate.target_runs + 1;
          const need = Math.max(0, targetToWin - (aggregate.runs ?? 0));
          return (
            <span className="text-amber-300 text-[11px]">
              Target {targetToWin} · Need {need}
              {isDLS && <span className="ml-1 text-[10px] uppercase tracking-wider text-amber-200/80">DLS</span>}
            </span>
          );
        })()}
        {/* 2026-06-06 (v2) — During an SO the chip swaps to a rose
            "Super Over N" badge so the operator never confuses an SO
            innings for a regular one. */}
        {isSuperOver ? (
          <span
            className="text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 bg-rose-500/20 text-rose-200 border border-rose-400/50 whitespace-nowrap font-semibold"
            title="Super Over — 1 over, 2 wickets per side"
          >
            ★ Super Over {superOverIndex || ""}
          </span>
        ) : currentInningsNumber > 0 ? (
          <span className="text-text-secondary text-[10px] uppercase tracking-wider">
            Inn {currentInningsNumber}
          </span>
        ) : null}
        {/* 2026-06-05 — retired-hurt chip(s). Surfaces who's off the
            field right now so the operator sees it without opening
            the live scorecard modal. Backend annotates batting_stats
            with is_retired = "had a retired-hurt wicket AND hasn't
            returned to face another ball". Auto-clears when the
            player comes back. */}
        {(battingStats || []).filter((s) => s.is_retired).map((s) => {
          const name = getBatterName(s.player_id) || "Batter";
          return (
            <span
              key={`retired-${s.player_id}`}
              className="text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 bg-amber-500/15 text-amber-300 border border-amber-400/30 whitespace-nowrap"
              title="Retired hurt — off the field. Clears automatically when they return to bat."
            >
              ✚ {name} retired
            </span>
          );
        })}
        {/* 2026-06-02 — Test-specific chips: Day, Session, Overs left.
            Counters maintained client-side (localStorage) until the
            backend day/session model lands. Operator bumps Day on
            Stumps / Session on Lunch-Tea-Dinner via More menu. */}
        {isTestMatch && (
          <>
            <span
              className="text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 bg-button-primary/15 text-button-primary border border-button-primary/30 whitespace-nowrap"
              title="Test day — bump on Stumps from More menu"
            >
              Day {testDay}
            </span>
            <span
              className="text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 bg-amber-500/15 text-amber-300 border border-amber-400/30 whitespace-nowrap"
              title="Session — bump on Lunch / Tea / Dinner from More menu"
            >
              Sess {testSession}
            </span>
            <span
              className={`text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 border whitespace-nowrap ${
                oversLeftToday <= 10
                  ? "bg-rose-500/15 text-rose-300 border-rose-400/30"
                  : "bg-emerald-500/15 text-emerald-300 border-emerald-400/30"
              }`}
              title={`90 overs/day target — ${oversBowledToday} bowled today`}
            >
              {oversLeftToday} ov left
            </span>
          </>
        )}
        {/* ── Secondary badges — inline on desktop (xl+), collapsed
            into the mobile ⓘ popover otherwise. ──────────────────── */}
        <div className="hidden xl:flex items-center gap-3">
          {/* Sync-status pill — visible-at-a-glance answer to "did my last ball save?".
              online + queue empty = green dot; queued = amber count; offline = red. */}
          <SyncPill ballQueue={ballQueue} />
          {/* Saved counter + relative-time of last save — concrete throughput signal */}
          <SaveCounterPill savedCount={savedCount} lastSavedAt={lastSavedAt} />
          {/* Integrity badge — silent unless aggregate.runs disagrees with sum of legal-ball
              runs. Click opens chat pre-loaded with a rebuild prompt. */}
          <IntegrityBadge aggregate={aggregate} recentBalls={recentBalls} />
          {/* Copy-live-link — public scorecard URL the scorer can share with viewers. */}
          <CopyLiveLinkButton matchId={id} />
          {/* Match Health (Tier 2) — superuser-only quick jump. */}
          {authUser?.is_superuser && (
            <a
              href={`/admin/match-health/${id}`}
              target="_blank"
              rel="noreferrer"
              className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-md bg-blue-500/15 border border-blue-500/40 text-blue-200 hover:bg-blue-500/25"
              title="Open Match Health (admin / support diagnostic)"
            >
              Health
            </a>
          )}
          {/* Live watchers — silent when 0. */}
          {liveSubscribers > 0 && (
            <span
              className="flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-md bg-rose-500/15 border border-rose-500/40 text-rose-300"
              title={`${liveSubscribers} viewer${liveSubscribers === 1 ? "" : "s"} subscribed to the live feed`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-rose-400 animate-pulse" />
              {liveSubscribers} watching
            </span>
          )}
          <div className="flex-1" />
          {/* Keyboard-shortcuts master toggle. Off by default. */}
          <button
            onClick={() => setShortcutsEnabled((v) => !v)}
            className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${
              shortcutsEnabled
                ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40"
                : "bg-bg-secondary text-text-secondary border-border-default hover:text-text-primary"
            }`}
            title={shortcutsEnabled
              ? "Keyboard shortcuts ON — 0-6 = runs, W/N/B/L = extras, K = wicket. Click to disable."
              : "Keyboard shortcuts OFF (prevents accidental scoring). Click to enable."}
          >
            ⌨ {shortcutsEnabled ? "ON" : "OFF"}
          </button>
          <ProgressDots selectedValues={selectedValues} columns={ACTIVITY_COLUMNS} />
          <MissingFieldsChips ball={ball} />
        </div>
        {/* Mobile ⓘ trigger — opens the popover that shows the above
            badges. Sits at the right of the compact mobile header. */}
        <button
          type="button"
          onClick={() => setMobileHeaderInfoOpen((v) => !v)}
          className="xl:hidden ml-auto shrink-0 w-6 h-6 rounded-full inline-flex items-center justify-center bg-bg-secondary border border-border-default text-text-secondary text-[11px] font-bold"
          title="Status badges"
          aria-expanded={mobileHeaderInfoOpen}
        >
          ⓘ
        </button>
      </div>

      {/* T220.3 — Mobile header info popover. Houses the secondary
          badges that collapsed off the compact phone header. Tap ⓘ to
          toggle. xl:hidden so desktop never renders this. */}
      {mobileHeaderInfoOpen && (
        <div className="xl:hidden absolute top-7 left-0 right-0 z-30 bg-bg-primary border-b border-border-default px-3 py-2 flex flex-wrap items-center gap-2 text-xs shadow-lg">
          <SyncPill ballQueue={ballQueue} />
          <SaveCounterPill savedCount={savedCount} lastSavedAt={lastSavedAt} />
          <IntegrityBadge aggregate={aggregate} recentBalls={recentBalls} />
          <CopyLiveLinkButton matchId={id} />
          {authUser?.is_superuser && (
            <a
              href={`/admin/match-health/${id}`}
              target="_blank"
              rel="noreferrer"
              className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-md bg-blue-500/15 border border-blue-500/40 text-blue-200"
            >
              Health
            </a>
          )}
          {liveSubscribers > 0 && (
            <span className="flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-md bg-rose-500/15 border border-rose-500/40 text-rose-300">
              <span className="w-1.5 h-1.5 rounded-full bg-rose-400 animate-pulse" />
              {liveSubscribers} watching
            </span>
          )}
          <ProgressDots selectedValues={selectedValues} columns={ACTIVITY_COLUMNS} />
          <MissingFieldsChips ball={ball} />
          <button
            type="button"
            onClick={() => setMobileHeaderInfoOpen(false)}
            className="ml-auto text-[11px] text-text-secondary hover:text-text-primary px-2 py-0.5 rounded border border-border-default"
          >
            Close
          </button>
        </div>
      )}

      {editingBallId && (
        <div className="absolute top-7 xl:top-9 left-0 right-0 z-40 bg-amber-500/15 border-b border-amber-500/40 text-amber-400 text-xs px-3 py-1 flex items-center justify-between">
          <span>● Editing past ball — score will recompute on save</span>
          <button onClick={cancelEdit} className="underline hover:text-amber-300">Cancel</button>
        </div>
      )}

      {/* Locked by another scorer — read-only mode */}
      {scorerLock.status === "locked-by-other" && (
        <div className="absolute top-7 xl:top-9 left-0 right-0 z-40 bg-rose-500/15 border-b border-rose-500/40 text-rose-300 text-xs px-3 py-1 flex items-center justify-between gap-2">
          <span className="truncate">
            🔒 This match is currently being scored by{" "}
            <span className="font-semibold text-text-primary">{scorerLock.holder?.holder_username || "another scorer"}</span>.
            You're in read-only mode.
          </span>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={scorerLock.forceTakeover}
              className="underline hover:text-rose-100 text-[10px] uppercase tracking-wider"
              title="Org admins only — yanks the lock and gives it to you. Logged."
            >
              Force takeover
            </button>
            <button
              onClick={scorerLock.reacquire}
              className="underline hover:text-rose-100 text-[10px] uppercase tracking-wider"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Save error — every failed save now surfaces a banner instead of failing silently */}
      {saveError && (
        <div
          data-testid="bbb-save-error"
          data-message={saveError}
          className="absolute top-7 xl:top-9 left-0 right-0 z-40 bg-rose-500/15 border-b border-rose-500/40 text-rose-300 text-xs px-3 py-1 flex items-center justify-between gap-2"
        >
          <span className="truncate">⚠ Save failed: {saveError}</span>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => { setSaveError(""); startDelayedSave(); }}
              className="underline hover:text-rose-200"
              title="Try the same ball again"
            >
              Retry
            </button>
            <button onClick={() => setSaveError("")} className="text-rose-300 hover:text-rose-100">✕</button>
          </div>
        </div>
      )}

      {/* Milestone toasts (50 / 100 / fifer) — hidden on Test format
          per founder ask. The emoji-led popup looks like an AI prompt
          and isn't wanted on the slower Test scoring rhythm. */}
      {milestone && !isTestMatch && (
        <div className="absolute top-7 xl:top-9 left-1/2 -translate-x-1/2 z-50 mt-2 bg-button-primary text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg animate-bounce">
          {milestone.text}
        </div>
      )}

      {/* Last-ball confirmation flash */}
      {lastBallFlash && (
        <div className={`absolute top-7 xl:top-9 left-1/2 -translate-x-1/2 z-50 mt-1 px-4 py-1.5 rounded-full text-sm font-medium shadow-lg ${
          lastBallFlash.kind === "wicket"   ? "bg-rose-600/90 text-white" :
          lastBallFlash.kind === "boundary" ? "bg-emerald-600/90 text-white" :
                                              "bg-blue-600/85 text-white"
        }`}>
          ✓ {lastBallFlash.text}
        </div>
      )}

      {/* Strike rotation toast — appears in top-right for ~2s after odd runs.
          top-20 (80px) clears every top-9 strip (~28-32px tall starting
          at 36px) plus the BallLayout exit strip on the same axis. */}
      {strikeToast && (
        <div className="absolute top-20 right-4 z-50 bg-bg-primary border border-emerald-500/40 text-emerald-300 text-xs px-3 py-1.5 rounded-full shadow-lg">
          ↻ Striker now: <span className="text-text-primary font-medium">{strikeToast}</span>
        </div>
      )}
      {/* 2026-06-02 — every ✨-prefixed toast (voice feedback, break
          confirms, AI tag results, etc.) is hidden on Test format per
          founder ask ("no AI prompts / text popups during Test coding").
          The toast container is shared between AI and non-AI flows but
          the operator perceives them all as AI, so we silence the lot
          on Test. */}
      {aiToast && !isTestMatch && (
        <div
          className={`absolute top-20 left-4 z-50 max-w-md text-xs px-3 py-1.5 rounded-md shadow-lg border ${
            aiToast.ok
              ? "bg-violet-500/15 border-violet-500/40 text-violet-200"
              : "bg-red-500/15 border-red-500/40 text-red-300"
          }`}
        >
          {aiToast.ok ? "✨" : "⚠"} {aiToast.msg}
        </div>
      )}
      {/* Left column — stacks video / recorder bar / runs / extras /
          wicket / batters / bowlers / scorecard / QC link. Always scrolls
          when content overflows the viewport: previously the lg+ breakpoint
          forced `overflow-visible`, which meant on a 14-15" laptop the
          bottom of the stack (batters / bowlers / scorecard / QC link) was
          clipped below the viewport with no way to reach it. The parent
          BBB shell is `h-dvh overflow-hidden`, so this column inherits a
          bounded height via flex → `overflow-y-auto` produces a real
          scrollbar. pr-1 reserves space for the scrollbar so the rightmost
          edge of panels doesn't kiss the gutter. */}
      {/* Mobile BBP tab strip — only visible below xl. Lets the operator
          switch between Spatial / Activity / Score with one tap instead
          of scrolling past two entire columns. Sticky at top-9 so it
          stays visible while scrolling inside the active column. Hidden
          behind editing / locked banners (z-40 vs z-30) when those
          fire — acceptable since those banners are rare and convey
          critical state. Founder ask T220.1, 2026-05-27.
          T220.5: also hidden in iPad-landscape (1024–1279 landscape) —
          that mode shows spatial + activity side-by-side instead of
          tabbing. */}
      {/* 2026-05-28 — left-to-right order locked at Score | Spatial |
          Performance per founder mental model. The internal key for
          the rightmost tab stays "activity" so all existing auto-
          advance / handler / scope code keeps working — only the
          label and position changed. Background bumped to fully
          opaque (was bg-bg-main/95) so the sticky header's text
          can't bleed through behind the tab strip on phone. */}
      <div className={`${isIpadLandscape ? "hidden" : ""} xl:hidden sticky top-7 z-30 -mx-1 px-1 py-1.5 bg-bg-main border-b border-border-default flex gap-1 order-0`}>
        {[
          { k: "score",    l: "Score" },
          { k: "spatial",  l: "Spatial" },
          { k: "activity", l: "Performance" },
        ].map(({ k, l }) => (
          <button
            key={k}
            type="button"
            onClick={() => setMobileBbpTab(k)}
            className={`flex-1 py-2 px-2 rounded-md text-[11px] font-semibold uppercase tracking-wider transition-colors ${
              mobileBbpTab === k
                ? "bg-button-primary text-white shadow-sm"
                : "bg-bg-secondary text-text-secondary hover:text-text-primary"
            }`}
            aria-pressed={mobileBbpTab === k}
          >
            {l}
          </button>
        ))}
      </div>

      <div className={`order-1 w-full xl:w-4/12 min-w-0 min-h-0 ${isIpadLandscape ? "hidden" : (mobileBbpTab === "score" ? "flex" : "hidden xl:flex")} flex-col gap-1.5 xl:overflow-y-auto pr-1 xl:bbb-left-scroll`}>
        {/* 2026-05-29 — Recording + voice controls moved ABOVE the video
            pane per founder ask (PC interface). Row contains: Start
            Recording (MatchRecorderControls) + Voice (mic, Tutorial,
            Calibrate, Noise Gate toggle). md:+ only — phones don't show
            the video and use the bottom action bar for voice.
            2026-05-29 — AI Suggest joined the same row per founder
            follow-up ("move AI Suggest into the existing space"). */}
        {/* 2026-05-29 — Calibrate + AI Suggest pulled out of
            VoiceScoringPanel and rendered DIRECTLY after Start
            Recording per founder ask ("move Calibrate and AI Suggest
            next to Start Recording — no need separate space for it").
            VoiceScoringPanel now only carries PTT + Tutorial + Noise
            gate, which wrap to row 2 below if the column is narrow. */}
        <div className={`${panelClass} hidden md:flex flex-wrap items-center gap-x-3 gap-y-1.5`}>
          <span className="inline-flex items-center">
            <MatchRecorderControls
              recorder={recorder}
              flat
              hasStream={!!videoStream}
              fileLoaded={fileUpload.fileLoaded}
            />
          </span>
          <button
            type="button"
            onClick={replayCalibration}
            aria-label="Run 60-second voice calibration"
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] uppercase tracking-wider font-semibold bg-emerald-500/15 border border-emerald-400/40 text-emerald-200 hover:bg-emerald-500/25 transition-colors"
            title="60-second voice test — read 8 prompts, see your recognition accuracy. Run it once on every new device."
          >
            <Gauge className="w-3 h-3" />
            Calibrate
          </button>
          {/* AI Score hidden 2026-06-10 per founder ask — will be unhidden
              once model accuracy is re-validated. To restore: flip
              SHOW_AI_SCORE to true. Endpoint + handler kept intact. */}
          {SHOW_AI_SCORE && (() => {
            const currentBal = walletBalance?.balance_credits ?? null;
            // 2026-06-05 — renamed AI Suggest -> AI Score per founder ask.
            // Endpoint swapped from /ai-coding/suggest/ (chip-fill, 7 ZC)
            // to /ai-score/ (outcome inference: runs/extras/wicket, 2 ZC).
            // Open to all paid tiers from day one (no closed-beta gate).
            const canAffordScore = currentBal == null || currentBal >= 2;
            const balHint = currentBal == null ? "" : ` · balance ${currentBal} ZC`;
            return (
              <button
                onClick={canAffordScore ? handleAiScore : undefined}
                disabled={aiBusy || !canAffordScore}
                className={`text-[10px] uppercase tracking-wider px-1.5 py-1 rounded-md border inline-flex items-center gap-1 ${
                  canAffordScore
                    ? "border-violet-500/40 bg-violet-500/10 text-violet-300 hover:bg-violet-500/20 disabled:opacity-50"
                    : "border-violet-500/30 bg-violet-500/5 text-violet-400/60 cursor-not-allowed"
                }`}
                title={
                  !canAffordScore
                    ? `Not enough Zencredits (need 2, balance ${currentBal}). Top up from the wallet badge.`
                    : `Send 8 frames around current video time to Claude — pre-fills runs / extras / wicket from the video. Costs 2 Zencredits.${balHint}`
                }
              >
                <Sparkles className="w-3 h-3" />
                {aiBusy ? "…" : "AI Score · 2 ZC"}
              </button>
            );
          })()}
          <span className="h-4 w-px bg-border-default/60" />
          {/* VoiceScoringPanel without onCalibrate → it skips its own
              Calibrate render. Leaves just PTT + Tutorial. (Noise gate
              moved to the spatial tab strip; AI/EN/Help previously
              removed; status text removed.) */}
          <VoiceScoringPanel
            voice={voice}
            flat
            aiFallbackEnabled={voiceAiFallback}
            onAiFallbackChange={handleVoiceAiToggle}
            onReplayTour={replayVoiceTour}
            matchId={id}
            onUtterance={pushVoiceUtterance}
          />
          {/* 2026-05-29 — Upload for clipping moved from the dense bar
              below the video to sit RIGHT AFTER Tutorial per founder
              ask. Still conditional on (file loaded) + (not currently
              live recording) so it doesn't render when there's nothing
              to upload. All upload states (idle / preparing / uploading
              / complete / error) handled inline. */}
          {fileUpload.fileLoaded && !liveActive && (
            <>
              {fileUpload.state === "idle" && (
                <button
                  onClick={() => fileUpload.uploadFile()}
                  className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-md border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20"
                  title="Upload this file to S3 so per-ball clips can be sliced from your clip in/out marks"
                >● Upload for clipping</button>
              )}
              {(fileUpload.state === "preparing" || fileUpload.state === "uploading") && (
                <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-300 inline-flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                  Uploading {fileUpload.totalBytes ? Math.round((fileUpload.bytesUploaded / fileUpload.totalBytes) * 100) : 0}%
                  <button
                    onClick={() => {
                      const pct = fileUpload.totalBytes
                        ? Math.round((fileUpload.bytesUploaded / fileUpload.totalBytes) * 100)
                        : 0;
                      const mb = Math.round(fileUpload.bytesUploaded / 1024 / 1024);
                      if (window.confirm(
                        `Cancel the upload?\n\nYou're ${pct}% done (${mb} MB already on S3). Cancelling will abort the multipart upload — those chunks are gone and you'll need to start over.\n\nClick OK to cancel, Cancel to keep uploading.`
                      )) {
                        fileUpload.cancelUpload();
                      }
                    }}
                    className="text-amber-300 hover:text-amber-100 ml-1 px-1"
                    title="Cancel upload (asks for confirmation)"
                  >×</button>
                </span>
              )}
              {fileUpload.state === "complete" && (
                <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-md border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 inline-flex items-center gap-1">
                  ✓ Uploaded — slicer queued
                </span>
              )}
              {fileUpload.state === "error" && (
                <button
                  onClick={() => fileUpload.uploadFile()}
                  className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-md border border-rose-500/40 bg-rose-500/10 text-rose-300 hover:bg-rose-500/20"
                  title={fileUpload.error}
                >⟳ Retry upload</button>
              )}
            </>
          )}
        </div>
        {/* Video pane — hidden on phones only (below md=768). iPad and
            Android tablets keep video back per 2026-05-27 founder
            follow-up #2. Phones still can't realistically score and
            capture video at once. Recorder controls below match the
            same md cutoff. */}
        <div className={`${panelClass} hidden md:block`}>
          <VideoPlayer
            ref={videoPlayerRef}
            matchId={id}
            onStream={setVideoStream}
            onFileLoad={(file) => {
              fileUpload.registerFile(file);
              // Persist to IDB so (a) the QC tab can read the file back
              // and play per-ball clips locally before the S3 upload +
              // MediaConvert slicer finishes, AND (b) a page refresh /
              // navigate-away-and-back rehydrates the player without
              // making the operator re-pick the file every time. See
              // VideoPlayer's restore effect for the read side.
              if (id && file) saveLocalMatchFile(id, file).catch(() => {});
            }}
            onFileTimeUpdate={fileUpload.updateTime}
            onFileClear={() => {
              fileUpload.registerFile(null);
              if (id) clearLocalMatchFile(id).catch(() => {});
            }}
          />
        </div>

        {/* B2C AI-feature promo — inline so it never overlaps the middle /
            right pane headers. Sits between the video viewport and the
            action bar, directly above the AI Tag button it promotes. */}
        {showAiPromo && (
          <div className="bg-blue-500/15 border border-blue-500/40 rounded-md px-3 py-1.5 flex items-center justify-between gap-3 text-[11px] text-blue-100">
            <div className="flex items-center gap-2 min-w-0">
              <Sparkles className="w-3.5 h-3.5 shrink-0 text-blue-300" />
              <span className="truncate">
                <strong>Speed up scoring with AI.</strong> Hit{" "}
                <span className="font-mono">AI Tag</span> below to auto-fill
                missing fields on every ball — 15 Zencredits per match.
              </span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => { dismissAiPromo(); handleAiTagMatch(); }}
                className="px-2 py-1 rounded-md bg-blue-500 hover:bg-blue-400 text-white uppercase tracking-wider text-[10px]"
              >
                Try AI Tag
              </button>
              <button
                onClick={dismissAiPromo}
                className="text-blue-200 hover:text-white px-1.5"
                aria-label="Dismiss"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* ── Single dense bar: recorder + voice + online + Start/End video
              clip + SAVE BALL + MORE. Replaces what used to be 4 stacked
              bars (cluttered the entire left pane).                     */}
        {/* File-upload orphan resume banner — shown if a previous tab crashed
            mid-upload for this match. Persists across reloads via IndexedDB. */}
        {fileUpload.orphan && !liveActive && (
          // Hidden on phones only — paired with the video pane visibility.
          <div className="hidden md:flex bg-amber-500/15 border border-amber-500/40 rounded-md px-3 py-1.5 items-center justify-between gap-3 text-[11px] text-amber-200">
            <span className="truncate">
              ⏸ Upload paused: <span className="text-text-primary">{fileUpload.orphan.file_name}</span>
              {" · "}{fileUpload.orphan.completed_parts?.length || 0} chunks already on S3.
              Pick the same file again and click resume.
            </span>
            <button
              onClick={() => {
                const parts = fileUpload.orphan?.completed_parts?.length || 0;
                const name = fileUpload.orphan?.file_name || "this upload";
                if (window.confirm(
                  `Discard the in-progress upload of "${name}"?\n\n${parts} chunks are already on S3 and will be wasted. The MediaConvert slicer will not run for this match unless you upload again from scratch.\n\nClick OK to discard, Cancel to keep the resume option.`
                )) {
                  fileUpload.cancelUpload();
                }
              }}
              className="shrink-0 text-amber-300 hover:text-amber-100 px-1"
            >Discard</button>
          </div>
        )}

        <div className={`${panelClass} flex flex-wrap items-center gap-x-3 gap-y-1.5`}>
          {/* 2026-05-29 — MatchRecorderControls moved above the video
              pane. Kept on phone (< md) via the previous bottom row
              context? Actually phone never showed it (was wrapped in
              hidden md:inline-flex above the move). Now strictly in
              the new row above the video — no duplicate render. */}

          {/* 2026-05-29 — File-upload control moved to the top header row
              above the video (right after Tutorial) per founder ask. The
              dense bar below the video no longer hosts a duplicate. */}
          {/* 2026-05-29 — VoiceScoringPanel moved above the video pane
              (PC interface). Modals stay portal-mounted here because
              they don't have visual flow. */}
          <VoiceTutorialModal open={voiceTourOpen} onClose={() => setVoiceTourOpen(false)} />
          {/* 2026-06-11 — Per-match voice-intent prompt. Blocks coding
              on first visit; choice persists per match. Yes → opens
              calibration; No → proceeds straight to coding. */}
          <VoiceIntentPrompt
            open={voiceIntentOpen}
            onYes={handleVoiceIntentYes}
            onNo={handleVoiceIntentNo}
          />
          {/* 2026-05-28 — `mandatory` hides the Skip button on the
              first-time auto-open path. Replay (from the voice panel's
              Calibrate link) opens a non-mandatory version. */}
          <VoiceCalibrationGauntlet
            open={calibrationOpen}
            onClose={() => setCalibrationOpen(false)}
            matchId={id}
            mandatory={shouldShowCalibration()}
          />
          {/* 2026-05-29 — NET indicator (OfflineIndicator) removed from
              the dense bar below the video per founder ask. Offline-
              queue status still bubbles up via the existing toast trail
              when a flush happens; the persistent pill was just noise. */}
          {/* 2026-05-29 — AI Suggest moved into the recording+voice row
              above the video pane. AI Tag was removed earlier the
              same day; only AI Suggest remained here, now relocated. */}
          {/* Right action group — pinned via ml-auto + flex-nowrap so
              the volatile chips on the left (OfflineIndicator "+1
              queued", upload progress, AI Suggest cost flicker, etc.)
              can't reflow Clip in / Clip out / Save Ball around. The
              outer container still wraps, but this whole block moves
              as a single unit, so the operator's muscle memory stays
              honest. Reported 2026-05-22. */}
          {/* Collision warning — striker and non-striker MUST be different
              players. Render as a visible block so the operator sees the
              cause when Save goes grey, not just an inscrutable disabled
              button. Triggers most often after a STT mis-hear (Rajat ↔
              Virat) or a manual same-player pick. */}
          {strikerNonStrikerCollision && (
            <div className="w-full mb-2 px-3 py-2 rounded-md border border-rose-500/50 bg-rose-500/10 text-rose-200 text-[12px] flex items-start gap-2">
              <span className="text-base leading-none">⚠</span>
              <span>
                <strong>Striker and non-striker are the same player.</strong>
                {" "}Save is blocked until you pick a different player at one end.
                Click the striker or non-striker name above to change.
              </span>
            </div>
          )}
          {/* 2026-06-06 — entire desktop right-action group (Clip in/out
              + SAVE BALL + Quick + MORE) is hidden on phones. shrink-0 +
              flex-nowrap was forcing the SAVE BALL label to slide off
              the left edge under the SCORE tab pill ("VE BALL" + QUICK +
              MORE visible only). Mobile uses the bottom sticky bar for
              SAVE + Undo + MORE — no need to duplicate up here. */}
          <div className="ml-auto shrink-0 hidden md:flex items-center gap-1 flex-nowrap">
          {/* Clip in / Clip out are paired with the live video player.
              Video pane is md:+ (iPad, Android tablet, desktop) so the
              clip buttons match. Phones (< md) get neither. */}
          <button
            onClick={() =>
              setBall((prev) => ({
                ...prev,
                video: {
                  ...prev.video,
                  video_source_id: videoSource.recordingId || prev.video?.video_source_id || "",
                  video_start_ms: videoSource.videoTimestampMs,
                },
              }))
            }
            className={`hidden md:inline-flex text-[10px] uppercase tracking-wider px-2 py-1 rounded-md disabled:opacity-40 ${
              ball.video?.video_start_ms != null
                ? "bg-emerald-500/15 border border-emerald-500/40 text-emerald-300"
                : "bg-bg-secondary text-text-secondary hover:text-text-primary"
            }`}
            disabled={!videoSource.isLive || ball.video?.video_start_ms != null}
            title={
              !videoSource.isLive
                ? "Load a video file or start the live recorder first — clip-in needs a video source to capture a timestamp from."
                : ball.video?.video_start_ms != null
                  ? "Clip-in already marked. Save the ball to lock it; use ↺ to reset."
                  : "Mark video clip start for this ball"
            }
          >{ball.video?.video_start_ms != null ? `✓ In @ ${(ball.video.video_start_ms / 1000).toFixed(1)}s` : "▶ Clip in"}</button>
          <button
            onClick={() =>
              setBall((prev) => ({
                ...prev,
                video: { ...prev.video, video_end_ms: videoSource.videoTimestampMs },
              }))
            }
            className={`hidden md:inline-flex text-[10px] uppercase tracking-wider px-2 py-1 rounded-md disabled:opacity-40 ${
              ball.video?.video_end_ms != null
                ? "bg-emerald-500/15 border border-emerald-500/40 text-emerald-300"
                : "bg-bg-secondary text-text-secondary hover:text-text-primary"
            }`}
            disabled={!videoSource.isLive || ball.video?.video_start_ms == null}
            title={
              !videoSource.isLive
                ? "Load a video file or start the live recorder first."
                : ball.video?.video_start_ms == null
                  ? "Mark Clip in first — clip-out needs a start timestamp."
                  : "Mark video clip end for this ball"
            }
          >{ball.video?.video_end_ms != null ? `✓ Out @ ${(ball.video.video_end_ms / 1000).toFixed(1)}s` : "■ Clip out"}</button>
          {/* Persona-1 / Feature-1: smart guard-rails chip rail.
              Renders inline above the save button so the operator
              sees the warning at the exact moment they're about to
              commit. One-tap × dismisses for this match only.
              Non-blocking — save still works through the warning. */}
          {guardRails.length > 0 && (
            <div className="w-full flex flex-wrap items-center gap-1 mb-1">
              {guardRails.map((w) => (
                <span
                  key={w.key}
                  className={`text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border ${
                    w.level === "warn"
                      ? "bg-amber-500/10 border-amber-500/40 text-amber-200"
                      : "bg-sky-500/10 border-sky-500/40 text-sky-200"
                  }`}
                  title={w.hint}
                >
                  ⚠ {w.text}
                  <button
                    type="button"
                    onClick={() => {
                      dismissGuardRail(id, w.key);
                      setDismissTick((n) => n + 1);
                    }}
                    className="ml-0.5 hover:text-white"
                    aria-label="Dismiss"
                  >×</button>
                </span>
              ))}
            </div>
          )}
          <button
            data-testid="bbb-save"
            onClick={fastSaveMode ? performImmediateSave : startDelayedSave}
            onDoubleClick={performImmediateSave}
            disabled={saveDisabled || saveInFlight}
            className={`${btnPrimary} relative overflow-hidden text-sm px-4 py-1.5`}
            title={
              strikerNonStrikerCollision
                ? "Striker and non-striker are the same player. Change one before saving."
                : fastSaveMode
                ? "Quick mode ON — single click saves instantly."
                : "Single click = 1-sec save (with cancel). Double-click = instant. Toggle Quick mode (lightning) to make every save instant."
            }
          >
            {savePending
              ? (editingBallId ? "Updating in 1s — dbl-click for instant" : "Saving in 1s — dbl-click for instant")
              : saveInFlight
                ? (editingBallId ? "Updating..." : "Saving...")
                : (editingBallId ? "UPDATE BALL" : t("bbb.save_ball"))}
            {savePending && (
              // CSS-only progress fill — animates 0→full over
              // SAVE_DELAY_MS via @keyframes zcd-save-progress.
              // Replaces a setInterval that fired setSaveProgress at
              // 20Hz which thrashed the BBB tree and made buttons
              // drop click events. No JS state, no re-render.
              <span
                className="absolute left-0 top-0 h-full w-full bg-white/20"
                style={{
                  transformOrigin: "left center",
                  animation: `zcd-save-progress ${SAVE_DELAY_MS}ms linear forwards`,
                }}
              />
            )}
          </button>
          {/* Quick-save mode toggle — single bolt icon. Spinners and
              fast-bowling cycles benefit from skipping the 1-sec
              confirmation altogether; operator can flip it any time. */}
          <button
            onClick={() => setFastSaveMode((v) => !v)}
            className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-md border flex items-center gap-1 ${
              fastSaveMode
                ? "bg-amber-400/20 border-amber-400/60 text-amber-200"
                : "bg-bg-secondary border-border-default text-text-secondary hover:text-text-primary"
            }`}
            title={fastSaveMode
              ? "Quick save: ON — single click saves instantly. Click to disable."
              : "Quick save: OFF — single click waits 1 s. Click to enable instant save."}
          >
            <Zap className="w-3 h-3" />
            {fastSaveMode ? "Quick: ON" : "Quick"}
          </button>
          {savePending && (
            <button onClick={cancelDelayedSave} className={btnGhost}>✕</button>
          )}
          <button data-testid="bbb-more-options" onClick={() => setMoreOptionsOpen(true)} className={btnGhost}>MORE</button>
          </div>
        </div>

        {/* ── Score ribbon (recent overs + partnership + req rate + phase)
              Lives in the left pane so the operator sees live state without
              eye-jumping to the centre column.                            */}
        <div className={panelClass}>
          <ScoreRibbon
            recentBalls={recentBalls}
            strikerStat={strikerStat}
            nonStrikerStat={nonStrikerStat}
            aggregate={aggregate}
            matchType={aggregate?.match_type || null}
            currentPartnership={currentPartnership}
            overBase={LIVE_OVER_BASE}
          />
          {/* 2026-05-29 — Verbose previous-ball commentary line removed
              per founder ask. The colored-square Recent strip above
              still shows the last few balls at a glance; full text
              commentary lives in the dedicated commentary feed below. */}
        </div>

        {/* Recorder status — chunk countdown + parts saved + the "clips
            finalise on Stop" reminder. Visible only when there's a
            recorder hook to read from; hidden when no recording is in
            scope (e.g. scoring-only matches). */}
        {recorder && (
          <div className={`${panelClass} flex items-center gap-2 flex-wrap`}>
            <RecorderStatusPill recorder={recorder} />
            <CodingHealthBar recorder={recorder} stream={videoStream} />
          </div>
        )}

        <div className={`${panelClass} ${ball.is_free_hit ? "ring-2 ring-amber-400/70 ring-offset-1 ring-offset-bg-main animate-pulse-slow" : ""}`}>
          {/* Color-graded run buttons: Wk = red, 0 = grey, 1-3 = blue, 4 = green, 5 = teal, 6 = purple
              Wicket button leads the row (before the score buttons) — distinct
              red so it's never confused with a run, and moved out of the
              extras row so a wicket isn't a fiddly side-toggle. */}
          {/* T219: stack 3→5→9 cols across breakpoints so portrait
              phones don't horizontal-scroll the runs panel.
              Sprint 4 (2026-05-27): bump child buttons to 44px min-height
              on phone for WCAG-friendly touch targets; sm+ keeps the
              compact btnBase py-0.5 density. */}
          <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-10 gap-2 px-2 sm:px-4 [&_button]:min-h-[44px] sm:[&_button]:min-h-0 [&_button]:text-sm sm:[&_button]:text-xs">
            <button
              onClick={() => {
                // Pre-fill the most common defaults so the operator
                // saves clicks per wicket. Striker is the default
                // dismissed player (covers BOWLED / LBW / CAUGHT /
                // STUMPED / HIT_WICKET). Bowler is the default
                // "dismissed by" credit. Operator can override either
                // in the modal before saving.
                setBall((prev) => ({
                  ...prev,
                  dismissed_player_id: prev.dismissed_player_id || displayStrikerId,
                  dismissed_by_id:     prev.dismissed_by_id     || displayBowlerId,
                }));
                setWicketModalOpen(true);
              }}
              data-testid="bbb-wicket"
              className={`${btnBase} ${
                ball.has_wicket
                  ? "bg-red-600 text-white shadow"
                  : "bg-red-600/20 text-red-200 hover:bg-red-600/40"
              }`}
              title="Wicket"
            >
              Wk
            </button>
            <button
              data-testid="bbb-runs-0"
              onClick={() =>
                setBall((prev) => ({ ...prev, completed_runs: 0, is_boundary: false }))
              }
              className={`${btnBase} ${
                ball.completed_runs === 0
                  ? "bg-zinc-500 text-white shadow"
                  : "bg-zinc-700/40 text-zinc-200 hover:bg-zinc-700/60"
              }`}
            >0</button>
            <button
              data-testid="bbb-runs-1"
              onClick={() =>
                setBall((prev) => ({ ...prev, completed_runs: 1, is_boundary: false }))
              }
              className={`${btnBase} ${
                ball.completed_runs === 1
                  ? "bg-blue-600 text-white shadow"
                  : "bg-blue-600/20 text-blue-200 hover:bg-blue-600/40"
              }`}
            >1</button>
            <button
              data-testid="bbb-runs-2"
              onClick={() =>
                setBall((prev) => ({ ...prev, completed_runs: 2, is_boundary: false }))
              }
              className={`${btnBase} ${
                ball.completed_runs === 2
                  ? "bg-blue-600 text-white shadow"
                  : "bg-blue-600/20 text-blue-200 hover:bg-blue-600/40"
              }`}
            >2</button>
            <button
              data-testid="bbb-runs-3"
              onClick={() =>
                setBall((prev) => ({ ...prev, completed_runs: 3, is_boundary: false }))
              }
              className={`${btnBase} ${
                ball.completed_runs === 3
                  ? "bg-blue-600 text-white shadow"
                  : "bg-blue-600/20 text-blue-200 hover:bg-blue-600/40"
              }`}
            >3</button>
            <button
              data-testid="bbb-runs-4"
              onClick={() =>
                setBall((prev) => ({ ...prev, completed_runs: 4, is_boundary: true }))
              }
              className={`${btnBase} ${
                ball.completed_runs === 4
                  ? "bg-emerald-600 text-white shadow"
                  : "bg-emerald-600/20 text-emerald-200 hover:bg-emerald-600/40"
              }`}
            >4</button>
            <button
              data-testid="bbb-runs-5"
              onClick={() =>
                setBall((prev) => ({
                  ...prev,
                  completed_runs: 5,
                  // 5 runs = boundary + 1 overthrow (most common). Keep
                  // is_boundary=true; operator can toggle off via the
                  // Boundary chip if it was 4 byes + 1 overthrow (rare).
                  is_boundary: true,
                }))
              }
              title="5 runs = 4 (boundary) + 1 overthrow. Toggle Boundary chip off if it was actually 4 byes + 1."
              className={`${btnBase} ${
                ball.completed_runs === 5
                  ? "bg-teal-600 text-white shadow"
                  : "bg-teal-600/20 text-teal-200 hover:bg-teal-600/40"
              }`}
            >5</button>
            <button
              data-testid="bbb-runs-6"
              onClick={() =>
                setBall((prev) => ({ ...prev, completed_runs: 6, is_boundary: true }))
              }
              className={`${btnBase} ${
                ball.completed_runs === 6
                  ? "bg-purple-600 text-white shadow"
                  : "bg-purple-600/20 text-purple-200 hover:bg-purple-600/40"
              }`}
            >6</button>
            <button
              data-testid="bbb-boundary-toggle"
              onClick={() =>
                setBall((prev) => ({
                  ...prev,
                  is_boundary: !prev.is_boundary,
                }))
              }
              className={`${ball.is_boundary ? btnToggleActive : btnToggle}`}
            >
              <Zap className="w-4" />
            </button>
            {/* Manual-runs chip — for the 7+ run case (e.g. 3 ran +
                4 overthrow-boundary). Click to open a tiny popover with
                a number input. Operator may also need to toggle the
                Boundary chip separately if a boundary was involved. */}
            <div className="relative">
              <button
                data-testid="bbb-runs-manual"
                onClick={() => {
                  setManualRunsInput(
                    ball.completed_runs != null ? String(ball.completed_runs) : ""
                  );
                  setManualRunsOpen((v) => !v);
                }}
                title="Enter runs manually (for 7+ run cases like 3 ran + 4 overthrow boundary)"
                className={`${btnToggle} !px-2`}
              >
                <MoreVertical className="w-4" />
              </button>
              {manualRunsOpen && (
                <div
                  className="absolute z-30 right-0 mt-1 w-44 rounded-md border border-border-default bg-bg-primary shadow-lg p-2 text-xs"
                  onKeyDown={(e) => {
                    if (e.key === "Escape") setManualRunsOpen(false);
                  }}
                >
                  <div className="text-text-secondary mb-1">Manual runs (0–10)</div>
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      min={0}
                      max={10}
                      autoFocus
                      value={manualRunsInput}
                      onChange={(e) => setManualRunsInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          const n = Number(manualRunsInput);
                          if (Number.isFinite(n) && n >= 0 && n <= 10) {
                            setBall((prev) => ({ ...prev, completed_runs: n }));
                            setManualRunsOpen(false);
                          }
                        }
                      }}
                      className="w-16 bg-bg-secondary text-text-primary px-1 py-0.5 rounded border border-border-default"
                    />
                    <button
                      data-testid="bbb-runs-manual-set"
                      onClick={() => {
                        const n = Number(manualRunsInput);
                        if (Number.isFinite(n) && n >= 0 && n <= 10) {
                          setBall((prev) => ({ ...prev, completed_runs: n }));
                          setManualRunsOpen(false);
                        }
                      }}
                      className="px-2 py-0.5 rounded bg-blue-600 text-white hover:bg-blue-500"
                    >
                      Set
                    </button>
                    <button
                      onClick={() => setManualRunsOpen(false)}
                      className="px-2 py-0.5 rounded bg-bg-secondary text-text-secondary hover:text-text-primary"
                    >
                      Cancel
                    </button>
                  </div>
                  <div className="text-[10px] text-text-secondary mt-1">
                    Toggle the boundary chip too if a 4/6 boundary was involved.
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className={panelClass}>
          <div className="flex flex-wrap gap-x-2 mt-2 justify-center text-sm">
            <button
              data-testid="bbb-dead-ball"
              onClick={() => setBall((p) => ({ ...p, is_dead_ball: !p.is_dead_ball }))}
              className={ball.is_dead_ball ? btnToggleActive : btnToggle}
              title="Law 20 — Dead ball (no runs / no extras count)"
            >
              DB
            </button>
            <button
              data-testid="bbb-wide"
              onClick={() => setBall((prev) => {
                const turningOn = !prev.is_wide;
                return {
                  ...prev,
                  is_wide: turningOn,
                  // 2026-06-05 — wide and no-ball are mutually exclusive
                  // (a delivery is either wide or no-ball, not both). Per
                  // ICC, no-ball wins when both are flagged; the UI mirrors
                  // that by clearing the OTHER toggle when this one is
                  // turned on.
                  is_no_ball: turningOn ? false : prev.is_no_ball,
                  // 2026-07-10 — a clean wide banks 1 run (the penalty
                  // alone). If a run-count button was already tapped, the
                  // backend adds it on top → "wd2" for a batter who
                  // didn't run. Reset completed_runs so the timeline
                  // shows plain "wd"; operator adds extras by tapping a
                  // run count AFTER toggling Wd on.
                  completed_runs: turningOn ? 0 : prev.completed_runs,
                  is_boundary: turningOn ? false : prev.is_boundary,
                };
              })}
              className={ball.is_wide ? btnToggleActive : btnToggle}
            >
              Wd
            </button>
            <button
              data-testid="bbb-no-ball"
              onClick={() => setBall((prev) => {
                const turningOn = !prev.is_no_ball;
                return {
                  ...prev,
                  is_no_ball: turningOn,
                  // Mutex with wide — see Wd chip handler above.
                  is_wide: turningOn ? false : prev.is_wide,
                  // Clear no_ball_type when toggling NB off so the dropdown
                  // doesn't keep a stale value from a previous NB.
                  no_ball_type: turningOn ? prev.no_ball_type : "",
                  // 2026-07-10 — same clean-toggle reset as Wd: a plain
                  // NB with no batter run banks 1 (penalty), not "nb+1".
                  completed_runs: turningOn ? 0 : prev.completed_runs,
                  is_boundary: turningOn ? false : prev.is_boundary,
                };
              })}
              className={ball.is_no_ball ? btnToggleActive : btnToggle}
            >
              NB
            </button>

            <button
              onClick={() =>
                setBall({
                  ...ball,
                  is_bye: !ball.is_bye,
                  is_leg_bye: false,
                })
              }
              className={ball.is_bye ? btnToggleActive : btnToggle}
            >
              B
            </button>
            <button
              onClick={() =>
                setBall({
                  ...ball,
                  is_bye: false,
                  is_leg_bye: !ball.is_leg_bye,
                })
              }
              className={ball.is_leg_bye ? btnToggleActive : btnToggle}
            >
              LB
            </button>
            <button
              onClick={() =>
                setBall((prev) => ({
                  ...prev,
                  is_short_run: !prev.is_short_run,
                }))
              }
              className={ball.is_short_run ? btnToggleActive : btnToggle}
            >
              SR
            </button>
            <button
              onClick={() =>
                setBall((prev) => ({
                  ...prev,
                  is_quick_running: !prev.is_quick_running,
                }))
              }
              className={ball.is_quick_running ? btnToggleActive : btnToggle}
            >
              QR
            </button>
            <button
              onClick={() =>
                setBall((prev) => ({
                  ...prev,
                  is_free_hit: !prev.is_free_hit,
                }))
              }
              className={ball.is_free_hit ? btnToggleActive : btnToggle}
            >
              FHit
            </button>

            <button
              onClick={() =>
                setBall((prev) => ({
                  ...prev,
                  drs: {
                    ...prev.drs,
                    is_appealed: !prev.drs?.is_appealed,
                  },
                }))
              }
              className={ball.drs?.is_appealed ? btnToggleActive : btnToggle}
            >
              DRS
            </button>
            {ball.has_wicket && (
              <div className="text-text-secondary mt-1">
                Wicket Summary: {wicketSummary}
              </div>
            )}
            {ball.drs?.is_appealed && (
              <div className="text-text-secondary mt-1">
                DRS: {ball.drs?.on_field_decision || "Select decision"}
              </div>
            )}
          </div>
        </div>
        {ball.drs?.is_appealed && (
          <div className={panelClass}>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-text-secondary">Review By</label>
                <select
                  value={ball.drs?.review_team_side || ""}
                  onChange={(e) => {
                    const side = e.target.value;
                    const teamId =
                      side === "BAT"
                        ? battingTeamId
                        : side === "BOWL"
                          ? bowlingTeamId
                          : null;
                    setBall((prev) => ({
                      ...prev,
                      drs: {
                        ...prev.drs,
                        review_team_side: side,
                        review_team_id: teamId,
                      },
                    }));
                  }}
                  className="bg-bg-secondary text-text-primary px-2 py-1 rounded w-full"
                >
                  <option value="">Select</option>
                  <option value="BAT">Batting</option>
                  <option value="BOWL">Bowling</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-text-secondary">
                  On-field Decision
                </label>
                <select
                  value={ball.drs?.on_field_decision || ""}
                  onChange={(e) =>
                    setBall((prev) => ({
                      ...prev,
                      drs: { ...prev.drs, on_field_decision: e.target.value },
                    }))
                  }
                  className="bg-bg-secondary text-text-primary px-2 py-1 rounded w-full"
                >
                  <option value="">Select</option>
                  <option value="WICKET">Wicket</option>
                  <option value="NOT_OUT">Not Out</option>
                  <option value="WIDE">Wide</option>
                  <option value="NO_BALL">No Ball</option>
                  <option value="LEGAL_BALL_NO_BALL">Legal Ball — No Ball</option>
                  <option value="LEGAL_BALL_WIDE">Legal Ball — Wide</option>
                </select>
              </div>
              {/* Dismissal type — surfaces only when the on-field
                  decision is WICKET (or the appeal is upheld). Lets
                  the operator pick CAUGHT / LBW / STUMPED / RUN_OUT
                  etc. so drs_outcome.apply_drs_to_wicket creates the
                  right BallWicket type instead of always falling back
                  to LBW. */}
              <div>
                <label className="text-xs text-text-secondary">
                  DRS dismissal type
                </label>
                <select
                  value={ball.drs?.dismissal_type ?? ""}
                  onChange={(e) =>
                    setBall((prev) => ({
                      ...prev,
                      drs: { ...prev.drs, dismissal_type: e.target.value },
                    }))
                  }
                  className="bg-bg-secondary text-text-primary px-2 py-1 rounded w-full"
                  title="Used when DRS upholds a wicket appeal. Defaults to LBW if blank."
                >
                  <option value="">— (defaults to LBW)</option>
                  <option value="LBW">LBW</option>
                  <option value="CAUGHT">Caught</option>
                  <option value="BOWLED">Bowled</option>
                  <option value="STUMPED">Stumped</option>
                  <option value="RUN_OUT">Run out</option>
                  <option value="HIT_WICKET">Hit wicket</option>
                </select>
              </div>
              {/* Caught-behind: Snicko / UltraEdge "spike?" — surfaces
                  only when the operator has picked CAUGHT as the DRS
                  dismissal type. Persisted to BallDRS.spike so post-
                  match analysis can filter caught-behind reviews by
                  whether the audio trace actually spiked. Founder ask
                  2026-07-08 (renamed from "edged" the same day). */}
              {ball.drs?.dismissal_type === "CAUGHT" && (
                <div>
                  <label className="text-xs text-text-secondary">
                    Spike?
                  </label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() =>
                        setBall((prev) => ({
                          ...prev,
                          drs: { ...prev.drs, spike: true },
                        }))
                      }
                      className={ball.drs?.spike ? btnToggleActive : btnToggle}
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setBall((prev) => ({
                          ...prev,
                          drs: { ...prev.drs, spike: false },
                        }))
                      }
                      className={!ball.drs?.spike ? btnToggleActive : btnToggle}
                    >
                      No
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3 mt-2">
              <div>
                <label className="text-xs text-text-secondary">
                  Decision By
                </label>
                <select
                  value={ball.drs?.decision_given_by_umpire ?? ""}
                  onChange={(e) =>
                    setBall((prev) => ({
                      ...prev,
                      drs: {
                        ...prev.drs,
                        decision_given_by_umpire: e.target.value || null,
                      },
                    }))
                  }
                  className="bg-bg-secondary text-text-primary px-2 py-1 rounded w-full"
                >
                  <option value="">None</option>
                  {(umpires || []).map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name || u.short_name || u.id}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-text-secondary">
                  Third Umpire (TV)
                </label>
                <select
                  value={ball.drs?.third_umpire ?? ""}
                  onChange={(e) =>
                    setBall((prev) => ({
                      ...prev,
                      drs: {
                        ...prev.drs,
                        third_umpire: e.target.value || null,
                      },
                    }))
                  }
                  className="bg-bg-secondary text-text-primary px-2 py-1 rounded w-full"
                >
                  <option value="">{thirdUmpireOptions.length ? "None" : "— not set in match —"}</option>
                  {thirdUmpireOptions.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name || u.short_name || u.id}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-text-secondary">
                  Reserve Umpire
                </label>
                <select
                  value={ball.drs?.fourth_umpire ?? ""}
                  onChange={(e) =>
                    setBall((prev) => ({
                      ...prev,
                      drs: {
                        ...prev.drs,
                        fourth_umpire: e.target.value || null,
                      },
                    }))
                  }
                  className="bg-bg-secondary text-text-primary px-2 py-1 rounded w-full"
                >
                  <option value="">{fourthUmpireOptions.length ? "None" : "— not set in match —"}</option>
                  {fourthUmpireOptions.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name || u.short_name || u.id}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="mt-2 flex items-center gap-3">
              <label className="text-xs text-text-secondary">Overruled</label>
              <button
                onClick={() =>
                  setBall((prev) => {
                    const nextOverruled = !prev.drs?.overruled;
                    const next = {
                      ...prev,
                      drs: { ...prev.drs, overruled: nextOverruled },
                    };
                    // The "Legal Ball — No Ball" and "Legal Ball — Wide"
                    // DRS verdicts only flip the corresponding extras
                    // toggle when the on-field decision was actually
                    // overruled. (On-field call was legal; TV umpire
                    // upheld a no-ball / wide. If NOT overruled, the
                    // legal call stands and no extras are awarded.)
                    if (nextOverruled) {
                      const onField = prev.drs?.on_field_decision;
                      if (onField === "LEGAL_BALL_NO_BALL") next.is_no_ball = true;
                      else if (onField === "LEGAL_BALL_WIDE") next.is_wide = true;
                    }
                    return next;
                  })
                }
                className={ball.drs?.overruled ? btnToggleActive : btnToggle}
              >
                {ball.drs?.overruled ? "Yes" : "No"}
              </button>
              <span className="text-xs text-text-secondary">
                Final: {ball.drs?.final_decision || "—"}
              </span>
            </div>

            {/* Modern DRS extras — impact zone, wicket impact, soft signal */}
            <div className="grid grid-cols-3 gap-2 mt-2 pt-2 border-t border-border-default">
              <div className="flex flex-col gap-1">
                <label className="text-[10px] uppercase tracking-wider text-text-secondary">
                  Impact zone
                </label>
                <select
                  value={ball.drs?.impact_zone || ""}
                  onChange={(e) =>
                    setBall((prev) => ({
                      ...prev,
                      drs: { ...prev.drs, impact_zone: e.target.value },
                    }))
                  }
                  className="bg-bg-secondary text-text-primary text-xs px-2 py-1 rounded"
                >
                  <option value="">—</option>
                  <option value="OUT_OFF">Outside off</option>
                  <option value="OFF">Off</option>
                  <option value="MID">Middle</option>
                  <option value="LEG">Leg</option>
                  <option value="OUT_LEG">Outside leg</option>
                </select>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-[10px] uppercase tracking-wider text-text-secondary">
                  Wicket impact
                </label>
                <select
                  value={ball.drs?.wicket_impact || ""}
                  onChange={(e) =>
                    setBall((prev) => ({
                      ...prev,
                      drs: { ...prev.drs, wicket_impact: e.target.value },
                    }))
                  }
                  className="bg-bg-secondary text-text-primary text-xs px-2 py-1 rounded"
                >
                  <option value="">—</option>
                  <option value="HITTING">Hitting</option>
                  <option value="MISSING">Missing</option>
                  <option value="UMPIRES_CALL">Umpire&apos;s call</option>
                </select>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-[10px] uppercase tracking-wider text-text-secondary">
                  Soft signal
                </label>
                <select
                  value={ball.drs?.soft_signal || ""}
                  onChange={(e) =>
                    setBall((prev) => ({
                      ...prev,
                      drs: { ...prev.drs, soft_signal: e.target.value },
                    }))
                  }
                  className="bg-bg-secondary text-text-primary text-xs px-2 py-1 rounded"
                >
                  <option value="">—</option>
                  <option value="OUT">Out</option>
                  <option value="NOT_OUT">Not out</option>
                  <option value="UNDECIDED">Undecided</option>
                </select>
              </div>
            </div>
          </div>
        )}
        {ball.is_dead_ball && (
          <div className={panelClass}>
            <label className="mr-2 text-text-secondary">Dead Ball Reason</label>
            <select
              value={ball.dead_ball_reason}
              onChange={(e) => setBall((p) => ({ ...p, dead_ball_reason: e.target.value }))}
              className="bg-bg-secondary text-text-primary px-2 py-1 rounded"
            >
              <option value="" className="bg-black text-white">Select reason</option>
              {[
                "BALL_LODGED", "BATTER_NOT_READY", "DISTRACTION_BY_FIELDER",
                "ANIMAL_ON_FIELD", "INJURY", "BALL_HIT_HELMET_ON_FIELD",
                "BALL_HIT_FIELD_OBJECT", "OTHER",
              ].map((t) => (
                <option key={t} value={t} className="bg-black text-white">
                  {t.replaceAll("_", " ")}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className={`${panelClass} flex items-center gap-2 text-[11px]`}>
          <span className="text-text-secondary uppercase tracking-wider text-[10px]">Appealed for</span>
          {["LBW", "CAUGHT", "RUN_OUT", "STUMPED", "OTHER"].map((v) => (
            <button
              key={v}
              onClick={() => setBall((p) => ({
                // Toggle: clicking the active chip clears the appeal.
                // No more redundant "—" button — clearing is via re-click.
                ...p, appealed_for: p.appealed_for === v ? "" : v,
              }))}
              title={
                ball.appealed_for === v
                  ? `${v} appeal recorded — click to clear`
                  : `Record ${v} appeal`
              }
              className={`px-2 py-0.5 rounded ${ball.appealed_for === v ? "bg-green-700 text-white" : "bg-bg-secondary text-text-secondary hover:bg-bg-secondary/80"}`}
            >
              {v}
            </button>
          ))}
          {ball.appealed_for && (
            <button
              onClick={() => setBall((p) => ({ ...p, appealed_for: "" }))}
              className="text-[10px] uppercase tracking-wider text-text-secondary/70 hover:text-text-primary px-2"
              aria-label="Clear appeal"
            >
              Clear
            </button>
          )}
        </div>

        {/* 2026-07-07 — Opta/vendor gap-review batch. Live-BBP chip lets
            the scorer capture fielding_position_landed without opening
            EditBallDialog afterwards. Picking "" (dropdown) clears the
            value. Batter guard was moved next to the ball-release row
            (2026-07-10) so it sits alongside the other release-context
            controls. */}
        <div className={`${panelClass} flex flex-wrap items-center gap-2 text-[11px]`}>
          <span className="text-text-secondary uppercase tracking-wider text-[10px]">Ball ended up at</span>
          <select
            value={ball.fielding_position_landed || ""}
            onChange={(e) => setBall((p) => ({
              ...p, fielding_position_landed: e.target.value,
            }))}
            className="bg-bg-secondary text-text-primary px-2 py-1 rounded"
          >
            <option value="" className="bg-black text-white">— Not recorded —</option>
            {[
              ["SLIP_1", "1st Slip"], ["SLIP_2", "2nd Slip"], ["SLIP_3", "3rd Slip"], ["SLIP_4", "4th Slip"],
              ["GULLY", "Gully"], ["LEG_SLIP", "Leg Slip"], ["SHORT_LEG", "Short Leg"],
              ["SILLY_POINT", "Silly Point"], ["SILLY_MID_ON", "Silly Mid-on"], ["SILLY_MID_OFF", "Silly Mid-off"],
              ["LEG_GULLY", "Leg Gully"],
              ["POINT", "Point"], ["COVER", "Cover"], ["EXTRA_COVER", "Extra Cover"],
              ["MID_OFF", "Mid-off"], ["MID_ON", "Mid-on"], ["MID_WICKET", "Mid-wicket"],
              ["SQUARE_LEG", "Square Leg"], ["FINE_LEG", "Fine Leg"], ["THIRD_MAN", "Third Man"],
              ["DEEP_POINT", "Deep Point"], ["DEEP_COVER", "Deep Cover"], ["DEEP_EXTRA_COVER", "Deep Extra Cover"],
              ["LONG_OFF", "Long-off"], ["LONG_ON", "Long-on"], ["DEEP_MID_WICKET", "Deep Mid-wicket"],
              ["DEEP_SQUARE_LEG", "Deep Square Leg"], ["DEEP_FINE_LEG", "Deep Fine Leg"], ["DEEP_THIRD_MAN", "Deep Third Man"],
              ["WICKET_KEEPER", "Wicket-keeper"], ["BOWLER", "Bowler"], ["OTHER", "Other"],
            ].map(([v, l]) => (
              <option key={v} value={v} className="bg-black text-white">{l}</option>
            ))}
          </select>
        </div>

        {ball.is_no_ball && (
          <div className={panelClass}>
            <label className="mr-2 text-text-secondary">No Ball Type</label>
            <select
              value={ball.no_ball_type}
              onChange={(e) =>
                setBall((prev) => ({ ...prev, no_ball_type: e.target.value }))
              }
              className="bg-bg-secondary text-text-primary px-2 py-1 rounded"
            >
              <option value="" className="bg-black text-white">
                Select type
              </option>
              {/* Cricket Law 21 categorisation — operator-curated list
                  (2026-05-16) covers every common cause of a no-ball
                  flagged by an umpire / TV official. */}
              {[
                { v: "front_foot",                    l: "Front Foot" },
                { v: "back_foot",                     l: "Back Foot" },
                { v: "bouncer",                       l: "Bouncer" },
                { v: "beamer",                        l: "Beamer" },
                { v: "breaking_wicket",               l: "Breaking Wicket" },
                { v: "fielders_behind_square",        l: "Fielders behind square" },
                { v: "fielding_restriction_violation", l: "Fielding Restriction Violation" },
                { v: "double_pitching_cut_strip_missing", l: "Double Pitching / Cut Strip Missing" },
                { v: "wicket_keeper_error",           l: "Wicket Keeper Error" },
                { v: "other",                         l: "Other" },
              ].map((t) => (
                <option key={t.v} value={t.v} className="bg-black text-white">
                  {t.l}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className={`${panelClass} text-xs`}>
          <section className="flex flex-col md:flex-row gap-2 text-xs">
            <div className="w-full md:w-3/4 min-w-0">
              <div className="relative overflow-x-auto bg-neutral-primary-soft shadow-xs rounded-xl">
                <table className="w-full text-xs text-left rtl:text-right text-body rounded-xl">
                  <thead className="text-xs text-body  rounded-xl bg-special-table-header">
                    <th
                      scope="col"
                      className="flex gap-4 justify-start items-center px-6 py-1 font-medium rounded-xl"
                    >
                      Batter{" "}
                      <button
                        onClick={() => {
                          if (!displayStrikerId || !displayNonStrikerId) return;
                          setBall((prev) => ({
                            ...prev,
                            striker: displayNonStrikerId,
                            non_striker: displayStrikerId,
                          }));
                          setSwapAnimKey((k) => k + 1);
                        }}
                        className={`${btnSecondary} `}
                      >
                        <RefreshCcw className="w-4" />
                      </button>
                    </th>
                    <th scope="col" className="px-1 py-1 font-medium">
                      Runs
                    </th>
                    <th scope="col" className="px-1 py-1 font-medium">
                      Balls
                    </th>
                    <th scope="col" className="px-1 py-1 font-medium">
                      4s
                    </th>
                    <th scope="col" className="px-1 py-1 font-medium">
                      6s
                    </th>
                    <th scope="col" className="px-1 py-1 font-medium">
                      SR
                    </th>
                  </thead>
                  <tbody>
                    <tr
                      className={`bg-neutral-primary-soft border-b border-gray-800 hover:bg-neutral-secondary-medium ${
                        swapAnimKey ? "swap-anim" : ""
                      }`}
                      key={`striker-${swapAnimKey}`}
                    >
                      <th
                        scope="row"
                        className="px-1 py-4 font-medium text-heading whitespace-nowrap"
                      >
                        {" "}
                        <button
                          onClick={() => openSelectionModal("striker")}
                          className="underline flex gap-2 items-center"
                        >
                          {displayStrikerId
                            ? getBatterName(displayStrikerId)
                            : "Not Set"}{" "}
                          {displayStrikerId && (
                            <span
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleHand(
                                  displayStrikerId,
                                  getBatterHand(displayStrikerId),
                                );
                              }}
                              className="text-[10px] px-2 py-0.5 rounded-full bg-bg-secondary text-text-primary"
                            >
                              {getEffectiveHand(
                                displayStrikerId,
                                getBatterHand(displayStrikerId),
                              ) === "LEFT"
                                ? "LH"
                                : "RH"}
                            </span>
                          )}
                          <p className="w-2 h-2 bg-green-500 rounded-full"></p>
                        </button>
                      </th>
                      <td className="px-1 py-4">{strikerStat.runs}</td>
                      <td className="px-1 py-4">{strikerStat.balls}</td>
                      <td className="px-1 py-4">{strikerStat.fours}</td>
                      <td className="px-1 py-4">{strikerStat.sixes}</td>
                      <td className="px-1 py-4">{batterStrikeRate(strikerStat)}</td>
                    </tr>
                    <tr
                      className={`bg-neutral-primary-soft border-b border-gray-800 hover:bg-neutral-secondary-medium ${
                        swapAnimKey ? "swap-anim" : ""
                      }`}
                      key={`nonstriker-${swapAnimKey}`}
                    >
                      <th
                        scope="row"
                        className="px-1 py-4 font-medium text-heading whitespace-nowrap"
                      >
                        {" "}
                        <button
                          onClick={() => openSelectionModal("non_striker")}
                          className="underline flex gap-2 items-center"
                        >
                          {displayNonStrikerId
                            ? getBatterName(displayNonStrikerId)
                            : "Not Set"}{" "}
                          {displayNonStrikerId && (
                            <span
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleHand(
                                  displayNonStrikerId,
                                  getBatterHand(displayNonStrikerId),
                                );
                              }}
                              className="text-[10px] px-2 py-0.5 rounded-full bg-bg-secondary text-text-primary"
                            >
                              {getEffectiveHand(
                                displayNonStrikerId,
                                getBatterHand(displayNonStrikerId),
                              ) === "LEFT"
                                ? "LH"
                                : "RH"}
                            </span>
                          )}
                        </button>{" "}
                      </th>
                      <td className="px-1 py-4">{nonStrikerStat.runs}</td>
                      <td className="px-1 py-4">{nonStrikerStat.balls}</td>
                      <td className="px-1 py-4">{nonStrikerStat.fours}</td>
                      <td className="px-1 py-4">{nonStrikerStat.sixes}</td>
                      <td className="px-1 py-4">{batterStrikeRate(nonStrikerStat)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="relative overflow-x-auto bg-neutral-primary-soft shadow-xs rounded-xl">
                <table className="w-full text-xs text-left rtl:text-right text-body rounded-xl">
                  <thead className="text-xs text-body  rounded-xl bg-special-table-header">
                    <th
                      scope="col"
                      className="flex gap-4 justify-start items-center px-6 py-1 font-medium rounded-xl"
                    >
                      Bowler{" "}
                    </th>
                    <th scope="col" className="px-1 py-1 font-medium">
                      Balls
                    </th>
                    <th scope="col" className="px-1 py-1 font-medium">
                      Runs
                    </th>
                    <th scope="col" className="px-1 py-1 font-medium">
                      W
                    </th>
                    <th scope="col" className="px-1 py-1 font-medium">
                      WD
                    </th>
                    <th scope="col" className="px-1 py-1 font-medium">
                      NB
                    </th>
                  </thead>
                  <tbody>
                    <tr className="bg-neutral-primary-soft border-b border-gray-800 hover:bg-neutral-secondary-medium">
                      <th
                        scope="row"
                        className="px-1 py-4 font-medium text-heading whitespace-nowrap"
                      >
                        {" "}
                        <button
                          onClick={() => openSelectionModal("bowler")}
                          className="underline flex gap-2 items-center"
                        >
                          {displayBowlerId
                            ? getBowlerName(displayBowlerId)
                            : "Not Set"}{" "}
                          {/* 2026-06-05 — bowling-hand pill always visible
                              next to the bowler name. For ambidextrous
                              bowlers (eg Kamindu Mendis) the chevron
                              prefix surfaces — the underlying Player.
                              bowling_hands array carries multiple hands.
                              For every bowler the L|R toggle lets the
                              coder override the hand for THIS delivery.
                              payload.bowler_hand picks up the override
                              via getEffectiveHand at save time, so
                              persist + ball_edit both receive the
                              chosen hand and store it on Ball.bowler_hand. */}
                          {displayBowlerId && (
                            <span
                              onClick={(e) => e.stopPropagation()}
                              className={`inline-flex items-center gap-0.5 text-[10px] rounded-full bg-bg-secondary p-0.5 ${
                                isAmbidextrousBowler(displayBowlerId)
                                  ? "border border-amber-500/40"
                                  : ""
                              }`}
                              title={
                                isAmbidextrousBowler(displayBowlerId)
                                  ? `Ambidextrous bowler — pick the hand used for this delivery${getBowlerStyle(displayBowlerId) ? " (default: " + getBowlerStyle(displayBowlerId) + ")" : ""}`
                                  : `Bowling hand for this delivery${getBowlerStyle(displayBowlerId) ? " (default: " + getBowlerStyle(displayBowlerId) + ")" : ""}`
                              }
                            >
                              {isAmbidextrousBowler(displayBowlerId) && (
                                <span className="text-[9px] px-0.5 text-amber-300" title="Ambidextrous">⇄</span>
                              )}
                              {["LEFT", "RIGHT"].map((h) => {
                                const active =
                                  getEffectiveHand(
                                    displayBowlerId,
                                    getBowlerHand(displayBowlerId),
                                  ) === h;
                                return (
                                  <button
                                    key={h}
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setBowlerHand(displayBowlerId, h);
                                    }}
                                    className={`px-1.5 py-0.5 rounded-full text-[10px] leading-none ${
                                      active
                                        ? "bg-accent-primary text-white"
                                        : "text-text-primary opacity-60 hover:opacity-100"
                                    }`}
                                  >
                                    {h === "LEFT" ? "L" : "R"}
                                  </button>
                                );
                              })}
                            </span>
                          )}
                          {/* 2026-06-05 — per-delivery bowling-style picker.
                              Native select for reliability (10 codes
                              across Pace + Spin groupings; not a pill
                              roster). Shows current effective style on
                              the closed state. Empty string = use the
                              player's profile default. */}
                          {displayBowlerId && (
                            <select
                              onClick={(e) => e.stopPropagation()}
                              value={getEffectiveBowlerStyle(displayBowlerId, getBowlerStyle(displayBowlerId)) || ""}
                              onChange={(e) => {
                                e.stopPropagation();
                                setBowlerStyleOverride(displayBowlerId, e.target.value);
                              }}
                              title={`Bowling style for this delivery${getBowlerStyle(displayBowlerId) ? " (player default: " + getBowlerStyle(displayBowlerId) + ")" : ""}`}
                              className="text-[10px] px-1.5 py-0.5 rounded-md bg-bg-secondary text-text-primary border border-border-default leading-none"
                            >
                              <option value="">— default —</option>
                              {BOWLING_STYLE_GROUPS.map((group) => (
                                <optgroup key={group.label} label={group.label}>
                                  {group.codes.map((code) => (
                                    <option key={code} value={code}>{code}</option>
                                  ))}
                                </optgroup>
                              ))}
                            </select>
                          )}
                        </button>
                      </th>
                      {/* 2026-05-30 — bowler balls now shown as overs
                          (X.Y) per cricket convention + founder ask.
                          Standard bowler stat is "overs bowled" not
                          "balls bowled". */}
                      <td className="px-1 py-4 font-mono">
                        {Math.floor((bowlerStat.balls || 0) / 6)}.{(bowlerStat.balls || 0) % 6} ov
                      </td>
                      <td className="px-1 py-4">
                        {bowlerStat.runs_conceded} r
                      </td>
                      <td className="px-1 py-4">{bowlerStat.wickets} w</td>
                      <td className="px-1 py-4">{bowlerStat.wides}</td>
                      <td className="px-1 py-4">{bowlerStat.no_balls}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div className="w-full md:w-1/4 px-2 flex flex-col gap-2">
              {/* Score panel removed (2026-05-19): redundant with the
                  sticky header at the top which already shows
                  runs/wickets, overs, and need-from-balls. The right
                  pane now jumps straight to the action buttons so the
                  wagon wheel sits higher in the layout. */}
              {/* Compact action row — buttons sit side-by-side instead of
                  stacked. Smaller padding + icon-first layout to claw back
                  vertical space in the right pane. */}
              {/* 2026-05-29 — text was overflowing the buttons because
                  uppercase + tracking-wider + small icon left no room
                  to shrink. Added min-w-0 + truncate + tighter
                  letter-spacing so the SCORECARD / UNDO BALL labels
                  stay inside their grid cells at the narrow right-pane
                  width. */}
              <div className="grid grid-cols-2 gap-1">
                <button
                  onClick={() => setScorecardOpen(true)}
                  className="rounded-md text-[10px] font-semibold uppercase tracking-tight px-2 py-1 bg-bg-secondary text-text-primary hover:bg-bg-main flex gap-1 items-center justify-center min-w-0 overflow-hidden"
                  title={t("bbb.scorecard")}
                >
                  <ClipboardList className="w-3 h-3 shrink-0" />
                  <span className="truncate">{t("bbb.scorecard")}</span>
                </button>
                <button
                  data-testid="bbb-undo"
                  onClick={handleUndoBallRequest}
                  disabled={(aggregate?.balls_recorded ?? 0) === 0}
                  className={`rounded-md text-[10px] font-semibold uppercase tracking-tight px-2 py-1 flex gap-1 items-center justify-center min-w-0 overflow-hidden transition-opacity ${
                    (aggregate?.balls_recorded ?? 0) > 0
                      ? "bg-amber-500/15 border border-amber-500/40 text-amber-300 hover:bg-amber-500/25"
                      : "bg-bg-secondary border border-bg-secondary text-text-secondary opacity-40"
                  }`}
                  title="Undo the last saved ball — server-side. Click REDO to bring it back if you undid by mistake."
                >
                  <RotateCcw className="w-3 h-3 shrink-0" />
                  <span className="truncate">{t("bbb.undo_ball")}</span>
                  {undoSecondsLeft > 0 && (
                    <span className="font-mono text-[9px] text-amber-400/70" title="Just-saved indicator">
                      {undoSecondsLeft}s
                    </span>
                  )}
                </button>
                {/* T141: bulk-undo picker. Compact native select so we
                    don't need a custom dropdown component. Picks the
                    common N values (3, 5, 10) — operator selects, the
                    visual diff preview shows the summary, then Confirm
                    fires the single-rebuild endpoint. Hidden on the
                    smallest viewports to keep the action bar tight. */}
                <select
                  onChange={(e) => {
                    const n = parseInt(e.target.value, 10);
                    e.target.value = "";  // reset so the same N can be re-picked
                    if (n > 0) {
                      const diff = { over: `last ${n}`, summary: `Remove last ${n} balls`, striker: "(batch)" };
                      setPendingUndo({ ...diff, _batchN: n });
                      if (undoPreviewTimerRef.current) clearTimeout(undoPreviewTimerRef.current);
                      undoPreviewTimerRef.current = setTimeout(() => setPendingUndo(null), 6000);
                    }
                  }}
                  disabled={(aggregate?.balls_recorded ?? 0) === 0}
                  defaultValue=""
                  className={`hidden sm:block rounded-md text-[10px] font-semibold uppercase tracking-wider px-1 py-1 ${
                    (aggregate?.balls_recorded ?? 0) > 0
                      ? "bg-amber-500/10 border border-amber-500/40 text-amber-300"
                      : "bg-bg-secondary border border-bg-secondary text-text-secondary opacity-40"
                  }`}
                  title="Undo last N balls (single rebuild)"
                >
                  <option value="">↶ N</option>
                  <option value="3">Undo last 3</option>
                  <option value="5">Undo last 5</option>
                  <option value="10">Undo last 10</option>
                </select>
                <button
                  onClick={handleRedoBall}
                  disabled={redoStack.length === 0 || redoInFlight}
                  className={`rounded-md text-[10px] font-semibold uppercase tracking-wider px-2 py-1 flex gap-1 items-center justify-center transition-opacity ${
                    redoStack.length > 0 && !redoInFlight
                      ? "bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25"
                      : "bg-bg-secondary border border-bg-secondary text-text-secondary opacity-40"
                  }`}
                  title={
                    redoInFlight
                      ? "Redo in progress…"
                      : redoStack.length > 0
                        ? `Redo (re-save the last undone ball${redoStack.length > 1 ? `; ${redoStack.length} pending` : ""})`
                        : "Nothing to redo — UNDO a ball first"
                  }
                >
                  <RotateCw className={`w-3 h-3 ${redoInFlight ? "animate-spin" : ""}`} />
                  Redo
                  {redoStack.length > 1 && (
                    <span className="font-mono text-[9px]">{redoStack.length}</span>
                  )}
                </button>
                <button
                  onClick={() => setEditBallDialogOpen(true)}
                  className="rounded-md text-[10px] font-semibold uppercase tracking-wider px-2 py-1 bg-blue-500/15 border border-blue-500/40 text-blue-300 hover:bg-blue-500/25 flex gap-1 items-center justify-center"
                  title="Edit any past ball — opens full editor dialog"
                >
                  <RefreshCcw className="w-3 h-3" />
                  {t("bbb.edit_ball")}
                </button>
                {/* QC opens via shared QcLink — window.open(url, name)
                    in onClick guarantees the existing QC tab is
                    reused + focused on repeat clicks. Plain
                    target-attribute alone wasn't reliable on Chrome
                    with rel=noopener; the JS path is. */}
                <QcLink matchId={id} variant="ribbon" />
              </div>
              {editingBallId && (
                <button
                  onClick={cancelEdit}
                  className="rounded-md text-[10px] uppercase tracking-wider px-2 py-1 bg-amber-500/15 border border-amber-500/40 text-amber-400 hover:bg-amber-500/25"
                >
                  ✕ Cancel edit
                </button>
              )}
              {/* Live score panel — placed BELOW the action buttons (per
                  2026-05-19 UI request). The previous location (above
                  the actions) duplicated the sticky header; this slot
                  gives the scorer the runs / wickets / overs right next
                  to the wagon wheel without re-pushing the actions down. */}
              <div
                className={`w-full rounded-lg p-2 flex flex-col gap-0.5 items-center ${
                  isSuperOver
                    ? "bg-rose-500/15 border border-rose-400/40"
                    : "bg-button-secondary"
                }`}
                data-testid="bbb-balls-recorded"
                data-count={aggregate?.balls_recorded ?? 0}
              >
                {/* 2026-06-06 (v2) — Super Over treatment in the
                    left-pane live-score card. Rose tint + "Super Over N"
                    chip so the operator sees at a glance that the
                    rules are different (max 2 wickets, 1 over). */}
                {isSuperOver && (
                  <p className="text-[9px] uppercase tracking-wider text-rose-200 font-semibold">
                    ★ Super Over {superOverIndex || ""}
                  </p>
                )}
                <p className="text-[11px] truncate w-full text-center">
                  {battingTeamName ? `${battingTeamName} ` : ""}
                </p>
                <p className="text-xl text-text-yellow font-[Orbitron] leading-none">
                  {/* "all out" suffix replaces "/N" when effective_wickets
                      hits the all-out threshold (10 regular / 2 SO). */}
                  {aggregate.runs}
                  {aggregate.is_all_out ? (
                    <span className="ml-1 text-xs text-rose-300">all out</span>
                  ) : (
                    <>/{aggregate.wickets}{isSuperOver ? <span className="text-text-secondary">/2</span> : null}</>
                  )}
                </p>
                <p className="text-[10px]">
                  {/* The Hundred — ball-count display (Ball X / 100,
                      Y left). All other formats stay on over.ball. */}
                  {isHundredMatch && !isSuperOver ? (
                    <>
                      Ball {legalBalls}/{hundredMaxBalls}
                      <span className="text-text-secondary ml-1">
                        · {hundredBallsRemaining} left
                      </span>
                    </>
                  ) : (
                    <>{oversDisplay} ov{isSuperOver ? <span className="text-text-secondary"> / 1.0</span> : null}</>
                  )}
                  {/* The Hundred phase chip — Powerplay / Middle / Death.
                      Colour-coded per phase. Only renders for HUNDRED
                      matches outside the super-five tie-break. Count is
                      relative to the phase's own start ball. */}
                  {isHundredMatch && !isSuperOver && hundredPhase && (
                    <span className={`ml-2 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider border align-middle ${hundredPhase.classes}`}>
                      {hundredPhase.name} {hundredPhase.count}/{hundredPhase.total}
                    </span>
                  )}
                  {/* The Hundred Strategic Timeout marker — set once
                      per innings when the batting captain calls the
                      ~2.5-min break. */}
                  {isHundredMatch && hundredStrategicTimeoutBall != null && (
                    <span className="ml-2 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider bg-indigo-500/20 text-indigo-200 border border-indigo-400/40 align-middle">
                      ⏸ Timeout @ ball {hundredStrategicTimeoutBall}
                    </span>
                  )}
                  {/* BBL phase chip. Hidden for non-BBL matches
                      (is_bbl=false). Shows Powerplay / Middle / Death
                      based on current_over_phase. During the 2 Power
                      Surge overs phase=="powerplay" AND isPowerSurge,
                      so the ⚡ Surge chip wins over the generic
                      Powerplay chip. */}
                  {isBblMatch && ppIsPowerSurge && (
                    <span className="ml-2 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider bg-purple-500/25 text-purple-200 border border-purple-400/40 align-middle">
                      ⚡ Surge {ppCurrentOver === ppSurgeStart ? "1" : "2"}/2
                    </span>
                  )}
                  {isBblMatch && !ppIsPowerSurge && ppPhase === "powerplay" && (
                    <span className="ml-2 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider bg-orange-500/25 text-orange-200 border border-orange-400/40 align-middle">
                      Powerplay {ppCurrentOver}/{ppInitialOvers ?? 4}
                    </span>
                  )}
                  {isBblMatch && ppPhase === "middle_overs" && (
                    <span className="ml-2 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider bg-sky-500/20 text-sky-200 border border-sky-400/40 align-middle">
                      Middle overs
                    </span>
                  )}
                  {isBblMatch && ppPhase === "death_overs" && (
                    <span className="ml-2 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider bg-rose-500/25 text-rose-200 border border-rose-400/40 align-middle">
                      Death overs
                    </span>
                  )}
                  {isBblMatch && !ppIsPowerSurge && ppSurgeStart && (
                    <span className="ml-2 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider bg-bg-secondary text-text-secondary border border-border-default align-middle">
                      Surge used ({ppSurgeStart}-{ppSurgeStart + 1})
                    </span>
                  )}
                  {/* Surge ACTIVATION WINDOW chip. Shows while the
                      batting team can still activate (BBL, not yet
                      used, current over inside the window).
                      Communicates the deadline so the operator
                      doesn't miss it. */}
                  {isBblMatch && ppSurgeAvailable && ppWindowEnd && (
                    <span className="ml-2 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider bg-purple-500/10 text-purple-300 border border-purple-500/30 align-middle">
                      Surge window: {ppCurrentOver}–{ppWindowEnd}
                    </span>
                  )}
                  {/* T10 floating powerplay chips: the mandatory
                      block, the floating over while it's being bowled,
                      the open window, and a reminder once it's used. */}
                  {hasFloatingPp && ppState.is_powerplay && !isFloatingPpOver && (
                    <span className="ml-2 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider bg-orange-500/25 text-orange-200 border border-orange-400/40 align-middle">
                      Powerplay {ppCurrentOver}/{ppInitialOvers ?? 2}
                    </span>
                  )}
                  {hasFloatingPp && isFloatingPpOver && (
                    <span className="ml-2 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider bg-purple-500/25 text-purple-200 border border-purple-400/40 align-middle">
                      ⚡ Floating powerplay
                    </span>
                  )}
                  {hasFloatingPp && floatingPpAvailable && floatingPpWindowEnd && (
                    <span className="ml-2 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider bg-purple-500/10 text-purple-300 border border-purple-500/30 align-middle">
                      Floating PP window: {ppCurrentOver}–{floatingPpWindowEnd}
                    </span>
                  )}
                  {hasFloatingPp && floatingPpOver && !isFloatingPpOver && (
                    <span className="ml-2 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider bg-bg-secondary text-text-secondary border border-border-default align-middle">
                      Floating PP used (over {floatingPpOver})
                    </span>
                  )}
                  {/* Fielding-restriction (Law 41.7) marker chip — visible
                      whenever an is_fielding_penalty row exists on the
                      innings. Tells the operator the bowling side must
                      keep an extra fielder inside the 30-yard circle. */}
                  {fieldingRestriction && (
                    <span
                      className="ml-2 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider bg-amber-500/20 text-amber-200 border border-amber-400/40 align-middle"
                      title={
                        fieldingRestrictionFromBallLabel
                          ? `Extra fielder must stay inside the 30-yard circle from ball ${fieldingRestrictionFromBallLabel}`
                          : "Extra fielder must stay inside the 30-yard circle from the current ball"
                      }
                    >
                      ⚠ Field restriction
                      {fieldingRestrictionFromBallLabel
                        ? ` from ${fieldingRestrictionFromBallLabel}`
                        : ""}
                    </span>
                  )}
                </p>
                {(currentRunRate != null || requiredRunRate != null) && (
                  <p className="text-[10px] font-mono">
                    {currentRunRate != null && (
                      <span className="text-text-secondary">CRR <span className="text-text-primary">{currentRunRate.toFixed(2)}</span></span>
                    )}
                    {requiredRunRate != null && (
                      <span className="ml-2 text-amber-300">RRR {requiredRunRate.toFixed(2)}</span>
                    )}
                  </p>
                )}
              </div>
            </div>
          </section>
        </div>
        {/* Vertical filler — absorbs any leftover space in the left pane on
            tall viewports (iPad landscape, desktop) so the column visually
            stretches to fit instead of leaving black space below the actions. */}
        <div className="hidden xl:block flex-1 min-h-0" aria-hidden="true" />
      </div>

      <div className={`order-3 xl:order-2 w-full xl:w-[30%] xl:pt-9 min-w-0 min-h-0 ${isIpadLandscape ? "flex w-1/2" : (mobileBbpTab === "spatial" ? "flex" : "hidden xl:flex")} flex-col gap-1.5 xl:overflow-visible`}>
        {/* middle column — 30% (bumped from 25% on 2026-05-27 so the
            ZoneMatrixPicker's 332px-min pitch-map grid has breathing
            room even at the smallest xl viewport, 1280px). Right column
            (performance-metrics accordion) takes the rest via flex-1
            (~36.7%) — was ~42%, a small reduction the user explicitly
            asked for to stop the pitch-map↔chip-grid overlap.
            xl:pt-9 leaves space for the sticky header which floats over
            this column + the right column on desktop. */}
        {/* (Release-state buttons moved BELOW the spatial picker per UI feedback) */}
        {/* 2026-05-28 — Voice cheat sheet (SpatialVoiceHelp) removed per
            founder ask. Operators have learned the voice grammar; the
            panel was just taking vertical space above the spatial tabs
            on every viewport (PC + tablet + phone). The full cheat-sheet
            lives in VoiceTutorialModal (60-second walkthrough) for
            anyone who needs to re-learn. */}
        {/* ── Ball speed (2026-10-16, founder ask) ─────
            First thing in the spatial column so it isn't missed. Optional
            (not every feed shows a speed gun); blank on every new ball.
            Persists to BallReleaseData.speed_kph → `ball_speed` in the
            BBB download. */}
        {(() => {
          const raw = ball.release?.speed_kph;
          const typed = raw != null && raw !== "";
          const num = typed ? Number(raw) : null;
          const bad = typed && !(num >= 30 && num <= 200);
          return (
            <div
              className={`${panelClass} flex items-center gap-2 text-[11px] border ${
                bad ? "border-rose-500/70" : typed ? "border-green-600/70" : "border-sky-500/50"
              }`}
            >
              <span className="text-sky-300 uppercase tracking-wider text-[10px] font-semibold whitespace-nowrap">
                Ball speed
              </span>
              <input
                type="number"
                inputMode="decimal"
                step="0.1"
                min="30"
                max="200"
                placeholder="e.g. 138"
                value={raw ?? ""}
                onChange={(e) => {
                  const v = e.target.value;
                  setBall((prev) => ({
                    ...prev,
                    release: { ...prev.release, speed_kph: v === "" ? null : v },
                  }));
                }}
                onWheel={(e) => e.currentTarget.blur()}
                className={`w-24 px-2 py-1 rounded text-xs font-mono ${
                  typed && !bad ? "bg-green-700 text-white" : "bg-bg-secondary text-text-primary"
                }`}
                data-testid="ball-speed"
                aria-label="Ball speed in km/h (optional)"
              />
              <span className="text-text-secondary">km/h</span>
              <span className={`ml-auto text-[10px] ${bad ? "text-rose-300" : "text-text-secondary"}`}>
                {bad ? "Check speed (30-200 km/h)" : "Optional - leave blank if not shown"}
              </span>
            </div>
          );
        })()}

        {/* ── Spatial tabs (3): Pitch map · Height impact · Wagon wheel ───── */}
        <div className={panelClass}>
          <div className="flex items-center gap-1 mb-1.5">
            {[
              { k: "pitch",  l: t("spatial.pitch_map") },
              { k: "impact", l: t("spatial.height_impact") },
              { k: "wagon",  l: t("spatial.wagon_wheel"),
                muted: !wagonRequired,
                title: !wagonRequired
                  ? "Wagon wheel not applicable (ball didn't come off the bat)"
                  : undefined },
            ].map(({ k, l, muted, title }) => (
              <button
                key={k}
                onClick={() => {
                  setSpatialTab(k);
                  // Drive the inner ZoneMatrixPicker matrix from the tab choice.
                  if (k === "pitch")  setActiveZoneMatrix("pitch");
                  if (k === "impact") setActiveZoneMatrix("height");
                }}
                title={title}
                className={`flex-1 text-[10px] px-2 py-1 rounded-md uppercase tracking-wider ${
                  spatialTab === k
                    ? "bg-button-primary text-white"
                    : muted
                      ? "bg-bg-secondary/40 text-text-secondary/50"
                      : "bg-bg-secondary text-text-secondary hover:text-text-primary"
                }`}
              >
                {l}{muted ? " · N/A" : ""}
              </button>
            ))}
            <button
              onClick={() => setShortcutsOpen(true)}
              className="text-[10px] px-1.5 py-1 rounded-md bg-bg-secondary text-text-secondary hover:text-text-primary"
              title="Keyboard shortcuts (?)"
            >
              ?
            </button>
            {/* 2026-05-29 — Noise gate relocated to ABOVE the
                Performance Metrics column (right pane). See the
                ActivityAccordion block lower in this file. */}
          </div>

          {(spatialTab === "pitch" || spatialTab === "impact") && (
            <ZoneMatrixPicker
              pitchZone={ball?.spatial?.pitch_zone ?? null}
              stumpZone={ball?.spatial?.stump_zone ?? null}
              heightZone={ball?.spatial?.height_zone ?? null}
              batterStumpZone={ball?.spatial?.batter_stump_zone ?? null}
              batterHand={
                displayStrikerId
                  ? getEffectiveHand(
                      displayStrikerId,
                      getBatterHand(displayStrikerId),
                    )
                  : "RIGHT"
              }
              activeMatrix={activeZoneMatrix}
              onActiveMatrixChange={setActiveZoneMatrix}
              onChange={(pitchZone, stumpZone, heightZone, batterStumpZone) =>
                setBall((prev) => ({
                  ...prev,
                  spatial: {
                    ...prev.spatial,
                    pitch_zone: pitchZone,
                    stump_zone: stumpZone,
                    height_zone: heightZone,
                    batter_stump_zone: batterStumpZone,
                  },
                }))
              }
            />
          )}

          {spatialTab === "wagon" && (
            <SpatialPicker
              ball={ball}
              setBall={setBall}
              players={players}
              subFielders={subFielders}
              currentBowlerId={displayBowlerId}
              wicketKeeperId={wicketKeeperId}
              onRequestWicketKeeper={() => setWicketKeeperModalOpen(true)}
              batterHand={getEffectiveHand(
                displayStrikerId,
                getBatterHand(displayStrikerId),
              )}
              structuredRegion={structuredRegion}
              onStructuredRegionChange={setStructuredRegion}
            />
          )}

        </div>

        {/* ── Bowler release point (2026-08-03, founder ask) ─────
            Sits directly below the wagon-wheel/spatial panel. Codes the
            release-point anomaly for this delivery — distinct from the
            Over/Around arm-angle toggle in the release-state row below.
            Defaults to Normal and resets to Normal after every saved
            ball; persists to BallReleaseData.bowler_release_point. */}
        <div className={`${panelClass} flex items-center gap-2 text-[11px]`}>
          <span className="text-text-secondary uppercase tracking-wider text-[10px] whitespace-nowrap">
            Bowler release point
          </span>
          <select
            value={ball.release?.bowler_release_point || "NORMAL"}
            onChange={(e) =>
              setBall((prev) => ({
                ...prev,
                release: {
                  ...prev.release,
                  bowler_release_point: e.target.value,
                },
              }))
            }
            className={`flex-1 px-2 py-1 rounded text-xs ${
              (ball.release?.bowler_release_point || "NORMAL") !== "NORMAL"
                ? "bg-green-700 text-white"
                : "bg-bg-secondary text-text-primary"
            }`}
            data-testid="bowler-release-point"
          >
            <option value="NORMAL">Normal</option>
            <option value="WIDE_OF_CREASE">Wide of crease</option>
            <option value="BEHIND_CREASE">Behind the crease</option>
            <option value="LOW_ARM">Low arm release</option>
          </select>
        </div>

        {/* ── Release-state row (New/Repl + Around/Over) ─────
            Moved below the spatial picker so the toggles+grids breathe and
            release context is captured AFTER the operator finishes the spatial
            selection (matches the natural scoring flow). The wicket-keeper
            position toggles used to sit between these two; operator dropped
            them from the live row — edit dialog still has the full picker. */}
        <div className={panelClass}>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex gap-2 text-center w-full justify-evenly rounded-lg bg-bg-secondary/50">
              {ballTypes.map((opt) => (
                <button
                  key={opt}
                  onClick={() =>
                    setBall((prev) => ({
                      ...prev,
                      release: { ...prev.release, ball_type: prev.release?.ball_type === opt ? "" : opt },
                    }))
                  }
                  className={`rounded-lg text-xs px-2 py-1 w-full ${
                    ball.release?.ball_type === opt ? "bg-green-700 text-white" : "bg-bg-primary text-text-secondary"
                  }`}
                >{opt}</button>
              ))}
            </div>
            <div className="flex gap-2 text-center w-full justify-evenly rounded-lg bg-bg-secondary/50">
              {bowlerReleaseSides.map((opt) => (
                <button
                  key={opt}
                  onClick={() =>
                    setBall((prev) => ({
                      ...prev,
                      release: {
                        ...prev.release,
                        bowler_release_position: prev.release?.bowler_release_position === opt ? "" : opt,
                      },
                    }))
                  }
                  className={`rounded-lg text-xs px-2 py-1 w-full ${
                    ball.release?.bowler_release_position === opt ? "bg-green-700 text-white" : "bg-bg-primary text-text-secondary"
                  }`}
                >{opt}</button>
              ))}
            </div>
          </div>
        </div>

        {/* 2026-07-10 — Batter guard moved here (was above "Ball ended up
            at"). Sits below the New/Old/Repl release-state row so the
            operator captures batter stance in the same release-context
            block. Clicking the active pill clears the value. */}
        <div className={`${panelClass} flex flex-wrap items-center gap-2 text-[11px]`}>
          <span className="text-text-secondary uppercase tracking-wider text-[10px]">Batter guard</span>
          {[
            { v: "LEG",     l: "Leg" },
            { v: "MIDDLE",  l: "Middle" },
            { v: "TWO_LEG", l: "Two-leg" },
            { v: "OFF",     l: "Off" },
            { v: "CENTER",  l: "Centre" },
          ].map(({ v, l }) => (
            <button
              key={v}
              onClick={() => setBall((p) => ({
                ...p, batter_guard: p.batter_guard === v ? "" : v,
              }))}
              title={
                ball.batter_guard === v
                  ? `${l} guard recorded — click to clear`
                  : `Record ${l} guard`
              }
              className={`px-2 py-0.5 rounded ${ball.batter_guard === v ? "bg-green-700 text-white" : "bg-bg-secondary text-text-secondary hover:bg-bg-secondary/80"}`}
            >
              {l}
            </button>
          ))}
          {ball.batter_guard && (
            <button
              onClick={() => setBall((p) => ({ ...p, batter_guard: "" }))}
              className="text-[10px] uppercase tracking-wider text-text-secondary/70 hover:text-text-primary px-2"
              aria-label="Clear guard"
            >
              Clear
            </button>
          )}
        </div>

        {/* (Score ribbon moved to LEFT pane for live operator visibility — see above) */}

        {/* OFFICIALS STRIP — Bowler-end umpire / Square-leg umpire / Keeper.
            Sits directly above the commentary feed so the operator can
            see who's on the field at a glance, and swap any of the three
            inline (e.g. for keeper rotation or umpire substitution).
            All three writes are local-only — they fold into the next
            ball's save via umpire_bowler_end / umpire_square_leg / the
            already-existing wicketKeeperId hook. */}
        <div className={`${panelClass} flex items-center gap-2 flex-wrap shrink-0`}>
          <span className="text-[10px] uppercase tracking-wider text-text-secondary">On field</span>
          {/* 2026-06-10 — current bowling end chip. Derived from
              firstOverBowlingEnd + completed_overs alternation (odd
              over = same end as Over 1; even = opposite). Always
              renders when the operator has picked the first-over end,
              falling back to generic "End A"/"End B" labels when the
              stadium doesn't have named ends configured.
              When no end is picked yet, render a "Pick end" button so
              the operator can re-open the picker any time (e.g. if
              they dismissed the auto-popup or are mid-innings on a
              match where the pick never happened). */}
          {firstOverBowlingEnd ? (() => {
            // Over N (1-indexed) = completed_overs + 1.
            // Odd over (1, 3, 5...) bowls from the SAME end as Over 1.
            // Even over (2, 4, 6...) bowls from the OPPOSITE end.
            // completed_overs increments server-side the moment 6 legal
            // deliveries land — Zustand re-render flips the chip on
            // the very next render after that aggregate update.
            const completed = aggregate?.completed_overs ?? 0;
            // 2026-09-02 — clamp to the innings' over cap. completed_overs
            // ticks the moment the 6th legal ball lands, so on ball 20.6 of
            // a 20-over innings this chip read "ov 21" — an over that will
            // never be bowled — and alternated the end onto it too (21 being
            // odd, it flipped back to the Over-1 end, so both halves of the
            // chip were wrong). It stays on screen because the operator is
            // left on the finished innings to take late corrections.
            //
            // Once the innings is done the over that still matters is the
            // last one actually bowled — a delayed wide / no-ball / DRS
            // correction lands there, not in a new over. That is
            // completed_overs, plus one when a part-over is in progress
            // (all out or target chased mid-over).
            const overCap = Number(aggregate?.max_overs)
              || Number(aggregate?.match_type?.max_overs)
              || null;
            const ballsPerOver =
              Number(aggregate?.match_type?.balls_per_over) || 6;
            const partOverInProgress =
              (Number(aggregate?.legal_balls) || 0) % ballsPerOver !== 0;
            const inningsDone =
              isInningsEnded || (overCap !== null && completed >= overCap);
            const lastOverBowled = completed + (partOverInProgress ? 1 : 0);
            const currentOverNum = inningsDone
              ? Math.max(
                  1,
                  overCap === null
                    ? lastOverBowled
                    : Math.min(lastOverBowled, overCap),
                )
              : completed + 1;
            const isCurrentOverOdd = currentOverNum % 2 === 1;
            const currentEnd = isCurrentOverOdd
              ? firstOverBowlingEnd
              : (firstOverBowlingEnd === "A" ? "B" : "A");
            const fallbackA = stadiumEndAName || "End A";
            const fallbackB = stadiumEndBName || "End B";
            const label = currentEnd === "A" ? fallbackA : fallbackB;
            return (
              <span
                className="flex items-center gap-1 text-[11px] px-2 py-0.5 rounded bg-blue-500/15 border border-blue-400/30 text-blue-200"
                title={inningsDone
                  ? `Innings over. Over ${currentOverNum} was the last one bowled, from ${label} — a late correction still lands there.`
                  : `Bowling end for over ${currentOverNum}. Auto-alternates every over: odd overs (1, 3, 5…) bowl from the SAME end as Over 1; even overs (2, 4, 6…) from the opposite end.`}
              >
                <span className="text-text-secondary">Bowling end:</span>
                <span className="font-semibold">{label}</span>
                <span className="text-text-secondary text-[9px] font-mono ml-1 opacity-75">
                  · ov {currentOverNum}{inningsDone ? " · last" : ""}
                </span>
              </span>
            );
          })() : (
            <button
              type="button"
              onClick={() => { setEndPickerForceOpen(true); setEndPickerError(""); }}
              className="flex items-center gap-1 text-[11px] px-2 py-0.5 rounded bg-amber-500/15 border border-amber-500/40 text-amber-200 hover:bg-amber-500/25"
              title="Pick which end Over 1 is bowled from. Subsequent overs auto-alternate."
            >
              <span className="text-text-secondary">Bowling end:</span>
              <span className="font-semibold underline">Pick end →</span>
            </button>
          )}
          <label className="flex items-center gap-1 text-[11px]">
            <span className="text-text-secondary">Bowler-end:</span>
            <select
              value={umpireBowlerEndId ?? ""}
              onChange={(e) => {
                const v = e.target.value || null;
                setUmpireBowlerEndId(v);
                // Inline pick also counts as a resolved prompt — persist
                // so a QC round-trip doesn't re-open the umpires modal.
                persistUmpiresForInnings(v, umpireSquareLegId, true);
                setUmpirePromptDismissed(true);
              }}
              className="bg-bg-secondary text-text-primary px-1.5 py-0.5 rounded text-[11px] max-w-[140px]"
            >
              <option value="">— pick umpire —</option>
              {(umpires || [])
                .filter((u) => u.id !== umpireSquareLegId)
                .map((u) => (
                  <option key={u.id} value={u.id}>{u.short_name || u.name || u.id}</option>
                ))}
            </select>
          </label>
          <label className="flex items-center gap-1 text-[11px]">
            <span className="text-text-secondary">Square-leg:</span>
            <select
              value={umpireSquareLegId ?? ""}
              onChange={(e) => {
                const v = e.target.value || null;
                setUmpireSquareLegId(v);
                persistUmpiresForInnings(umpireBowlerEndId, v, true);
                setUmpirePromptDismissed(true);
              }}
              className="bg-bg-secondary text-text-primary px-1.5 py-0.5 rounded text-[11px] max-w-[140px]"
            >
              <option value="">— pick umpire —</option>
              {(umpires || [])
                .filter((u) => u.id !== umpireBowlerEndId)
                .map((u) => (
                  <option key={u.id} value={u.id}>{u.short_name || u.name || u.id}</option>
                ))}
            </select>
          </label>
          {/* Keeper picker — switchable mid-game. Includes:
              * Bowling-team Playing XI (optgroup 1)
              * Substitute fielders / squad reserves (optgroup 2) so the
                operator can name a sub-keeper when the listed keeper
                got injured mid-innings (Sanju-out-Urvil-in case
                reported 2026-05-22).
              Picks routed through setWicketKeeperManually so the
              choice survives every subsequent server-state refresh
              (see keeperManualForInningsRef above). */}
          <label className="flex items-center gap-1 text-[11px]">
            <span className="text-text-secondary">Keeper:</span>
            <select
              value={wicketKeeperId ?? ""}
              onChange={(e) => setWicketKeeperManually(e.target.value || null)}
              className="bg-bg-secondary text-text-primary px-1.5 py-0.5 rounded text-[11px] max-w-[180px]"
              title="Change wicket-keeper — picks from the XI + squad reserves. Useful when the listed keeper is injured / off field."
            >
              <option value="">— pick keeper —</option>
              <optgroup label="Playing XI">
                {(players?.bowlers || []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nick_name || p.name || `${p.first_name || ""} ${p.last_name || ""}`.trim() || p.id}
                  </option>
                ))}
              </optgroup>
              {(subFielders || []).length > 0 && (
                <optgroup label="Substitutes (squad reserves)">
                  {(subFielders || []).map((p) => (
                    <option key={p.id} value={p.id}>
                      {(p.nick_name || p.name || `${p.first_name || ""} ${p.last_name || ""}`.trim() || p.id) + " (sub)"}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
          </label>
          {umpireBowlerEndId && umpireBowlerEndId === umpireSquareLegId && (
            <span className="text-[10px] text-rose-300">⚠ same umpire at both ends</span>
          )}
        </div>

        {/* Ball-by-ball commentary feed — click any row to edit that ball.
            flex-1 makes this panel absorb the remaining vertical space in the
            centre pane so the column doesn't end short of viewport. */}
        {recentBalls.length > 0 && (
          <div className={`${panelClass} flex flex-col gap-1 flex-1 min-h-0 overflow-hidden`}>
            <div className="flex items-center justify-between shrink-0">
              <span className="text-[10px] uppercase tracking-wider text-text-secondary">
                Commentary · click row to edit
              </span>
              <button
                onClick={openPastBalls}
                className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-bg-secondary text-text-secondary hover:text-text-primary"
                title="Open full past-balls picker"
              >
                All balls
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto pr-1 flex flex-col-reverse">
              {(() => {
                // Interleave one-line over-summary rows between balls when an
                // over completes. Saves the scorer the cricinfo-style "that
                // over: X runs, Y wickets" recap they'd otherwise compute by hand.
                const sorted = [...recentBalls].sort(
                  (a, b) => (a.over_number - b.over_number) || (a.ball_in_over - b.ball_in_over),
                );
                const rows = [];
                for (let i = 0; i < sorted.length; i++) {
                  const b = sorted[i];
                  rows.push({ kind: "ball", ball: b });
                  const next = sorted[i + 1];
                  const overChanged = !next || next.over_number !== b.over_number;
                  if (overChanged) {
                    const overBalls = sorted.filter((x) => x.over_number === b.over_number);
                    const legal = overBalls.filter((x) => x.is_legal).length;
                    if (legal >= 6) {
                      const runs = overBalls.reduce((s, x) => s + (x.runs_total ?? 0), 0);
                      const wkts = overBalls.filter((x) => x.wicket).length;
                      const fours = overBalls.filter((x) => x.runs_off_bat === 4).length;
                      const sixes = overBalls.filter((x) => x.runs_off_bat === 6).length;
                      const bits = [];
                      if (wkts) bits.push(`${wkts} wkt${wkts !== 1 ? "s" : ""}`);
                      if (fours) bits.push(`${fours}×4`);
                      if (sixes) bits.push(`${sixes}×6`);
                      rows.push({
                        kind: "over_summary",
                        key: `summary-${b.over_number}`,
                        text: `Over ${b.over_number}: ${runs} run${runs !== 1 ? "s" : ""}${bits.length ? ` · ${bits.join(", ")}` : ""}`,
                      });
                    }
                  }
                }
                return rows.map((r) => {
                  if (r.kind === "over_summary") {
                    return (
                      <div
                        key={r.key}
                        className="text-[10px] uppercase tracking-wider text-emerald-300 bg-emerald-500/10 border-y border-emerald-500/20 px-2 py-1 my-0.5"
                      >
                        {r.text}
                      </div>
                    );
                  }
                  const b = r.ball;
                  const isVoiceCoded = voiceCodedBallIds.has(b.id);
                  return (
                    <button
                      key={b.id}
                      onClick={() => enterEditMode(b.id)}
                      className={`text-left text-[11px] flex gap-1.5 py-0.5 px-1 rounded hover:bg-bg-secondary/40 transition-colors ${
                        editingBallId === b.id ? "bg-amber-500/15 border-l-2 border-amber-500/60" :
                        b.wicket ? "border-l-2 border-red-500/60" :
                        b.runs_off_bat === 6 ? "border-l-2 border-purple-500/60" :
                        b.runs_off_bat === 4 ? "border-l-2 border-emerald-500/60" : ""
                      }`}
                      title={isVoiceCoded ? "Voice-coded — click to open Edit + 🎤 Voice tab for playback" : "Click to edit"}
                    >
                      <span className="font-mono text-[10px] text-text-secondary shrink-0 w-9">
                        {formatBallReference(aggregate?.match_type, { overNumber: b.over_number, ballInOver: b.ball_in_over, overBase: LIVE_OVER_BASE })}{!b.is_legal && "*"}
                      </span>
                      {isVoiceCoded && (
                        <span className="text-[9px] text-purple-300 shrink-0" aria-label="voice-coded">🎤</span>
                      )}
                      <span className="text-text-primary truncate">
                        {b.commentary || `${b.striker || "?"} → ${b.runs_total} run${b.runs_total !== 1 ? "s" : ""}${b.wicket ? ` · ${b.wicket}` : ""}`}
                      </span>
                      {/* Overthrow breakdown pill — shows "1 ran + 2 ot"
                          alongside the commentary so reviewers can see at
                          a glance that the operator's "3 runs" was 1
                          off-bat + 2 overthrow. runs_off_bat already
                          includes the overthrow portion under the founder
                          model (2026-05-30). */}
                      {(b.overthrow_runs || 0) > 0 && (
                        <span
                          className="ml-auto shrink-0 font-mono text-[10px] text-violet-300 bg-violet-500/10 border border-violet-400/30 rounded px-1.5 py-0.5"
                          title={`Off-bat ${Math.max(0, (b.runs_off_bat || 0) - (b.overthrow_runs || 0))} + overthrow ${b.overthrow_runs}`}
                        >
                          {Math.max(0, (b.runs_off_bat || 0) - (b.overthrow_runs || 0))}
                          +{b.overthrow_runs} ot
                        </span>
                      )}
                    </button>
                  );
                });
              })()}
            </div>
          </div>
        )}

        {/* Did batters cross — only relevant on caught dismissals */}
        {ball.has_wicket && ball.wicket_type === "CAUGHT" && (
          <div className={`${panelClass} flex items-center gap-2`}>
            <span className="text-[10px] uppercase tracking-wider text-text-secondary">Did batters cross?</span>
            <button onClick={() => setBall((prev) => ({ ...prev, batters_crossed: true }))}
              className={ball.batters_crossed === true ? btnToggleActive : btnToggle}>Yes</button>
            <button onClick={() => setBall((prev) => ({ ...prev, batters_crossed: false }))}
              className={ball.batters_crossed === false ? btnToggleActive : btnToggle}>No</button>
          </div>
        )}
      </div>

      {/* RIGHT PANE — voice cheat sheet + activity accordion. The
          cheat sheet sits ABOVE the accordion (founder ask 2026-05-25)
          so operators learning voice commands have the vocabulary one
          tap away while their eyes stay on the chip grid.
          Sprint 4 (2026-05-27): order-2 on phone so the accordion sits
          immediately below the score-input column instead of 3 screens
          down behind the spatial picker. Original left→middle→right
          layout restored at xl:order-3. */}
      <div className={`order-2 xl:order-3 w-full xl:flex-1 xl:pt-9 min-w-0 min-h-0 ${isIpadLandscape ? "flex w-1/2 overflow-y-auto" : (mobileBbpTab === "activity" ? "flex" : "hidden xl:flex")} flex-col gap-1 text-xs xl:overflow-hidden`}>
        {/* 2026-05-29 — Noise gate sits ABOVE the Performance Metrics
            accordion per founder ask. Right-aligned on its own row so
            it doesn't compete with the cheat-sheet width. */}
        {voice?.ptt?.supported && typeof voice.ptt.setNoiseGateEnabled === "function" && (
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => voice.ptt.setNoiseGateEnabled(!voice.ptt.noiseGateEnabled)}
              aria-pressed={!!voice.ptt.noiseGateEnabled}
              title={
                voice.ptt.noiseGateEnabled
                  ? "Noise gate ON — high-pass + compressor before Whisper. Click to disable if your voice is quiet."
                  : "Noise gate OFF — raw mic audio. Click to re-enable filtering."
              }
              className={`text-[10px] font-medium px-2 py-1 rounded-md border transition-colors inline-flex items-center gap-1 ${
                voice.ptt.noiseGateEnabled
                  ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20"
                  : "border-bg-secondary bg-bg-primary/60 text-text-secondary hover:text-text-primary"
              }`}
            >
              🛡 Noise gate {voice.ptt.noiseGateEnabled ? "ON" : "OFF"}
            </button>
          </div>
        )}
        <VoiceCommandsHelp />
        <div className={`${panelClass} flex-1 min-h-0 overflow-hidden`}>
          <ActivityAccordion
            config={ACTIVITIES_CONFIG}
            order={ACTIVITY_FLOW}
            selected={selectedValues}
            onSelect={handleActivitySelect}
            isGroupRelevant={isGroupRelevant}
            bowlerType={bowlerType}
          />
        </div>
      </div>

      {/* Edit ball dialog — full editor for any past ball, continue-editing pattern.
          The backend's get_selectable_players read-model only returns batters +
          bowlers (the XIs); the Wicket tab's run-out / caught-by / stumped-by
          dropdowns are typed as `fielders`, so we compose XI bowlers +
          substitutes (squad reserves) here and ship them as `players.fielders`
          — same source the wagon-wheel fielder picker reads from. Subs are
          flagged with " (sub)" so the operator can tell them apart. */}
      <EditBallDialog
        open={editBallDialogOpen}
        onClose={() => setEditBallDialogOpen(false)}
        inningsId={inningsId}
        matchId={id}
        matchType={aggregate?.match_type}
        initialBallId={recentBalls[0]?.id || null}
        // 2026-06-05 — pass the already-loaded subFielders so the
        // dialog's wagon-wheel picker shows subs IMMEDIATELY on open,
        // without waiting for its own /squad/ fetch to complete after
        // ball + scorecard load. Without this, the picker briefly
        // (or sometimes permanently, depending on dependency timing)
        // showed only XI under "Substitute fielders".
        subFielders={subFielders}
        players={{
          ...players,
          fielders: [
            ...(players?.bowlers || []),
            ...(subFielders || []).map((p) => ({
              ...p,
              // Render-time hint: append "(sub)" to the displayed name so
              // the operator sees which roster the player came from.
              nick_name: p.nick_name
                ? `${p.nick_name} (sub)`
                : `${[p.first_name, p.last_name].filter(Boolean).join(" ") || "Sub"} (sub)`,
              is_substitute: true,
            })),
          ],
        }}
        onSaved={async (savedBallId) => {
          // CF-P0-05 — observe-correction. If this ball had a recent
          // AI suggestion (AI Suggest or AI Score) and the operator's
          // edit changed any of the suggested fields, the util fires
          // /api/ai-learning/observe/ so the fine-tune corpus captures
          // the actual decision instead of recording the original
          // suggestion as "accepted". Best-effort — never blocks UI.
          fireAiObserveOnSave({ ballId: savedBallId, matchId: id });

          // Refresh the live page so the edit is visible immediately —
          // aggregate, batting/bowling stats, recent balls strip, and the
          // commentary feed all derive from this server state.
          // The aggregate-change useEffect can't be relied on because edits
          // (e.g. analytics-only changes) don't necessarily change totals.
          try {
            const fresh = await loadBallSession(id);
            if (fresh) applyServerState(fresh);
            if (inningsId) {
              const res = await listInningsBalls(inningsId);
              setRecentBalls((res?.data?.results || []).slice(-12));
            }
          } catch { /* keep dialog responsive even if refresh blips */ }
        }}
      />

      {/* Finalising overlay — shown while EC2 ffmpeg streams chunks
          into the master MP4 after the operator hits Stop Recording.
          Auto-mounts when recorder.finalizeState is non-null. Operator
          can dismiss at any time via the X in the header — the master
          concat keeps building server-side regardless, the chunks
          are durable in S3. We just stop showing the polling overlay.
          The "stuck overlay" trap was the worst bug reported by
          testers — never trap them again. */}
      {recorder.finalizeState && (
        <FinalisingOverlay
          finalizeState={recorder.finalizeState}
          onDismiss={() => {
            // The recorder hook doesn't expose a setFinalizeState
            // setter; reload via navigation to clear the state. We
            // go to the scorecard rather than just hiding because
            // post-Stop the operator usually wants the next page
            // anyway. They can navigate elsewhere from there.
            navigate(`/cricket/matches/${id}/scorecard`);
          }}
        />
      )}

      {/* First-15-matches coding walkthrough. Auto-opens once per
          new match for ~15 matches per operator, then stays as a
          tucked-away "?" pill the operator can re-open any time. */}
      <CodingWalkthrough matchId={id} />

      {/* Floating chat assistant — read questions, propose undo / edit, admin rebuild.
          Hidden for Test matches per founder ask (2026-06-02): the chat FAB
          is an AI surface and Test scoring runs without AI features. */}
      {!isTestMatch && (
        <ScorerChat matchId={id} onAfterMutation={async () => {
          try {
            const fresh = await loadBallSession(id);
            if (fresh) applyServerState(fresh);
            if (inningsId) {
              const res = await listInningsBalls(inningsId);
              setRecentBalls((res?.data?.results || []).slice(-12));
            }
          } catch { /* no-op */ }
        }} />
      )}

      {/* Impact-player / Concussion-sub dialog. After a successful swap the
          backend has already updated PlayingXI + the live aggregate (current
          striker / non-striker / bowler), so we just need to refresh the
          session here for the new player to render in the BBB UI. */}
      {/* 2026-10-01 — wicket confirmation. Save Ball on a wicket ball
          opens this instead of saving: the coder checks the dismissed
          batter, dismissal type, bowler and fielders. Correct saves the
          ball; Wrong closes it so the wicket can be fixed. */}
      {wicketConfirmOpen && ball.has_wicket && (() => {
        // Substitute fielders live only in players.fielders.
        const nameOf = (pid) => (pid
          ? getDisplayName(
              [...(players?.batters || []), ...(players?.bowlers || []),
               ...(players?.fielders || [])].find((p) => p?.id === pid))
          : null);
        const outId = ball.dismissed_player_id;
        const stat = getBattingStat(outId);
        // This ball counts for the batter only when they faced it: runs
        // off the bat unless it was a bye / leg-bye / wide, and a ball
        // faced unless it was a wide.
        const facedIt = outId && outId === (ball.striker ?? currentStrikerId);
        const batRuns = facedIt && !ball.is_wide && !ball.is_bye && !ball.is_leg_bye
          ? (ball.completed_runs || 0) : 0;
        const ballsFaced = (stat.balls || 0) + (facedIt && !ball.is_wide ? 1 : 0);
        const typeLabel = (ball.wicket_type || "")
          .toLowerCase().split("_")
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
        const fielders = isCaught ? [["Caught by", ball.caught_by_id]]
          : isStumped ? [["Stumped by", ball.stumped_by_id]]
          : isRunOut ? [["Run out by", ball.run_out_fielder_1_id],
                        ["Assisted by", ball.run_out_fielder_2_id]]
          : [];
        const rows = [
          ["Batter out", `${nameOf(outId) || "—"}  ${(stat.runs || 0) + batRuns} (${ballsFaced})`],
          ["Dismissal", typeLabel],
          ["Bowler", nameOf(ball.bowler ?? currentBowlerId) || "—"],
          ...fielders.filter(([, pid]) => pid).map(([k, pid]) => [k, nameOf(pid)]),
        ];
        return (
          <div className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-4">
            <div
              role="dialog"
              aria-label="Confirm wicket"
              data-testid="wicket-confirm"
              className="bg-bg-secondary border border-red-500/50 rounded-2xl max-w-md w-full p-6 shadow-2xl"
            >
              <h2 className="text-lg font-semibold text-text-primary">Confirm the wicket</h2>
              <p className="text-sm text-text-secondary mt-1 mb-4">
                Check the batter, dismissal and fielders before this ball is saved.
              </p>
              <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-2 text-sm">
                {rows.map(([k, v]) => (
                  <div key={k} className="contents">
                    <dt className="text-text-secondary">{k}</dt>
                    <dd className="text-text-primary font-medium">{v}</dd>
                  </div>
                ))}
              </dl>
              {wicketSummary && (
                <p className="mt-4 text-xs text-text-secondary font-mono">{wicketSummary}</p>
              )}
              <div className="grid grid-cols-2 gap-3 mt-6">
                <button
                  type="button"
                  data-testid="wicket-confirm-wrong"
                  onClick={() => setWicketConfirmOpen(false)}
                  className="px-4 py-3 rounded-lg bg-bg-primary border border-border-default text-text-primary font-semibold"
                >
                  ✗ Wrong — edit
                </button>
                <button
                  type="button"
                  data-testid="wicket-confirm-correct"
                  onClick={() => {
                    confirmedWicketSigRef.current = wicketSig;
                    setWicketConfirmOpen(false);
                    performImmediateSave();
                  }}
                  className="px-4 py-3 rounded-lg bg-green-700 hover:bg-green-800 text-white font-semibold"
                >
                  ✓ Correct — save
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* 2026-06-10 — pre-1st-ball stadium-end picker. Asks which end
          Over 1 is bowled from. Backend auto-derives every subsequent
          ball's bowling_end by alternation. Shown only when the
          stadium has BOTH end names set + no balls scored yet. */}
      {showEndPicker && (
        <div className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-4">
          <div className="bg-bg-secondary border border-blue-500/50 rounded-2xl max-w-md w-full p-6 shadow-2xl">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-400 text-xl shrink-0">⛳</div>
              <div>
                <h2 className="text-lg font-semibold text-text-primary">Which end is Over 1 bowled from?</h2>
                <p className="text-sm text-text-secondary mt-1">
                  Pick the bowler's end for the very first over. Every alternate over will then swap automatically and be saved on each ball.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => handlePickEnd("A")}
                disabled={endPickerBusy}
                className="px-4 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold transition-colors disabled:opacity-60"
              >
                {stadiumEndAName || "End A"}
                <div className="text-[10px] font-normal opacity-70 mt-0.5">End A</div>
              </button>
              <button
                type="button"
                onClick={() => handlePickEnd("B")}
                disabled={endPickerBusy}
                className="px-4 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold transition-colors disabled:opacity-60"
              >
                {stadiumEndBName || "End B"}
                <div className="text-[10px] font-normal opacity-70 mt-0.5">End B</div>
              </button>
            </div>
            {endPickerError && (
              <p className="text-red-400 text-xs mt-3">{endPickerError}</p>
            )}
            {endPickerForceOpen && (
              <button
                type="button"
                onClick={() => { setEndPickerForceOpen(false); setEndPickerError(""); }}
                className="w-full mt-3 px-3 py-2 rounded bg-bg-primary text-text-secondary text-sm"
                disabled={endPickerBusy}
              >
                Cancel
              </button>
            )}
            <p className="text-text-secondary text-[11px] mt-3">
              {stadiumEndAName && stadiumEndBName
                ? "Tip: end names come from the stadium record. Edit them on the Stadium page if these are wrong."
                : "Tip: name the two ends on the Stadium page (e.g. 'Pavilion End', 'Members End') and they'll appear here next time."}
            </p>
          </div>
        </div>
      )}

      {/* 2026-06-10 — one-time recording reminder. Founder ask: scorers
          keep starting matches without arming the recorder, then
          clip-in/clip-out has no video to slice. This blocks the screen
          until the operator acknowledges. */}
      {showRecordingWarning && (
        <div className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-4">
          <div className="bg-bg-secondary border border-amber-500/50 rounded-2xl max-w-md w-full p-6 shadow-2xl">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-400 text-xl shrink-0">!</div>
              <div>
                <h2 className="text-lg font-semibold text-text-primary">Get the match video to the server</h2>
                <p className="text-sm text-text-secondary mt-1">
                  <span className="font-semibold text-text-primary">Live camera / capture card:</span> press{" "}
                  <span className="font-semibold text-text-primary">Start Recording</span> before the first ball.
                </p>
                <p className="text-sm text-text-secondary mt-2">
                  <span className="font-semibold text-text-primary">Coding from a video file:</span> no recording — load
                  the file, then click <span className="font-semibold text-text-primary">● Upload for clipping</span>.
                  You can keep coding while it uploads. Without the upload, QC will have no ball videos for this match.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={dismissRecordingWarning}
              className="w-full px-4 py-2.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-black font-semibold transition-colors"
            >
              OK, got it
            </button>
          </div>
        </div>
      )}

      <SubstitutionModal
        open={!!substitutionModal}
        onClose={() => setSubstitutionModal(null)}
        matchId={id}
        type={substitutionModal || "IMPACT"}
        onApplied={async (sub) => {
          // 2026-06-10 — server-side swap updates PlayingXI + aggregate
          // pointers. SPA-side we have to:
          //   (a) patch the in-progress `ball` React state so chips
          //       swap immediately (displayStrikerId falls back to
          //       ball.striker before aggregate.current_striker)
          //   (b) optimistically swap players.batters / players.bowlers
          //       so the "Select Batter" / "Select Bowler" pickers show
          //       the incoming player even if loadBallSession's network
          //       call lags or fails
          //   (c) re-fetch the session as the authoritative source
          const out = sub?.player_out ? String(sub.player_out) : null;
          const inn = sub?.player_in ? String(sub.player_in) : null;
          const inRow = sub?._inPlayer || null;
          const outRow = sub?._outPlayer || null;

          // (a) — in-progress ball state
          if (out && inn) {
            setBall((prev) => {
              const next = { ...prev };
              if (String(prev?.striker || "") === out) next.striker = inn;
              if (String(prev?.non_striker || "") === out) next.non_striker = inn;
              if (String(prev?.bowler || "") === out) next.bowler = inn;
              return next;
            });
          }

          // (b) — optimistic players-list swap. Build a minimal "batter"
          // / "bowler" record from the squad row + the outgoing player's
          // own batting_position / captain / keeper flags (so the new
          // player slides into the same slot in the XI ordering).
          if (out && inn && inRow) {
            useScoringStore.setState((prev) => {
              const buildEntry = (oldEntry) => ({
                id: String(inn),
                first_name: inRow.first_name || "",
                last_name: inRow.last_name || "",
                nick_name: inRow.nick_name || "",
                jersey_number: inRow.jersey_number || "",
                batting_hand: inRow.batting_hand || oldEntry?.batting_hand || "",
                bowling_hand: inRow.bowling_hand || oldEntry?.bowling_hand || "",
                bowling_hands: inRow.bowling_hands || oldEntry?.bowling_hands || [],
                bowling_style: inRow.bowling_style || oldEntry?.bowling_style || "",
                bowling_type: inRow.bowling_type || oldEntry?.bowling_type || "",
                is_wicket_keeper: oldEntry?.is_wicket_keeper ?? false,
                is_captain: oldEntry?.is_captain ?? false,
              });
              const swapIn = (list) =>
                (list || []).map((p) =>
                  String(p.id) === out ? buildEntry(p) : p
                );
              return {
                players: {
                  ...prev.players,
                  batters: swapIn(prev.players?.batters),
                  bowlers: swapIn(prev.players?.bowlers),
                },
              };
            });
          }

          // (c) — authoritative re-fetch. Surface a console warning if
          // it fails so we'd at least see it in logs; UI is already
          // optimistically correct from (a) + (b).
          try {
            const fresh = await loadBallSession(id);
            if (fresh) applyServerState(fresh);
          } catch (e) {
            console.warn("session refresh after substitution failed", e);
          }
        }}
      />

      <EditPlayingXIModal
        open={editXIOpen}
        onClose={() => { setEditXIOpen(false); setEditXIInitialPlayerId(null); }}
        matchId={id}
        initialPlayerId={editXIInitialPlayerId}
        onOpenSubstitution={(type) => setSubstitutionModal(type || "CONCUSSION")}
        onApplied={async () => {
          // Server rewrote PlayingXI + Ball / BallWicket rows for the
          // wrong-player swap. Re-fetch the ball session so displayed
          // batters / bowlers / dismissal chips reflect the new ids.
          try {
            const fresh = await loadBallSession(id);
            if (fresh) applyServerState(fresh);
          } catch (e) {
            console.warn("session refresh after XI correction failed", e);
          }
        }}
      />

      {/* Match-settings modal — rain-affected + custom powerplay overs.
          Single-int override matches the user's "first N overs" shape for
          T20-style formats. ODI 3-phase override is supported by the
          backend JSON schema but not yet by this UI (TASKS.md follow-up). */}
      {/* 2026-05-29 — Overs Remaining adjustment modal (founder ask).
          Default max_overs per format (T20=20, ODI=50, Test=90) is read
          from match_type / aggregate.max_overs. Operator can edit with
          a reason audit-logged on the PATCH. Auto-reduces by completed
          overs in the display. */}
      {oversRemainingOpen && (() => {
        const matchTypeCode = aggregate?.match_type?.code || "";
        const isTest = matchTypeCode === "TEST";
        const formatDefault = formatDefaultOvers(aggregate?.match_type);
        const currentMax = aggregate?.max_overs || aggregate?.match_type?.max_overs || formatDefault;
        const completedOvers = aggregate?.completed_overs ?? 0;

        // ── New max overs section (per-innings cap) ────────────────────
        const newMaxNum = Number(newMaxOversInput);
        const newMaxValid =
          Number.isFinite(newMaxNum)
          && newMaxNum >= completedOvers
          && newMaxNum <= 200;
        const newMaxDirty = String(currentMax) !== newMaxOversInput;
        const newMaxCanSave =
          newMaxValid && newMaxDirty && !!newMaxReason && !newMaxSaving;
        const handleSaveNewMax = async () => {
          if (!newMaxCanSave || !aggregate?.innings_id) return;
          setNewMaxSaving(true);
          try {
            const apiAxios = (await import("../../api/axios")).default;
            await apiAxios.post("/api/ballbyball/apply-dls/", {
              match_id: id,
              revised_max_overs: newMaxNum,
              adjustment_reason: newMaxReason,
            });
            // Apply optional PP-overs + max-overs-per-bowler overrides
            // alongside the overs cut. Operator-entered values win over
            // the format defaults (see laws.py + _build_powerplay_state).
            // Read the current override first so neither key clobbers
            // the other when only one is touched in this session.
            const ppNum = Number(reducedPPOversInput);
            const mopbNum = Number(reducedMaxOversPerBowlerInput);
            const ppDirty = reducedPPOversInput !== "" && Number.isFinite(ppNum) && ppNum > 0;
            const mopbDirty = reducedMaxOversPerBowlerInput !== "" && Number.isFinite(mopbNum) && mopbNum > 0;
            if (ppDirty || mopbDirty) {
              let existingOverride = {};
              try {
                const r = await apiAxios.get(`/api/matches/matches/${id}/`);
                existingOverride = r.data?.powerplay_override || {};
              } catch { /* fall through with empty override */ }
              const override = { ...existingOverride };
              if (ppDirty) override.powerplay_overs = ppNum;
              if (mopbDirty) override.max_overs_per_bowler = mopbNum;
              await apiAxios.patch(`/api/matches/matches/${id}/`, {
                is_rain_affected: true,
                powerplay_override: override,
              });
            }
            if (newMaxReason === "End current innings" && newMaxNum === completedOvers) {
              try { await endActiveInnings(id); }
              catch (e) { console.warn("auto-end failed", e); }
            }
            const fresh = await loadBallSession(id);
            if (fresh) applyServerState(fresh);
            setReducedPPOversInput("");
            setReducedMaxOversPerBowlerInput("");
            setOversRemainingOpen(false);
          } catch (e) {
            alert(`Save failed: ${e?.response?.data?.detail || e.message}`);
          } finally {
            setNewMaxSaving(false);
          }
        };

        // ── Remaining overs section ────────────────────────────────────
        // For Test: per-DAY semantics — input value mirrors oversLeftToday
        //   (resets to 90 at the start of every new day via Stumps), and
        //   Save writes the per-day counter (setOversLeftManual). The
        //   per-innings max isn't touched (Test has no per-innings cap
        //   unless the operator declares).
        // For limited overs: per-INNINGS semantics — input = max - completed,
        //   Save writes max_overs = completed + remaining via apply-dls.
        const remainingNum = Number(oversRemainingInput);
        const remainingValid =
          Number.isFinite(remainingNum) && remainingNum >= 0 && remainingNum <= 200;
        const remainingSeed = isTest
          ? oversLeftToday
          : Math.max(0, currentMax - completedOvers);
        const remainingDirty = String(remainingSeed) !== oversRemainingInput;
        const remainingCanSave =
          remainingValid && remainingDirty && !!oversAdjustReason && !oversAdjustSaving;
        const handleSaveRemaining = async () => {
          if (!remainingCanSave) return;
          setOversAdjustSaving(true);
          try {
            if (isTest) {
              // Per-day adjustment — just update the client-side counter
              // that drives the top "ov left" chip. No backend write,
              // since the per-day quota is a UX overlay not a model field.
              setOversLeftManual(remainingNum);
              if (oversAdjustReason === "End current innings" && remainingNum === 0) {
                try { await endActiveInnings(id); }
                catch (e) { console.warn("auto-end failed", e); }
                const fresh = await loadBallSession(id);
                if (fresh) applyServerState(fresh);
              }
            } else {
              // Limited overs — set the per-innings max via apply-dls.
              const newCap = completedOvers + remainingNum;
              const apiAxios = (await import("../../api/axios")).default;
              await apiAxios.post("/api/ballbyball/apply-dls/", {
                match_id: id,
                revised_max_overs: newCap,
                adjustment_reason: oversAdjustReason,
              });
              // Optional PP-overs + per-bowler cap overrides — see
              // handleSaveNewMax. GET-then-merge so neither key clobbers
              // the other when only one is touched.
              const ppNum = Number(reducedPPOversInput);
              const mopbNum = Number(reducedMaxOversPerBowlerInput);
              const ppDirty = reducedPPOversInput !== "" && Number.isFinite(ppNum) && ppNum > 0;
              const mopbDirty = reducedMaxOversPerBowlerInput !== "" && Number.isFinite(mopbNum) && mopbNum > 0;
              if (ppDirty || mopbDirty) {
                let existingOverride = {};
                try {
                  const r = await apiAxios.get(`/api/matches/matches/${id}/`);
                  existingOverride = r.data?.powerplay_override || {};
                } catch { /* fall through with empty override */ }
                const override = { ...existingOverride };
                if (ppDirty) override.powerplay_overs = ppNum;
                if (mopbDirty) override.max_overs_per_bowler = mopbNum;
                await apiAxios.patch(`/api/matches/matches/${id}/`, {
                  is_rain_affected: true,
                  powerplay_override: override,
                });
              }
              if (oversAdjustReason === "End current innings" && remainingNum === 0) {
                try { await endActiveInnings(id); }
                catch (e) { console.warn("auto-end failed", e); }
              }
              const fresh = await loadBallSession(id);
              if (fresh) applyServerState(fresh);
            }
            setReducedPPOversInput("");
            setReducedMaxOversPerBowlerInput("");
            setOversRemainingOpen(false);
          } catch (e) {
            alert(`Save failed: ${e?.response?.data?.detail || e.message}`);
          } finally {
            setOversAdjustSaving(false);
          }
        };

        return (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[60] p-4 overflow-y-auto">
            <div className="bg-bg-primary rounded-2xl max-w-md w-full border border-bg-secondary p-6 modal-panel my-8">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-semibold text-text-primary">Overs Remaining</h3>
                <button onClick={() => setOversRemainingOpen(false)} className="text-text-secondary hover:text-text-primary">✕</button>
              </div>
              <div className="grid grid-cols-3 gap-2 mb-4 text-center">
                <div className="bg-bg-secondary/40 rounded-md py-2">
                  <div className="text-[10px] uppercase tracking-wider text-text-secondary">Format default</div>
                  <div className="text-lg font-mono font-bold text-text-primary">{formatDefault}</div>
                </div>
                <div className="bg-bg-secondary/40 rounded-md py-2">
                  <div className="text-[10px] uppercase tracking-wider text-text-secondary">{isTest ? "Today's max" : "Current max"}</div>
                  <div className="text-lg font-mono font-bold text-text-primary">{isTest ? 90 : currentMax}</div>
                </div>
                <div className="bg-emerald-500/15 border border-emerald-500/30 rounded-md py-2">
                  <div className="text-[10px] uppercase tracking-wider text-emerald-300">{isTest ? "Today's remaining" : "Remaining"}</div>
                  <div className="text-lg font-mono font-bold text-emerald-200">
                    {remainingValid ? remainingNum : remainingSeed}
                  </div>
                </div>
              </div>

              {/* ── Optional powerplay-overs override ────────────────────
                  When overs are cut (e.g. 20 → 10) the PP overs need to
                  scale too (4 → 2 for BBL). Operator enters the new PP
                  count here; saving either section below also PATCHes
                  Match.powerplay_override so the selector picks it up.
                  Hidden for Test (no PP concept). */}
              {!isTest && (
                <div className="border border-amber-500/40 bg-amber-500/5 rounded-lg p-3 mb-3">
                  <div className="text-xs font-semibold uppercase tracking-wider text-amber-200 mb-2">
                    Powerplay overs (optional)
                  </div>
                  <input
                    type="number"
                    value={reducedPPOversInput}
                    onChange={(e) => setReducedPPOversInput(e.target.value)}
                    min={0}
                    placeholder="e.g. 2 for a 10-over rain rule"
                    className="w-full bg-bg-secondary text-text-primary px-2 py-1.5 rounded mb-1 font-mono"
                  />
                  <p className="text-[11px] text-text-secondary">
                    Leave blank to keep the existing PP overs. Saved
                    alongside whichever section you submit below;
                    flags the match as rain-affected.
                  </p>
                </div>
              )}

              {/* ── Optional max-overs-per-bowler override ──────────────
                  Rain-curtailed innings need the per-bowler quota to
                  shrink too — T20 20→10 typically drops the cap from
                  4 to 2 (ICC: max_overs / 5). The bowler picker filters
                  out anyone already at quota, AND laws.py rejects the
                  over-start at the backend, so an over-quota bowler
                  cannot be assigned. Hidden for Test (no per-innings cap). */}
              {!isTest && (
                <div className="border border-sky-500/40 bg-sky-500/5 rounded-lg p-3 mb-3">
                  <div className="text-xs font-semibold uppercase tracking-wider text-sky-200 mb-2">
                    Max overs per bowler (optional)
                  </div>
                  <input
                    type="number"
                    value={reducedMaxOversPerBowlerInput}
                    onChange={(e) => setReducedMaxOversPerBowlerInput(e.target.value)}
                    min={0}
                    placeholder="e.g. 2 for a 10-over rain rule"
                    className="w-full bg-bg-secondary text-text-primary px-2 py-1.5 rounded mb-1 font-mono"
                  />
                  <p className="text-[11px] text-text-secondary">
                    Leave blank to keep the format default
                    ({aggregate?.match_type?.max_overs_per_bowler ?? "—"}).
                    Bowlers at quota are filtered out of the picker;
                    backend also blocks over-start past this cap.
                  </p>
                </div>
              )}

              {/* ── Section 1: New max overs (per-innings cap) ───────── */}
              <div className="border border-bg-secondary rounded-lg p-3 mb-3">
                <div className="text-xs font-semibold uppercase tracking-wider text-text-secondary mb-2">New max overs</div>
                <input
                  type="number"
                  value={newMaxOversInput}
                  onChange={(e) => setNewMaxOversInput(e.target.value)}
                  min={1}
                  max={200}
                  className="w-full bg-bg-secondary text-text-primary px-2 py-1.5 rounded mb-2 font-mono"
                />
                <label className="block text-xs text-text-secondary mb-1">Reason</label>
                <select
                  value={newMaxReason}
                  onChange={(e) => setNewMaxReason(e.target.value)}
                  className="w-full bg-bg-secondary text-text-primary px-2 py-1.5 rounded mb-2"
                >
                  <option value="">Select reason…</option>
                  {OVERS_ADJUST_REASONS.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
                <button
                  onClick={handleSaveNewMax}
                  disabled={!newMaxCanSave}
                  className={`w-full py-2 rounded font-semibold ${
                    newMaxCanSave
                      ? "bg-button-primary text-white hover:bg-button-primary/90"
                      : "bg-bg-secondary text-text-secondary opacity-50 cursor-not-allowed"
                  }`}
                >{newMaxSaving ? "Saving…" : "Save new max"}</button>
              </div>

              {/* ── Section 2: Remaining overs ─────────────────────────── */}
              <div className="border border-bg-secondary rounded-lg p-3 mb-3">
                <div className="text-xs font-semibold uppercase tracking-wider text-text-secondary mb-2">
                  {isTest ? "Remaining overs (today)" : "Remaining overs"}
                </div>
                <input
                  type="number"
                  value={oversRemainingInput}
                  onChange={(e) => setOversRemainingInput(e.target.value)}
                  min={0}
                  max={200}
                  className="w-full bg-bg-secondary text-text-primary px-2 py-1.5 rounded mb-2 font-mono"
                />
                <label className="block text-xs text-text-secondary mb-1">Reason</label>
                <select
                  value={oversAdjustReason}
                  onChange={(e) => setOversAdjustReason(e.target.value)}
                  className="w-full bg-bg-secondary text-text-primary px-2 py-1.5 rounded mb-2"
                >
                  <option value="">Select reason…</option>
                  {OVERS_ADJUST_REASONS.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
                <button
                  onClick={handleSaveRemaining}
                  disabled={!remainingCanSave}
                  className={`w-full py-2 rounded font-semibold ${
                    remainingCanSave
                      ? "bg-button-primary text-white hover:bg-button-primary/90"
                      : "bg-bg-secondary text-text-secondary opacity-50 cursor-not-allowed"
                  }`}
                >{oversAdjustSaving ? "Saving…" : "Save remaining"}</button>
              </div>

              <button
                onClick={() => setOversRemainingOpen(false)}
                className="w-full bg-bg-secondary py-2 rounded text-text-secondary"
                disabled={oversAdjustSaving || newMaxSaving}
              >Cancel</button>

              <p className="mt-3 text-[10px] text-text-secondary">
                {isTest
                  ? "New max overs sets the innings cap (declaration). Remaining overs is the per-day quota — resets to 90 at the start of every new day (after Stumps)."
                  : "New max overs sets the innings cap. Remaining overs is computed from cap minus completed. Either input writes the same per-innings value."}
                {" "}Reason is logged for audit.
              </p>
            </div>
          </div>
        );
      })()}

      {/* 2026-05-30 — Penalty modal (founder ask). Records the ball
          from which fielding restrictions (extra fielder inside the
          30-yard circle, slow-over-rate style) apply to the bowling
          side. No runs awarded — this is a marker only. Two-mode
          picker:
            - "Current ball" — marker stamps with no from_ball ref
            - "Past ball"    — picks a saved ball from recentBalls
          POSTs to /apply-penalty/ with is_fielding_penalty=True and
          runs=0; the backend stores it as a marker on the InningsPenalty
          row (is_fielding_penalty=True, runs=0). */}
      {fieldingPenaltyOpen && (() => {
        const canSave = (
          fieldingPenaltyMode === "current"
          || (fieldingPenaltyMode === "past" && !!fieldingPenaltyBallId)
        ) && !fieldingPenaltySaving;
        const handleSavePenalty = async () => {
          if (!canSave || !aggregate?.innings_id) return;
          setFieldingPenaltySaving(true);
          try {
            const apiAxios = (await import("../../api/axios")).default;
            await apiAxios.post("/api/ballbyball/apply-penalty/", {
              match_id: id,
              innings_id: aggregate.innings_id,
              runs: 0,
              awarded_to: "BATTING",
              reason: "FIELDING_RESTRICTION",
              is_fielding_penalty: true,
              from_ball_id: fieldingPenaltyMode === "past" ? fieldingPenaltyBallId : "",
            });
            const fresh = await loadBallSession(id);
            if (fresh) applyServerState(fresh);
            setFieldingPenaltyOpen(false);
          } catch (e) {
            alert(`Save failed: ${e?.response?.data?.detail || e.message}`);
          } finally {
            setFieldingPenaltySaving(false);
          }
        };
        return (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
            <div className="bg-bg-primary rounded-2xl max-w-md w-full border border-amber-500/40 p-6 modal-panel">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-base font-semibold text-amber-200">
                  Penalty · Fielding Restrictions
                </h3>
                <button
                  onClick={() => setFieldingPenaltyOpen(false)}
                  className="text-text-secondary hover:text-text-primary"
                  aria-label="Close"
                >✕</button>
              </div>
              <p className="text-[11px] text-text-secondary mb-4">
                Marks the ball from which fielding restrictions apply
                to the bowling side — an extra fielder must remain
                inside the 30-yard circle (typically triggered by a
                slow over-rate). No runs awarded; this is a marker.
                Pick whether it applies from the current ball or
                retroactively from a saved past ball.
              </p>
              <div className="space-y-2 mb-4">
                <label className="flex items-start gap-2 cursor-pointer p-2 rounded border border-bg-secondary hover:border-amber-500/40">
                  <input
                    type="radio"
                    name="penalty-mode"
                    value="current"
                    checked={fieldingPenaltyMode === "current"}
                    onChange={() => { setFieldingPenaltyMode("current"); setFieldingPenaltyBallId(""); }}
                    className="mt-0.5"
                  />
                  <div className="flex-1">
                    <div className="text-sm text-text-primary font-medium">From current ball</div>
                    <div className="text-[10px] text-text-secondary">Marks the restriction from now — no specific past ball reference.</div>
                  </div>
                </label>
                <label className="flex items-start gap-2 cursor-pointer p-2 rounded border border-bg-secondary hover:border-amber-500/40">
                  <input
                    type="radio"
                    name="penalty-mode"
                    value="past"
                    checked={fieldingPenaltyMode === "past"}
                    onChange={() => setFieldingPenaltyMode("past")}
                    className="mt-0.5"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-text-primary font-medium">From a past ball</div>
                    <div className="text-[10px] text-text-secondary mb-1">Restriction starts retroactively from the chosen delivery.</div>
                    {fieldingPenaltyMode === "past" && (
                      <select
                        value={fieldingPenaltyBallId}
                        onChange={(e) => setFieldingPenaltyBallId(e.target.value)}
                        className="w-full bg-bg-secondary text-text-primary px-2 py-1 rounded text-xs"
                      >
                        <option value="">Select a ball…</option>
                        {(recentBalls || []).slice().reverse().map((b) => {
                          const ballRef = formatBallReference(aggregate?.match_type, { overNumber: b.over_number, ballInOver: b.ball_in_over, overBase: LIVE_OVER_BASE });
                          const label = `${ballRef} — ${b.striker || "?"} ${b.runs_off_bat ?? 0}r${b.has_wicket ? " · wkt" : ""}`;
                          return (
                            <option key={b.id} value={b.id}>{label}</option>
                          );
                        })}
                      </select>
                    )}
                  </div>
                </label>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setFieldingPenaltyOpen(false)}
                  className="flex-1 bg-bg-secondary py-2 rounded text-text-secondary"
                  disabled={fieldingPenaltySaving}
                >Cancel</button>
                <button
                  onClick={handleSavePenalty}
                  disabled={!canSave}
                  className={`flex-1 py-2 rounded font-semibold ${
                    canSave
                      ? "bg-amber-500 text-bg-primary hover:bg-amber-400"
                      : "bg-bg-secondary text-text-secondary opacity-50 cursor-not-allowed"
                  }`}
                >{fieldingPenaltySaving ? "Saving…" : "Apply Penalty"}</button>
              </div>
            </div>
          </div>
        );
      })()}

      {matchSettingsOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
          <div className="bg-bg-primary rounded-2xl max-w-md w-full border border-bg-secondary p-6">
            <h3 className="text-base font-semibold mb-3">Match settings</h3>
            <label className="flex items-center gap-2 text-sm mb-4">
              <input
                type="checkbox"
                checked={isRainAffected}
                onChange={(e) => setIsRainAffected(e.target.checked)}
              />
              <span>Rain affected — override powerplay overs</span>
            </label>
            {isRainAffected && (
              <div className="mb-4">
                <label className="block text-[10px] uppercase tracking-wider text-text-secondary mb-1">
                  Powerplay overs (first N legal overs)
                </label>
                <input
                  type="number"
                  min="0"
                  value={customPPOvers}
                  onChange={(e) => setCustomPPOvers(e.target.value)}
                  className="w-full bg-bg-secondary text-text-primary px-3 py-2 rounded"
                  placeholder="e.g. 4"
                />
                <p className="text-[11px] text-text-secondary mt-1">
                  Leave blank to fall back to the format default.
                </p>
              </div>
            )}
            {isRainAffected && (
              <div className="mb-4">
                <label className="block text-[10px] uppercase tracking-wider text-text-secondary mb-1">
                  Max overs per bowler
                </label>
                <input
                  type="number"
                  min="0"
                  value={customMaxOversPerBowler}
                  onChange={(e) => setCustomMaxOversPerBowler(e.target.value)}
                  className="w-full bg-bg-secondary text-text-primary px-3 py-2 rounded"
                  placeholder={`e.g. ${aggregate?.match_type?.max_overs_per_bowler ?? "4"}`}
                />
                <p className="text-[11px] text-text-secondary mt-1">
                  Leave blank to fall back to the format default
                  ({aggregate?.match_type?.max_overs_per_bowler ?? "—"}).
                  Bowler picker filters out anyone at quota; backend
                  also blocks over-start past this cap.
                </p>
              </div>
            )}
            {/* 2026-06-10 founder ask — "Reduce match overs" lives
                under Match Settings so the operator can curtail a
                rain-affected match (e.g. 20→18 overs) right next to
                the rain / powerplay toggle. Opens the existing Overs
                Remaining modal with the audit reason pre-filled. The
                cut applies to BOTH innings via apply-dls (the same
                code path DLS uses). */}
            <div className="border-t border-bg-secondary/60 pt-3 mb-4">
              <div className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5">Match overs</div>
              <p className="text-xs text-text-secondary mb-2">
                Need to cut total overs (e.g. 20-over match curtailed to 18 by rain)? Use this — the new cap impacts the scorecard + remaining-overs math on every subsequent ball.
              </p>
              <button
                type="button"
                onClick={() => {
                  const mt = aggregate?.match_type?.code;
                  const formatDefault = formatDefaultOvers(aggregate?.match_type);
                  const currentMax =
                    aggregate?.max_overs
                    || aggregate?.match_type?.max_overs
                    || formatDefault;
                  setNewMaxOversInput(String(currentMax));
                  setNewMaxReason("Match overs reduction (rain)");
                  const remainingSeed =
                    mt === "TEST"
                      ? oversLeftToday
                      : Math.max(0, currentMax - (aggregate?.completed_overs ?? 0));
                  setOversRemainingInput(String(remainingSeed));
                  setOversAdjustReason("Match overs reduction (rain)");
                  setMatchSettingsOpen(false);
                  setOversRemainingOpen(true);
                }}
                className="w-full bg-sky-500/15 border border-sky-500/40 text-sky-200 py-2 rounded text-sm font-semibold"
              >
                REDUCE MATCH OVERS
              </button>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setMatchSettingsOpen(false)}
                className="flex-1 px-3 py-2 rounded bg-bg-secondary text-text-secondary"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  const override = {};
                  if (isRainAffected && customPPOvers !== "") {
                    override.powerplay_overs = Number(customPPOvers);
                  }
                  if (isRainAffected && customMaxOversPerBowler !== "") {
                    const n = Number(customMaxOversPerBowler);
                    if (Number.isFinite(n) && n > 0) {
                      override.max_overs_per_bowler = n;
                    }
                  }
                  try {
                    await (await import("../../api/axios")).default.patch(
                      `/api/matches/matches/${id}/`,
                      {
                        is_rain_affected: isRainAffected,
                        powerplay_override: override,
                      }
                    );
                    const fresh = await loadBallSession(id);
                    if (fresh) applyServerState(fresh);
                    setMatchSettingsOpen(false);
                  } catch (e) {
                    alert("Save failed: " + (e?.response?.data?.detail || e.message));
                  }
                }}
                className="flex-1 px-3 py-2 rounded bg-button-primary text-white"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Closure type modal — Law 15 */}
      {closureModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
          <div className="bg-bg-primary rounded-2xl max-w-md w-full border border-bg-secondary p-6">
            <h3 className="text-base font-semibold mb-3">End innings — closure type</h3>
            <p className="text-text-secondary text-xs mb-3">Tag how this innings ended for scorecard reporting.</p>
            <select value={closureType} onChange={(e) => setClosureType(e.target.value)}
              className="w-full bg-bg-secondary text-text-primary px-3 py-2 rounded mb-4">
              <option value="ALL_OUT">All out</option>
              <option value="OVERS_COMPLETED">Overs completed</option>
              <option value="DECLARED">Declared (Test)</option>
              <option value="TARGET_REACHED">Target reached</option>
              <option value="FORFEITED">Forfeited</option>
              <option value="ABANDONED">Abandoned</option>
              <option value="RAIN_AFFECTED">Rain affected</option>
            </select>
            <div className="flex gap-2">
              <button onClick={() => setClosureModal(false)} disabled={closureSaving} className="flex-1 px-3 py-2 rounded bg-bg-secondary text-text-secondary disabled:opacity-50">Cancel</button>
              <button onClick={confirmEndInnings} disabled={closureSaving} className="flex-1 px-3 py-2 rounded bg-button-primary text-white disabled:opacity-50">{closureSaving ? "Ending…" : "End innings"}</button>
            </div>
          </div>
        </div>
      )}

      {/* Result type modal — Law 16 */}
      {resultModal && (
        <div data-testid="bbb-result-modal" className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
          <div className="bg-bg-primary rounded-2xl max-w-md w-full border border-bg-secondary p-6">
            <h3 className="text-base font-semibold mb-3">End match — result type</h3>
            <select data-testid="bbb-result-type" value={resultType} onChange={(e) => setResultType(e.target.value)}
              className="w-full bg-bg-secondary text-text-primary px-3 py-2 rounded mb-4">
              <option value="DECISIVE">Decisive (winner)</option>
              <option value="TIE">Tie</option>
              <option value="NO_RESULT">No result (rain etc.)</option>
              <option value="ABANDONED">Abandoned</option>
              <option value="AWARDED">Awarded (forfeit)</option>
            </select>
            <div className="flex gap-2">
              <button onClick={() => setResultModal(false)} className="flex-1 px-3 py-2 rounded bg-bg-secondary text-text-secondary">Cancel</button>
              <button data-testid="bbb-result-confirm" onClick={confirmEndMatch} className="flex-1 px-3 py-2 rounded bg-button-primary text-white">End match</button>
            </div>
          </div>
        </div>
      )}

      {/* Past balls picker — click any ball to load it into edit mode */}
      {pastBallsOpen && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[60] p-4"
          onClick={() => setPastBallsOpen(false)}
        >
          <div
            className="bg-bg-primary rounded-2xl max-w-2xl w-full max-h-[80vh] border border-bg-secondary shadow-2xl p-4 flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-semibold">
                All balls
                <span className="text-xs text-text-secondary font-normal ml-2">
                  (innings #{currentInningsNumber || "—"})
                </span>
              </h3>
              <button
                onClick={() => setPastBallsOpen(false)}
                className="text-text-secondary hover:text-text-primary text-sm"
              >
                ✕
              </button>
            </div>

            <p className="text-[11px] text-text-secondary mb-2">
              Read-only listing of every ball in this innings. To modify
              a delivery use <strong>Edit Ball</strong> in the right pane
              or the QC screen.
            </p>

            {pastBallsLoading ? (
              <div className="text-center text-text-secondary py-8">Loading…</div>
            ) : pastBalls.length === 0 ? (
              <div className="text-center text-text-secondary py-8">No balls scored yet.</div>
            ) : (
              <div className="overflow-y-auto flex-1 -mx-2 px-2">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-bg-primary text-text-secondary">
                    <tr>
                      <th className="text-left py-1.5 px-2">Over</th>
                      <th className="text-left py-1.5 px-2">Batter</th>
                      <th className="text-left py-1.5 px-2">Bowler</th>
                      <th className="text-right py-1.5 px-2">Runs</th>
                      <th className="text-left py-1.5 px-2">Flags</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pastBalls.slice().reverse().map((b) => (
                      <tr
                        key={b.id}
                        className={`border-t border-border-default/40 ${
                          b.id === editingBallId ? "bg-amber-500/10" : ""
                        }`}
                      >
                        <td className="py-1.5 px-2 font-mono">
                          {formatBallReference(aggregate?.match_type, { overNumber: b.over_number, ballInOver: b.ball_in_over, overBase: LIVE_OVER_BASE })}
                          {!b.is_legal && <span className="text-amber-400">*</span>}
                        </td>
                        <td className="py-1.5 px-2 truncate max-w-[140px]">{b.striker || "—"}</td>
                        <td className="py-1.5 px-2 truncate max-w-[140px]">{b.bowler || "—"}</td>
                        <td className="py-1.5 px-2 font-mono text-right">
                          {b.runs_total}
                          {(b.overthrow_runs || 0) > 0 ? (
                            // Overthrow ball — show off-bat + overthrow
                            // split (e.g. "(1+2 ot)" for 3 coded with 2
                            // tagged as overthrow). runs_off_bat already
                            // includes the overthrow portion under the
                            // founder model (2026-05-30).
                            <span
                              className="text-violet-300 text-[10px] ml-1"
                              title={`Off-bat ${Math.max(0, (b.runs_off_bat || 0) - (b.overthrow_runs || 0))} + overthrow ${b.overthrow_runs}`}
                            >
                              ({Math.max(0, (b.runs_off_bat || 0) - (b.overthrow_runs || 0))}+{b.overthrow_runs} ot)
                            </span>
                          ) : (b.runs_off_bat !== b.runs_total && (
                            <span className="text-text-secondary text-[10px] ml-1">
                              ({b.runs_off_bat}+{b.extras})
                            </span>
                          ))}
                        </td>
                        <td className="py-1.5 px-2">
                          {b.flags?.map((f) => (
                            <span
                              key={f}
                              className="inline-block text-[9px] uppercase mr-1 px-1 rounded bg-bg-secondary text-text-secondary"
                            >
                              {f}
                            </span>
                          ))}
                          {b.wicket && (
                            <span className="inline-block text-[9px] uppercase px-1 rounded bg-red-500/20 text-red-400">
                              W ({b.wicket})
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Shortcuts overlay (toggle with `?`) */}
      {shortcutsOpen && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[60] p-4"
          onClick={() => setShortcutsOpen(false)}
        >
          <div
            className="bg-bg-primary rounded-2xl max-w-md w-full border border-bg-secondary shadow-2xl p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-semibold">Keyboard shortcuts</h3>
              <button
                onClick={() => setShortcutsOpen(false)}
                className="text-text-secondary hover:text-text-primary text-sm"
              >
                ✕
              </button>
            </div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-xs">
              {[
                ["0", "Dot ball"],
                ["1 / 2 / 3", "Runs"],
                ["4 / 6", "Boundary"],
                ["W", "Wide"],
                ["N", "No-ball"],
                ["B", "Bye"],
                ["L", "Leg-bye"],
                ["K", "Toggle wicket"],
                ["Enter / Space", "SAVE BALL"],
                ["U / Backspace", "UNDO"],
                ["T", "Cycle spatial tabs"],
                ["Esc", "Cancel pending save"],
                ["?", "Toggle this help"],
              ].map(([k, desc]) => (
                <div key={k} className="flex items-center gap-2">
                  <kbd className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-bg-secondary border border-border-default text-text-primary">
                    {k}
                  </kbd>
                  <span className="text-text-secondary">{desc}</span>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-text-secondary mt-3">
              Shortcuts are ignored while typing in inputs / dropdowns.
            </p>
          </div>
        </div>
      )}
      {/* Innings-ended modal — only shown for the cases the auto-start
          effect skips: follow-on decision, super-over confirmation, or
          if the operator wants to manually re-trigger after an error.
          In the normal limited-overs flow the auto-start has already
          fired and applyServerState has rendered the new innings, so
          this never appears. */}
      {showInningsEndPrompt && (
        <div data-testid="innings-end-modal" className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-bg-primary rounded-2xl max-w-md w-full border border-bg-secondary shadow-2xl p-6 modal-panel">
            <h3 className="text-xl font-semibold text-text-primary mb-2">
              Innings appears over
            </h3>
            <p className="text-text-secondary mb-4">
              {actions?.ask_follow_on
                ? "Follow-on is available. Pick whether to enforce."
                : actions?.confirm_super_over
                  ? "Match tied — start the super over to break the tie."
                  : "The innings conditions have been met (all-out, overs exhausted, or target chased). End the innings and start the next, or keep scoring if you have a late correction to add (delayed wide / no-ball / DRS review)."}
            </p>
            {!actions?.ask_follow_on && !actions?.confirm_super_over && (
              <div className="flex flex-col gap-2">
                <button
                  data-testid="innings-end-start-next"
                  onClick={handleStartNextInnings}
                  className="w-full px-3 py-2 rounded bg-button-primary text-white"
                >
                  END INNINGS &amp; START NEXT
                </button>
                <button
                  data-testid="innings-end-continue-scoring"
                  onClick={() => setInningsEndDismissedSig(inningsEndSig)}
                  className="w-full px-3 py-2 rounded bg-bg-secondary text-text-secondary hover:text-text-primary text-sm"
                >
                  Continue scoring — add a late correction
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {showMatchEndPrompt && (
        <div data-testid="match-end-modal" className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-bg-primary rounded-2xl max-w-md w-full border border-bg-secondary shadow-2xl p-6 modal-panel">
            <h3 className="text-xl font-semibold text-text-primary mb-2">
              Match appears over
            </h3>
            <p className="text-text-secondary mb-4">
              The last legal ball of the final innings has been recorded. Confirm to end the match and set the result, or keep scoring for late corrections (delayed wide / no-ball / DRS review).
            </p>
            <div className="flex flex-col gap-2">
              <button
                data-testid="match-end-confirm"
                onClick={confirmEndMatch}
                className="w-full px-3 py-2 rounded bg-button-primary text-white"
              >
                CONFIRM &amp; END MATCH
              </button>
              <button
                data-testid="match-end-continue-scoring"
                onClick={() => setMatchEndDismissedSig(inningsEndSig)}
                className="w-full px-3 py-2 rounded bg-bg-secondary text-text-secondary hover:text-text-primary text-sm"
              >
                Continue scoring — add a late correction
              </button>
              <button
                data-testid="match-end-go-to-list"
                onClick={handleGoToMatches}
                className="w-full px-3 py-2 rounded bg-bg-secondary text-text-secondary hover:text-text-primary text-sm"
              >
                Go to matches list
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Just-saved toast — teaches the undo affordance. The Undo button +
          countdown have lived in the action bar for months but most
          scorers never noticed them; QC reports show people manually
          editing balls they could have undone in one keystroke. This
          floats on every successful save while the 30s window is open
          and disappears with it. */}
      {/* T140: visual diff preview before the undo actually fires.
          Floats centered-top so the operator can't miss it. Confirm
          locks in (or pressing U again). Cancel dismisses. Auto-
          times-out in 6s. "Skip preview" persists to localStorage so
          power users don't see it again. */}
      {pendingUndo && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[70] pointer-events-auto animate-fade-in">
          <div className="flex flex-col gap-1 bg-amber-500/95 border border-amber-300 text-bg-main shadow-2xl rounded-lg px-4 py-3 backdrop-blur-sm max-w-md">
            <div className="text-[11px] uppercase tracking-wider font-semibold opacity-80">
              Undo ball {pendingUndo.over}?
            </div>
            <div className="text-sm font-medium">
              {pendingUndo.summary} · {pendingUndo.striker}
            </div>
            <div className="flex items-center gap-2 mt-1">
              <button
                onClick={confirmPendingUndo}
                className="flex-1 px-3 py-1.5 rounded-md bg-bg-main text-amber-300 font-bold text-xs uppercase tracking-wider hover:bg-bg-secondary"
                autoFocus
              >
                Confirm Undo
              </button>
              <button
                onClick={cancelPendingUndo}
                className="px-3 py-1.5 rounded-md bg-amber-600/40 text-bg-main text-xs uppercase tracking-wider hover:bg-amber-700/40"
              >
                Keep
              </button>
            </div>
            <label className="flex items-center gap-1.5 text-[10px] opacity-70 cursor-pointer mt-1">
              <input
                type="checkbox"
                onChange={(e) => {
                  try {
                    if (e.target.checked) localStorage.setItem("zcd_skip_undo_preview", "1");
                    else localStorage.removeItem("zcd_skip_undo_preview");
                  } catch {}
                }}
                className="w-3 h-3"
              />
              Skip this preview from now on (Reset in account settings)
            </label>
          </div>
        </div>
      )}
      {/* T145: reason annotation popover. Appears after a successful
          undo and asks why in 4 one-tap categories. Logged via
          trackEvent("undo_reason"). Self-dismisses in 8s; operator
          can also opt out permanently. The reason data feeds the
          per-persona analytics dash (T127 + T204). */}
      {undoReasonPrompt && (
        <div className="fixed top-36 right-4 z-[68] pointer-events-auto max-w-xs animate-fade-in">
          <div className="bg-bg-primary border border-purple-500/40 rounded-lg shadow-xl px-3 py-2.5">
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="text-[11px] uppercase tracking-wider text-purple-300 font-semibold">
                Why the undo? <span className="opacity-50">(optional)</span>
              </div>
              <button
                onClick={() => setUndoReasonPrompt(null)}
                className="text-text-secondary hover:text-text-primary text-xs leading-none"
                aria-label="Dismiss"
              >×</button>
            </div>
            <div className="grid grid-cols-2 gap-1 mb-2">
              {[
                { k: "stt_mishear",   label: "STT mishear" },
                { k: "wrong_batter",  label: "Wrong batter" },
                { k: "free_hit_missed", label: "Free-hit missed" },
                { k: "wrong_runs",    label: "Wrong runs" },
                { k: "wrong_wicket",  label: "Wrong wicket" },
                { k: "other",         label: "Other" },
              ].map((r) => (
                <button
                  key={r.k}
                  onClick={() => captureUndoReason(r.k, {
                    kind: undoReasonPrompt.kind,
                    count: undoReasonPrompt.count,
                  })}
                  className="text-[11px] px-2 py-1 rounded bg-bg-secondary text-text-primary hover:bg-purple-500/20 hover:text-purple-200 border border-border-default"
                >
                  {r.label}
                </button>
              ))}
            </div>
            <label className="flex items-center gap-1.5 text-[9px] text-text-secondary cursor-pointer">
              <input
                type="checkbox"
                onChange={(e) => {
                  try {
                    if (e.target.checked) localStorage.setItem("zcd_skip_undo_reason", "1");
                    else localStorage.removeItem("zcd_skip_undo_reason");
                  } catch {}
                }}
                className="w-3 h-3"
              />
              Don't ask me again
            </label>
          </div>
        </div>
      )}
      {/* T143: history scrubber panel. Always-visible toggle button
          bottom-right; expands to a 320px panel showing the last
          50 actions (newest first). Each row is one of: save,
          undo, undo_batch, redo. Clicking a save row opens that
          ball in the EditBallDialog if it still exists. */}
      {/* 2026-05-28 — bottom-24 on phone (above the T220.2 action bar
          which is fixed at bottom-0 ~80px tall). 2026-06-05 — desktop
          bumped from xl:bottom-4 to xl:bottom-20 so the History pill
          stacks ABOVE the ScorerChat "Ask" FAB at bottom-3 (was
          overlapping). CodingWalkthrough "?" pill stacks above this
          again at xl:bottom-36. */}
      <div className="fixed bottom-24 xl:bottom-20 right-4 z-[65] pointer-events-auto">
        {!historyOpen ? (
          <button
            onClick={() => setHistoryOpen(true)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-bg-primary border border-border-default shadow-lg text-xs text-text-primary hover:bg-bg-secondary"
            title="Open scoring history"
          >
            <History className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">History</span>
            <span className="font-mono text-[10px] text-text-secondary">{historyLog.log.length}</span>
          </button>
        ) : (
          <div className="w-80 max-h-[60vh] bg-bg-primary border border-border-default rounded-lg shadow-2xl flex flex-col overflow-hidden">
            <div className="px-3 py-2 flex items-center justify-between border-b border-border-default bg-bg-secondary/40">
              <div className="flex items-center gap-1.5 text-xs font-semibold text-text-primary">
                <History className="w-3.5 h-3.5" />
                Scoring history
                <span className="font-mono text-[10px] text-text-secondary">{historyLog.log.length}</span>
              </div>
              <button
                onClick={() => setHistoryOpen(false)}
                className="text-text-secondary hover:text-text-primary text-sm leading-none"
                aria-label="Close history"
              >×</button>
            </div>
            {/* T144: bookmarks strip. Operator drops a marker before
                a risky sequence; later "Restore to X" rolls back
                everything since. Routed through the visual diff
                preview (T140) so a misclick gets a 6s confirm. */}
            <div className="px-3 py-2 border-b border-border-default bg-bg-secondary/20">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] uppercase tracking-wider text-text-secondary">
                  Bookmarks ({bookmarks.bookmarks.length})
                </span>
                <button
                  onClick={() => {
                    const label = window.prompt("Bookmark label (optional):", "") || "";
                    dropBookmark(label);
                  }}
                  disabled={(aggregate?.balls_recorded ?? 0) === 0}
                  className="text-[10px] px-2 py-0.5 rounded bg-purple-500/15 border border-purple-500/40 text-purple-200 hover:bg-purple-500/25 disabled:opacity-40 disabled:hover:bg-purple-500/15"
                  title="Drop a bookmark at the current ball — operator can restore to this point later via batch undo"
                >
                  + Drop bookmark
                </button>
              </div>
              {bookmarks.bookmarks.length === 0 ? (
                <div className="text-[10px] text-text-secondary opacity-70 italic mb-2">
                  No bookmarks yet. Drop one before a risky sequence.
                </div>
              ) : (
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {[...bookmarks.bookmarks].reverse().map((b) => {
                    const ballsSince = (aggregate?.balls_recorded ?? 0) - (b.balls_recorded || 0);
                    return (
                      <div key={b.id} className="flex items-center gap-1.5 text-[11px]">
                        <span className="flex-1 min-w-0 truncate text-text-primary" title={`${b.label} · @${b.last_ball_summary}`}>
                          📍 {b.label}
                        </span>
                        <span className="font-mono text-[9px] text-text-secondary shrink-0">
                          {ballsSince >= 0 ? `+${ballsSince}` : "—"}
                        </span>
                        <button
                          onClick={() => restoreToBookmark(b)}
                          disabled={ballsSince <= 0 || ballsSince > 25}
                          className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 border border-amber-500/40 text-amber-200 hover:bg-amber-500/25 disabled:opacity-30"
                          title={
                            ballsSince <= 0   ? "Nothing to restore from this bookmark" :
                            ballsSince > 25   ? "Too many balls since this bookmark — use QC rebuild" :
                            `Restore to this bookmark — batch-undo ${ballsSince} ball${ballsSince === 1 ? "" : "s"}`
                          }
                        >Restore</button>
                        <button
                          onClick={() => bookmarks.remove(b.id)}
                          className="text-[10px] text-text-secondary hover:text-rose-300 px-1"
                          title="Remove bookmark"
                        >×</button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            {/* T147: ghost-ball trash. Server-side 24h restore
                window. Listed below bookmarks. Each row shows the
                deleted ball + time-to-expiry; Restore replays it. */}
            <div className="px-3 py-2 border-b border-border-default bg-bg-secondary/20">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] uppercase tracking-wider text-text-secondary">
                  🗑 Trash ({trash.length}) <span className="opacity-60 normal-case">· 24h restore</span>
                </span>
                <button
                  onClick={refreshTrash}
                  className="text-[10px] text-text-secondary hover:text-text-primary px-1"
                  title="Refresh trash list"
                >↻</button>
              </div>
              {trash.length === 0 ? (
                <div className="text-[10px] text-text-secondary opacity-70 italic">
                  No deleted balls. Trash holds undone balls for 24h.
                </div>
              ) : (
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {trash.map((t) => {
                    const expiresIn = Math.max(0, new Date(t.expires_at) - Date.now());
                    const hrsLeft = Math.floor(expiresIn / 3_600_000);
                    const minsLeft = Math.floor((expiresIn % 3_600_000) / 60_000);
                    const ttl = hrsLeft > 0 ? `${hrsLeft}h` : `${minsLeft}m`;
                    return (
                      <div key={t.id} className="flex items-center gap-1.5 text-[11px]">
                        <span className="flex-1 min-w-0 truncate text-text-primary" title={t.label}>
                          {t.label}
                        </span>
                        <span className="font-mono text-[9px] text-text-secondary shrink-0" title="Time until permanent purge">
                          {ttl}
                        </span>
                        <button
                          onClick={() => handleRestoreFromTrash(t.id)}
                          className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25"
                          title="Restore this ball — replays the saved payload through the normal ball-save path"
                        >Restore</button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="overflow-y-auto flex-1">
              {historyLog.log.length === 0 ? (
                <div className="px-3 py-4 text-[11px] text-text-secondary text-center">
                  No actions yet. Save a ball to begin.
                </div>
              ) : (
                [...historyLog.log].reverse().map((e) => {
                  const tStr = new Date(e.ts).toLocaleTimeString("en-IN", {
                    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
                  });
                  const icon =
                    e.kind === "save"        ? "✓" :
                    e.kind === "undo"        ? "↺" :
                    e.kind === "undo_batch"  ? "⇤" :
                    e.kind === "redo"        ? "↻" : "·";
                  const color =
                    e.kind === "save"        ? "text-emerald-300" :
                    e.kind === "undo"        ? "text-amber-300" :
                    e.kind === "undo_batch"  ? "text-rose-300" :
                    e.kind === "redo"        ? "text-sky-300" : "text-text-secondary";
                  let desc = "";
                  if (e.kind === "save") {
                    const r = e.runs ?? 0;
                    desc = e.wicket ? `Wicket · ${r} run${r === 1 ? "" : "s"}` :
                           r === 0 ? "Dot ball" :
                           r === 4 ? "Four" : r === 6 ? "Six" : `${r} run${r === 1 ? "" : "s"}`;
                    if (e.striker) desc += ` · ${e.striker}`;
                  } else if (e.kind === "undo") {
                    desc = "Undid last ball";
                  } else if (e.kind === "undo_batch") {
                    desc = `Undid ${e.count} balls (${e.first_over} → ${e.last_over})`;
                  } else if (e.kind === "redo") {
                    const r = e.runs ?? 0;
                    desc = `Re-saved · ${r} run${r === 1 ? "" : "s"}${e.wicket ? " · wicket" : ""}`;
                  }
                  const clickable = e.kind === "save" && e.ball_id;
                  return (
                    <button
                      key={e.id}
                      onClick={clickable ? () => { setEditingBallId(e.ball_id); setHistoryOpen(false); } : undefined}
                      disabled={!clickable}
                      className={`w-full text-left px-3 py-1.5 flex items-start gap-2 text-[11px] border-b border-border-default/50 ${
                        clickable ? "hover:bg-bg-secondary cursor-pointer" : "cursor-default"
                      }`}
                      title={clickable ? "Click to edit this ball" : undefined}
                    >
                      <span className={`font-mono ${color} shrink-0`}>{icon}</span>
                      <span className="font-mono text-[9px] text-text-secondary shrink-0 mt-0.5">{tStr}</span>
                      <span className="text-text-primary leading-tight truncate">{desc}</span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
      {undoSecondsLeft > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] pointer-events-auto">
          <button
            onClick={handleUndoBallRequest}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500/20 border border-amber-400/60 text-amber-100 text-xs shadow-lg backdrop-blur-sm hover:bg-amber-500/30"
            title="Undo the last saved ball — also U on your keyboard"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            <span className="font-semibold">Saved</span>
            <span className="opacity-80">· Press</span>
            <kbd className="px-1.5 py-0.5 rounded bg-amber-600/40 border border-amber-500/60 text-amber-50 text-[10px] font-mono">U</kbd>
            <span className="opacity-80">to undo</span>
            <span className="font-mono opacity-70">({undoSecondsLeft}s)</span>
          </button>
        </div>
      )}

      {selectionModal.open && (
        <div
          data-testid={`pick-${selectionModal.type}-modal`}
          className={`fixed inset-0 flex items-center justify-center z-50 p-4 ${
            selectionModal.type === "bowler"
              ? "bg-black/15"
              : "bg-black/70 backdrop-blur-sm"
          }`}
        >
          {/* For bowler picks: keep the backdrop near-transparent (no blur)
              so the operator can see the live match video / pitch behind
              while choosing the next bowler. Modal panel stays solid +
              ring-bordered so the buttons remain readable against any
              video frame underneath. */}
          <div
            className={`bg-bg-primary rounded-2xl max-w-md w-full border shadow-2xl modal-panel ${
              selectionModal.type === "bowler"
                ? "border-emerald-500/40 ring-1 ring-emerald-500/30"
                : "border-bg-secondary"
            }`}
          >
            <div className="p-6 border-b border-bg-secondary flex items-center justify-between">
              <h3 className="text-xl font-semibold text-text-primary">
                {selectionModal.type === "bowler"
                  ? `Select Bowler${bowlingTeamName ? ` (${bowlingTeamName})` : ""}`
                  : selectionModal.type === "non_striker"
                    ? `Select Non-Striker${battingTeamName ? ` (${battingTeamName})` : ""}`
                    : `Select Striker${battingTeamName ? ` (${battingTeamName})` : ""}`}
              </h3>
              {!selectionModal.forced && (
                <button
                  onClick={closeSelectionModal}
                  className="text-text-secondary hover:text-text-primary transition-colors"
                >
                  ✕
                </button>
              )}
            </div>
            <div className="p-6 space-y-3 max-h-96 overflow-y-auto">
              {modalPlayers.length === 0 ? (
                <div className="text-text-secondary">No players available</div>
              ) : (
                modalPlayers.map((p) => {
                  const isBowlerPick = selectionModal.type === "bowler";
                  // Law 17.5: a bowler cannot bowl consecutive overs.
                  // The Hundred exception: a bowler MAY bowl 2 sets in
                  // a row (10 balls from the same end). We block the
                  // just-bowled bowler only when their consecutive-set
                  // streak has reached max_consecutive_sets_per_bowler:
                  //   T20/ODI/Test → cap 1 → any "just bowled" match blocks
                  //   Hundred      → cap 2 → allow at streak=1 (5 balls),
                  //                          block at streak=2 (10 balls)
                  const isBlocked =
                    isBowlerPick &&
                    p._justBowled &&
                    (p._prevBowlerStreak ?? 0) >= (p._maxConsecutiveSets ?? 1);
                  const bpo = aggregate?.match_type?.balls_per_over ?? 6;
                  const maxPer = aggregate?.match_type?.max_overs_per_bowler ?? null;
                  const isHundred = (aggregate?.match_type?.code || "").toUpperCase() === "HUNDRED";
                  const ballsUsed = ((p._overs ?? 0) * bpo);
                  const quotaLabel = isHundred
                    ? (maxPer != null ? `${ballsUsed}/${maxPer * bpo} balls` : `${ballsUsed} balls`)
                    : `${p._overs ?? 0}${maxPer != null ? `/${maxPer}` : ""} ov`;
                  return (
                    <button
                      key={p.id}
                      data-testid={`pick-${selectionModal.type}-${p.id}`}
                      onClick={() => { if (!isBlocked) handleSelectPlayer(p.id); }}
                      disabled={isBlocked}
                      title={
                        isBlocked
                          ? (isHundred
                              ? "This bowler has just bowled 10 consecutive balls — the Hundred cap is 2 sets per spell."
                              : "Just bowled — Law 17.5 forbids consecutive spells")
                          : undefined
                      }
                      className={`w-full text-left px-4 py-3 rounded-lg border transition-colors flex items-center justify-between ${
                        isBlocked
                          ? "bg-rose-500/5 border-rose-500/30 text-text-secondary cursor-not-allowed opacity-70"
                          : "bg-bg-main text-text-primary border-bg-secondary hover:border-button-primary/30"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {p._justBowled && (
                          <span
                            className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded ${
                              isBlocked
                                ? "bg-rose-500/30 text-rose-200"
                                : "bg-amber-500/25 text-amber-200"
                            }`}
                          >
                            {isHundred
                              ? isBlocked
                                ? "Bowled 10 balls — rotate off"
                                : "Just bowled 5 — same-end continue?"
                              : "Just bowled"}
                          </span>
                        )}
                        <span>
                          {p.nick_name ||
                            [p.first_name, p.last_name].filter(Boolean).join(" ")}
                        </span>
                      </div>
                      {isBowlerPick && (
                        <span className="text-[10px] font-mono text-text-secondary">
                          {quotaLabel}
                        </span>
                      )}
                    </button>
                  );
                })
              )}
            </div>
            {!selectionModal.forced && (
              <div className="p-6 pt-0">
                <button
                  onClick={closeSelectionModal}
                  className="w-full py-3 px-6 bg-bg-secondary text-text-secondary font-medium rounded-lg hover:bg-bg-main transition-colors"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Full cricinfo-style scorecard modal — fetches authoritative data
          from /api/matches/<id>/scorecard/ so it reflects state through the
          last persisted ball. Replaces the previous lightweight 2-column
          summary. */}
      <LiveScorecardModal
        open={scorecardOpen}
        matchId={id}
        onClose={() => setScorecardOpen(false)}
        refreshKey={`${aggregate?.runs ?? 0}-${aggregate?.wickets ?? 0}-${aggregate?.legal_balls ?? 0}-${aggregate?.innings_number ?? 0}`}
      />

      {wicketModalOpen && (
        // T220.4: full-bleed sheet on phone (items-end + rounded-t), centered
        // card on md+. Body scrolls; header + Done footer stay sticky on
        // phone so fielders never fall off the bottom of the screen.
        <div data-testid="wicket-modal" className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-end md:items-center justify-center z-50 p-0 md:p-4">
          <div className="bg-bg-primary rounded-t-2xl md:rounded-2xl max-w-2xl w-full border border-bg-secondary shadow-2xl modal-panel flex flex-col max-h-[95vh] md:max-h-[90vh]">
            <div className="flex items-center justify-between p-4 md:p-6 pb-3 md:pb-4 border-b border-bg-secondary/60 md:border-b-0">
              <h3 className="text-base md:text-xl font-semibold text-text-primary">
                Wicket Details
              </h3>
              <div className="flex items-center gap-3">
                <button
                  onClick={() =>
                    setBall((prev) => ({
                      ...prev,
                      has_wicket: false,
                      wicket_type: null,
                      dismissed_player_id: null,
                      dismissed_by_id: null,
                      caught_by_id: null,
                      stumped_by_id: null,
                      run_out_fielder_1_id: null,
                      run_out_fielder_2_id: null,
                      fielding: {
                        fielder1: null,
                        fielder2: null,
                        action: "",
                        fielding_position: "",
                        runs_saved: null,
                        runs_misfielded: null,
                        overthrow_runs: null,
                      },
                    }))
                  }
                  className="text-text-secondary hover:text-text-primary transition-colors text-sm px-2 py-1"
                >
                  Clear
                </button>
                <button
                  data-testid="wicket-modal-close"
                  onClick={() => setWicketModalOpen(false)}
                  className="text-text-secondary hover:text-text-primary transition-colors text-xl leading-none w-10 h-10 flex items-center justify-center"
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-4 md:px-6 pt-3 md:pt-0 pb-4 md:pb-6">

            {/* Law-21 caution: on a no-ball the only legal dismissals are
                RUN_OUT, HIT_BALL_TWICE, OBSTRUCTING_FIELD. Other types
                (BOWLED/LBW/CAUGHT/STUMPED/HIT_WICKET) cannot stand even
                if the umpire flagged the ball. Surface this so the
                scorer isn't recording an invalid wicket. */}
            {ball.is_no_ball && ball.wicket_type
              && !["RUN_OUT", "HIT_BALL_TWICE", "OBSTRUCTING_FIELD", "MANKAD"].includes(ball.wicket_type) && (
              <div className="mb-3 rounded border border-amber-500/40 bg-amber-500/10 text-amber-200 text-[11px] px-3 py-2">
                ⚠ Law 21.18: on a no-ball, only RUN OUT / HIT BALL TWICE /
                OBSTRUCTING FIELD are legal dismissals. The selected wicket
                type ({ball.wicket_type}) won't be valid — clear it or change
                to a permitted type before saving.
              </div>
            )}

            <div className="mb-4 grid grid-cols-2 sm:grid-cols-3 gap-2">
              {wicketTypes.map((t) => (
                <button
                  key={t}
                  data-testid={`wicket-type-${t}`}
                  aria-label={`Wicket type: ${t}`}
                  title={WICKET_TYPE_HINTS[t] || t}
                  // T220.4: min-h-[56px] gives the wicket-type buttons a
                  // chunky 44px+ tap target on phone without forcing it on
                  // md+ where the modal has more room.
                  onClick={() => {
                    const nextType = ball.wicket_type === t ? null : t;
                    if (nextType === "STUMPED" && !wicketKeeperId) {
                      setWicketKeeperModalOpen(true);
                    }
                    setBall((prev) => {
                      return {
                        ...prev,
                        has_wicket: Boolean(nextType),
                        wicket_type: nextType,
                        fielding: {
                          ...prev.fielding,
                          action:
                            nextType === "BOWLED"
                              ? "Bowled"
                              : (prev.fielding?.action ?? ""),
                        },
                        dismissed_by_id: bowlerWicketTypes.has(nextType)
                          ? (prev.dismissed_by_id ?? displayBowlerId ?? null)
                          : null,
                        caught_by_id:
                          nextType === "CAUGHT" ? prev.caught_by_id : null,
                        stumped_by_id:
                          nextType === "STUMPED"
                            ? (wicketKeeperId ?? prev.stumped_by_id ?? null)
                            : null,
                        run_out_fielder_1_id:
                          nextType === "RUN_OUT"
                            ? prev.run_out_fielder_1_id
                            : null,
                        run_out_fielder_2_id:
                          nextType === "RUN_OUT"
                            ? prev.run_out_fielder_2_id
                            : null,
                      };
                    });
                  }}
                  className={`min-h-[56px] md:min-h-0 px-2 py-3 md:py-2 rounded-lg border text-sm md:text-sm break-words font-medium ${
                    ball.wicket_type === t
                      ? "border-green-500 bg-green-500/10 text-text-primary"
                      : "border-bg-secondary text-text-secondary"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>

            {/* Run-out subtype chip strip — only when RUN_OUT is selected */}
            {ball.wicket_type === "RUN_OUT" && (
              <div className="mb-3 flex items-center gap-2 text-[11px]">
                <span className="text-text-secondary uppercase tracking-wider text-[10px]">Run-out type</span>
                {[
                  { v: "STANDARD", l: "Standard" },
                  { v: "MANKAD",   l: "Mankad (non-striker)" },
                ].map(({ v, l }) => (
                  <button
                    key={v}
                    onClick={() => setBall((prev) => ({ ...prev, run_out_subtype: prev.run_out_subtype === v ? "" : v }))}
                    className={`px-2 py-1 rounded ${ball.run_out_subtype === v ? "bg-green-700 text-white" : "bg-bg-secondary text-text-primary"}`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            )}

            <div className="space-y-2">
              <div>
                <label className="mr-2">Dismissed Player</label>
                <select
                  value={ball.dismissed_player_id ?? ""}
                  onChange={(e) => {
                    const nextPlayer = e.target.value || null;
                    // Cricket law: most wicket types only apply to the
                    // striker (bowled, lbw, caught, stumped, hit-wicket).
                    // But a handful CAN dismiss / leave the non-striker:
                    //   RUN_OUT, MANKAD, RETIRED_HURT, RETIRED_OUT,
                    //   RETIRED_NOT_OUT, OBSTRUCTING_FIELD, TIMED_OUT
                    // (Krishna's 2026-05-25 report: couldn't pick the
                    // non-striker for retired hurt.)
                    const NON_STRIKER_ALLOWED = new Set([
                      "RUN_OUT", "MANKAD",
                      "RETIRED_HURT", "RETIRED_OUT", "RETIRED_NOT_OUT",
                      "OBSTRUCTING_FIELD", "TIMED_OUT",
                    ]);
                    if (
                      nextPlayer &&
                      nextPlayer === displayNonStrikerId &&
                      !NON_STRIKER_ALLOWED.has(ball.wicket_type || "")
                    ) {
                      alert(
                        `${ball.wicket_type || "This wicket type"} can only dismiss the striker. ` +
                        "Run-out, mankad, retired, obstruction, and timed-out can dismiss either end."
                      );
                      return;
                    }
                    setBall((prev) => ({
                      ...prev,
                      dismissed_player_id: nextPlayer,
                    }));
                  }}
                >
                  <option value="" className="bg-black text-white">
                    Select player
                  </option>
                  {players?.batters?.map((p) => (
                    <option
                      key={p.id}
                      value={p.id}
                      className="bg-black text-white"
                    >
                      { p?.nick_name || p?.name || [p?.first_name, p?.last_name].filter(Boolean).join(" ") || "Unknown"}
                    </option>
                  ))}
                </select>
              </div>

              {bowlerWicketTypes.has(ball.wicket_type) && (
                <div>
                  <label className="mr-2">Dismissed By (Bowler)</label>
                  <select
                    value={ball.dismissed_by_id ?? ""}
                    onChange={(e) =>
                      setBall((prev) => ({
                        ...prev,
                        dismissed_by_id: e.target.value || null,
                      }))
                    }
                  >
                    <option value="" className="bg-black text-white">
                      Select bowler
                    </option>
                    {players?.bowlers?.map((p) => (
                      <option
                        key={p.id}
                        value={p.id}
                        className="bg-black text-white"
                      >
                        {getDisplayName(p)}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Wicket-fielder pickers — render XI in an optgroup PLUS
                  bowling-team squad reserves so substitute fielders (Law 24)
                  can catch / run-out / stump. Inline-styled optgroup so
                  the labels are readable on the dark dropdown panel.

                  T220.4: on phone (<md) we render the SAME pool as an
                  inline button grid so the operator sees every fielder
                  at once with chunky tap targets — no nested OS dropdown
                  scroll. md+ keeps the native select (works fine with
                  mouse). */}
              {(() => {
                const xiPlayers = players?.bowlers || [];
                const subs = subFielders || [];
                const optgroupStyle = {
                  background: "#1f2937",
                  color: "#fef3c7",
                  fontStyle: "normal",
                  fontWeight: 700,
                };
                const renderOptions = () => (
                  <>
                    <optgroup label="— Playing XI —" style={optgroupStyle}>
                      {xiPlayers.map((p) => (
                        <option key={p.id} value={p.id} className="bg-black text-white">
                          {getDisplayName(p)}
                        </option>
                      ))}
                    </optgroup>
                    {subs.length > 0 && (
                      <optgroup label="— Substitute fielders (squad reserves) —" style={optgroupStyle}>
                        {subs.map((p) => (
                          <option key={p.id} value={p.id} className="bg-black text-white">
                            {getDisplayName(p)} (sub)
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </>
                );
                // T220.4: phone-only inline grid. Same data, chunky
                // tap targets, sub tag inline. selectedId highlights
                // the active fielder; onPick writes the chosen id.
                const PhoneFielderGrid = ({ selectedId, onPick }) => (
                  <div className="md:hidden mt-1">
                    <div className="text-[10px] uppercase tracking-wider text-text-secondary/70 mb-1">
                      Playing XI
                    </div>
                    <div className="grid grid-cols-2 gap-1.5">
                      {xiPlayers.map((p) => {
                        const active = selectedId === p.id;
                        return (
                          <button
                            key={p.id}
                            type="button"
                            onClick={() => onPick(active ? null : p.id)}
                            className={`min-h-[44px] px-2 py-2 rounded-lg border text-xs text-left break-words leading-tight ${
                              active
                                ? "border-green-500 bg-green-500/10 text-text-primary font-medium"
                                : "border-bg-secondary text-text-secondary"
                            }`}
                          >
                            {getDisplayName(p)}
                          </button>
                        );
                      })}
                    </div>
                    {subs.length > 0 && (
                      <>
                        <div className="text-[10px] uppercase tracking-wider text-text-secondary/70 mt-2 mb-1">
                          Substitute fielders
                        </div>
                        <div className="grid grid-cols-2 gap-1.5">
                          {subs.map((p) => {
                            const active = selectedId === p.id;
                            return (
                              <button
                                key={p.id}
                                type="button"
                                onClick={() => onPick(active ? null : p.id)}
                                className={`min-h-[44px] px-2 py-2 rounded-lg border text-xs text-left break-words leading-tight ${
                                  active
                                    ? "border-green-500 bg-green-500/10 text-text-primary font-medium"
                                    : "border-bg-secondary text-text-secondary"
                                }`}
                              >
                                {getDisplayName(p)} <span className="text-text-secondary/60">(sub)</span>
                              </button>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </div>
                );
                return (
                  <>
                    {isCaught && (
                      <div>
                        <label className="mr-2 block md:inline">Caught By</label>
                        <select
                          data-testid="wicket-fielder-caught-by"
                          value={ball.caught_by_id ?? ""}
                          onChange={(e) =>
                            setBall((prev) => ({
                              ...prev,
                              caught_by_id: e.target.value || null,
                            }))
                          }
                          className="hidden md:inline-block"
                        >
                          <option value="" className="bg-black text-white">Select fielder</option>
                          {renderOptions()}
                        </select>
                        <PhoneFielderGrid
                          selectedId={ball.caught_by_id}
                          onPick={(id) => setBall((prev) => ({ ...prev, caught_by_id: id }))}
                        />
                      </div>
                    )}

                    {isStumped && (
                      <div>
                        <label className="mr-2 block md:inline">Stumped By</label>
                        <select
                          data-testid="wicket-fielder-stumped-by"
                          value={ball.stumped_by_id ?? ""}
                          onChange={(e) =>
                            setBall((prev) => ({
                              ...prev,
                              stumped_by_id: e.target.value || null,
                            }))
                          }
                          className="hidden md:inline-block"
                        >
                          <option value="" className="bg-black text-white">Select wicket-keeper</option>
                          {renderOptions()}
                        </select>
                        <PhoneFielderGrid
                          selectedId={ball.stumped_by_id}
                          onPick={(id) => setBall((prev) => ({ ...prev, stumped_by_id: id }))}
                        />
                      </div>
                    )}

                    {isRunOut && (
                      <div>
                        <label className="mr-2 block md:inline">Run Out Fielder 1</label>
                        <select
                          data-testid="wicket-fielder-run-out-1"
                          value={ball.run_out_fielder_1_id ?? ""}
                          onChange={(e) =>
                            setBall((prev) => ({
                              ...prev,
                              run_out_fielder_1_id: e.target.value || null,
                            }))
                          }
                          className="hidden md:inline-block"
                        >
                          <option value="" className="bg-black text-white">Select fielder</option>
                          {renderOptions()}
                        </select>
                        <PhoneFielderGrid
                          selectedId={ball.run_out_fielder_1_id}
                          onPick={(id) => setBall((prev) => ({ ...prev, run_out_fielder_1_id: id }))}
                        />
                      </div>
                    )}

                    {isRunOut && (
                      <div>
                        <label className="mr-2 block md:inline">Run Out Fielder 2 (Optional)</label>
                        <select
                          data-testid="wicket-fielder-run-out-2"
                          value={ball.run_out_fielder_2_id ?? ""}
                          onChange={(e) =>
                            setBall((prev) => ({
                              ...prev,
                              run_out_fielder_2_id: e.target.value || null,
                            }))
                          }
                          className="hidden md:inline-block"
                        >
                          <option value="" className="bg-black text-white">Select fielder</option>
                          {renderOptions()}
                        </select>
                        <PhoneFielderGrid
                          selectedId={ball.run_out_fielder_2_id}
                          onPick={(id) => setBall((prev) => ({ ...prev, run_out_fielder_2_id: id }))}
                        />
                      </div>
                    )}
                  </>
                );
              })()}

              <div>
                <label className="text-xs text-text-secondary">
                  Decision By
                </label>
                <select
                  value={ball.decision_given_by_umpire ?? ""}
                  onChange={(e) =>
                    setBall((prev) => ({
                      ...prev,
                      decision_given_by_umpire: e.target.value || null,
                      drs: {
                        ...prev.drs,
                        decision_given_by_umpire: e.target.value || null,
                      },
                    }))
                  }
                  className="bg-bg-secondary text-text-primary px-2 py-1 rounded w-full"
                >
                  <option value="">None</option>
                  {/* Two on-field umpires (Match.umpires M2M, ≤ 2). */}
                  {(umpires || []).map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.display_label || u.short_name || u.name || u.id}
                    </option>
                  ))}
                  {/* 3rd (TV) and Reserve umpire — always shown so the
                      operator can pick them. When the match has a
                      specific umpire configured, the option carries that
                      umpire's ID; when none is set, the option is
                      disabled with a hint to configure it on the match
                      details page. */}
                  {thirdUmpireOptions.length > 0 ? (
                    thirdUmpireOptions.map((u) => (
                      <option key={`3rd-${u.id}`} value={u.id}>
                        {(u.display_label || u.short_name || u.name || u.id) + " (3rd umpire)"}
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>3rd umpire — not set on match</option>
                  )}
                  {fourthUmpireOptions.length > 0 ? (
                    fourthUmpireOptions.map((u) => (
                      <option key={`4th-${u.id}`} value={u.id}>
                        {(u.display_label || u.short_name || u.name || u.id) + " (Reserve umpire)"}
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>Reserve umpire — not set on match</option>
                  )}
                </select>
              </div>
            </div>
            </div>
            {/* T220.4: sticky Done footer — phone-only. The desktop modal
                already has the ✕ in the header and saving happens via the
                main Save Ball button, but on phone a chunky "Done" makes
                it explicit that this modal is just a form widget — close
                here, save the ball from the bottom action bar. */}
            <div className="md:hidden border-t border-bg-secondary/60 p-3" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
              <button
                data-testid="wicket-modal-done"
                onClick={() => setWicketModalOpen(false)}
                className="w-full min-h-[48px] bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {moreOptionsOpen && (
        // 2026-05-29 — wider panel (max-w-md → max-w-2xl), bounded
        // height + scrollable body (flex-col + max-h-[85vh] + inner
        // overflow-y-auto) so the long list of options never overflows
        // the viewport on smaller laptops. Founder ask.
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-bg-primary rounded-2xl max-w-2xl w-full border border-bg-secondary shadow-2xl modal-panel flex flex-col max-h-[85vh]">
            <div className="flex items-center justify-between px-6 pt-5 pb-3 border-b border-bg-secondary/60">
              <h3 className="text-xl font-semibold text-text-primary">
                More Options
              </h3>
              <button
                onClick={() => setMoreOptionsOpen(false)}
                className="text-text-secondary hover:text-text-primary transition-colors text-xl leading-none w-9 h-9 flex items-center justify-center"
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 pt-4 pb-6">
            <div className="space-y-3">
              {/* 2026-06-09 — Floodlights toggle. Stays inline in the
                  More Options list (no separate modal) so the operator
                  can flip it in one tap during a day-night switchover.
                  Tri-state pill: ON / OFF / —. */}
              <button
                onClick={handleToggleFloodlights}
                disabled={floodlightsBusy}
                className="w-full bg-bg-secondary py-2 rounded flex items-center justify-between px-3 disabled:opacity-60"
              >
                <span className="flex items-center gap-2">
                  <span aria-hidden="true">💡</span>
                  FLOODLIGHTS
                </span>
                <span className={
                  floodlightsOn === true
                    ? "px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider bg-amber-500/25 text-amber-200 border border-amber-400/50"
                    : floodlightsOn === false
                    ? "px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider bg-bg-secondary text-text-secondary border border-border-default"
                    : "px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider text-text-secondary border border-border-default"
                }>
                  {floodlightsOn === true ? "ON" : floodlightsOn === false ? "OFF" : "—"}
                </span>
              </button>
              <button
                onClick={async () => {
                  try {
                    const r = await (await import("../../api/axios")).default.get(`/api/matches/matches/${id}/`);
                    setIsRainAffected(!!r.data?.is_rain_affected);
                    setCustomPPOvers(
                      r.data?.powerplay_override?.powerplay_overs ?? ""
                    );
                    setCustomMaxOversPerBowler(
                      r.data?.powerplay_override?.max_overs_per_bowler ?? ""
                    );
                  } catch { /* fall back to fresh state */ }
                  setMoreOptionsOpen(false);
                  setMatchSettingsOpen(true);
                }}
                className="w-full bg-bg-secondary py-2 rounded"
              >
                MATCH SETTINGS (RAIN / POWERPLAY)
              </button>
              {/* 2026-05-30 — Penalty (founder ask). Sits right
                  next to Match Settings (powerplay). Records the ball
                  from which fielding restrictions (extra fielder
                  inside the 30-yard circle, slow-over-rate style)
                  apply to the bowling side. No runs awarded — this
                  is a marker only. */}
              <button
                onClick={() => {
                  setFieldingPenaltyMode("current");
                  setFieldingPenaltyBallId("");
                  setMoreOptionsOpen(false);
                  setFieldingPenaltyOpen(true);
                }}
                className="w-full bg-amber-500/15 border border-amber-500/40 text-amber-200 py-2 rounded"
              >
                PENALTY (FIELDING RESTRICTION)
              </button>
              {/* BBL Power Surge. Only renders when the match is
                  T20_BBL AND the batting team is allowed to fire the
                  2-over Surge from the current over (over >= 11,
                  Surge not yet activated, fits in the innings). The
                  button locks in overs (current, current+1) as the
                  Power Surge window — max 2 fielders outside the circle
                  (same as the initial 4-over powerplay). BBL rule:
                  available any time after the 10th over. */}
              {isBblMatch && ppSurgeAvailable && (
                <div className="space-y-1">
                  <button
                    onClick={handleStartPowerSurge}
                    className="w-full bg-purple-500/15 border border-purple-500/40 text-purple-200 py-2 rounded"
                  >
                    ⚡ START POWER SURGE (OVER {ppCurrentOver} + {ppCurrentOver + 1})
                  </button>
                  {/* Activation-window hint. Tells the operator the
                      full range of overs the batting team can still
                      pick from (start = max(current, 11), end =
                      max_overs - 1) so they know when the option
                      expires. */}
                  {ppWindowStart != null && ppWindowEnd != null && (
                    <div className="text-[10px] text-text-secondary px-1 text-center">
                      Batting team's window: overs{" "}
                      <span className="text-purple-300 font-mono">
                        {ppWindowStart}
                      </span>{" "}
                      →{" "}
                      <span className="text-purple-300 font-mono">
                        {ppWindowEnd}
                      </span>{" "}
                      (must finish by over {ppWindowEnd + 1}).
                    </div>
                  )}
                </div>
              )}
              {isBblMatch && ppSurgeStart && (
                <div className="w-full bg-bg-secondary border border-border-default text-text-secondary text-xs py-2 px-3 rounded">
                  Power Surge already used (overs {ppSurgeStart}-{ppSurgeStart + 1}).
                </div>
              )}
              {/* T10 floating powerplay. Offered at the start of any
                  over in the window (3-9 on a full innings) until the
                  batting team takes it; locks the CURRENT over in. */}
              {hasFloatingPp && floatingPpAvailable && (
                <div className="space-y-1">
                  <button
                    onClick={handleTakeFloatingPowerplay}
                    className="w-full bg-purple-500/15 border border-purple-500/40 text-purple-200 py-2 rounded"
                  >
                    ⚡ TAKE FLOATING POWERPLAY (OVER {ppCurrentOver})
                  </button>
                  {floatingPpWindowEnd != null && (
                    <div className="text-[10px] text-text-secondary px-1 text-center">
                      Batting team can take it up to over{" "}
                      <span className="text-purple-300 font-mono">{floatingPpWindowEnd}</span>.
                    </div>
                  )}
                </div>
              )}
              {hasFloatingPp && floatingPpOver && (
                <div className="w-full bg-bg-secondary border border-border-default text-text-secondary text-xs py-2 px-3 rounded">
                  Floating powerplay already used (over {floatingPpOver}).
                </div>
              )}
              {/* The Hundred Strategic Timeout button. Only renders
                  while the batting side still has its (single) timeout
                  to spend — backend gates HUNDRED-only + once-per-innings.
                  Records the current ball as the marker so the timeline
                  and chip on the header line up. */}
              {hundredStrategicTimeoutAvailable && (
                <button
                  onClick={handleTakeStrategicTimeout}
                  className="w-full bg-indigo-500/15 border border-indigo-500/40 text-indigo-200 py-2 rounded"
                >
                  ⏸ TAKE STRATEGIC TIMEOUT
                </button>
              )}
              {isHundredMatch && hundredStrategicTimeoutBall != null && (
                <div className="w-full bg-bg-secondary border border-border-default text-text-secondary text-xs py-2 px-3 rounded">
                  Strategic timeout taken at ball {hundredStrategicTimeoutBall}.
                </div>
              )}
              <button onClick={handleEndCurrentInnings} className="w-full">
                END CURRENT INNINGS
              </button>
              {/* 2026-06-05 — Phantom-ball stop-gap (founder ask).
                  Two adjacent buttons for irregular umpiring:
                    1. END THIS OVER NOW — umpire called the over after
                       fewer than 6 real balls; fill the over with
                       phantom balls so it completes cleanly.
                    2. ADD ONE EXTRA BALL — umpire bowled an extra
                       (7th/8th/…); add one phantom delivery at the
                       next slot.
                  Phantom balls count toward legal_balls + completed_overs
                  + bowler.balls only — runs, wickets, batter stats,
                  partnership runs are ALL unaffected. */}
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={async () => {
                    if (!inningsId) return;
                    const unit = isHundredMatch ? "set" : "over";
                    const ok = window.confirm(
                      `End this ${unit} now?\n\n` +
                      `The current ${unit} will be filled with phantom balls so it ` +
                      "completes cleanly. Phantom balls do not affect runs, " +
                      "wickets, batter / bowler stats, or the partnership — " +
                      `they only keep the ${unit} count math correct.`
                    );
                    if (!ok) return;
                    setMoreOptionsOpen(false);
                    try {
                      const { endOverNow } = await import("../../api/ballbyball.api");
                      const r = await endOverNow(inningsId);
                      flashAiToast(true, `${isHundredMatch ? "Set" : "Over"} completed with ${r?.data?.phantoms_created ?? 0} phantom ball(s).`);
                      const fresh = await loadBallSession(id);
                      if (fresh) applyServerState(fresh);
                      listInningsBalls(inningsId)
                        .then((rb) => setRecentBalls((rb?.data?.results || []).slice(-12)))
                        .catch(() => {});
                    } catch (e) {
                      flashAiToast(false, `Couldn't end the ${isHundredMatch ? "set" : "over"} — ` + (e?.response?.data?.detail || e?.message || "unknown error"));
                    }
                  }}
                  className="w-full bg-amber-500/15 border border-amber-500/40 text-amber-200 py-2 rounded text-sm"
                  title={`Fill the current ${isHundredMatch ? "set" : "over"} with phantom balls so it completes (no stat impact)`}
                >
                  {isHundredMatch ? "END THIS SET NOW" : "END THIS OVER NOW"}
                </button>
                <button
                  onClick={async () => {
                    if (!inningsId) return;
                    const ok = window.confirm(
                      "Add one extra (phantom) ball?\n\n" +
                      "Records an extra delivery at the next slot. Does not " +
                      "affect runs, wickets, batter / bowler stats, or " +
                      "partnership — use for irregular umpiring (umpire " +
                      "bowled a 7th/8th ball)."
                    );
                    if (!ok) return;
                    setMoreOptionsOpen(false);
                    try {
                      const { addPhantomBall } = await import("../../api/ballbyball.api");
                      const r = await addPhantomBall(inningsId);
                      flashAiToast(true, `Phantom ball added at ${r?.data?.over_number}.${r?.data?.ball_in_over}.`);
                      const fresh = await loadBallSession(id);
                      if (fresh) applyServerState(fresh);
                      listInningsBalls(inningsId)
                        .then((rb) => setRecentBalls((rb?.data?.results || []).slice(-12)))
                        .catch(() => {});
                    } catch (e) {
                      flashAiToast(false, "Couldn't add phantom ball — " + (e?.response?.data?.detail || e?.message || "unknown error"));
                    }
                  }}
                  className="w-full bg-amber-500/15 border border-amber-500/40 text-amber-200 py-2 rounded text-sm"
                  title="Add one phantom ball at the next slot (no stat impact)"
                >
                  ADD ONE EXTRA BALL
                </button>
              </div>
              {/* 2026-05-29 — Overs Remaining (founder ask). Opens a
                  separate sub-modal where the operator can adjust the
                  innings's max_overs with an explicit reason (rain /
                  bad light / etc.). Default comes from match_type. */}
              <button
                onClick={() => {
                  const mt = aggregate?.match_type?.code;
                  const formatDefault = formatDefaultOvers(aggregate?.match_type);
                  const currentMax =
                    aggregate?.max_overs
                    || aggregate?.match_type?.max_overs
                    || formatDefault;
                  // New max overs input — pre-filled with current effective
                  // per-innings cap.
                  setNewMaxOversInput(String(currentMax));
                  setNewMaxReason("");
                  // Remaining overs input — for Test this is per-DAY
                  // (90 - bowled today) so the modal reads "90 left" at
                  // the start of every new day after Stumps. For limited
                  // overs it's per-innings (max - completed).
                  const remainingSeed =
                    mt === "TEST"
                      ? oversLeftToday
                      : Math.max(0, currentMax - (aggregate?.completed_overs ?? 0));
                  setOversRemainingInput(String(remainingSeed));
                  setOversAdjustReason("");
                  setMoreOptionsOpen(false);
                  setOversRemainingOpen(true);
                }}
                className="w-full bg-bg-secondary py-2 rounded"
              >
                OVERS REMAINING
              </button>
              {/* 2026-06-10 founder ask — rain-affected overs/balls reduction.
                  Originally gated to legal_balls===0 (pre-first-ball only),
                  ungated 2026-07-18 so the Hundred (and every other format)
                  can be curtailed mid-innings from a single entry point.
                  Reason auto-stamps "Rain affected at start" before ball 1,
                  else "Rain affected" so the audit log reflects when the
                  cut was made. Label switches to "REDUCE BALLS" for the
                  Hundred (balls-per-over=5) so a Hundred scorer isn't
                  asked to think in overs on the trigger button.
                  (`is_rain_affected` boolean removed from match creation
                  form 2026-06-10 — this is its replacement.) */}
              <button
                onClick={() => {
                  const mt = aggregate?.match_type?.code;
                  const formatDefault = formatDefaultOvers(aggregate?.match_type);
                  const currentMax =
                    aggregate?.max_overs
                    || aggregate?.match_type?.max_overs
                    || formatDefault;
                  const preFirstBall = (aggregate?.legal_balls ?? 0) === 0;
                  const reason = preFirstBall ? "Rain affected at start" : "Rain affected";
                  setNewMaxOversInput(String(currentMax));
                  setNewMaxReason(reason);
                  const remainingSeed =
                    mt === "TEST"
                      ? oversLeftToday
                      : Math.max(0, currentMax - (aggregate?.completed_overs ?? 0));
                  setOversRemainingInput(String(remainingSeed));
                  setOversAdjustReason(reason);
                  setMoreOptionsOpen(false);
                  setOversRemainingOpen(true);
                }}
                className="w-full bg-sky-500/15 border border-sky-500/40 text-sky-200 py-2 rounded"
                title={isHundredMatch
                  ? "Cut total balls because of rain — works before OR during the innings. Modal input is in overs (1 over = 5 balls); e.g. enter 10 for a 50-ball innings."
                  : "Cut total overs because of rain — works before OR during the innings. Audit reason is auto-stamped."}
              >
                {isHundredMatch ? "RAIN AFFECTED — REDUCE BALLS" : "RAIN AFFECTED — REDUCE OVERS"}
              </button>
              {/* Rain-affected end-innings shortcut — used when play is
                  stopped due to weather. Tags closure_type=RAIN_AFFECTED,
                  flips match.is_rain_affected=true (so DLS unlocks for the
                  chase), and starts the next innings in one click. */}
              <button
                onClick={handleEndInningsRainAffected}
                className="w-full bg-blue-500/15 border border-blue-500/40 text-blue-300 py-2 rounded"
              >
                END INNINGS — RAIN AFFECTED
              </button>
              {/* Rain-affected toggle — gates the DLS section below. The
                  operator flips this when rain interrupts the match; saving
                  immediately PATCHes the match record so the rest of the app
                  (Apply DLS, scorecard footer, AI report) sees the same
                  state. Persisted into match.is_rain_affected. */}
              <button
                onClick={async () => {
                  const next = !isRainAffected;
                  setIsRainAffected(next); // optimistic
                  try {
                    await (await import("../../api/axios")).default.patch(
                      `/api/matches/matches/${id}/`,
                      { is_rain_affected: next },
                    );
                  } catch (e) {
                    setIsRainAffected(!next); // rollback on failure
                    alert("Couldn't update rain-affected state: " + (e?.response?.data?.detail || e.message));
                  }
                }}
                className={`w-full py-2 rounded border ${
                  isRainAffected
                    ? "bg-blue-500/15 border-blue-500/40 text-blue-300"
                    : "bg-bg-secondary border-border-default text-text-primary"
                }`}
              >
                {isRainAffected
                  ? "✓ RAIN AFFECTED · DLS UNLOCKED"
                  : "RAIN AFFECTED (TAP TO ENABLE DLS)"}
              </button>
              {/* Test-cricket declaration — direct path (no closure modal). Routes
                  through the dedicated declareInnings endpoint which sets
                  closure_type=DECLARED atomically. Only shown for Test format. */}
              {aggregate?.match_type?.code === "TEST" && (
                <button
                  data-testid="bbb-declare-innings"
                  onClick={async () => {
                    if (!window.confirm("Declare this innings closed? The innings will end immediately and the scorecard will show 'declared'.")) return;
                    setMoreOptionsOpen(false);
                    await declareInnings(id);
                    const res = await startNextInnings(id);
                    applyServerState(res.data);
                  }}
                  className="w-full bg-amber-500/15 border border-amber-500/40 text-amber-300 py-2 rounded"
                >
                  DECLARE INNINGS
                </button>
              )}
              <button data-testid="bbb-end-match" onClick={handleEndCurrentMatch} className="w-full">
                END CURRENT MATCH
              </button>

              {/* Player substitutions — Impact Player (IPL) + Concussion sub.
                  Closes the More Options modal and opens the SubstitutionModal
                  for the chosen type. After confirm, the page re-pulls
                  session state so the new XI / striker / bowler are picked up. */}
              <button
                onClick={() => { setMoreOptionsOpen(false); setSubstitutionModal("IMPACT"); }}
                className="w-full bg-blue-500/15 border border-blue-500/40 text-blue-300 py-2 rounded"
              >
                IMPACT PLAYER
              </button>
              <button
                onClick={() => { setMoreOptionsOpen(false); setSubstitutionModal("CONCUSSION"); }}
                className="w-full bg-amber-500/15 border border-amber-500/40 text-amber-300 py-2 rounded"
              >
                CONCUSSION SUB
              </button>

              {/* Test day / session controls — only relevant for
                  Test-format matches. Counters persist per-match in
                  localStorage. Manual bump until backend day/session
                  state lands. */}
              {isTestMatch && (
                <div className="mt-2 p-3 rounded bg-bg-secondary/40 border border-bg-secondary">
                  <div className="text-text-secondary mb-2 text-[11px] uppercase tracking-wider">
                    Test day controls
                    <span className="ml-2 text-[10px] normal-case tracking-normal text-text-secondary opacity-70">
                      · breaks drive the day/session cycle
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mb-2 text-center text-[11px]">
                    <label className="bg-button-primary/10 border border-button-primary/30 rounded px-2 py-1 block">
                      <div className="text-text-secondary">Day</div>
                      <input
                        type="number"
                        min={1}
                        max={5}
                        value={testDay}
                        onChange={(e) => setTestDayManual(e.target.value)}
                        className="w-full bg-transparent text-center text-text-primary font-mono text-sm outline-none focus:bg-bg-secondary/60"
                      />
                    </label>
                    <label className="bg-amber-500/10 border border-amber-400/30 rounded px-2 py-1 block">
                      <div className="text-text-secondary">Session</div>
                      <input
                        type="number"
                        min={1}
                        max={3}
                        value={testSession}
                        onChange={(e) => setTestSessionManual(e.target.value)}
                        className="w-full bg-transparent text-center text-text-primary font-mono text-sm outline-none focus:bg-bg-secondary/60"
                      />
                    </label>
                    <label className="bg-emerald-500/10 border border-emerald-400/30 rounded px-2 py-1 block">
                      <div className="text-text-secondary">Ov today</div>
                      <input
                        type="number"
                        min={0}
                        max={90}
                        value={oversBowledToday}
                        readOnly
                        className="w-full bg-transparent text-center text-text-primary font-mono text-sm outline-none"
                      />
                    </label>
                  </div>

                  {/* Three explicit break buttons that drive the cycle:
                      Lunch → S2, Tea → S3, Stumps → Day+1 S1 (cap Day 5 S3).
                      Each also stamps the break_type on the next-ball
                      draft so the recorded ball carries the marker. */}
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      type="button"
                      onClick={() => { markTestLunch(); setMoreOptionsOpen(false); }}
                      disabled={testSession >= 2}
                      className="py-2 rounded bg-amber-500/20 border border-amber-500/40 text-amber-200 text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                      title="End Session 1 → start Session 2 (Afternoon)"
                    >
                      Lunch
                    </button>
                    <button
                      type="button"
                      onClick={() => { markTestTea(); setMoreOptionsOpen(false); }}
                      disabled={testSession >= 3}
                      className="py-2 rounded bg-amber-500/20 border border-amber-500/40 text-amber-200 text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                      title="End Session 2 → start Session 3 (Evening)"
                    >
                      Tea
                    </button>
                    <button
                      type="button"
                      onClick={() => { markTestStumps(); setMoreOptionsOpen(false); }}
                      disabled={testDay >= 5 && testSession >= 3}
                      className="py-2 rounded bg-button-primary/20 border border-button-primary/40 text-button-primary text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                      title={testDay >= 5 && testSession >= 3 ? "Final session — match should end here" : `Stumps · End of Day ${testDay} → start Day ${Math.min(testDay + 1, 5)}`}
                    >
                      Stumps
                    </button>
                  </div>
                  <p className="text-[10px] text-text-secondary mt-2">
                    Click <strong>Lunch / Tea / Stumps</strong> as breaks
                    happen — that's what advances the session and day.
                    90 overs is the day's quota (no cap per session — sessions
                    are time-driven). The Day / Session fields are editable
                    if the count drifts (rebuild, mid-day start).
                  </p>
                </div>
              )}

              {/* In Play / Paused — freezes scoring without ending the
                  innings. Operator can resume from the same menu.
                  Persists per-match in localStorage (see top of file). */}
              <div className="mt-2">
                <div className="text-text-secondary mb-2">Match state</div>
                <button
                  onClick={() => { toggleMatchPaused(); setMoreOptionsOpen(false); }}
                  className={`w-full py-2 rounded font-semibold ${
                    matchPaused
                      ? "bg-emerald-600 text-white"
                      : "bg-amber-500/20 border border-amber-500/50 text-amber-200"
                  }`}
                >
                  {matchPaused ? "▶ RESUME — IN PLAY" : "⏸ PAUSE MATCH"}
                </button>
                <p className="text-[10px] text-text-secondary mt-1">
                  {matchPaused
                    ? "Match paused — Save Ball is disabled until resumed."
                    : "Pause stops scoring. Use it for rain delays / breaks where you'll step away."}
                </p>
              </div>

              <div className="mt-2">
                <div className="text-text-secondary mb-2">Break</div>
                <button
                  onClick={() =>
                    setBall((prev) => ({
                      ...prev,
                      release: {
                        ...prev.release,
                        is_break: !prev.release?.is_break,
                        break_type: prev.release?.is_break
                          ? ""
                          : (prev.release?.break_type || ""),
                      },
                    }))
                  }
                  className={`w-full py-2 rounded ${
                    ball.release?.is_break
                      ? "bg-[#41264E] text-white"
                      : "bg-bg-secondary text-text-primary"
                  }`}
                >
                  {ball.release?.is_break ? "BREAK SELECTED" : "MARK BREAK"}
                </button>
                {ball.release?.is_break && (
                  <select
                    value={ball.release?.break_type ?? ""}
                    onChange={(e) =>
                      setBall((prev) => ({
                        ...prev,
                        release: {
                          ...prev.release,
                          break_type: e.target.value,
                        },
                      }))
                    }
                    className="w-full mt-2 bg-bg-secondary text-text-primary px-2 py-2 rounded"
                  >
                    <option value="">Select break type</option>
                    {BREAK_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              <div className="mt-2">
                <div className="text-text-secondary mb-2 flex items-center justify-between">
                  <span>Apply DLS</span>
                  {!isRainAffected && (
                    <span className="text-[10px] uppercase tracking-wider text-amber-300/80">
                      Enable Rain Affected first
                    </span>
                  )}
                </div>
                <div className={`flex gap-2 mb-2 ${!isRainAffected ? "opacity-40 pointer-events-none" : ""}`}>
                  <input
                    type="number"
                    value={dlsRuns}
                    onChange={(e) => setDlsRuns(e.target.value)}
                    placeholder="Revised target"
                    disabled={!isRainAffected}
                    className="w-full bg-bg-secondary text-text-primary px-2 py-1 rounded disabled:cursor-not-allowed"
                  />
                  <input
                    type="number"
                    value={dlsOvers}
                    onChange={(e) => setDlsOvers(e.target.value)}
                    placeholder="Revised overs"
                    disabled={!isRainAffected}
                    className="w-full bg-bg-secondary text-text-primary px-2 py-1 rounded disabled:cursor-not-allowed"
                  />
                </div>
                <button
                  onClick={handleApplyDls}
                  disabled={!isRainAffected}
                  className="w-full disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  APPLY DLS
                </button>
              </div>
              <div className="mt-4">
                <div className="text-text-secondary mb-2">Penalty Runs</div>
                <div className="grid grid-cols-2 gap-2 text-xs text-text-secondary mb-1">
                  <div>Runs</div>
                  <div>Awarded To</div>
                </div>
                <div className="flex gap-2 mb-2">
                  <input
                    type="number"
                    value={penaltyRuns}
                    onChange={(e) => setPenaltyRuns(e.target.value)}
                    placeholder="Runs"
                    className="w-full bg-bg-secondary text-text-primary px-2 py-1 rounded"
                  />
                  <select
                    value={penaltyAwardedTo}
                    onChange={(e) => setPenaltyAwardedTo(e.target.value)}
                    className="w-full bg-bg-secondary text-text-primary px-2 py-1 rounded"
                  >
                    <option value="BATTING">Batting</option>
                    <option value="BOWLING">Bowling</option>
                  </select>
                </div>
                <div className="text-xs text-text-secondary mb-1">Reason</div>
                <div className="flex gap-2 mb-2">
                  <select
                    value={penaltyReason}
                    onChange={(e) => setPenaltyReason(e.target.value)}
                    className="w-full bg-bg-secondary text-text-primary px-2 py-1 rounded"
                  >
                    <option value="">Select reason</option>
                    <option value="HELMET_ON_GROUND">Helmet on ground</option>
                    <option value="OBSTRUCTING_FIELD">
                      Obstructing the field
                    </option>
                    <option value="DAMAGE_PITCH">Damaging the pitch</option>
                    <option value="DANGEROUS_PLAY">Dangerous play</option>
                    <option value="TIME_WASTING">Time wasting</option>
                    <option value="UNFAIR_PLAY">Unfair play</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>
                {penaltyReason === "OTHER" && (
                  <input
                    type="text"
                    value={penaltyReasonOther}
                    onChange={(e) => setPenaltyReasonOther(e.target.value)}
                    placeholder="Other reason"
                    className="w-full bg-bg-secondary text-text-primary px-2 py-1 rounded mb-2"
                  />
                )}
                <button onClick={handleApplyPenalty} className="w-full">
                  APPLY PENALTY
                </button>
              </div>
            </div>
            </div>
          </div>
        </div>
      )}

      {followOnModalOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-bg-primary rounded-2xl max-w-md w-full border border-bg-secondary shadow-2xl p-6 modal-panel">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-text-primary">
                Follow-On Decision
              </h3>
              <button
                onClick={() => setFollowOnModalOpen(false)}
                className="text-text-secondary hover:text-text-primary transition-colors"
              >
                ✕
              </button>
            </div>
            {actions?.follow_on_lead != null && (
              <div className="mb-4 rounded-lg bg-bg-secondary px-3 py-2 text-sm text-text-primary">
                <div className="font-medium">
                  {actions?.follow_on_leading_team_name || "Team batting first"}
                  {" "}leads by{" "}
                  <span className="text-emerald-500">
                    {actions.follow_on_lead}
                  </span>{" "}
                  run{actions.follow_on_lead === 1 ? "" : "s"}.
                </div>
                <div className="text-xs text-text-secondary mt-0.5">
                  Lead is at or above the 200-run threshold, so the follow-on
                  is available (ICC Law 14, 5-day Test).
                </div>
              </div>
            )}
            <div className="text-text-secondary mb-4 text-sm">
              Enforce the follow-on? "Yes" makes the trailing side bat
              again immediately. "No" continues with the normal innings
              swap.
            </div>
            <div className="flex gap-2">
              <button
                disabled={followOnSubmitting}
                onClick={async () => {
                  if (followOnSubmitting) return;
                  setFollowOnSubmitting(true);
                  setFollowOnModalOpen(false);
                  try {
                    await handleStartNextInningsWithFollowOn(true);
                  } finally {
                    setFollowOnSubmitting(false);
                  }
                }}
                className="w-full py-2 rounded bg-button-primary text-text-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {followOnSubmitting ? "Working…" : "Yes, enforce"}
              </button>
              <button
                disabled={followOnSubmitting}
                onClick={async () => {
                  if (followOnSubmitting) return;
                  setFollowOnSubmitting(true);
                  setFollowOnModalOpen(false);
                  try {
                    await handleStartNextInningsWithFollowOn(false);
                  } finally {
                    setFollowOnSubmitting(false);
                  }
                }}
                className="w-full py-2 rounded bg-bg-secondary text-text-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {followOnSubmitting ? "Working…" : "No, normal innings"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 2026-06-06 (v3) — Match-tied permission popup. Adapts copy
          for first SO (regular tied chase) vs subsequent SO (the
          previous SO pair also tied). Multi-SO recursion is per
          founder spec: keep playing SOs until decisive. */}
      {superOverModalOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-bg-primary rounded-2xl max-w-md w-full border border-bg-secondary shadow-2xl p-6 modal-panel">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-text-primary">
                {isSuperOver ? "Super Over tied" : "Match tied"}
              </h3>
              <button
                onClick={() => setSuperOverModalOpen(false)}
                className="text-text-secondary hover:text-text-primary transition-colors"
              >
                ✕
              </button>
            </div>
            <div className="text-text-secondary mb-4 text-sm">
              {isSuperOver
                ? "The Super Over also tied. Play another Super Over, or end the match as a tie?"
                : "The match is tied. Start a Super Over to decide a winner, or end the match as a tie?"}
            </div>
            <div className="flex gap-2">
              <button
                onClick={async () => {
                  superOverPromptRespondedRef.current = true;
                  setSuperOverModalOpen(false);
                  await handleStartSuperOver();
                }}
                className="w-full py-2 rounded bg-button-primary text-text-primary"
              >
                {isSuperOver ? "Play another Super Over" : "Start Super Over"}
              </button>
              <button
                onClick={async () => {
                  superOverPromptRespondedRef.current = true;
                  setSuperOverModalOpen(false);
                  await handleEndCurrentMatch();
                }}
                className="w-full py-2 rounded bg-bg-secondary text-text-primary"
              >
                End as tie
              </button>
            </div>
          </div>
        </div>
      )}

      {umpireModalOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-bg-primary rounded-2xl max-w-md w-full border border-bg-secondary shadow-2xl modal-panel">
            <div className="p-6 border-b border-bg-secondary flex items-center justify-between">
              <h3 className="text-xl font-semibold text-text-primary">
                Select Umpires
              </h3>
              <button
                onClick={() => {
                  setUmpireModalOpen(false);
                  setUmpirePromptDismissed(true);
                  persistUmpiresForInnings(umpireBowlerEndId, umpireSquareLegId, true);
                }}
                className="text-text-secondary hover:text-text-primary transition-colors"
              >
                ✕
              </button>
            </div>
            <div className="p-6 space-y-3">
              <div>
                <label className="text-xs text-text-secondary">
                  Bowler End
                </label>
                <select
                  value={umpireBowlerEndId ?? ""}
                  onChange={(e) => setUmpireBowlerEndId(e.target.value || null)}
                  className="w-full bg-bg-secondary text-text-primary px-2 py-1 rounded"
                >
                  <option value="">None</option>
                  {/* Exclude whoever's at square leg so the same umpire can't
                      be assigned to both ends. */}
                  {(umpires || []).filter((u) => u.id !== umpireSquareLegId).map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.short_name || u.name || u.id}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-text-secondary">
                  Square Leg
                </label>
                <select
                  value={umpireSquareLegId ?? ""}
                  onChange={(e) => setUmpireSquareLegId(e.target.value || null)}
                  className="w-full bg-bg-secondary text-text-primary px-2 py-1 rounded"
                >
                  <option value="">None</option>
                  {(umpires || []).filter((u) => u.id !== umpireBowlerEndId).map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.short_name || u.name || u.id}
                    </option>
                  ))}
                </select>
              </div>
              {umpireBowlerEndId && umpireBowlerEndId === umpireSquareLegId && (
                <div className="text-[11px] text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded px-2 py-1">
                  Same umpire can't stand at both ends — pick a different umpire for one of the positions.
                </div>
              )}
              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => {
                    setUmpireBowlerEndId(null);
                    setUmpireSquareLegId(null);
                  }}
                  className="w-full py-2 rounded bg-bg-secondary text-text-primary"
                >
                  Clear
                </button>
                <button
                  onClick={() => {
                    setUmpireModalOpen(false);
                    setUmpirePromptDismissed(true);
                    persistUmpiresForInnings(umpireBowlerEndId, umpireSquareLegId, true);
                  }}
                  className="w-full py-2 rounded bg-button-primary text-text-primary"
                >
                  Done
                </button>
                <button
                  onClick={() => {
                    setUmpireModalOpen(false);
                    setUmpirePromptDismissed(true);
                    persistUmpiresForInnings(null, null, true);
                  }}
                  className="w-full py-2 rounded bg-bg-secondary text-text-primary"
                >
                  Skip
                </button>
              </div>
              <div className="text-[10px] text-text-secondary">
                If only one umpire is selected, they will remain at bowler end.
              </div>
            </div>
          </div>
        </div>
      )}

      {wicketKeeperModalOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-bg-primary rounded-2xl max-w-md w-full border border-bg-secondary shadow-2xl modal-panel">
            <div className="p-6 border-b border-bg-secondary flex items-center justify-between">
              <h3 className="text-xl font-semibold text-text-primary">
                Select Wicket Keeper
              </h3>
              <button
                onClick={() => setWicketKeeperModalOpen(false)}
                className="text-text-secondary hover:text-text-primary transition-colors"
              >
                ✕
              </button>
            </div>
            <div className="p-6 space-y-3 max-h-96 overflow-y-auto">
              {(players?.bowlers ?? []).length === 0 ? (
                <div className="text-text-secondary">No players available</div>
              ) : (
                <>
                  <div className="text-[10px] uppercase tracking-wider text-text-secondary px-1">Playing XI</div>
                  {players.bowlers.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => {
                        setWicketKeeperManually(p.id);
                        setWicketKeeperModalOpen(false);
                      }}
                      className="w-full text-left px-4 py-3 bg-bg-main text-text-primary rounded-lg border border-bg-secondary hover:border-button-primary/30 transition-colors"
                    >
                      {getDisplayName(p)}
                    </button>
                  ))}
                  {(subFielders || []).length > 0 && (
                    <>
                      <div className="text-[10px] uppercase tracking-wider text-text-secondary px-1 mt-3">Substitutes (squad reserves)</div>
                      {subFielders.map((p) => (
                        <button
                          key={p.id}
                          onClick={() => {
                            setWicketKeeperManually(p.id);
                            setWicketKeeperModalOpen(false);
                          }}
                          className="w-full text-left px-4 py-3 bg-bg-main text-text-primary rounded-lg border border-emerald-500/30 hover:border-emerald-500/60 transition-colors"
                        >
                          {getDisplayName(p)} <span className="text-[10px] text-emerald-300">· sub</span>
                        </button>
                      ))}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── T220.5 iPad-landscape score strip ──────────────────────────
          When the BBP is in iPad-landscape mode (1024–1279 landscape) we
          hide the score column to give the spatial + activity columns
          full real estate side-by-side. The operator still needs to see
          the headline numbers (team / runs / overs / target) at all
          times — this slim bar shows them, parked just above the T220.2
          action bar. Hidden on phones (the existing compact header
          already serves) and on desktop (which has the inline score
          column). */}
      {isIpadLandscape && (
        <div
          className="fixed left-0 right-0 z-40 bg-bg-primary/95 backdrop-blur border-t border-border-default flex items-center justify-center gap-3 px-3 py-1.5 text-xs"
          style={{ bottom: "calc(3.5rem + env(safe-area-inset-bottom, 0px))" }}
        >
          <span className="font-semibold text-text-primary truncate max-w-[200px]">
            {battingTeamName || "Batting"}
          </span>
          <span className="font-bold font-mono text-text-primary text-sm">
            {aggregate?.runs ?? 0}
            <span className="text-text-secondary">/</span>
            {aggregate?.wickets ?? 0}
          </span>
          <span className="text-text-secondary font-mono text-[11px]">
            ({formatInningsProgress(aggregate?.match_type, { legalBalls: aggregate?.legal_balls ?? 0, completedOvers: aggregate?.completed_overs ?? 0 })})
          </span>
          {currentRunRate != null && (
            <span className="text-text-secondary font-mono text-[11px]">CRR {currentRunRate.toFixed(2)}</span>
          )}
          {requiredRunRate != null && (
            <span className="text-amber-300 font-mono text-[11px]">RRR {requiredRunRate.toFixed(2)}</span>
          )}
          {aggregate?.is_chasing && (aggregate?.revised_target_runs != null || aggregate?.target_runs != null) && (
            <span className="text-amber-200 font-mono text-[11px]">
              tgt {aggregate?.revised_target_runs ?? aggregate?.target_runs}
            </span>
          )}
        </div>
      )}

      {/* ── T220.2 Fixed-bottom action bar (mobile / tablet only) ──────
          Phone / iPad users need PTT mic + Save Ball reachable from any
          tab — without this they had to switch back to the Score tab
          every time. Sits at the viewport bottom with safe-area-inset
          so iOS Safari's home indicator doesn't obscure it. z-40 so it
          beats column content (which is z-0 / unstacked) but stays
          below modals (z-[60]+) and ScorerChat (z-[65] fullscreen).
          xl:hidden so desktop sees the existing inline action bar
          unchanged.
          The host BBP scroll container (min-h-dvh on mobile) gets
          `pb-16` so scrolled content doesn't hide behind the bar. */}
      <div
        className="xl:hidden fixed left-0 right-0 bottom-0 z-40 bg-bg-primary/95 backdrop-blur border-t border-border-default flex items-center gap-2 px-3 py-2"
        style={{ paddingBottom: "calc(0.5rem + env(safe-area-inset-bottom, 0px))" }}
      >
        {voice?.ptt?.supported && (
          <button
            type="button"
            // 2026-05-28 — unified pointer events + pointer capture. Old
            // mouse+touch dual handlers had two bugs: (a) onMouseLeave
            // killed recording the instant the operator's finger drifted
            // off the button mid-press; (b) touch sometimes also fires
            // synthesized mouse events, double-firing startRecording.
            // setPointerCapture pins pointerup to this button regardless
            // of finger position, fixing both.
            onPointerDown={(e) => {
              e.preventDefault();
              try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* ignore */ }
              voice.ptt.startRecording();
            }}
            onPointerUp={(e) => {
              try { if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
              voice.ptt.stopRecording();
            }}
            onPointerCancel={(e) => {
              try { if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
              if (voice.ptt.recording) voice.ptt.stopRecording();
            }}
            onContextMenu={(e) => e.preventDefault()}
            disabled={!voice.ptt.supported}
            aria-pressed={!!voice.ptt.recording}
            title={
              voice.ptt.recording ? "Release to transcribe" :
              voice.ptt.transcribing ? "Transcribing…" :
              "Hold to record (Whisper)"
            }
            className={`relative overflow-hidden flex items-center justify-center w-14 h-12 rounded-xl font-semibold text-xs touch-manipulation transition-colors select-none shrink-0 ${
              voice.ptt.recording ? "bg-red-500/30 border border-red-500/60 text-red-100" :
              voice.ptt.transcribing ? "bg-amber-500/20 border border-amber-500/50 text-amber-200" :
              "bg-purple-500/20 border border-purple-500/50 text-purple-200"
            }`}
          >
            <Mic className="w-5 h-5" />
          </button>
        )}
        <button
          type="button"
          data-testid="bbb-save"
          onClick={fastSaveMode ? performImmediateSave : startDelayedSave}
          onDoubleClick={performImmediateSave}
          disabled={saveDisabled || saveInFlight}
          // 2026-05-28 — was flex-1 (greedy fill). Capped at max-w-[180px]
          // so the SAVE BALL button doesn't stretch into the History /
          // How-to-code floating pills' visual lane on the right edge.
          // The floating pills are also pushed above the action bar
          // (see CodingWalkthrough.jsx + the history toggle).
          className="relative overflow-hidden flex-1 max-w-[180px] h-11 rounded-xl font-bold text-sm bg-button-primary text-white shadow-sm disabled:opacity-40 disabled:bg-bg-secondary disabled:text-text-secondary touch-manipulation"
          title={
            strikerNonStrikerCollision
              ? "Striker = non-striker. Fix that first."
              : fastSaveMode ? "Quick mode ON" : "Tap=1s save / double-tap=instant"
          }
        >
          {savePending
            ? (editingBallId ? "Updating in 1s…" : "Saving in 1s…")
            : saveInFlight
              ? (editingBallId ? "Updating…" : "Saving…")
              : (editingBallId ? "UPDATE BALL" : "SAVE BALL")}
          {savePending && (
            <span
              className="absolute left-0 top-0 h-full w-full bg-white/25"
              style={{
                transformOrigin: "left center",
                animation: `zcd-save-progress ${SAVE_DELAY_MS}ms linear forwards`,
              }}
            />
          )}
        </button>
        <button
          type="button"
          onClick={handleUndoBallRequest}
          disabled={(aggregate?.balls_recorded ?? 0) === 0}
          className="flex items-center justify-center w-12 h-12 rounded-xl bg-bg-secondary border border-border-default text-text-primary disabled:opacity-40 touch-manipulation"
          title="Undo last ball"
        >
          <RotateCcw className="w-4 h-4" />
        </button>
        {/* 2026-06-06 — MORE moved here for mobile. The desktop SAVE / Quick
            / MORE strip is hidden on phones (founder ask: top row was
            clipped under the SCORE tab), so the bottom sticky bar needs
            to surface MORE for Match Settings / Penalty / End innings /
            Phantom-ball flows. */}
        <button
          type="button"
          onClick={() => setMoreOptionsOpen(true)}
          className="flex items-center justify-center w-12 h-12 rounded-xl bg-bg-secondary border border-border-default text-text-primary text-[10px] font-semibold tracking-wider touch-manipulation"
          title="More options (Match Settings, Penalty, End Innings…)"
        >
          MORE
        </button>
      </div>
    </div>
  );
}

/**
 * ActivityWizard — prompts the scorer through analytics one step at a time.
 *
 * Order matches a delivery's natural rhythm: bowler → ball-flight → batter response → outcome.
 * Skips Control automatically when Air Movement is "Ground".
 * Auto-advances on selection; lets you click any step pill to revisit.
 */
